import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  FormEvent,
  TouchEvent,
} from "react";
import "./App.css";
import { supabase } from "./supabaseClient";
import BankImportModal from "./BankImportModal";
import ActionMenu from "./components/ActionMenu";

// Security: Escape HTML to prevent XSS attacks
function escapeHtml(str: string | number | null | undefined): string {
  if (str === null || str === undefined) return "";
  const s = String(str);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

type Trainer = {
  id: string;
  name: string;
  nachname?: string;
  email?: string;
  telefon?: string;
  stundensatz?: number;
  notiz?: string;
  adresse?: string;
  iban?: string;
  ustIdNr?: string;
  kleinunternehmer?: boolean;
};

type SepaSequenz = "FRST" | "RCUR" | "OOFF" | "FNAL";
type SepaLastschriftart = "CORE" | "B2B";

// Eine Zeile im Camp-SEPA-Export (Betrag pro Teilnehmer individuell anpassbar)
type CampSepaRow = {
  spielerId: string;
  name: string;        // Kontoinhaber (ggf. abweichender Empfänger)
  spielerName: string; // Name des Teilnehmers
  iban: string;
  mandatsreferenz: string;
  unterschriftsdatum: string;
  sequenz: SepaSequenz;
  lastschriftart: SepaLastschriftart;
  betrag: number;
};

type Spieler = {
  id: string;
  vorname: string;
  nachname?: string;
  kontaktEmail?: string;
  zusaetzlicheEmails?: string[];
  kontaktTelefon?: string;
  rechnungsAdresse?: string;
  notizen?: string;
  iban?: string;
  bankname?: string;
  mandatsreferenz?: string;
  unterschriftsdatum?: string;
  sepaSequenz?: SepaSequenz;
  sepaLastschriftart?: SepaLastschriftart;
  // Abweichender Rechnungsempfänger (z.B. Eltern bei Kindern)
  abweichenderEmpfaenger?: boolean;
  empfaengerName?: string;
  // Labels für Newsletter-Filterung
  labels?: string[];
  // Archiviert: Spieler nicht mehr aktiv, bleibt in den Daten erhalten
  // (alte Trainings/Abrechnungen unverändert), wird aber aus aktiven Listen ausgeblendet
  archiviert?: boolean;
};

const GLAEUBIGER_ID = "DE58ZZZ00002765947";
const GLAEUBIGER_NAME = "Tennisschule Zlatan Palazov und Artur Ivanenko GbR";
const GLAEUBIGER_IBAN = "DE74160400000136875200";
const GLAEUBIGER_BIC = "COBADEFFXXX";

type Tarif = {
  id: string;
  name: string;
  preisProStunde: number;
  abrechnung: "proTraining" | "proSpieler" | "monatlich";
  beschreibung?: string;
  // Archiviert: Tarif nicht mehr wählbar, bleibt aber für bestehende Trainings/Abrechnungen erhalten
  archiviert?: boolean;
};

type TrainingStatus = "geplant" | "durchgefuehrt" | "abgesagt";

type AbrechnungTab = "spieler" | "trainer";
type VerwaltungTab = "spieler" | "trainer" | "tarife" | "newsletter";
type FormulareTab = "anmeldung" | "sepa" | "tenniscamp" | "probetraining" | "kennlerntennis";

type Verfuegbarkeit = {
  montag: string;
  dienstag: string;
  mittwoch: string;
  donnerstag: string;
  freitag: string;
  samstag: string;
  sonntag: string;
};

type RegistrationRequest = {
  id: string;
  account_id: string;
  name: string;
  email: string;
  telefon: string | null;
  verfuegbarkeit: Verfuegbarkeit | null;
  trainingsart: string | null;
  trainings_pro_woche: number | null;
  erfahrungslevel: string | null;
  alter_jahre: number | null;
  nachricht: string | null;
  created_at: string;
  status: string;
  anlage?: string | null;
  ist_vereinsmitglied?: boolean | null;
  trainee_vorname?: string | null;
  trainee_nachname?: string | null;
  kontakt_vorname?: string | null;
  kontakt_nachname?: string | null;
  kontakt_strasse?: string | null;
  kontakt_plz?: string | null;
  kontakt_ort?: string | null;
  abweichende_kontaktperson?: boolean | null;
  gruppenwuensche?: string | null;
  archiviert?: boolean | null;
};

function getRegistrationTraineeName(req: RegistrationRequest): string {
  const v = (req.trainee_vorname || "").trim();
  const n = (req.trainee_nachname || "").trim();
  const full = `${v} ${n}`.trim();
  if (full) return full;
  return (req.name || "").trim();
}

function getRegistrationKontaktName(req: RegistrationRequest): string {
  const v = (req.kontakt_vorname || "").trim();
  const n = (req.kontakt_nachname || "").trim();
  const full = `${v} ${n}`.trim();
  if (full) return full;
  return (req.name || "").trim();
}

function getRegistrationKontaktAdresse(req: RegistrationRequest): string {
  const strasse = (req.kontakt_strasse || "").trim();
  const plz = (req.kontakt_plz || "").trim();
  const ort = (req.kontakt_ort || "").trim();
  if (!strasse && !plz && !ort) return "";
  return `${strasse}${strasse ? ", " : ""}${plz} ${ort}`.trim();
}

function isRegistrationAbweichend(req: RegistrationRequest): boolean {
  if (req.abweichende_kontaktperson != null) return req.abweichende_kontaktperson;
  const trainee = getRegistrationTraineeName(req).toLowerCase();
  const kontakt = getRegistrationKontaktName(req).toLowerCase();
  return Boolean(trainee && kontakt && trainee !== kontakt);
}

type SepaMandate = {
  id: string;
  account_id: string;
  vorname: string;
  nachname: string;
  ist_kind: boolean;
  elternteil_name: string | null;
  strasse: string;
  plz: string;
  ort: string;
  iban: string;
  email: string;
  telefon?: string | null;
  mandatsreferenz: string;
  unterschriftsdatum: string;
  created_at: string;
  status?: string;
  anlage?: string;
  archiviert?: boolean | null;
};

type TenniscampAnmeldung = {
  id: string;
  account_id: string;
  camp_id: string;
  camp_label: string;
  camp_dates: string;
  camp_type: "kind" | "erwachsene";
  teilnehmer_vorname: string;
  teilnehmer_nachname: string;
  zahlungspflichtiger_vorname: string | null;
  zahlungspflichtiger_nachname: string | null;
  alter: number;
  telefon: string;
  email: string;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  iban: string;
  bemerkungen: string | null;
  niveau: string | null;
  spielstand_beschreibung: string | null;
  mitglied: boolean | null;
  mandatsreferenz: string;
  sepa_zustimmung: boolean;
  status: string;
  created_at: string;
  archiviert?: boolean | null;
};

type ProbetrainingAnfrage = {
  id: string;
  account_id: string;
  vorname: string;
  nachname: string;
  alter: number;
  hat_tennis_gespielt: boolean;
  spielstand: string;
  spielstaerke_beschreibung?: string | null;
  trainingsart?: string;
  anlage?: string;
  ist_vereinsmitglied: boolean;
  email: string | null;
  telefon: string | null;
  verfuegbarkeit: Record<string, string>;
  status: string;
  created_at: string;
  archiviert?: boolean | null;
};

type KennlerntennisAnfrage = {
  id: string;
  account_id: string;
  vorname: string;
  nachname: string;
  alter: number;
  email: string;
  telefon: string;
  spielstand: string;
  spielstaerke_beschreibung?: string | null;
  termin?: string | null;
  ist_vereinsmitglied: boolean;
  // Wird seit 08/2026 nicht mehr abgefragt - bei neuen Anfragen null.
  interesse_weiterfuehrend?: boolean | null;
  status: string;
  created_at: string;
  archiviert?: boolean | null;
};

type Training = {
  id: string;
  trainerId?: string;
  // Tenniscamp: mehrere Trainer führen das Camp durch; jeder bekommt seinen
  // normalen Stundenlohn für die Camp-Stunden. trainerId bleibt der erste/primäre.
  trainerIds?: string[];
  datum: string;
  uhrzeitVon: string;
  uhrzeitBis: string;
  spielerIds: string[];
  tarifId?: string;
  status: TrainingStatus;
  notiz?: string;
  serieId?: string;
  customPreisProStunde?: number;
  customAbrechnung?: "proTraining" | "proSpieler";
  barBezahlt?: boolean;
  anlage?: string;
  isSpontanBuchung?: boolean;
  isPrivat?: boolean;
  isTenniscamp?: boolean; // Tenniscamp über einen Zeitraum: keine Spieler-Abrechnung, Trainer wird normal vergütet
  campGebuehr?: number; // Tenniscamp: fester Teilnehmer-Beitrag fürs ganze Camp (nur für SEPA-XML, keine Monatsabrechnung); wird auf allen Tagen der Serie gespiegelt
  cancelFee?: number; // pro-Spieler-Betrag bei Absage mit Teilgebühr (nur proTraining/proSpieler)
  actualMinutes?: number; // tatsächliche Dauer in Minuten (wenn Training nur teilweise durchgeführt)
};

type SpontaneStundeBuchung = {
  name: string;
  email: string;
  telefon?: string;
  gebuchtAm: string;
  hinweis?: string;
};

type SpontaneStunde = {
  id: string;
  datum: string;
  uhrzeitVon: string;
  uhrzeitBis: string;
  trainerId: string;
  tarifId?: string;
  customPreisProStunde?: number;
  status: "offen" | "gebucht";
  anlage: "Wedding" | "Britz";
  veroeffentlicht: boolean;
  buchung?: SpontaneStundeBuchung;
  trainingId?: string;
};

type PaymentsMap = Record<string, boolean>; // key: `${monat}__${spielerId}`
// key: trainingId -- beim Tenniscamp mit mehreren Trainern `${trainingId}__${trainerId}`,
// weil sich dort mehrere Trainer denselben Termin teilen (siehe trainerPaymentKey)
type TrainerPaymentsMap = Record<string, boolean>;
type TrainerMonthSettledMap = Record<string, boolean>; // key: `${monat}__${trainerId}`
type MonthlyAdjustments = Record<string, number>; // key: `${monat}__${spielerId}`, value: Anpassungsbetrag in EUR
type WirdAbgebuchtMap = Record<string, boolean>; // key: `${monat}__${spielerId}`

type Notiz = {
  id: string;
  titel: string;
  inhalt: string;
  erstelltAm: string;
  aktualisiertAm: string;
};

type Vertretung = {
  trainingId: string;
  vertretungTrainerId?: string; // Optional - wenn leer, dann "Vertretung offen"
};

type WeiteresTabs = "vertretung" | "spontan" | "rechner";

type TrainerZuschlag = {
  id: string;
  betrag: number; // positiv = Zuschlag, negativ = Abzug
  notiz: string;
};
type TrainerZuschlagMap = Record<string, TrainerZuschlag[]>; // key: `${month}__${trainerId}`

type AppState = {
  trainers: Trainer[];
  spieler: Spieler[];
  tarife: Tarif[];
  trainings: Training[];
  payments: PaymentsMap;
  trainerPayments: TrainerPaymentsMap;
  trainerMonthSettled?: TrainerMonthSettledMap;
  trainerBarSettled?: TrainerMonthSettledMap;
  notizen?: Notiz[];
  monthlyAdjustments?: MonthlyAdjustments;
  vertretungen?: Vertretung[];
  wirdAbgebucht?: WirdAbgebuchtMap;
  trainerHonorarAnpassungen?: Record<string, number>; // trainingId -> manuell gesetztes Honorar
  trainerZuschlaege?: TrainerZuschlagMap;
};

type Tab = "kalender" | "training" | "verwaltung" | "formulare" | "abrechnung" | "weiteres";
type Role = "admin" | "trainer";

type AuthUser = {
  id: string;
  email: string | null;
  role: Role;
  accountId: string | null;
  trainerId: string | null;
};

type ViewMode = "week" | "day";
type AbrechnungFilter = "alle" | "bezahlt" | "offen" | "bar";

const STORAGE_KEY = "tennis_planner_multi_trainer_v6";
const TRAINER_INVOICE_SETTINGS_KEY = "trainer_invoice_settings";
const LEGACY_KEYS = [
  "tennis_planner_single_trainer",
  "tennis_planner_single_trainer_v5",
  "tennis_planner_single_trainer_v4",
  "tennis_planner_single_trainer_v3",
  "tennis_planner_single_trainer_v2",
  "tennis_planner_single_trainer_v1",
];

function uid() {
  return crypto.randomUUID();
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toIsoDate(raw: string): string {
  if (!raw) return "";
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
  }
  return s;
}

function nowISOSeconds() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function normalizeIban(raw: string): string {
  return (raw || "").replace(/\s+/g, "").toUpperCase();
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function sanitizeSepaName(s: string): string {
  // SEPA erlaubt nur bestimmte Zeichen, Umlaute werden ersetzt
  return s
    .replace(/ä/g, "ae").replace(/Ä/g, "Ae")
    .replace(/ö/g, "oe").replace(/Ö/g, "Oe")
    .replace(/ü/g, "ue").replace(/Ü/g, "Ue")
    .replace(/ß/g, "ss")
    .replace(/[^A-Za-z0-9 .,'/\-+()&]/g, "")
    .trim()
    .substring(0, 70);
}

function parseCsvSemicolon(text: string): string[][] {
  // Einfacher CSV-Parser für Semikolon-getrennte Zeilen ohne Anführungszeichen
  const cleaned = text.replace(/^\uFEFF/, ""); // BOM entfernen
  return cleaned
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0)
    .map((line) => line.split(";").map((c) => c.trim()));
}

function MONATE_DE(yyyymm: string): string {
  const months = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const [y, m] = yyyymm.split("-").map(Number);
  if (!m || m < 1 || m > 12) return yyyymm;
  return `${months[m - 1]} ${y}`;
}

function toMinutes(hhmm: string) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function startOfWeekISO(dateISO: string) {
  const d = new Date(dateISO + "T12:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysISO(dateISO: string, days: number) {
  const d = new Date(dateISO + "T12:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatShort(dateISO: string) {
  const d = new Date(dateISO + "T12:00:00");
  const w = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"][(d.getDay() + 6) % 7];
  return `${w} ${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.`;
}

function formatWeekRange(weekStartISO: string) {
  const start = new Date(weekStartISO + "T12:00:00");
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const months = [
    "Jan.",
    "Feb.",
    "März",
    "Apr.",
    "Mai",
    "Juni",
    "Juli",
    "Aug.",
    "Sep.",
    "Okt.",
    "Nov.",
    "Dez.",
  ];

  const sDay = start.getDate();
  const eDay = end.getDate();
  const sMonth = start.getMonth();
  const eMonth = end.getMonth();
  const year = end.getFullYear();

  if (sMonth === eMonth) {
    return `${sDay} – ${eDay}. ${months[eMonth]} ${year}`;
  }
  return `${sDay}. ${months[sMonth]} – ${eDay}. ${months[eMonth]} ${year}`;
}

function formatMonthLabel(monthISO: string) {
  const parts = monthISO.split("-");
  const year = parts[0] ?? "";
  const month = parts[1] ?? "";
  return `${pad2(Number(month))}.${year}`;
}

function euro(n: number) {
  if (!Number.isFinite(n)) return "0,00 EUR";
  return `${n.toFixed(2).replace(".", ",")} EUR`;
}

function maskIban(iban: string | undefined): string {
  if (!iban) return "---";
  const cleaned = iban.replace(/\s/g, "");
  if (cleaned.length <= 8) return iban;
  const first4 = cleaned.slice(0, 4);
  const last4 = cleaned.slice(-4);
  return `${first4} **** **** **** ${last4}`;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

function statusLabel(s: TrainingStatus) {
  return s === "geplant"
    ? "offen"
    : s === "durchgefuehrt"
    ? "durchgeführt"
    : "abgesagt";
}

function statusDotColor(s: TrainingStatus) {
  if (s === "durchgefuehrt") return "#22c55e";
  if (s === "abgesagt") return "#ef4444";
  return "#3b82f6";
}

function paymentKey(monat: string, spielerId: string) {
  return `${monat}__${spielerId}`;
}

function trainerMonthSettledKey(monat: string, trainerId: string) {
  return `${monat}__${trainerId}`;
}

function weekdayOccurrencesInMonth(monthISO: string, weekday: number): number {
  const [year, month] = monthISO.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  let count = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    if (new Date(year, month - 1, d).getDay() === weekday) count++;
  }
  return count;
}

function durationMin(von: string, bis: string) {
  const a = toMinutes(von);
  const b = toMinutes(bis);
  return Math.max(0, b - a);
}

// Anzahl der Stunden eines Trainings (Doppelstunde = 2). Bei verkürzter
// Stunde wird die tatsächliche Dauer (actualMinutes) verwendet.
function trainingStundenAnzahl(t: Training) {
  const planned = durationMin(t.uhrzeitVon, t.uhrzeitBis);
  const actual =
    t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < planned
      ? t.actualMinutes
      : planned;
  return actual / 60;
}

// Summiert die Stunden einer Trainingsliste (Doppelstunden zählen doppelt).
function summeStunden(list: Training[]) {
  return round2(list.reduce((acc, t) => acc + trainingStundenAnzahl(t), 0));
}

// Zeitspanne fuer Listen: bei verkuerzter Stunde die tatsaechliche Dauer mit anzeigen.
function zeitspanneAnzeige(t: Training) {
  const planned = durationMin(t.uhrzeitVon, t.uhrzeitBis);
  const zeit = `${t.uhrzeitVon} - ${t.uhrzeitBis}`;
  return t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < planned
    ? `${zeit} (${t.actualMinutes} Min)`
    : zeit;
}

function getFullName(s: Spieler) {
  return s.nachname ? `${s.vorname} ${s.nachname}` : s.vorname;
}

function getPreisConfig(
  t: Training,
  tarifByIdMap: Map<string, Tarif>
): {
  preisProStunde: number;
  abrechnung: "proTraining" | "proSpieler" | "monatlich";
} | null {
  if (t.tarifId) {
    const tarif = tarifByIdMap.get(t.tarifId);
    if (tarif) {
      return {
        preisProStunde: tarif.preisProStunde,
        abrechnung: tarif.abrechnung,
      };
    }
  }

  if (
    typeof t.customPreisProStunde === "number" &&
    t.customPreisProStunde > 0
  ) {
    return {
      preisProStunde: t.customPreisProStunde,
      abrechnung: t.customAbrechnung ?? "proTraining",
    };
  }

  return null;
}

function generateFinalInvoiceHTML(data: {
  rechnungssteller: string;
  adresse: string;
  ustIdNr: string;
  rechnungsnummer: string;
  rechnungsdatum: string;
  leistungszeitraum: string;
  positionBeschreibung: string;
  stundenAnzahl: number;
  preisProStunde: number;
  iban: string;
  kleinunternehmer: boolean;
  useCustomTotal?: boolean;
  customGesamtbetrag?: number;
}): string {
  const {
    rechnungssteller,
    adresse,
    ustIdNr,
    rechnungsnummer,
    rechnungsdatum,
    leistungszeitraum,
    positionBeschreibung,
    stundenAnzahl,
    preisProStunde,
    iban,
    kleinunternehmer,
    useCustomTotal,
    customGesamtbetrag,
  } = data;

  const zwischensumme = stundenAnzahl * preisProStunde;
  // Bei manuellem Gesamtbetrag: Korrektur berechnen (Differenz zwischen gewünschtem und berechnetem Betrag)
  const korrektur = useCustomTotal && customGesamtbetrag !== undefined
    ? customGesamtbetrag - zwischensumme * (kleinunternehmer ? 1 : 1.19)
    : 0;
  const zwischensummeMitKorrektur = zwischensumme + korrektur;
  const mwst = kleinunternehmer ? 0 : zwischensummeMitKorrektur * 0.19;
  const gesamtbetrag = zwischensummeMitKorrektur + mwst;

  const formatEuro = (amount: number) => amount.toFixed(2).replace('.', ',') + ' €';
  const adresseHtml = adresse.split('\n').map(line => `${line}`).join('<br>');

  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Rechnung ${rechnungsnummer}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12pt;
      line-height: 1.5;
      color: #333;
      padding: 2cm;
      max-width: 21cm;
      margin: 0 auto;
    }
    .header {
      margin-bottom: 2cm;
    }
    .title {
      font-size: 24pt;
      font-weight: bold;
      margin-bottom: 1.5cm;
      color: #1a1a1a;
    }
    .addresses {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1cm;
    }
    .address-block {
      width: 45%;
    }
    .address-label {
      font-size: 10pt;
      color: #666;
      margin-bottom: 0.3cm;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .address-content {
      font-size: 11pt;
    }
    .meta-info {
      margin-top: 1cm;
      margin-bottom: 1cm;
      padding: 0.5cm;
      background-color: #f8f8f8;
      border-radius: 4px;
    }
    .meta-row {
      display: flex;
      margin-bottom: 0.2cm;
    }
    .meta-label {
      width: 160px;
      font-weight: bold;
    }
    .content {
      margin-top: 1cm;
      margin-bottom: 1cm;
    }
    .intro {
      margin-bottom: 1cm;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1cm 0;
    }
    th, td {
      padding: 0.3cm 0.5cm;
      text-align: left;
      border-bottom: 1px solid #ddd;
    }
    th {
      background-color: #f5f5f5;
      font-weight: bold;
    }
    .text-right {
      text-align: right;
    }
    .summary-table {
      width: 50%;
      margin-left: auto;
      margin-top: 0.5cm;
    }
    .summary-table td {
      border-bottom: none;
      padding: 0.2cm 0.5cm;
    }
    .summary-table .total-row td {
      border-top: 2px solid #333;
      font-weight: bold;
      font-size: 14pt;
      padding-top: 0.4cm;
    }
    .kleinunternehmer-note {
      margin-top: 0.5cm;
      font-size: 10pt;
      color: #666;
      font-style: italic;
    }
    .payment-info {
      margin-top: 1.5cm;
      padding: 0.5cm;
      background-color: #f0f7ff;
      border-radius: 4px;
    }
    .payment-info strong {
      display: block;
      margin-bottom: 0.3cm;
    }
    .footer {
      margin-top: 2cm;
    }
    .signature {
      margin-top: 1.5cm;
    }
    @media print {
      body {
        padding: 0;
      }
      @page {
        margin: 2cm;
        size: A4;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="title">RECHNUNG</div>

    <div class="addresses">
      <div class="address-block">
        <div class="address-label">Rechnungssteller</div>
        <div class="address-content">
          <strong>${rechnungssteller}</strong><br>
          ${adresseHtml}
          ${ustIdNr ? `<br>Steuernummer: ${ustIdNr}` : ''}
        </div>
      </div>
      <div class="address-block">
        <div class="address-label">Rechnungsempfänger</div>
        <div class="address-content">
          <strong>Tennisschule Zlatan Palazov und<br>Artur Ivanenko GbR</strong><br>
          Ricarda-Huch-Straße 40<br>
          14480 Potsdam
        </div>
      </div>
    </div>

    <div class="meta-info">
      <div class="meta-row">
        <span class="meta-label">Rechnungsnummer:</span>
        <span>${rechnungsnummer}</span>
      </div>
      <div class="meta-row">
        <span class="meta-label">Rechnungsdatum:</span>
        <span>${rechnungsdatum}</span>
      </div>
      <div class="meta-row">
        <span class="meta-label">Leistungszeitraum:</span>
        <span>${leistungszeitraum}</span>
      </div>
    </div>
  </div>

  <div class="content">
    <div class="intro">
      <p>Sehr geehrte Damen und Herren,</p>
      <p>für die im Leistungszeitraum erbrachten Trainerstunden erlaube ich mir, folgende Rechnung zu stellen:</p>
    </div>

    <table>
      <thead>
        <tr>
          <th>Position</th>
          <th class="text-right">Anzahl</th>
          <th class="text-right">Preis</th>
          <th class="text-right">Gesamt</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${positionBeschreibung}</td>
          <td class="text-right">${stundenAnzahl}</td>
          <td class="text-right">${formatEuro(preisProStunde)}</td>
          <td class="text-right">${formatEuro(zwischensumme)}</td>
        </tr>
        ${useCustomTotal && korrektur !== 0 ? `
        <tr>
          <td>Korrektur</td>
          <td class="text-right"></td>
          <td class="text-right"></td>
          <td class="text-right">${formatEuro(korrektur)}</td>
        </tr>
        ` : ''}
      </tbody>
    </table>

    <table class="summary-table">
      <tbody>
        <tr>
          <td>Zwischensumme:</td>
          <td class="text-right">${formatEuro(zwischensummeMitKorrektur)}</td>
        </tr>
        ${!kleinunternehmer ? `
        <tr>
          <td>MwSt. 19%:</td>
          <td class="text-right">${formatEuro(mwst)}</td>
        </tr>
        ` : ''}
        <tr class="total-row">
          <td>Gesamtbetrag:</td>
          <td class="text-right">${formatEuro(gesamtbetrag)}</td>
        </tr>
      </tbody>
    </table>

    ${kleinunternehmer ? `
    <div class="kleinunternehmer-note">
      Gemäß §19 UStG wird keine Umsatzsteuer berechnet.
    </div>
    ` : ''}

    <div class="payment-info">
      <strong>Bitte überweisen Sie den Betrag innerhalb von 14 Tagen auf folgendes Konto:</strong>
      <div class="meta-row">
        <span class="meta-label">IBAN:</span>
        <span>${maskIban(iban)}</span>
      </div>
      <div class="meta-row">
        <span class="meta-label">Kontoinhaber:</span>
        <span>${rechnungssteller}</span>
      </div>
    </div>
  </div>

  <div class="footer">
    <p>Vielen Dank für die Zusammenarbeit.</p>
    <div class="signature">
      <p>Mit freundlichen Grüßen</p>
      <p><strong>${rechnungssteller}</strong></p>
    </div>
  </div>
</body>
</html>`;
}

function ensureTrainerList(
  parsed: Partial<AppState> & {
    trainer?: Trainer | { name: string; email?: string };
  }
): Trainer[] {
  const inputList = Array.isArray(parsed?.trainers) ? parsed!.trainers : [];
  const normalized = inputList
    .filter(Boolean)
    .map((t, idx) => ({
      id: t.id || `trainer-${idx + 1}`,
      name: t.name?.trim() || `Trainer ${idx + 1}`,
      nachname: (t as any).nachname?.trim() || undefined,
      email: t.email?.trim() || undefined,
      telefon: (t as any).telefon?.trim() || undefined,
      stundensatz:
        typeof (t as any).stundensatz === "number"
          ? (t as any).stundensatz
          : Number((t as any).stundensatz) || 0,
      notiz: (t as any).notiz?.trim() || undefined,
      adresse: (t as any).adresse?.trim() || undefined,
      iban: (t as any).iban?.trim() || undefined,
      ustIdNr: (t as any).ustIdNr?.trim() || undefined,
      kleinunternehmer: (t as any).kleinunternehmer ?? false,
    }));

  if (normalized.length > 0) return normalized;

  const single = (parsed as any)?.trainer as Trainer | undefined;
  return [
    {
      id: "trainer-1",
      name: single?.name?.trim() || "Trainer",
      email: single?.email?.trim() || undefined,
      notiz: single?.notiz?.trim() || undefined,
    },
  ];
}

function normalizeState(parsed: Partial<AppState> | null | undefined): AppState {
  const trainers = ensureTrainerList(parsed || {});
  const defaultTrainerId = trainers[0]?.id || "trainer-1";

  // Migration: name → vorname für Spieler
  const migratedSpieler = (parsed?.spieler ?? []).map((s: any) => ({
    ...s,
    vorname: s.vorname ?? s.name ?? "",
    nachname: s.nachname ?? "",
  }));

  return {
    trainers,
    spieler: migratedSpieler,
    tarife: parsed?.tarife ?? [],
    trainings: (parsed?.trainings ?? []).map((t, idx) => ({
      ...t,
      id: t.id || `training-${idx + 1}`,
      trainerId:
        t.trainerId && trainers.some((tr) => tr.id === t.trainerId)
          ? t.trainerId
          : defaultTrainerId,
      anlage: t.anlage ?? "Wedding",
    })),
    payments: parsed?.payments ?? {},
    trainerPayments: parsed?.trainerPayments ?? {},
    trainerMonthSettled: parsed?.trainerMonthSettled ?? {},
    trainerBarSettled: parsed?.trainerBarSettled ?? {},
    notizen: parsed?.notizen ?? [],
    monthlyAdjustments: parsed?.monthlyAdjustments ?? {},
    vertretungen: parsed?.vertretungen ?? [],
    wirdAbgebucht: parsed?.wirdAbgebucht ?? {},
    trainerHonorarAnpassungen: parsed?.trainerHonorarAnpassungen ?? {},
    trainerZuschlaege: parsed?.trainerZuschlaege ?? {},
  };
}

function readStateWithMeta(): { state: AppState; usedKey: string | null } {
  const tryParse = (raw: string | null) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Partial<AppState>;
    } catch {
      return null;
    }
  };

  const primary = tryParse(localStorage.getItem(STORAGE_KEY));
  if (primary) return { state: normalizeState(primary), usedKey: STORAGE_KEY };

  for (const k of LEGACY_KEYS) {
    const legacy = tryParse(localStorage.getItem(k));
    if (legacy)
      return {
        state: normalizeState(legacy),
        usedKey: k,
      };
  }

  return { state: normalizeState(null), usedKey: null };
}

function writeState(state: AppState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

/* ::::: Swipe Hook für mobile Navigation ::::: */

function useSwipe(
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  threshold = 50
) {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const onTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = null;
    touchEndY.current = null;
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    
    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current !== null && touchStartY.current !== null
      ? Math.abs(touchEndY.current - touchStartY.current)
      : 0;
    
    // Nur horizontale Swipes erkennen (nicht bei vertikalem Scrollen)
    if (Math.abs(deltaX) > threshold && Math.abs(deltaX) > deltaY) {
      if (deltaX > 0) {
        onSwipeRight();
      } else {
        onSwipeLeft();
      }
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    touchEndY.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

/* ::::: Auth UI ::::: */

function AuthScreen() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) setError(error.message);
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) setError(error.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authWrapper">
      <div className="card authCard">
        <h1>{mode === "login" ? "Login" : "Registrieren"}</h1>
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error ? (
            <div className="muted" style={{ color: "#b91c1c", marginTop: 8 }}>
              {error}
            </div>
          ) : null}

          <div style={{ marginTop: 16 }}>
            <button className="btn" type="submit" disabled={busy}>
              {busy
                ? "Bitte warten..."
                : mode === "login"
                ? "Einloggen"
                : "Registrieren"}
            </button>
          </div>
        </form>

        <div style={{ marginTop: 16 }}>
          {mode === "login" ? (
            <button
              className="btn btnGhost"
              onClick={() => setMode("register")}
            >
              Noch kein Konto? Registrieren
            </button>
          ) : (
            <button className="btn btnGhost" onClick={() => setMode("login")}>
              Bereits registriert? Einloggen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ::::: Haupt-App ::::: */

export default function App() {
  const initialRef = useRef<{
    state: AppState;
    usedKey: string | null;
  } | null>(null);

  if (initialRef.current === null) {
    initialRef.current = readStateWithMeta();
  }
  const initial = initialRef.current;

  // Aktuellen Wochentag berechnen (0 = Montag, 6 = Sonntag)
  const getTodayDayIndex = () => {
    const today = new Date();
    return (today.getDay() + 6) % 7; // Umrechnung: Sonntag=0 -> 6, Montag=1 -> 0, etc.
  };

  // Mobile-Erkennung für initiale Ansicht
  const isMobileInit = typeof window !== "undefined" && window.innerWidth <= 768;

  const [tab, setTab] = useState<Tab>("kalender");
  const [viewMode, setViewMode] = useState<ViewMode>(isMobileInit ? "day" : "week");
  const [dayIndex, setDayIndex] = useState<number>(getTodayDayIndex());
  const [kalenderTrainerFilter, setKalenderTrainerFilter] =
    useState<string[]>([]);
  const [showTrainerDropdown, setShowTrainerDropdown] = useState(false);
  const [kalenderAnlageFilter, setKalenderAnlageFilter] = useState<"alle" | "Wedding" | "Britz">("alle");

  const [abrechnungTab, setAbrechnungTab] =
    useState<AbrechnungTab>("spieler");
  const [verwaltungTab, setVerwaltungTab] =
    useState<VerwaltungTab>("trainer");
  const [formulareTab, setFormulareTab] =
    useState<FormulareTab>("anmeldung");
  // Archivierte Formular-Anfragen anzeigen (geteilt über alle Formular-Tabs)
  const [formZeigeArchiv, setFormZeigeArchiv] = useState(false);
  const [anmeldungAnlageFilter, setAnmeldungAnlageFilter] =
    useState<"alle" | "Wedding" | "Britz">("alle");
  const [anmeldungNameSuche, setAnmeldungNameSuche] = useState("");
  const [anmeldungTagFilter, setAnmeldungTagFilter] =
    useState<"alle" | "montag" | "dienstag" | "mittwoch" | "donnerstag" | "freitag" | "samstag" | "sonntag">("alle");
  const [anmeldungStatusFilter, setAnmeldungStatusFilter] =
    useState<"alle" | "offen" | "erledigt">("offen");

  // Newsletter State
  const [newsletterSubject, setNewsletterSubject] = useState("");
  const [newsletterBody, setNewsletterBody] = useState("");
  const [newsletterLabelFilter, setNewsletterLabelFilter] = useState<string>("alle");
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterSelectedPlayers, setNewsletterSelectedPlayers] = useState<string[]>([]);
  const [newsletterPlayerSearch, setNewsletterPlayerSearch] = useState("");
  const [newsletterExcludedPlayers, setNewsletterExcludedPlayers] = useState<string[]>([]);
  const [newsletterExtraEmails, setNewsletterExtraEmails] = useState<{email: string, name: string}[]>([]);
  // Freie Eingabe: E-Mail-Adresse, die zu keinem Spieler gehoert.
  const [newsletterManualEmail, setNewsletterManualEmail] = useState("");
  const [newsletterManualName, setNewsletterManualName] = useState("");
  const [newsletterManualInfo, setNewsletterManualInfo] = useState<string | null>(null);
  const [newsletterAbsender, setNewsletterAbsender] = useState<"Artur" | "Zlatan">("Artur");

  const [trainers, setTrainers] = useState<Trainer[]>(initial.state.trainers);
  const [spieler, setSpieler] = useState<Spieler[]>(initial.state.spieler);
  const [tarife, setTarife] = useState<Tarif[]>(initial.state.tarife);
  const [trainings, setTrainings] = useState<Training[]>(
    initial.state.trainings
  );
  const [payments, setPayments] = useState<PaymentsMap>(
    initial.state.payments ?? {}
  );
  const [trainerPayments, setTrainerPayments] =
    useState<TrainerPaymentsMap>(initial.state.trainerPayments ?? {});
  const [trainerMonthSettled, setTrainerMonthSettled] =
    useState<TrainerMonthSettledMap>(initial.state.trainerMonthSettled ?? {});
  const [trainerBarSettled, setTrainerBarSettled] =
    useState<TrainerMonthSettledMap>(initial.state.trainerBarSettled ?? {});
  const [trainerHonorarAnpassungen, setTrainerHonorarAnpassungen] =
    useState<Record<string, number>>(initial.state.trainerHonorarAnpassungen ?? {});
  const [trainerZuschlaege, setTrainerZuschlaege] =
    useState<TrainerZuschlagMap>(initial.state.trainerZuschlaege ?? {});
  const [honorarAnpassungEdit, setHonorarAnpassungEdit] =
    useState<{ trainingId: string; value: string } | null>(null);
  const [zuschlagForm, setZuschlagForm] =
    useState<{ betrag: string; notiz: string }>({ betrag: "", notiz: "" });
  const [adminTrainerPaymentView, setAdminTrainerPaymentView] =
    useState<"none" | "bar" | "nichtBar">("none");
  const [notizen, setNotizen] = useState<Notiz[]>(
    initial.state.notizen ?? []
  );
  const [monthlyAdjustments, setMonthlyAdjustments] = useState<MonthlyAdjustments>(
    initial.state.monthlyAdjustments ?? {}
  );
  const [wirdAbgebucht, setWirdAbgebucht] = useState<WirdAbgebuchtMap>(
    initial.state.wirdAbgebucht ?? {}
  );
  const [vertretungen, setVertretungen] = useState<Vertretung[]>(
    initial.state.vertretungen ?? []
  );
  const [spontaneStunden, setSpontaneStunden] = useState<SpontaneStunde[]>([]);
  const [loadingSpontaneStunden, setLoadingSpontaneStunden] = useState(false);
  const [weiteresTabs, setWeiteresTabs] = useState<WeiteresTabs>("vertretung");
  const [vertretungTrainerId, setVertretungTrainerId] = useState<string>("");
  const [vertretungDaten, setVertretungDaten] = useState<string[]>([]);
  const [expandedVertretungTrainer, setExpandedVertretungTrainer] = useState<string[]>([]);

  // Reset expanded state and clean up past dates when switching to vertretung tab
  useEffect(() => {
    if (tab === "weiteres" && weiteresTabs === "vertretung") {
      setExpandedVertretungTrainer([]);
      // Vergangene Daten automatisch entfernen
      const heute = todayISO();
      setVertretungDaten(prev => prev.filter(d => d >= heute));
    }
  }, [tab, weiteresTabs]);
  const [vertretungDatumPreview, setVertretungDatumPreview] = useState<string>("");
  const [vertretungPendingDates, setVertretungPendingDates] = useState<string[]>([]);
  const [vertretungModus, setVertretungModus] = useState<"einzeln" | "zeitraum">("einzeln");
  const [vertretungVon, setVertretungVon] = useState<string>("");
  const [vertretungBis, setVertretungBis] = useState<string>("");
  const [vertretungNotifyDialog, setVertretungNotifyDialog] = useState<{
    trainingId: string;
    newTrainerId: string;
    training: Training | null;
  } | null>(null);
  const [vertretungNotifySending, setVertretungNotifySending] = useState(false);

  // Undo-Funktion: Speichert Snapshot des States vor Änderungen
  const [undoSnapshot, setUndoSnapshot] = useState<{
    trainings: Training[];
    spieler: Spieler[];
    trainers: Trainer[];
    tarife: Tarif[];
    payments: PaymentsMap;
    trainerPayments: TrainerPaymentsMap;
    vertretungen: Vertretung[];
    monthlyAdjustments: MonthlyAdjustments;
    wirdAbgebucht: WirdAbgebuchtMap;
    message: string;
    timestamp: number;
  } | null>(null);

  // Undo ausführen
  const undo = () => {
    if (!undoSnapshot) return;
    setTrainings(undoSnapshot.trainings);
    setSpieler(undoSnapshot.spieler);
    setTrainers(undoSnapshot.trainers);
    setTarife(undoSnapshot.tarife);
    setPayments(undoSnapshot.payments);
    setTrainerPayments(undoSnapshot.trainerPayments);
    setVertretungen(undoSnapshot.vertretungen);
    setMonthlyAdjustments(undoSnapshot.monthlyAdjustments);
    setWirdAbgebucht(undoSnapshot.wirdAbgebucht);
    setUndoSnapshot(null);
  };

  // Snapshot speichern vor wichtigen Aktionen
  const saveUndoSnapshot = (message: string) => {
    setUndoSnapshot({
      trainings,
      spieler,
      trainers,
      tarife,
      payments,
      trainerPayments,
      vertretungen,
      monthlyAdjustments,
      wirdAbgebucht,
      message,
      timestamp: Date.now(),
    });
  };

  // Newsletter: Finale Empfängerliste berechnen
  const getNewsletterRecipients = useCallback(() => {
    // Label-gefilterte Spieler
    let labelFiltered: Spieler[];
    if (newsletterLabelFilter === "keine") {
      labelFiltered = [];
    } else {
      labelFiltered = spieler.filter(s =>
        s.kontaktEmail &&
        (newsletterLabelFilter === "alle" || s.labels?.includes(newsletterLabelFilter))
      );
    }

    // Manuell ausgewählte Spieler
    const selectedPlayers = spieler.filter(s =>
      s.kontaktEmail && newsletterSelectedPlayers.includes(s.id)
    );

    // Merge + Deduplizierung
    const recipientMap = new Map<string, Spieler>();
    [...labelFiltered, ...selectedPlayers].forEach(s => {
      recipientMap.set(s.id, s);
    });

    // Ausschlüsse anwenden
    newsletterExcludedPlayers.forEach(id => {
      recipientMap.delete(id);
    });

    // Archivierte Spieler nie anschreiben
    return Array.from(recipientMap.values()).filter(s => !s.archiviert);
  }, [spieler, newsletterLabelFilter, newsletterSelectedPlayers, newsletterExcludedPlayers]);

  // Newsletter: frei eingetippte E-Mail-Adressen als Empfaenger uebernehmen.
  // Fuer Leute, die (noch) nicht als Spieler in der Verwaltung stehen. Erlaubt eine
  // einzelne Adresse oder eine ganze Liste - getrennt durch Komma, Semikolon,
  // Zeilenumbruch oder Leerzeichen, auch im Format "Name <adresse@example.com>".
  function addNewsletterManualEmail() {
    const eingabe = newsletterManualEmail.trim();
    setNewsletterManualInfo(null);
    if (!eingabe) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Eingabe in einzelne Eintraege zerlegen. "Name <mail>" bleibt dabei zusammen,
    // deshalb wird erst an Komma/Semikolon/Zeilenumbruch getrennt und nur der Rest
    // notfalls noch an Leerzeichen.
    const kandidaten: { email: string; name: string }[] = [];
    eingabe.split(/[,;\r\n]+/).forEach(teil => {
      const eintrag = teil.trim();
      if (!eintrag) return;

      const mitName = eintrag.match(/^(.*?)\s*<\s*([^<>\s]+)\s*>$/);
      if (mitName) {
        kandidaten.push({
          email: mitName[2].trim(),
          name: mitName[1].replace(/^["']|["']$/g, "").trim(),
        });
      } else if (/\s/.test(eintrag)) {
        eintrag.split(/\s+/).forEach(e => kandidaten.push({ email: e, name: "" }));
      } else {
        kandidaten.push({ email: eintrag, name: "" });
      }
    });

    // Alles, was schon Empfaenger ist - inklusive der Zweitadressen von Spielern.
    const vorhanden = new Set<string>();
    newsletterExtraEmails.forEach(e => vorhanden.add(e.email.toLowerCase()));
    getNewsletterRecipients().forEach(s => {
      [s.kontaktEmail, ...(s.zusaetzlicheEmails || [])]
        .filter(Boolean)
        .forEach(e => vorhanden.add((e as string).toLowerCase()));
    });

    const neue: { email: string; name: string }[] = [];
    const ungueltig: string[] = [];
    const doppelt: string[] = [];

    kandidaten.forEach(k => {
      if (!emailRegex.test(k.email)) {
        ungueltig.push(k.email);
        return;
      }
      const key = k.email.toLowerCase();
      if (vorhanden.has(key)) {
        doppelt.push(k.email);
        return;
      }
      vorhanden.add(key);
      neue.push(k);
    });

    // Das Namensfeld passt nur zu einer einzelnen Adresse - bei einer Liste waere
    // nicht klar, zu wem der Name gehoert.
    const manuellerName = newsletterManualName.trim();
    if (manuellerName && neue.length === 1 && !neue[0].name) {
      neue[0].name = manuellerName;
    }

    if (neue.length === 0) {
      setNewsletterError(
        ungueltig.length > 0
          ? `Keine Adresse übernommen. Ungültig: ${ungueltig.join(", ")}`
          : "Alle eingegebenen Adressen stehen bereits in der Empfängerliste."
      );
      return;
    }

    setNewsletterError(null);
    setNewsletterExtraEmails(prev => [...prev, ...neue]);
    setNewsletterManualEmail("");
    setNewsletterManualName("");

    const hinweis = [`${neue.length} ${neue.length === 1 ? "Adresse" : "Adressen"} hinzugefügt`];
    if (doppelt.length > 0) hinweis.push(`${doppelt.length} bereits vorhanden`);
    if (ungueltig.length > 0) hinweis.push(`ungültig übersprungen: ${ungueltig.join(", ")}`);
    setNewsletterManualInfo(hinweis.join(" · "));
  }

  // Undo nach 60 Sekunden automatisch entfernen
  useEffect(() => {
    if (!undoSnapshot) return;
    const timeout = setTimeout(() => {
      setUndoSnapshot(null);
    }, 60000);
    return () => clearTimeout(timeout);
  }, [undoSnapshot]);

  const [cancelNotifyDialog, setCancelNotifyDialog] = useState<{
    trainings: Training[];
    onConfirm: () => void;
  } | null>(null);
  const [cancelNotifySending, setCancelNotifySending] = useState(false);
  const [cancelNotifySubject, setCancelNotifySubject] = useState("");
  const [cancelNotifyBody, setCancelNotifyBody] = useState("");
  const [reverseAdjustmentDialog, setReverseAdjustmentDialog] = useState<{
    training: Training;
    refundPerPlayer: number;
    onConfirm: (reverseAdjustment: boolean) => void;
  } | null>(null);
  const [editingAdjustment, setEditingAdjustment] = useState<{
    spielerId: string;
    value: string;
  } | null>(null);
  const [payConfirm, setPayConfirm] = useState<{
    monat: string;
    spielerId: string;
    spielerName: string;
    amount: number;
  } | null>(null);
  const [cancelTrainingDialog, setCancelTrainingDialog] = useState<{
    trainings: Training[];
    action: 'cancel' | 'delete';
    fromSaveTraining?: boolean;
    fullPricePerTraining?: number;
  } | null>(null);
  const [cancelAdjustmentAmount, setCancelAdjustmentAmount] = useState<string>("15");
  const [invoiceDialog, setInvoiceDialog] = useState<{
    stundenAnzahl: number;
    iban: string;
    adresse: string;
    ustIdNr: string;
    kleinunternehmer: boolean;
  } | null>(null);
  const [invoiceError, setInvoiceError] = useState<string>("");
  const [invoicePreview, setInvoicePreview] = useState<{
    rechnungssteller: string;
    adresse: string;
    ustIdNr: string;
    rechnungsnummer: string;
    rechnungsdatum: string;
    leistungszeitraum: string;
    positionBeschreibung: string;
    stundenAnzahl: number;
    preisProStunde: number;
    iban: string;
    kleinunternehmer: boolean;
    useCustomTotal: boolean;
    customGesamtbetrag: number;
  } | null>(null);

  const [weekAnchor, setWeekAnchor] = useState<string>(todayISO());

  const [trainerName, setTrainerName] = useState(
    initial.state.trainers[0]?.name ?? ""
  );
  const [trainerEmail, setTrainerEmail] = useState(
    initial.state.trainers[0]?.email ?? ""
  );
  const [trainerStundensatz, setTrainerStundensatz] = useState<number | "">(
    initial.state.trainers[0]?.stundensatz ?? 0
  );
  const [trainerNotiz, setTrainerNotiz] = useState("");
  const [trainerNachname, setTrainerNachname] = useState("");
  const [trainerTelefon, setTrainerTelefon] = useState("");
  const [trainerAdresse, setTrainerAdresse] = useState("");
  const [trainerIban, setTrainerIban] = useState("");
  const [trainerUstIdNr, setTrainerUstIdNr] = useState("");
  const [trainerKleinunternehmer, setTrainerKleinunternehmer] = useState(false);
  const [editingTrainerId, setEditingTrainerId] = useState<string | null>(null);

  const [spielerVorname, setSpielerVorname] = useState("");
  const [spielerNachname, setSpielerNachname] = useState("");
  const [spielerEmail, setSpielerEmail] = useState("");
  const [spielerZusaetzlicheEmails, setSpielerZusaetzlicheEmails] = useState<string[]>([]);
  const [spielerNeueEmail, setSpielerNeueEmail] = useState("");
  const [spielerTelefon, setSpielerTelefon] = useState("");
  const [spielerRechnung, setSpielerRechnung] = useState("");
  const [spielerNotizen, setSpielerNotizen] = useState("");
  const [spielerIban, setSpielerIban] = useState("");
  const [spielerBankname, setSpielerBankname] = useState("");
  const [spielerMandatsreferenz, setSpielerMandatsreferenz] = useState("");
  const [spielerUnterschriftsdatum, setSpielerUnterschriftsdatum] = useState("");
  const [spielerSepaSequenz, setSpielerSepaSequenz] = useState<SepaSequenz>("RCUR");
  const [spielerSepaLastschriftart, setSpielerSepaLastschriftart] = useState<SepaLastschriftart>("CORE");
  const [spielerAbweichenderEmpfaenger, setSpielerAbweichenderEmpfaenger] = useState(false);
  const [spielerEmpfaengerName, setSpielerEmpfaengerName] = useState("");
  const [spielerLabels, setSpielerLabels] = useState<string[]>([]);
  const [newLabelInput, setNewLabelInput] = useState("");
  const [editingSpielerId, setEditingSpielerId] = useState<string | null>(null);

  const [tarifName, setTarifName] = useState("");
  const [tarifPreisProStunde, setTarifPreisProStunde] = useState(60);
  const [tarifAbrechnung, setTarifAbrechnung] = useState<
    "proTraining" | "proSpieler" | "monatlich"
  >("proTraining");
  const [tarifBeschreibung, setTarifBeschreibung] = useState("");
  const [editingTarifId, setEditingTarifId] = useState<string | null>(null);

  const [registrationRequests, setRegistrationRequests] = useState<RegistrationRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [selectedRequestIds, setSelectedRequestIds] = useState<Set<string>>(new Set());
  const [showAdoptConfirmDialog, setShowAdoptConfirmDialog] = useState(false);

  const [sepaMandates, setSepaMandates] = useState<SepaMandate[]>([]);
  const [loadingSepaMandates, setLoadingSepaMandates] = useState(false);
  const [expandedSepaMandateId, setExpandedSepaMandateId] = useState<string | null>(null);
  const [sepaMandateSearch, setSepaMandateSearch] = useState("");
  const [sepaMandateSortDesc, setSepaMandateSortDesc] = useState(true);

  const [tenniscampAnmeldungen, setTenniscampAnmeldungen] = useState<TenniscampAnmeldung[]>([]);
  const [loadingTenniscampAnmeldungen, setLoadingTenniscampAnmeldungen] = useState(false);
  const [expandedTenniscampId, setExpandedTenniscampId] = useState<string | null>(null);
  const [selectedTenniscampIds, setSelectedTenniscampIds] = useState<Set<string>>(new Set());
  const [tenniscampStatusFilter, setTenniscampStatusFilter] = useState<"alle" | "offen" | "storniert">("offen");
  const [tenniscampNameSuche, setTenniscampNameSuche] = useState("");
  const [tenniscampTypFilter, setTenniscampTypFilter] = useState<"alle" | "kind" | "erwachsene">("alle");
  const [tenniscampCampFilter, setTenniscampCampFilter] = useState<string>("alle");

  const [probetrainingAnfragen, setProbetrainingAnfragen] = useState<ProbetrainingAnfrage[]>([]);
  const [loadingProbetrainingAnfragen, setLoadingProbetrainingAnfragen] = useState(false);
  const [expandedProbetrainingId, setExpandedProbetrainingId] = useState<string | null>(null);
  const [selectedProbetrainingIds, setSelectedProbetrainingIds] = useState<Set<string>>(new Set());
  const [probetrainingAnlageFilter, setProbetrainingAnlageFilter] = useState<"alle" | "Wedding" | "Britz">("alle");
  const [probetrainingNameSuche, setProbetrainingNameSuche] = useState("");
  const [probetrainingTagFilter, setProbetrainingTagFilter] = useState<"alle" | "montag" | "dienstag" | "mittwoch" | "donnerstag" | "freitag" | "samstag" | "sonntag">("alle");
  const [probetrainingStatusFilter, setProbetrainingStatusFilter] = useState<string>("offen");

  const [kennlerntennisAnfragen, setKennlerntennisAnfragen] = useState<KennlerntennisAnfrage[]>([]);
  const [loadingKennlerntennisAnfragen, setLoadingKennlerntennisAnfragen] = useState(false);
  const [expandedKennlerntennisId, setExpandedKennlerntennisId] = useState<string | null>(null);
  const [kennlerntennisStatusFilter, setKennlerntennisStatusFilter] = useState<string>("offen");

  // Spontane Stunden Form
  const [spontanDatum, setSpontanDatum] = useState(todayISO());
  const [spontanVon, setSpontanVon] = useState("14:00");
  const [spontanBis, setSpontanBis] = useState("15:00");
  const [spontanTrainerId, setSpontanTrainerId] = useState("");
  const [spontanTarifId, setSpontanTarifId] = useState("");
  const [spontanCustomPreis, setSpontanCustomPreis] = useState<number | "">("");
  const [spontanAnlage, setSpontanAnlage] = useState<"Wedding" | "Britz">("Wedding");
  const [spontanVeroeffentlicht, setSpontanVeroeffentlicht] = useState(false);
  const [editingSpontanId, setEditingSpontanId] = useState<string | null>(null);
  // Steuert, ob das Erstell-/Bearbeiten-Formular im Spontan-Tab aufgeklappt ist
  const [spontanFormOpen, setSpontanFormOpen] = useState(false);
  // Listen-Filter für den Spontan-Tab
  const [spontanFilter, setSpontanFilter] = useState<"alle" | "offen" | "gebucht">("alle");
  const [spontanZeigeVergangene, setSpontanZeigeVergangene] = useState(false);
  // Direkt im Kalender einen offenen Spontan-Slot bearbeiten (Zeit/Trainer/Datum)
  const [calSpontanEdit, setCalSpontanEdit] = useState<SpontaneStunde | null>(null);
  const [calSpontanDatum, setCalSpontanDatum] = useState("");
  const [calSpontanVon, setCalSpontanVon] = useState("");
  const [calSpontanBis, setCalSpontanBis] = useState("");
  const [calSpontanTrainerId, setCalSpontanTrainerId] = useState("");

  // Sascha-Rechner (Weiteres > Rechner)
  const [rechnerGehalt, setRechnerGehalt] = useState("220");
  const [rechnerStundenFuerGehalt, setRechnerStundenFuerGehalt] = useState("11");
  const [rechnerNichtBarStunden, setRechnerNichtBarStunden] = useState("");
  const [rechnerBarStunden, setRechnerBarStunden] = useState("");
  const [rechnerZuschlag, setRechnerZuschlag] = useState("");
  const [rechnerMonat, setRechnerMonat] = useState("");
  const [rechnerStep, setRechnerStep] = useState<1 | 2 | 3>(1);
  const [rechnerBarAuszahlung, setRechnerBarAuszahlung] = useState<boolean | null>(null);
  const [rechnerResults, setRechnerResults] = useState<{
    zusatzStunden: number;
    effectiveGehalt: number;
    zuschlag: number;
    arturAnteil: number;
    zlatanBrutto: number;
    zlatanAbgabe: number;
    zlatanNetto: number;
    abhebeBetrag: number;
    ausgleichZahlung: number;
  } | null>(null);

  const [tTrainerId, setTTrainerId] = useState(
    initial.state.trainers[0]?.id ?? ""
  );
  const [tDatum, setTDatum] = useState(todayISO());
  const [tVon, setTVon] = useState("16:00");
  const [tBis, setTBis] = useState("17:00");
  const [tTarifId, setTTarifId] = useState("");
  const [tStatus, setTStatus] = useState<TrainingStatus>("geplant");
  const [tActualMinutes, setTActualMinutes] = useState<string>("");
  const [tNotiz, setTNotiz] = useState("");
  const [tCustomPreisProStunde, setTCustomPreisProStunde] = useState<
    number | ""
  >("");
  const [tCustomAbrechnung, setTCustomAbrechnung] =
    useState<"proTraining" | "proSpieler">("proTraining");
  const [tAnlage, setTAnlage] = useState("Wedding");
  const [tIsPrivat, setTIsPrivat] = useState(false);
  const [tIsKurzfristig, setTIsKurzfristig] = useState(false);
  const [tIsTenniscamp, setTIsTenniscamp] = useState(false);
  const [tCampBisDatum, setTCampBisDatum] = useState(todayISO());

  const [spielerSuche, setSpielerSuche] = useState("");
  const [tSpielerIds, setTSpielerIds] = useState<string[]>([]);
  // Tenniscamp: fester Teilnehmer-Beitrag fürs ganze Camp (für SEPA-XML)
  const [tCampGebuehr, setTCampGebuehr] = useState<number | "">("");
  // Tenniscamp: mehrere Trainer, die das Camp durchführen (jeder bekommt Stundenlohn)
  const [tCampTrainerIds, setTCampTrainerIds] = useState<string[]>([]);

  const [repeatWeekly, setRepeatWeekly] = useState(true);
  const [repeatUntil, setRepeatUntil] = useState("2026-07-12");
  const [repeatPeriods, setRepeatPeriods] = useState<{von: string; bis: string}[]>([{ von: todayISO(), bis: "2026-07-12" }, { von: "2026-08-24", bis: "2026-09-30" }]);
  const [applySerieScope, setApplySerieScope] =
    useState<"nurDieses" | "abHeute">("nurDieses");

  const [selectedTrainingId, setSelectedTrainingId] = useState<string | null>(
    null
  );
  const [selectedTrainingIds, setSelectedTrainingIds] = useState<string[]>([]);
  const [batchTrainerId, setBatchTrainerId] = useState<string>("");

  const [abrechnungMonat, setAbrechnungMonat] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
  });

  const [abrechnungFilter, setAbrechnungFilter] =
    useState<AbrechnungFilter>("alle");
  const [abrechnungTrainerFilter, setAbrechnungTrainerFilter] =
    useState<string>("alle");
  const [abrechnungSpielerSuche, setAbrechnungSpielerSuche] = useState("");
  const [abrechnungTagFilter, setAbrechnungTagFilter] = useState<string>("alle");
  const [abrechnungAbgebuchtFilter, setAbrechnungAbgebuchtFilter] = useState<string>("alle");
  const [selectedSpielerForDetail, setSelectedSpielerForDetail] = useState<string | null>(null);
  const [selectedTrainerPaymentView, setSelectedTrainerPaymentView] = useState<"none" | "bar" | "nichtBar" | "privat">("none");
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);

  // States für Formular-Sichtbarkeit in der Verwaltung
  const [showTrainerForm, setShowTrainerForm] = useState(false);
  const [showSpielerForm, setShowSpielerForm] = useState(false);
  const [showTarifForm, setShowTarifForm] = useState(false);
  const [verwaltungSpielerSuche, setVerwaltungSpielerSuche] = useState("");
  const [spielerError, setSpielerError] = useState<string | null>(null);

  // States für Kontaktbuch-CSV-Import
  type KontaktbuchRow = {
    name: string;
    vorname: string;
    nachname: string;
    iban: string;
    bankname: string;
    mandatsreferenz: string;
    unterschriftsdatum: string;
    email: string;
    telefon: string;
    adresse: string;
    empfaenger: string;
    anlage: "" | "Wedding" | "Britz";
    issues: string[];
  };
  const [showKontaktbuchModal, setShowKontaktbuchModal] = useState(false);
  const [kontaktbuchRows, setKontaktbuchRows] = useState<KontaktbuchRow[]>([]);
  const [kontaktbuchSelected, setKontaktbuchSelected] = useState<Set<number>>(new Set());

  // States für SEPA-XML-Export
  const [sepaExportSelection, setSepaExportSelection] = useState<Set<string>>(new Set());
  const [showSepaExportModal, setShowSepaExportModal] = useState(false);
  // Tenniscamp-SEPA-Export (Teilnehmer + individuelle Beträge)
  const [campSepaModal, setCampSepaModal] = useState<{ titel: string; rows: CampSepaRow[] } | null>(null);
  const [showBankImportModal, setShowBankImportModal] = useState(false);

  // State für Spieler-Label-Filter in Verwaltung
  const [verwaltungLabelFilter, setVerwaltungLabelFilter] = useState<string>("alle");
  // Archivierte Spieler in der Verwaltungsliste anzeigen (statt aktive)
  const [verwaltungZeigeArchiv, setVerwaltungZeigeArchiv] = useState(false);
  // Archivierte Tarife in der Tarif-Verwaltung anzeigen (statt aktive)
  const [tarifZeigeArchiv, setTarifZeigeArchiv] = useState(false);


  // States für Trainingsinfo-E-Mail
  const [showTrainingInfoEmail, setShowTrainingInfoEmail] = useState(false);
  const [trainingInfoEmailSubject, setTrainingInfoEmailSubject] = useState("");
  const [trainingInfoEmailBody, setTrainingInfoEmailBody] = useState("");
  const [trainingInfoIncludeSepa, setTrainingInfoIncludeSepa] = useState(false);
  const [trainingInfoIncludeProbe, setTrainingInfoIncludeProbe] = useState(false);
  const [trainingInfoIncludeMitglied, setTrainingInfoIncludeMitglied] = useState(false);
  const [trainingInfoIncludeErwachsene, setTrainingInfoIncludeErwachsene] = useState(false);
  const [trainingInfoIncludeBeitragsordnung, setTrainingInfoIncludeBeitragsordnung] = useState(false);
  const [trainingInfoIncludeProbetraining, setTrainingInfoIncludeProbetraining] = useState(false);
  const [trainingInfoEmailSending, setTrainingInfoEmailSending] = useState(false);
  const [trainingInfoExcluded, setTrainingInfoExcluded] = useState<string[]>([]);

  const clickTimerRef = useRef<number | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const [doneFlashId, setDoneFlashId] = useState<string | null>(null);
  const [longPressTriggered, setLongPressTriggered] = useState(false);

  const hasMountedRef = useRef(false);

  const skipSaveRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);
  const lastKnownUpdatedAtRef = useRef<string | null>(null);

  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFinished, setProfileFinished] = useState(false);
  const [initialSynced, setInitialSynced] = useState(false);

  /* ::::: Local / Cloud Storage Migration ::::: */

  useEffect(() => {
    const usedKey = initial.usedKey;
    if (usedKey && usedKey !== STORAGE_KEY) {
      writeState(initial.state);
      for (const k of LEGACY_KEYS) {
        if (k !== STORAGE_KEY && localStorage.getItem(k))
          localStorage.removeItem(k);
      }
    }
    hasMountedRef.current = true;
  }, [initial.usedKey, initial.state]);

  useEffect(() => {
    if (!hasMountedRef.current) return;
    writeState({
      trainers,
      spieler,
      tarife,
      trainings,
      payments,
      trainerPayments,
      trainerMonthSettled,
      trainerBarSettled,
      notizen,
      monthlyAdjustments,
      vertretungen,
      wirdAbgebucht,
      trainerHonorarAnpassungen,
      trainerZuschlaege,
    });
  }, [trainers, spieler, tarife, trainings, payments, trainerPayments, trainerMonthSettled, trainerBarSettled, notizen, monthlyAdjustments, vertretungen, wirdAbgebucht, trainerHonorarAnpassungen, trainerZuschlaege]);


  /* ::::: Auth State von Supabase lesen ::::: */

  // Verhindere das "Synchronisiere" bei Tab-Wechsel
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Wenn der Tab wieder sichtbar wird und wir bereits synchronisiert waren,
      // müssen wir nichts zurücksetzen
      if (document.visibilityState === "visible" && initialSynced) {
        // Session prüfen ohne States zurückzusetzen
        supabase.auth.getSession().then((res) => {
          const session = res.data.session;
          if (session && authUser?.id === session.user.id) {
            // Gleicher User, alles gut - nichts tun
            return;
          }
          // Anderer User oder ausgeloggt - dann müssen wir wirklich neu laden
          if (!session && authUser) {
            setAuthUser(null);
            setInitialSynced(false);
            setProfileFinished(false);
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [initialSynced, authUser]);

    useEffect(() => {
    supabase.auth.getSession().then((res) => {
      const session = res.data.session;
      setAuthUser(
        session
          ? {
              id: session.user.id,
              email: session.user.email ?? null,
              role: "admin",
              accountId: session.user.id, // <--- wichtig: nicht mehr null
              trainerId: null,
            }
          : null
      );
      setAuthLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(
      (event: string, session: any) => {
        // Bei Token-Refresh oder Initial-Session nichts zurücksetzen
        if (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
          // Beim initialen Laden nur den User setzen wenn er sich unterscheidet
          if (session) {
            setAuthUser((prev) => {
              if (prev?.id === session.user.id) return prev;
              return {
                id: session.user.id,
                email: session.user.email ?? null,
                role: "admin",
                accountId: session.user.id,
                trainerId: null,
              };
            });
          }
          return;
        }
        
        // Nur bei echten Auth-Änderungen (SIGNED_IN, SIGNED_OUT) den Sync zurücksetzen
        if (event === "SIGNED_OUT") {
          setAuthUser(null);
          setInitialSynced(false);
          setProfileFinished(false);
          return;
        }
        
        if (event === "SIGNED_IN" && session) {
          // Nur zurücksetzen wenn es wirklich ein anderer User ist
          setAuthUser((prev) => {
            if (prev?.id === session.user.id) {
              // Gleicher User, kein Reset nötig
              return prev;
            }
            // Anderer User, Reset nötig
            setInitialSynced(false);
            setProfileFinished(false);
            return {
              id: session.user.id,
              email: session.user.email ?? null,
              role: "admin",
              accountId: session.user.id,
              trainerId: null,
            };
          });
          return;
        }
        
        // Fallback für andere Events
        if (session) {
          setAuthUser((prev) => {
            if (prev?.id === session.user.id) return prev;
            return {
              id: session.user.id,
              email: session.user.email ?? null,
              role: "admin",
              accountId: session.user.id,
              trainerId: null,
            };
          });
        }
      }
    );

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);


  /* ::::: Profil aus user_profiles laden (Rolle, Account, Trainer) ::::: */

    useEffect(() => {
    if (!authUser?.id) {
      setProfileFinished(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    setProfileFinished(false);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("role, account_id, trainer_id")
          .eq("user_id", authUser.id)
          .maybeSingle();

        if (error) {
          console.error("Fehler beim Laden des Profils", error);
        }

        if (cancelled) return;

        if (data) {
          // Profil gefunden: Werte aus user_profiles benutzen
          setAuthUser((prev) =>
            prev
              ? {
                  ...prev,
                  role: (data.role as Role) || "admin",
                  accountId: data.account_id ?? prev.id,
                  trainerId: data.trainer_id ?? null,
                }
              : prev
          );
        } else {
          // Kein Profil: mindestens accountId auf eigene user.id setzen
          setAuthUser((prev) =>
            prev
              ? {
                  ...prev,
                  accountId: prev.accountId ?? prev.id,
                }
              : prev
          );
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
          setProfileFinished(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authUser?.id]);


  /* ::::: Initialen Zustand laden: lokal oder Supabase ::::: */

  useEffect(() => {
    if (authLoading || profileLoading) return;
    if (initialSynced) return;

    async function loadState() {
      if (!authUser) {
        const local = readStateWithMeta();
        setTrainers(local.state.trainers);
        setSpieler(local.state.spieler);
        setTarife(local.state.tarife);
        setTrainings(local.state.trainings);
        setPayments(local.state.payments ?? {});
        setTrainerPayments(local.state.trainerPayments ?? {});
        setNotizen(local.state.notizen ?? []);
        setVertretungen(local.state.vertretungen ?? []);
        setInitialSynced(true);
        return;
      }

      if (!profileFinished) {
        return;
      }

      if (!authUser.accountId) {
        const local = readStateWithMeta();
        setTrainers(local.state.trainers);
        setSpieler(local.state.spieler);
        setTarife(local.state.tarife);
        setTrainings(local.state.trainings);
        setPayments(local.state.payments ?? {});
        setTrainerPayments(local.state.trainerPayments ?? {});
        setTrainerMonthSettled(local.state.trainerMonthSettled ?? {});
        setTrainerBarSettled(local.state.trainerBarSettled ?? {});
        setNotizen(local.state.notizen ?? []);
        setVertretungen(local.state.vertretungen ?? []);
        setInitialSynced(true);
        return;
      }

      const { data, error } = await supabase
        .from("account_state")
        .select("data, updated_at")
        .eq("account_id", authUser.accountId)
        .maybeSingle();

      if (error) {
        console.error("Fehler beim Laden des Zustands aus Supabase", error);
      }

      if (data && data.data) {
        // Speichere den initialen Timestamp
        if (data.updated_at) {
          lastKnownUpdatedAtRef.current = data.updated_at;
        }

        const cloud = normalizeState(data.data as Partial<AppState>);
        setTrainers(cloud.trainers);
        setSpieler(cloud.spieler);
        setTarife(cloud.tarife);
        setTrainings(cloud.trainings);
        setPayments(cloud.payments ?? {});
        setTrainerPayments(cloud.trainerPayments ?? {});
        setTrainerMonthSettled(cloud.trainerMonthSettled ?? {});
        setTrainerBarSettled(cloud.trainerBarSettled ?? {});
        setNotizen(cloud.notizen ?? []);
        setMonthlyAdjustments(cloud.monthlyAdjustments ?? {});
        setVertretungen(cloud.vertretungen ?? []);
        setWirdAbgebucht(cloud.wirdAbgebucht ?? {});
        setTrainerHonorarAnpassungen(cloud.trainerHonorarAnpassungen ?? {});
        setTrainerZuschlaege(cloud.trainerZuschlaege ?? {});
      } else {
        const local = readStateWithMeta();
        setTrainers(local.state.trainers);
        setSpieler(local.state.spieler);
        setTarife(local.state.tarife);
        setTrainings(local.state.trainings);
        setPayments(local.state.payments ?? {});
        setTrainerPayments(local.state.trainerPayments ?? {});
        setTrainerMonthSettled(local.state.trainerMonthSettled ?? {});
        setTrainerBarSettled(local.state.trainerBarSettled ?? {});
        setNotizen(local.state.notizen ?? []);
        setMonthlyAdjustments(local.state.monthlyAdjustments ?? {});
        setVertretungen(local.state.vertretungen ?? []);
        setWirdAbgebucht(local.state.wirdAbgebucht ?? {});
        setTrainerHonorarAnpassungen(local.state.trainerHonorarAnpassungen ?? {});
        setTrainerZuschlaege(local.state.trainerZuschlaege ?? {});
      }

      setInitialSynced(true);
    }

    loadState();
  }, [authLoading, profileLoading, authUser, initialSynced, profileFinished]);

  /* ::::: Realtime Sync (nur für Admin) ::::: */

  function applyCloudState(raw: Partial<AppState>, updatedAt?: string) {
    if (updatedAt) lastKnownUpdatedAtRef.current = updatedAt;
    skipSaveRef.current = true;
    const cloud = normalizeState(raw);
    setTrainers(cloud.trainers);
    setSpieler(cloud.spieler);
    setTarife(cloud.tarife);
    setTrainings(cloud.trainings);
    setPayments(cloud.payments ?? {});
    setTrainerPayments(cloud.trainerPayments ?? {});
    setTrainerMonthSettled(cloud.trainerMonthSettled ?? {});
    setTrainerBarSettled(cloud.trainerBarSettled ?? {});
    setNotizen(cloud.notizen ?? []);
    setMonthlyAdjustments(cloud.monthlyAdjustments ?? {});
    setVertretungen(cloud.vertretungen ?? []);
    setWirdAbgebucht(cloud.wirdAbgebucht ?? {});
    setTrainerHonorarAnpassungen(cloud.trainerHonorarAnpassungen ?? {});
    setTrainerZuschlaege(cloud.trainerZuschlaege ?? {});
  }

  // Zustand per REST nachladen und übernehmen, wenn er neuer ist als der
  // lokale Stand. Nötig, weil Realtime bei großen account_state-Zeilen
  // (> 1 MB) die Daten nicht mitliefert ("Payload Too Large") - das Event
  // kommt dann ohne data an.
  async function refetchAccountState() {
    if (!authUser?.accountId) return;
    const { data } = await supabase
      .from("account_state")
      .select("data, updated_at")
      .eq("account_id", authUser.accountId)
      .maybeSingle();
    if (!data?.data) return;
    if (
      data.updated_at &&
      lastKnownUpdatedAtRef.current &&
      data.updated_at <= lastKnownUpdatedAtRef.current
    ) {
      return;
    }
    applyCloudState(data.data as Partial<AppState>, data.updated_at ?? undefined);
  }

  const stateRefetchTimerRef = useRef<number | null>(null);
  function scheduleStateRefetch() {
    if (stateRefetchTimerRef.current) window.clearTimeout(stateRefetchTimerRef.current);
    stateRefetchTimerRef.current = window.setTimeout(() => {
      stateRefetchTimerRef.current = null;
      void refetchAccountState();
    }, 400);
  }

  useEffect(() => {
    if (!authUser?.accountId) return;
    if (!initialSynced) return;
    if (authUser.role === "trainer") return; // Trainer brauchen kein Realtime

    const channel = supabase
      .channel(`account_state:${authUser.accountId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "account_state",
          filter: `account_id=eq.${authUser.accountId}`,
        },
        (payload) => {
          const newRow = payload.new as any;
          const incomingUpdatedAt = newRow?.updated_at as string | undefined;

          if (
            incomingUpdatedAt &&
            lastKnownUpdatedAtRef.current &&
            incomingUpdatedAt <= lastKnownUpdatedAtRef.current
          ) {
            return; // älteres oder bereits bekanntes Update ignorieren
          }

          if (newRow?.data) {
            applyCloudState(newRow.data as Partial<AppState>, incomingUpdatedAt);
          } else {
            scheduleStateRefetch();
          }
        }
      )
      .subscribe();

    // Website-Buchungen/-Absagen: spontane_stunden-Zeilen sind klein und
    // kommen zuverlässig per Realtime an. Liste aktualisieren und den
    // Kalender (account_state) nachladen.
    const spontanChannel = supabase
      .channel(`spontane_stunden:${authUser.accountId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "spontane_stunden",
          filter: `account_id=eq.${authUser.accountId}`,
        },
        () => {
          fetchSpontaneStunden();
          scheduleStateRefetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(spontanChannel);
      if (stateRefetchTimerRef.current) {
        window.clearTimeout(stateRefetchTimerRef.current);
        stateRefetchTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.accountId, authUser?.role, initialSynced]);


  /* ::::: Zustand nach Supabase schreiben (debounced) ::::: */

  useEffect(() => {
    if (!authUser) return;
    if (!authUser.accountId) return;
    if (!initialSynced) return;
    if (authUser.role === "trainer") return; // Trainer schreiben nicht

    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(() => {
      const payload: AppState = {
        trainers,
        spieler,
        tarife,
        trainings,
        payments,
        trainerPayments,
        trainerMonthSettled,
        trainerBarSettled,
        notizen,
        monthlyAdjustments,
        vertretungen,
        wirdAbgebucht,
        trainerHonorarAnpassungen,
        trainerZuschlaege,
      };

      const updatedAt = new Date().toISOString();
      lastKnownUpdatedAtRef.current = updatedAt;

      supabase
        .from("account_state")
        .upsert({
          account_id: authUser.accountId,
          data: payload,
          updated_at: updatedAt,
        })
        .then(({ error }) => {
          if (error) {
            console.error(
              "Fehler beim Speichern des Zustands in Supabase",
              error
            );
          }
        });
    }, 1000);

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [
    authUser,
    initialSynced,
    trainers,
    spieler,
    tarife,
    trainings,
    payments,
    trainerPayments,
    trainerMonthSettled,
    trainerBarSettled,
    notizen,
    monthlyAdjustments,
    vertretungen,
    wirdAbgebucht,
    trainerHonorarAnpassungen,
    trainerZuschlaege,
  ]);


  useEffect(() => {
    return () => {
      if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (tab === "formulare" && authUser?.accountId) {
      fetchRegistrationRequests();
      fetchSepaMandates();
      fetchTenniscampAnmeldungen();
      fetchProbetrainingAnfragen();
      fetchKennlerntennisAnfragen();
    }
    if (tab === "weiteres" && weiteresTabs === "spontan" && authUser?.accountId) {
      fetchSpontaneStunden();
      if (!spontanTrainerId && trainers.length > 0) {
        setSpontanTrainerId(trainers[0].id);
      }
    }
    if (tab === "kalender" && authUser?.accountId) {
      fetchSpontaneStunden();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, verwaltungTab, authUser?.accountId]);

  // Backstop: gebuchte spontane Stunden ohne (gültiges) Kalender-Training
  // automatisch übernehmen. Die Übernahme passiert primär serverseitig direkt
  // bei der Online-Buchung (RPC spontan_buchen); dieser Effekt heilt Fälle,
  // in denen das Training durch einen parallelen App-Save verloren ging oder
  // die Übernahme bei der Buchung fehlschlug. Die RPC ist idempotent.
  const spontanReconcileRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!authUser?.accountId || authUser.role === "trainer" || !initialSynced) return;
    spontaneStunden.forEach((s) => {
      const buchung = s.buchung;
      if (s.status !== "gebucht" || !buchung) return;
      const training = s.trainingId ? trainings.find((t) => t.id === s.trainingId) : undefined;
      if (training) {
        // Vollständig übernommen, wenn der gebuchte Spieler im Training steht
        const gebuchterSpieler = spieler.find(
          (p) => p.kontaktEmail?.toLowerCase() === buchung.email.toLowerCase()
        );
        if (gebuchterSpieler && training.spielerIds.includes(gebuchterSpieler.id)) return;
      }
      if (spontanReconcileRef.current.has(s.id)) return;
      spontanReconcileRef.current.add(s.id);
      supabase
        .rpc("spontan_buchung_uebernehmen", { slot_id: s.id })
        .then(({ data, error }) => {
          if (error || !data) {
            console.error("Automatische Spontan-Übernahme fehlgeschlagen:", error);
            spontanReconcileRef.current.delete(s.id);
            return;
          }
          setSpontaneStunden((prev) =>
            prev.map((x) => (x.id === s.id ? { ...x, trainingId: data as string } : x))
          );
        });
    });
  }, [spontaneStunden, trainings, spieler, authUser, initialSynced]);

  // Heilt bereits divergierte Verknüpfungen: Wurde ein aus einer spontanen Stunde
  // entstandenes Training früher (vor dem Sync-Fix) im Kalender geändert, steht die
  // verknüpfte spontane_stunden-Zeile oft noch auf alten Werten (z.B. alter Trainer,
  // alte Zeit). Der saveTraining-Sync greift nur bei NEUEN Änderungen – dieser
  // Effekt gleicht jede verknüpfte Zeile beim Laden einmalig an ihr Training an.
  // Status/Buchung/Veröffentlichung bleiben unangetastet.
  const spontanFieldSyncRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!authUser?.accountId || authUser.role === "trainer" || !initialSynced) return;
    spontaneStunden.forEach((s) => {
      if (!s.trainingId) return;
      const training = trainings.find((t) => t.id === s.trainingId);
      if (!training) return;
      const anlage: "Wedding" | "Britz" =
        training.anlage === "Britz" || training.anlage === "Wedding" ? training.anlage : s.anlage;
      const tTarif = training.tarifId || undefined;
      const tPreis = training.customPreisProStunde;
      // Training-Trainer kann optional sein – dann den vorhandenen Slot-Trainer
      // behalten (SpontaneStunde.trainerId ist Pflichtfeld, nie undefined setzen).
      const tTrainer = training.trainerId ?? s.trainerId;
      const diverged =
        s.datum !== training.datum ||
        s.uhrzeitVon.slice(0, 5) !== training.uhrzeitVon.slice(0, 5) ||
        s.uhrzeitBis.slice(0, 5) !== training.uhrzeitBis.slice(0, 5) ||
        s.trainerId !== tTrainer ||
        (s.tarifId || undefined) !== tTarif ||
        (s.customPreisProStunde ?? null) !== (tPreis ?? null) ||
        s.anlage !== anlage;
      if (!diverged) return;
      // Pro Slot nur einmal versuchen (verhindert Endlos-Loop bei Werten, die
      // sich nach dem Schreiben nicht exakt gleich zurücklesen lassen).
      if (spontanFieldSyncRef.current.has(s.id)) return;
      spontanFieldSyncRef.current.add(s.id);
      supabase
        .from("spontane_stunden")
        .update({
          datum: training.datum,
          uhrzeit_von: training.uhrzeitVon,
          uhrzeit_bis: training.uhrzeitBis,
          trainer_id: tTrainer,
          tarif_id: tTarif || null,
          custom_preis_pro_stunde: tPreis ?? null,
          anlage,
        })
        .eq("id", s.id)
        .then(({ error }) => {
          if (error) {
            console.error("Error reconciling spontane stunde with training:", error);
            spontanFieldSyncRef.current.delete(s.id);
            return;
          }
          setSpontaneStunden((prev) =>
            prev.map((x) =>
              x.id === s.id
                ? {
                    ...x,
                    datum: training.datum,
                    uhrzeitVon: training.uhrzeitVon,
                    uhrzeitBis: training.uhrzeitBis,
                    trainerId: tTrainer,
                    tarifId: tTarif,
                    customPreisProStunde: tPreis,
                    anlage,
                  }
                : x
            )
          );
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spontaneStunden, trainings, authUser, initialSynced]);

  const trainerById = useMemo(
    () => new Map(trainers.map((t) => [t.id, t])),
    [trainers]
  );

  const spielerById = useMemo(
    () => new Map(spieler.map((s) => [s.id, s])),
    [spieler]
  );

  // Zähle wie oft jeder Vorname vorkommt (für Anzeigename-Logik)
  const vornameCount = useMemo(() => {
    const counts = new Map<string, number>();
    spieler.forEach((s) => {
      const vn = s.vorname.toLowerCase().trim();
      counts.set(vn, (counts.get(vn) ?? 0) + 1);
    });
    return counts;
  }, [spieler]);



  // Helper: Anzeigename (nur Vorname, außer bei Duplikaten)
  const getDisplayName = (s: Spieler) => {
    const vn = s.vorname.toLowerCase().trim();
    if ((vornameCount.get(vn) ?? 0) > 1 && s.nachname) {
      return `${s.vorname} ${s.nachname.charAt(0)}.`;
    }
    return s.vorname;
  };

  // Helper: Name per ID abrufen (für Kalender - kurzer Name)
  const getSpielerDisplayName = (id: string) => {
    const s = spielerById.get(id);
    return s ? getDisplayName(s) : "Unbekannt";
  };

  // Helper: Vollständiger Name per ID abrufen
  const getSpielerFullName = useCallback((id: string) => {
    const s = spielerById.get(id);
    return s ? getFullName(s) : "Unbekannt";
  }, [spielerById]);

  // Alle verfügbaren Labels aus Spielern sammeln
  const allLabels = useMemo(() => {
    // Feste Wunsch-Reihenfolge der Labels (statt alphabetisch); unbekannte/neue
    // Labels landen alphabetisch dahinter.
    const reihenfolge = ["Wedding", "Wedding Erwachsene", "Wedding Jugend", "Britz"];
    const rang = (l: string) => {
      const i = reihenfolge.indexOf(l);
      return i === -1 ? reihenfolge.length : i;
    };
    const labelSet = new Set<string>();
    spieler.forEach((s) => {
      s.labels?.forEach((label) => labelSet.add(label));
    });
    return Array.from(labelSet).sort((a, b) => {
      const ra = rang(a);
      const rb = rang(b);
      return ra !== rb ? ra - rb : a.localeCompare(b, "de");
    });
  }, [spieler]);

  const tarifById = useMemo(
    () => new Map(tarife.map((t) => [t.id, t])),
    [tarife]
  );

  // Distinkte Camps aus den vorhandenen Tenniscamp-Anmeldungen (für Filter-Dropdown)
  const tenniscampCampOptions = useMemo(() => {
    const map = new Map<string, string>();
    tenniscampAnmeldungen.forEach((a) => {
      if (a.camp_id) map.set(a.camp_id, a.camp_label || a.camp_id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tenniscampAnmeldungen]);

  // Gefilterte Tenniscamp-Anmeldungen (Status + Typ + Camp + Namenssuche)
  const filteredTenniscampAnmeldungen = useMemo(() => {
    const suche = tenniscampNameSuche.trim().toLowerCase();
    return tenniscampAnmeldungen.filter((a) => {
      if (formZeigeArchiv ? !a.archiviert : !!a.archiviert) return false;
      if (tenniscampStatusFilter === "offen" && a.status === "storniert") return false;
      if (tenniscampStatusFilter === "storniert" && a.status !== "storniert") return false;
      if (tenniscampTypFilter !== "alle" && a.camp_type !== tenniscampTypFilter) return false;
      if (tenniscampCampFilter !== "alle" && a.camp_id !== tenniscampCampFilter) return false;
      if (suche) {
        const name = `${a.teilnehmer_vorname} ${a.teilnehmer_nachname}`.toLowerCase();
        const zahler = `${a.zahlungspflichtiger_vorname ?? ""} ${a.zahlungspflichtiger_nachname ?? ""}`.toLowerCase();
        if (!name.includes(suche) && !zahler.includes(suche)) return false;
      }
      return true;
    });
  }, [
    tenniscampAnmeldungen,
    tenniscampStatusFilter,
    tenniscampTypFilter,
    tenniscampCampFilter,
    tenniscampNameSuche,
    formZeigeArchiv,
  ]);

  const isTrainer = authUser?.role === "trainer";
  const ownTrainerId =
    (authUser?.trainerId &&
      trainers.some((t) => t.id === authUser.trainerId) &&
      authUser.trainerId) ||
    trainers[0]?.id ||
    "";
  const defaultTrainerId = trainers[0]?.id ?? "";

  // Ordnet ein Training einem Trainer zu. Beim Tenniscamp mit mehreren Trainern
  // gehört das Training zu JEDEM beteiligten Trainer (nicht nur zum primären),
  // damit es bei allen Camp-Trainern als Training/Honorar erscheint.
  const trainingGehoertZuTrainer = useCallback((t: Training, trainerId: string): boolean => {
    if (t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1) {
      return t.trainerIds.includes(trainerId);
    }
    const vertretung = vertretungen.find((v) => v.trainingId === t.id);
    const tid = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
    return tid === trainerId;
  }, [vertretungen, defaultTrainerId]);

  const istCampMitMehrerenTrainern = useCallback(
    (t: Training) => !!(t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1),
    []
  );

  // Beim Tenniscamp teilen sich mehrere Trainer denselben Termin. Der Bezahlt-Haken
  // hing bisher nur am Training -- "Als abgerechnet markieren" bei EINEM Camp-Trainer
  // hat das Camp damit auch bei allen anderen als bezahlt gezaehlt und es aus deren
  // "Honorar offen" verschwinden lassen. Beim Camp wird der Haken deshalb pro Trainer
  // gespeichert.
  const trainerPaymentKey = useCallback(
    (t: Training, trainerId: string) =>
      istCampMitMehrerenTrainern(t) ? `${t.id}__${trainerId}` : t.id,
    [istCampMitMehrerenTrainern]
  );

  const honorarBezahltFuer = useCallback(
    (t: Training, trainerId: string): boolean => {
      if (t.barBezahlt) return true;
      if (!istCampMitMehrerenTrainern(t)) return !!trainerPayments[t.id];
      const proTrainer = trainerPayments[`${t.id}__${trainerId}`];
      if (typeof proTrainer === "boolean") return proTrainer;
      // Altbestand: ein globaler Haken am Camp-Termin stammt aus der Abrechnung
      // irgendeines Camp-Trainers. Er gilt nur fuer die Trainer, deren Monat auch
      // wirklich als abgerechnet markiert ist.
      if (trainerPayments[t.id]) {
        return !!trainerMonthSettled[
          trainerMonthSettledKey(t.datum.substring(0, 7), trainerId)
        ];
      }
      return false;
    },
    [istCampMitMehrerenTrainern, trainerPayments, trainerMonthSettled]
  );

  // Bezahlt-Status fuer die Filter der Trainer-Abrechnung: mit Trainerfilter aus
  // dessen Sicht, ohne Filter gilt ein Camp erst als bezahlt, wenn es das fuer
  // alle beteiligten Trainer ist.
  const honorarBezahltImFilter = useCallback(
    (t: Training): boolean => {
      if (abrechnungTrainerFilter !== "alle") {
        return honorarBezahltFuer(t, abrechnungTrainerFilter);
      }
      if (istCampMitMehrerenTrainern(t)) {
        return t.trainerIds!.every((id) => honorarBezahltFuer(t, id));
      }
      return honorarBezahltFuer(t, t.trainerId || defaultTrainerId);
    },
    [
      abrechnungTrainerFilter,
      honorarBezahltFuer,
      istCampMitMehrerenTrainern,
      defaultTrainerId,
    ]
  );

  const selectedTrainerName =
    trainerById.get(tTrainerId)?.name ??
    trainerById.get(defaultTrainerId)?.name ??
    "Trainer";
  const trainerFilterLabel =
    abrechnungTrainerFilter === "alle"
      ? trainers.length === 1
        ? trainers[0]?.name ?? "Alle Trainer"
        : "Alle Trainer"
      : trainerById.get(abrechnungTrainerFilter)?.name ?? "Trainer";

  const kalenderTrainerFilterLabel =
    kalenderTrainerFilter.length === 0
      ? trainers.length === 1
        ? trainers[0]?.name ?? "Alle Trainer"
        : "Alle Trainer"
      : kalenderTrainerFilter.length === 1
        ? trainerById.get(kalenderTrainerFilter[0])?.name ?? "Trainer"
        : `${kalenderTrainerFilter.length} Trainer`;

  const visibleTabs: Tab[] = isTrainer
    ? ["kalender", "abrechnung"]
    : ["kalender", "training", "verwaltung", "formulare", "abrechnung", "weiteres"];

  const roleLabel = isTrainer ? "Trainer" : "Admin";

  const trainerOptionsForSelect = isTrainer
    ? trainers.filter(
        (t) => t.id === ownTrainerId || !ownTrainerId || trainers.length === 1
      )
    : trainers;

  useEffect(() => {
    if (!trainers.length) return;
    if (isTrainer) {
      if (ownTrainerId && tTrainerId !== ownTrainerId) {
        setTTrainerId(ownTrainerId);
      }
      return;
    }
    if (!tTrainerId || !trainers.some((t) => t.id === tTrainerId)) {
      setTTrainerId(trainers[0].id);
    }
  }, [tTrainerId, trainers, isTrainer, ownTrainerId]);

  useEffect(() => {
    if (!trainers.length) return;
    if (isTrainer) {
      if (ownTrainerId && abrechnungTrainerFilter !== ownTrainerId) {
        setAbrechnungTrainerFilter(ownTrainerId);
      }
      return;
    }
    if (
      abrechnungTrainerFilter !== "alle" &&
      !trainers.some((t) => t.id === abrechnungTrainerFilter)
    ) {
      setAbrechnungTrainerFilter("alle");
    }
  }, [abrechnungTrainerFilter, trainers, isTrainer, ownTrainerId]);

  useEffect(() => {
    if (!trainers.length) return;
    if (isTrainer) {
      if (ownTrainerId && !kalenderTrainerFilter.includes(ownTrainerId)) {
        setKalenderTrainerFilter([ownTrainerId]);
      }
      return;
    }
    // Entferne Trainer-IDs aus dem Filter, die nicht mehr existieren
    const validIds = kalenderTrainerFilter.filter(id =>
      trainers.some((t) => t.id === id)
    );
    if (validIds.length !== kalenderTrainerFilter.length) {
      setKalenderTrainerFilter(validIds);
    }
  }, [kalenderTrainerFilter, trainers, isTrainer, ownTrainerId]);

  useEffect(() => {
    if (isTrainer && tab === "verwaltung") {
      setTab("kalender");
    }
  }, [isTrainer, tab]);

  useEffect(() => {
    if (isTrainer) {
      setAbrechnungTab("trainer");
    } else {
      setAbrechnungTab("spieler");
    }
  }, [isTrainer]);

  const weekStart = useMemo(() => startOfWeekISO(weekAnchor), [weekAnchor]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i)),
    [weekStart]
  );

  // Swipe-Handler für mobile Kalender-Navigation
  const handleSwipeLeft = useCallback(() => {
    if (viewMode === "day") {
      // Nächster Tag
      const newIndex = (dayIndex + 1) % 7;
      setDayIndex(newIndex);
      if (newIndex === 0) {
        // Nächste Woche wenn wir von Sonntag zu Montag wechseln
        setWeekAnchor(addDaysISO(weekStart, 7));
      }
    } else {
      // Nächste Woche
      setWeekAnchor(addDaysISO(weekStart, 7));
    }
  }, [viewMode, dayIndex, weekStart]);

  const handleSwipeRight = useCallback(() => {
    if (viewMode === "day") {
      // Vorheriger Tag
      const newIndex = dayIndex === 0 ? 6 : dayIndex - 1;
      setDayIndex(newIndex);
      if (dayIndex === 0) {
        // Vorherige Woche wenn wir von Montag zu Sonntag wechseln
        setWeekAnchor(addDaysISO(weekStart, -7));
      }
    } else {
      // Vorherige Woche
      setWeekAnchor(addDaysISO(weekStart, -7));
    }
  }, [viewMode, dayIndex, weekStart]);

  const calendarSwipeHandlers = useSwipe(handleSwipeLeft, handleSwipeRight, 50);

  const hours = useMemo(() => {
    const startHour = 7;
    const endHour = 22;
    return Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
  }, []);

  const trainingsInWeek = useMemo(() => {
    const end = addDaysISO(weekStart, 7);
    return trainings
      .filter((t) => t.datum >= weekStart && t.datum < end)
      .filter((t) => {
        if (kalenderAnlageFilter !== "alle" && (t.anlage ?? "Wedding") !== kalenderAnlageFilter) return false;
        if (kalenderTrainerFilter.length === 0) return true;
        const tid = t.trainerId || defaultTrainerId;
        // Bei Vertretung: NUR beim Vertretungstrainer anzeigen, nicht mehr beim ursprünglichen Trainer
        const vertretung = vertretungen.find(v => v.trainingId === t.id);
        if (vertretung) {
          // Wenn Vertretung offen (kein Vertretungstrainer), bei niemandem im Filter anzeigen
          if (!vertretung.vertretungTrainerId) return false;
          // Nur beim Vertretungstrainer anzeigen
          return kalenderTrainerFilter.includes(vertretung.vertretungTrainerId);
        }
        // Keine Vertretung: normaler Trainer (Camp: jeder beteiligte Trainer)
        if (t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1) {
          return t.trainerIds.some((id) => kalenderTrainerFilter.includes(id));
        }
        return kalenderTrainerFilter.includes(tid);
      })
      .sort((a, b) =>
        (a.datum + a.uhrzeitVon).localeCompare(b.datum + b.uhrzeitVon)
      );
  }, [trainings, weekStart, kalenderTrainerFilter, kalenderAnlageFilter, defaultTrainerId, vertretungen]);

  // Offene (noch nicht gebuchte) spontane Stunden der sichtbaren Woche - werden im
  // Kalender als gestrichelte "Spontan · frei"-Platzhalter angezeigt, damit der
  // Trainer seine Slots auch ohne Buchung sieht. Gleiche Trainer-/Anlage-Filter wie Trainings.
  const offeneSpontanInWeek = useMemo(() => {
    const end = addDaysISO(weekStart, 7);
    return spontaneStunden
      .filter((s) => s.status === "offen" && !s.trainingId)
      .filter((s) => s.datum >= weekStart && s.datum < end)
      .filter((s) => {
        const anlage = s.anlage ?? "Wedding";
        if (kalenderAnlageFilter !== "alle" && anlage !== kalenderAnlageFilter) return false;
        if (kalenderTrainerFilter.length === 0) return true;
        return kalenderTrainerFilter.includes(s.trainerId);
      })
      .sort((a, b) => (a.datum + a.uhrzeitVon).localeCompare(b.datum + b.uhrzeitVon));
  }, [spontaneStunden, weekStart, kalenderTrainerFilter, kalenderAnlageFilter]);

  // Trainings, die mit einer offenen (noch nicht gebuchten) spontanen Stunde
  // verknüpft sind (z.B. „direkt im Kalender als spontan gegeben"): Diese werden
  // wie die „Spontan · frei"-Platzhalter dargestellt, bis sie gebucht werden.
  const offeneSpontanTrainingIds = useMemo(() => {
    const set = new Set<string>();
    spontaneStunden.forEach((s) => {
      if (s.status === "offen" && s.trainingId) set.add(s.trainingId);
    });
    return set;
  }, [spontaneStunden]);

  // Vergangene, noch nicht abgeschlossene Trainings (Status "geplant", Endzeit
  // bereits vorbei). Erinnerung oben im Kalender, damit nichts vergessen wird.
  // Offene Spontan-Platzhalter (noch nicht gebucht) sind ausgenommen - die sind
  // keine echten Trainings. Gleiche Trainer-/Anlage-Filter wie der Kalender.
  const offeneVergangeneTrainings = useMemo(() => {
    const jetzt = new Date();
    return trainings
      .filter((t) => t.status === "geplant" && !offeneSpontanTrainingIds.has(t.id))
      .filter((t) => {
        const ende = new Date(`${t.datum}T${t.uhrzeitBis || "23:59"}`);
        return !isNaN(ende.getTime()) && ende < jetzt;
      })
      .filter((t) => {
        if (kalenderAnlageFilter !== "alle" && (t.anlage ?? "Wedding") !== kalenderAnlageFilter) return false;
        if (kalenderTrainerFilter.length === 0) return true;
        const tid = t.trainerId || defaultTrainerId;
        const vertretung = vertretungen.find((v) => v.trainingId === t.id);
        if (vertretung) {
          if (!vertretung.vertretungTrainerId) return false;
          return kalenderTrainerFilter.includes(vertretung.vertretungTrainerId);
        }
        if (t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1) {
          return t.trainerIds.some((id) => kalenderTrainerFilter.includes(id));
        }
        return kalenderTrainerFilter.includes(tid);
      })
      .sort((a, b) => (a.datum + a.uhrzeitVon).localeCompare(b.datum + b.uhrzeitVon));
  }, [trainings, offeneSpontanTrainingIds, kalenderTrainerFilter, kalenderAnlageFilter, defaultTrainerId, vertretungen]);

  const filteredSpielerForPick = useMemo(() => {
    // Archivierte Spieler können keinen neuen Trainings mehr zugeordnet werden
    const aktive = spieler.filter((s) => !s.archiviert);
    const q = spielerSuche.trim().toLowerCase();
    if (!q) return aktive;
    return aktive.filter(
      (s) =>
        s.vorname.toLowerCase().includes(q) ||
        (s.nachname ?? "").toLowerCase().includes(q) ||
        (s.kontaktEmail ?? "").toLowerCase().includes(q)
    );
  }, [spieler, spielerSuche]);

  function addTrainer() {
    const name = trainerName.trim();
    if (!name) return;
    const rate =
      trainerStundensatz === "" ? 0 : Number(trainerStundensatz) || 0;

    const neu: Trainer = {
      id: uid(),
      name,
      nachname: trainerNachname.trim() || undefined,
      email: trainerEmail.trim() || undefined,
      telefon: trainerTelefon.trim() || undefined,
      stundensatz: rate,
      notiz: trainerNotiz.trim() || undefined,
      adresse: trainerAdresse.trim() || undefined,
      iban: trainerIban.trim() || undefined,
      ustIdNr: trainerUstIdNr.trim() || undefined,
      kleinunternehmer: trainerKleinunternehmer,
    };

    setTrainers((prev) => [...prev, neu]);
    setTrainerName("");
    setTrainerNachname("");
    setTrainerEmail("");
    setTrainerTelefon("");
    setTrainerStundensatz(0);
    setTrainerNotiz("");
    setTrainerAdresse("");
    setTrainerIban("");
    setTrainerUstIdNr("");
    setTrainerKleinunternehmer(false);
    setEditingTrainerId(null);
    if (!tTrainerId) setTTrainerId(neu.id);
  }

  function startEditTrainer(t: Trainer) {
    setEditingTrainerId(t.id);
    setTrainerName(t.name);
    setTrainerNachname(t.nachname ?? "");
    setTrainerEmail(t.email ?? "");
    setTrainerTelefon(t.telefon ?? "");
    setTrainerStundensatz(typeof t.stundensatz === "number" ? t.stundensatz : 0);
    setTrainerNotiz(t.notiz ?? "");
    setTrainerAdresse(t.adresse ?? "");
    setTrainerIban(t.iban ?? "");
    setTrainerUstIdNr(t.ustIdNr ?? "");
    setTrainerKleinunternehmer(t.kleinunternehmer ?? false);
  }

  function saveTrainer() {
    if (!editingTrainerId) return;
    const name = trainerName.trim();
    if (!name) return;
    const rate =
      trainerStundensatz === "" ? 0 : Number(trainerStundensatz) || 0;

    setTrainers((prev) =>
      prev.map((t) =>
        t.id === editingTrainerId
          ? {
              ...t,
              name,
              nachname: trainerNachname.trim() || undefined,
              email: trainerEmail.trim() || undefined,
              telefon: trainerTelefon.trim() || undefined,
              stundensatz: rate,
              notiz: trainerNotiz.trim() || undefined,
              adresse: trainerAdresse.trim() || undefined,
              iban: trainerIban.trim() || undefined,
              ustIdNr: trainerUstIdNr.trim() || undefined,
              kleinunternehmer: trainerKleinunternehmer,
            }
          : t
      )
    );

    setEditingTrainerId(null);
    setTrainerName("");
    setTrainerNachname("");
    setTrainerEmail("");
    setTrainerTelefon("");
    setTrainerStundensatz(0);
    setTrainerNotiz("");
    setTrainerAdresse("");
    setTrainerIban("");
    setTrainerUstIdNr("");
    setTrainerKleinunternehmer(false);
  }

  function deleteTrainer(id: string) {
    if (trainers.length <= 1) return;
    const trainerName = trainerById.get(id)?.name ?? "Trainer";
    saveUndoSnapshot(`Trainer "${trainerName}" gelöscht`);
    const remaining = trainers.filter((t) => t.id !== id);
    const fallbackId = remaining[0]?.id ?? id;
    setTrainers(remaining);
    setTrainings((prev) =>
      prev.map((t) =>
        t.trainerId === id ? { ...t, trainerId: fallbackId } : t
      )
    );
    if (tTrainerId === id) setTTrainerId(fallbackId);
    if (abrechnungTrainerFilter === id) {
      setAbrechnungTrainerFilter("alle");
    }
  }

  function deleteSpieler(id: string) {
    const spielerToDelete = spieler.find(s => s.id === id);
    const name = spielerToDelete ? getFullName(spielerToDelete) : "Spieler";

    if (!window.confirm(`Möchtest du "${name}" wirklich löschen? Der Spieler wird auch aus allen Trainings entfernt.`)) {
      return;
    }

    saveUndoSnapshot(`Spieler "${name}" gelöscht`);
    setSpieler((prev) => prev.filter((s) => s.id !== id));
    setTrainings((prev) =>
      prev.map((t) => ({
        ...t,
        spielerIds: t.spielerIds.filter((sid) => sid !== id),
      }))
    );
    if (editingSpielerId === id) {
      setEditingSpielerId(null);
      setSpielerVorname("");
      setSpielerNachname("");
      setSpielerEmail("");
      setSpielerTelefon("");
      setSpielerRechnung("");
      setSpielerNotizen("");
      setSpielerIban("");
      setSpielerMandatsreferenz("");
      setSpielerUnterschriftsdatum("");
      setSpielerAbweichenderEmpfaenger(false);
      setSpielerEmpfaengerName("");
    }
  }

  function archiveSpieler(id: string) {
    const s = spieler.find((sp) => sp.id === id);
    const name = s ? getFullName(s) : "Spieler";
    saveUndoSnapshot(`Spieler "${name}" archiviert`);
    setSpieler((prev) =>
      prev.map((sp) => (sp.id === id ? { ...sp, archiviert: true } : sp))
    );
  }

  function unarchiveSpieler(id: string) {
    const s = spieler.find((sp) => sp.id === id);
    const name = s ? getFullName(s) : "Spieler";
    saveUndoSnapshot(`Spieler "${name}" reaktiviert`);
    setSpieler((prev) =>
      prev.map((sp) => (sp.id === id ? { ...sp, archiviert: false } : sp))
    );
  }

  function deleteTarif(id: string) {
    const tarifName = tarifById.get(id)?.name ?? "Tarif";
    saveUndoSnapshot(`Tarif "${tarifName}" gelöscht`);
    setTarife((prev) => prev.filter((t) => t.id !== id));
    setTrainings((prev) =>
      prev.map((t) => ({
        ...t,
        tarifId: t.tarifId === id ? undefined : t.tarifId,
      }))
    );
    if (editingTarifId === id) {
      setEditingTarifId(null);
      setTarifName("");
      setTarifPreisProStunde(60);
      setTarifAbrechnung("proTraining");
      setTarifBeschreibung("");
    }
    if (tTarifId === id) {
      setTTarifId("");
    }
  }

  function archiveTarif(id: string) {
    const name = tarifById.get(id)?.name ?? "Tarif";
    saveUndoSnapshot(`Tarif "${name}" archiviert`);
    setTarife((prev) =>
      prev.map((t) => (t.id === id ? { ...t, archiviert: true } : t))
    );
  }

  function unarchiveTarif(id: string) {
    const name = tarifById.get(id)?.name ?? "Tarif";
    saveUndoSnapshot(`Tarif "${name}" reaktiviert`);
    setTarife((prev) =>
      prev.map((t) => (t.id === id ? { ...t, archiviert: false } : t))
    );
  }

  function addSpieler() {
    const vorname = spielerVorname.trim();
    const nachname = spielerNachname.trim();
    if (!vorname) return;

    // Duplikatscheck: Vorname+Nachname Kombination muss eindeutig sein
    const fullNameLower = `${vorname} ${nachname}`.toLowerCase().trim();

    const duplicate = spieler.find((s) => {
      const existingFullName = `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim();
      return existingFullName === fullNameLower;
    });

    if (duplicate) {
      setSpielerError("Es existiert bereits ein Spieler mit diesem Namen.");
      return;
    }

    setSpielerError(null);

    const neu: Spieler = {
      id: uid(),
      vorname,
      nachname: nachname || undefined,
      kontaktEmail: spielerEmail.trim() || undefined,
      zusaetzlicheEmails: spielerZusaetzlicheEmails.length > 0 ? spielerZusaetzlicheEmails : undefined,
      kontaktTelefon: spielerTelefon.trim() || undefined,
      rechnungsAdresse: spielerRechnung.trim() || undefined,
      notizen: spielerNotizen.trim() || undefined,
      iban: spielerIban.trim() || undefined,
      bankname: spielerBankname.trim() || undefined,
      mandatsreferenz: spielerMandatsreferenz.trim() || undefined,
      unterschriftsdatum: spielerUnterschriftsdatum.trim() || undefined,
      sepaSequenz: spielerSepaSequenz,
      sepaLastschriftart: spielerSepaLastschriftart,
      abweichenderEmpfaenger: spielerAbweichenderEmpfaenger || undefined,
      empfaengerName: spielerEmpfaengerName.trim() || undefined,
      labels: spielerLabels.length > 0 ? spielerLabels : undefined,
    };

    setSpieler((prev) => [...prev, neu]);
    setEditingSpielerId(null);
    setSpielerVorname("");
    setSpielerNachname("");
    setSpielerEmail("");
    setSpielerZusaetzlicheEmails([]);
    setSpielerNeueEmail("");
    setSpielerTelefon("");
    setSpielerRechnung("");
    setSpielerNotizen("");
    setSpielerIban("");
    setSpielerBankname("");
    setSpielerMandatsreferenz("");
    setSpielerUnterschriftsdatum("");
    setSpielerSepaSequenz("RCUR");
    setSpielerSepaLastschriftart("CORE");
    setSpielerAbweichenderEmpfaenger(false);
    setSpielerEmpfaengerName("");
    setSpielerLabels([]);
    setNewLabelInput("");
    setShowSpielerForm(false);
  }

  function startEditSpieler(s: Spieler) {
    setEditingSpielerId(s.id);
    setSpielerVorname(s.vorname);
    setSpielerNachname(s.nachname ?? "");
    setSpielerEmail(s.kontaktEmail ?? "");
    setSpielerZusaetzlicheEmails(s.zusaetzlicheEmails ?? []);
    setSpielerNeueEmail("");
    setSpielerTelefon(s.kontaktTelefon ?? "");
    setSpielerRechnung(s.rechnungsAdresse ?? "");
    setSpielerNotizen(s.notizen ?? "");
    setSpielerIban(s.iban ?? "");
    setSpielerBankname(s.bankname ?? "");
    setSpielerMandatsreferenz(s.mandatsreferenz ?? "");
    setSpielerUnterschriftsdatum(s.unterschriftsdatum ?? "");
    setSpielerSepaSequenz(s.sepaSequenz ?? "RCUR");
    setSpielerSepaLastschriftart(s.sepaLastschriftart ?? "CORE");
    setSpielerAbweichenderEmpfaenger(s.abweichenderEmpfaenger ?? false);
    setSpielerEmpfaengerName(s.empfaengerName ?? "");
    setSpielerLabels(s.labels ?? []);
    setNewLabelInput("");
    // Scroll zum Formular
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  }

  function saveSpieler() {
    if (!editingSpielerId) return;
    const vorname = spielerVorname.trim();
    const nachname = spielerNachname.trim();
    if (!vorname) return;

    // Duplikatscheck: Vorname+Nachname Kombination muss eindeutig sein
    const fullNameLower = `${vorname} ${nachname}`.toLowerCase().trim();

    const duplicate = spieler.find((s) => {
      // Nicht mit sich selbst vergleichen
      if (s.id === editingSpielerId) return false;

      const existingFullName = `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim();
      return existingFullName === fullNameLower;
    });

    if (duplicate) {
      setSpielerError("Es existiert bereits ein Spieler mit diesem Namen.");
      return;
    }

    setSpielerError(null);

    setSpieler((prev) =>
      prev.map((s) =>
        s.id === editingSpielerId
          ? {
              ...s,
              vorname,
              nachname: nachname || undefined,
              kontaktEmail: spielerEmail.trim() || undefined,
              zusaetzlicheEmails: spielerZusaetzlicheEmails.length > 0 ? spielerZusaetzlicheEmails : undefined,
              kontaktTelefon: spielerTelefon.trim() || undefined,
              rechnungsAdresse: spielerRechnung.trim() || undefined,
              notizen: spielerNotizen.trim() || undefined,
              iban: spielerIban.trim() || undefined,
              bankname: spielerBankname.trim() || undefined,
              mandatsreferenz: spielerMandatsreferenz.trim() || undefined,
              unterschriftsdatum: spielerUnterschriftsdatum.trim() || undefined,
              sepaSequenz: spielerSepaSequenz,
              sepaLastschriftart: spielerSepaLastschriftart,
              abweichenderEmpfaenger: spielerAbweichenderEmpfaenger || undefined,
              empfaengerName: spielerEmpfaengerName.trim() || undefined,
              labels: spielerLabels.length > 0 ? spielerLabels : undefined,
            }
          : s
      )
    );

    setEditingSpielerId(null);
    setSpielerVorname("");
    setSpielerNachname("");
    setSpielerEmail("");
    setSpielerZusaetzlicheEmails([]);
    setSpielerNeueEmail("");
    setSpielerTelefon("");
    setSpielerRechnung("");
    setSpielerNotizen("");
    setSpielerIban("");
    setSpielerBankname("");
    setSpielerMandatsreferenz("");
    setSpielerUnterschriftsdatum("");
    setSpielerSepaSequenz("RCUR");
    setSpielerSepaLastschriftart("CORE");
    setSpielerAbweichenderEmpfaenger(false);
    setSpielerEmpfaengerName("");
    setSpielerLabels([]);
    setNewLabelInput("");
    setShowSpielerForm(false);
  }

  function handleKontaktbuchFileSelect(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const rows = parseCsvSemicolon(text);
      if (rows.length === 0) {
        alert("CSV-Datei ist leer.");
        return;
      }
      const header = rows[0].map((h) => h.toLowerCase());
      const idx = {
        name: header.findIndex((h) => h.includes("name")),
        iban: header.findIndex((h) => h === "iban"),
        bankname: header.findIndex((h) => h.includes("bank")),
        mandatsreferenz: header.findIndex((h) => h.includes("mandat")),
        unterschriftsdatum: header.findIndex((h) => h.includes("unter")),
        email: header.findIndex((h) => h.includes("mail")),
        telefon: header.findIndex((h) => h.includes("telefon") || h.includes("handy") || h.includes("mobil") || h.includes("phone") || h === "tel"),
        adresse: header.findIndex((h) => h.includes("adresse")),
        empfaenger: header.findIndex((h) => h.includes("empf") || h.includes("rechnung") || h.includes("eltern")),
        anlage: header.findIndex((h) => h.includes("anlage")),
      };
      if (idx.name < 0 || idx.iban < 0) {
        alert("CSV-Header muss mindestens 'Name' und 'IBAN' enthalten.");
        return;
      }
      const parsed: KontaktbuchRow[] = rows.slice(1).map((cols) => {
        const rawName = (cols[idx.name] || "").trim();
        const rawIban = (cols[idx.iban] || "").trim();
        const rawBankname = idx.bankname >= 0 ? (cols[idx.bankname] || "").trim() : "";
        const rawMandat = idx.mandatsreferenz >= 0 ? (cols[idx.mandatsreferenz] || "").trim() : "";
        const rawDatum = idx.unterschriftsdatum >= 0 ? (cols[idx.unterschriftsdatum] || "").trim() : "";
        const rawEmail = idx.email >= 0 ? (cols[idx.email] || "").trim() : "";
        const rawTelefon = idx.telefon >= 0 ? (cols[idx.telefon] || "").trim() : "";
        const rawAdresse = idx.adresse >= 0 ? (cols[idx.adresse] || "").trim() : "";
        const rawEmpfaenger = idx.empfaenger >= 0 ? (cols[idx.empfaenger] || "").trim() : "";
        const rawAnlage = idx.anlage >= 0 ? (cols[idx.anlage] || "").trim() : "";

        const issues: string[] = [];

        // Bereinigung
        const name = rawName.replace(/\s+/g, " ").trim();
        if (rawName !== name) issues.push("Whitespace im Namen bereinigt");

        const iban = normalizeIban(rawIban);
        if (iban && !/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(iban)) {
          issues.push("IBAN-Format ungültig");
        }

        let mandat = rawMandat.replace(/[,\s]+$/g, "").trim();
        if (rawMandat !== mandat) issues.push("Mandatsreferenz bereinigt (Komma/Space am Ende)");
        if (/^DE\d{2}ZZZ/.test(mandat)) {
          issues.push("Mandatsreferenz sieht aus wie Gläubiger-ID — bitte prüfen");
        }

        const datum = toIsoDate(rawDatum);
        if (rawDatum && rawDatum !== datum) issues.push("Datum auf ISO-Format normalisiert");

        // Name → Vorname/Nachname split (letztes Token = Nachname, alles davor = Vorname)
        const tokens = name.split(/\s+/);
        let vorname = name;
        let nachname = "";
        if (tokens.length >= 2) {
          vorname = tokens.slice(0, -1).join(" ");
          nachname = tokens[tokens.length - 1];
        }

        const email = rawEmail;
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          issues.push("E-Mail-Format ungültig");
        }

        const telefon = rawTelefon.replace(/\s+/g, " ").trim();
        const adresse = rawAdresse.replace(/\s+/g, " ").trim();
        const empfaenger = rawEmpfaenger.replace(/\s+/g, " ").trim();

        let anlage: "" | "Wedding" | "Britz" = "";
        if (rawAnlage) {
          const normAnlage = rawAnlage.toLowerCase();
          if (normAnlage === "wedding") anlage = "Wedding";
          else if (normAnlage === "britz") anlage = "Britz";
          else issues.push("Anlage muss 'Wedding' oder 'Britz' sein");
        }

        return {
          name,
          vorname,
          nachname,
          iban,
          bankname: rawBankname,
          mandatsreferenz: mandat,
          unterschriftsdatum: datum,
          email,
          telefon,
          adresse,
          empfaenger,
          anlage,
          issues,
        };
      });

      // Default-Auswahl: alle ohne kritische Issues + mit IBAN
      const defaultSelected = new Set<number>();
      parsed.forEach((r, i) => {
        if (r.iban && !r.issues.some((x) => x.includes("Gläubiger-ID") || x.includes("ungültig"))) {
          defaultSelected.add(i);
        }
      });
      setKontaktbuchRows(parsed);
      setKontaktbuchSelected(defaultSelected);
      setShowKontaktbuchModal(true);
    };
    reader.readAsText(file, "utf-8");
  }

  function applyKontaktbuchImport() {
    const updates = Array.from(kontaktbuchSelected).map((i) => kontaktbuchRows[i]);
    const next = [...spieler];
    const newSpieler: Spieler[] = [];
    let matched = 0;
    let createdNew = 0;

    updates.forEach((row) => {
      const fullLower = `${row.vorname} ${row.nachname}`.toLowerCase().trim();
      const idxFound = next.findIndex(
        (s) => `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullLower
      );
      if (idxFound >= 0) {
        const prevLabels = next[idxFound].labels ?? [];
        const mergedLabels = row.anlage && !prevLabels.includes(row.anlage)
          ? [...prevLabels, row.anlage]
          : prevLabels;
        next[idxFound] = {
          ...next[idxFound],
          iban: row.iban || next[idxFound].iban,
          bankname: row.bankname || next[idxFound].bankname,
          mandatsreferenz: row.mandatsreferenz || next[idxFound].mandatsreferenz,
          unterschriftsdatum: row.unterschriftsdatum || next[idxFound].unterschriftsdatum,
          kontaktEmail: row.email || next[idxFound].kontaktEmail,
          kontaktTelefon: row.telefon || next[idxFound].kontaktTelefon,
          rechnungsAdresse: row.adresse || next[idxFound].rechnungsAdresse,
          abweichenderEmpfaenger: row.empfaenger ? true : next[idxFound].abweichenderEmpfaenger,
          empfaengerName: row.empfaenger || next[idxFound].empfaengerName,
          labels: mergedLabels.length > 0 ? mergedLabels : next[idxFound].labels,
          sepaSequenz: next[idxFound].sepaSequenz ?? "RCUR",
          sepaLastschriftart: next[idxFound].sepaLastschriftart ?? "CORE",
        };
        matched++;
      } else {
        const neu: Spieler = {
          id: uid(),
          vorname: row.vorname,
          nachname: row.nachname || undefined,
          iban: row.iban || undefined,
          bankname: row.bankname || undefined,
          mandatsreferenz: row.mandatsreferenz || undefined,
          unterschriftsdatum: row.unterschriftsdatum || undefined,
          kontaktEmail: row.email || undefined,
          kontaktTelefon: row.telefon || undefined,
          rechnungsAdresse: row.adresse || undefined,
          abweichenderEmpfaenger: row.empfaenger ? true : undefined,
          empfaengerName: row.empfaenger || undefined,
          labels: row.anlage ? [row.anlage] : undefined,
          sepaSequenz: "RCUR",
          sepaLastschriftart: "CORE",
        };
        newSpieler.push(neu);
        createdNew++;
      }
    });

    setSpieler([...next, ...newSpieler]);
    setShowKontaktbuchModal(false);
    setKontaktbuchRows([]);
    setKontaktbuchSelected(new Set());
    alert(`Import fertig: ${matched} aktualisiert, ${createdNew} neu angelegt.`);
  }

  function generateSepaXml(items: Array<{
    spielerId: string;
    name: string;
    iban: string;
    mandatsreferenz: string;
    unterschriftsdatum: string;
    sequenz: SepaSequenz;
    lastschriftart: SepaLastschriftart;
    betrag: number;
    verwendungszweck: string;
  }>): string {
    const msgId = `SEPA-${Date.now()}`;
    const pmtInfId = `PMT-${Date.now()}`;
    const creationDateTime = nowISOSeconds();
    // Fälligkeitsdatum: heute + 2 Werktage (SEPA CORE: min. 1 Tag bei wiederkehrend)
    const fallig = new Date();
    fallig.setDate(fallig.getDate() + 2);
    const reqdColltnDt = `${fallig.getFullYear()}-${pad2(fallig.getMonth() + 1)}-${pad2(fallig.getDate())}`;

    // Bei mehreren Sequenzarten muss pro Sequenztyp ein eigener PaymentInformation-Block erstellt werden
    const bySequenz = new Map<string, typeof items>();
    items.forEach((it) => {
      const key = `${it.sequenz}__${it.lastschriftart}`;
      if (!bySequenz.has(key)) bySequenz.set(key, []);
      bySequenz.get(key)!.push(it);
    });

    const totalSum = items.reduce((acc, it) => acc + it.betrag, 0);
    const totalCount = items.length;

    let pmtInfXml = "";
    let pmtIdx = 0;
    bySequenz.forEach((group, key) => {
      pmtIdx++;
      const [seq, lastschriftart] = key.split("__") as [SepaSequenz, SepaLastschriftart];
      const groupSum = group.reduce((a, b) => a + b.betrag, 0);
      const localInstrm = lastschriftart === "B2B" ? "B2B" : "CORE";

      const txInfo = group.map((it) => {
        const endToEndId = `${it.spielerId.substring(0, 8)}-${pmtInfId.substring(4, 12)}`;
        return `      <DrctDbtTxInf>
        <PmtId>
          <EndToEndId>${escapeXml(endToEndId)}</EndToEndId>
        </PmtId>
        <InstdAmt Ccy="EUR">${it.betrag.toFixed(2)}</InstdAmt>
        <DrctDbtTx>
          <MndtRltdInf>
            <MndtId>${escapeXml(it.mandatsreferenz)}</MndtId>
            <DtOfSgntr>${escapeXml(toIsoDate(it.unterschriftsdatum))}</DtOfSgntr>
          </MndtRltdInf>
        </DrctDbtTx>
        <DbtrAgt>
          <FinInstnId>
            <Othr><Id>NOTPROVIDED</Id></Othr>
          </FinInstnId>
        </DbtrAgt>
        <Dbtr>
          <Nm>${escapeXml(sanitizeSepaName(it.name))}</Nm>
        </Dbtr>
        <DbtrAcct>
          <Id>
            <IBAN>${escapeXml(normalizeIban(it.iban))}</IBAN>
          </Id>
        </DbtrAcct>
        <RmtInf>
          <Ustrd>${escapeXml(it.verwendungszweck.substring(0, 140))}</Ustrd>
        </RmtInf>
      </DrctDbtTxInf>`;
      }).join("\n");

      pmtInfXml += `
    <PmtInf>
      <PmtInfId>${escapeXml(pmtInfId)}-${pmtIdx}</PmtInfId>
      <PmtMtd>DD</PmtMtd>
      <BtchBookg>false</BtchBookg>
      <NbOfTxs>${group.length}</NbOfTxs>
      <CtrlSum>${groupSum.toFixed(2)}</CtrlSum>
      <PmtTpInf>
        <SvcLvl><Cd>SEPA</Cd></SvcLvl>
        <LclInstrm><Cd>${localInstrm}</Cd></LclInstrm>
        <SeqTp>${seq}</SeqTp>
      </PmtTpInf>
      <ReqdColltnDt>${reqdColltnDt}</ReqdColltnDt>
      <Cdtr>
        <Nm>${escapeXml(sanitizeSepaName(GLAEUBIGER_NAME))}</Nm>
      </Cdtr>
      <CdtrAcct>
        <Id>
          <IBAN>${GLAEUBIGER_IBAN}</IBAN>
        </Id>
      </CdtrAcct>
      <CdtrAgt>
        <FinInstnId>
          <BIC>${GLAEUBIGER_BIC}</BIC>
        </FinInstnId>
      </CdtrAgt>
      <ChrgBr>SLEV</ChrgBr>
      <CdtrSchmeId>
        <Id>
          <PrvtId>
            <Othr>
              <Id>${GLAEUBIGER_ID}</Id>
              <SchmeNm><Prtry>SEPA</Prtry></SchmeNm>
            </Othr>
          </PrvtId>
        </Id>
      </CdtrSchmeId>
${txInfo}
    </PmtInf>`;
    });

    return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.008.001.02" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <CstmrDrctDbtInitn>
    <GrpHdr>
      <MsgId>${escapeXml(msgId)}</MsgId>
      <CreDtTm>${creationDateTime}</CreDtTm>
      <NbOfTxs>${totalCount}</NbOfTxs>
      <CtrlSum>${totalSum.toFixed(2)}</CtrlSum>
      <InitgPty>
        <Nm>${escapeXml(sanitizeSepaName(GLAEUBIGER_NAME))}</Nm>
      </InitgPty>
    </GrpHdr>${pmtInfXml}
  </CstmrDrctDbtInitn>
</Document>
`;
  }

  // Tenniscamp: SEPA-Export-Dialog für die Teilnehmer öffnen.
  // Betrag wird pro Teilnehmer mit der Camp-Gebühr vorbelegt und ist im Dialog anpassbar.
  function openCampSepaModal(spielerIds: string[], gebuehr: number, titel: string) {
    const rows: CampSepaRow[] = spielerIds
      .map((id) => {
        const sp = spielerById.get(id);
        if (!sp) return null;
        // Bei abweichendem Rechnungsempfänger (z.B. Eltern bei Kindern) muss der
        // Kontoinhaber als Dbtr im SEPA-XML stehen, sonst weist die Bank die LS zurück.
        const kontoinhaber = sp.abweichenderEmpfaenger && sp.empfaengerName?.trim()
          ? sp.empfaengerName.trim()
          : getFullName(sp);
        return {
          spielerId: id,
          name: kontoinhaber,
          spielerName: getFullName(sp),
          iban: sp.iban || "",
          mandatsreferenz: sp.mandatsreferenz || "",
          unterschriftsdatum: sp.unterschriftsdatum || "",
          sequenz: (sp.sepaSequenz ?? "RCUR") as SepaSequenz,
          lastschriftart: (sp.sepaLastschriftart ?? "CORE") as SepaLastschriftart,
          betrag: gebuehr > 0 ? gebuehr : 0,
        };
      })
      .filter((x): x is CampSepaRow => x !== null);
    if (rows.length === 0) {
      window.alert("Keine Teilnehmer ausgewählt.");
      return;
    }
    setCampSepaModal({ titel, rows });
  }

  function addTarif() {
    const name = tarifName.trim();
    if (!name) return;

    const neu: Tarif = {
      id: uid(),
      name,
      preisProStunde: Number.isFinite(tarifPreisProStunde)
        ? tarifPreisProStunde
        : 0,
      abrechnung: tarifAbrechnung,
      beschreibung: tarifBeschreibung.trim() || undefined,
    };

    setTarife((prev) => [...prev, neu]);
    setTarifName("");
    setTarifPreisProStunde(60);
    setTarifAbrechnung("proTraining");
    setTarifBeschreibung("");
    setTTarifId((prev) => (prev ? prev : neu.id));
    setEditingTarifId(null);
  }

  function startEditTarif(t: Tarif) {
    setEditingTarifId(t.id);
    setTarifName(t.name);
    setTarifPreisProStunde(t.preisProStunde);
    setTarifAbrechnung(t.abrechnung);
    setTarifBeschreibung(t.beschreibung ?? "");
  }

  function saveTarif() {
    if (!editingTarifId) return;
    const name = tarifName.trim();
    if (!name) return;

    setTarife((prev) =>
      prev.map((t) =>
        t.id === editingTarifId
          ? {
              ...t,
              name,
              preisProStunde: Number.isFinite(tarifPreisProStunde)
                ? tarifPreisProStunde
                : 0,
              abrechnung: tarifAbrechnung,
              beschreibung: tarifBeschreibung.trim() || undefined,
            }
          : t
      )
    );

    setEditingTarifId(null);
    setTarifName("");
    setTarifPreisProStunde(60);
    setTarifAbrechnung("proTraining");
    setTarifBeschreibung("");
  }

  async function fetchRegistrationRequests() {
    if (!authUser?.accountId) return;
    setLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from("registration_requests")
        .select("*")
        .eq("account_id", authUser.accountId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching registration requests:", error);
        return;
      }
      setRegistrationRequests(data || []);
    } catch (err) {
      console.error("Error fetching registration requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  }

  async function fetchSepaMandates() {
    if (!authUser?.accountId) return;
    setLoadingSepaMandates(true);
    try {
      const { data, error } = await supabase
        .from("sepa_mandates")
        .select("*")
        .eq("account_id", authUser.accountId)
        .order("vorname", { ascending: true });

      if (error) {
        console.error("Error fetching SEPA mandates:", error);
        return;
      }
      setSepaMandates(data || []);
    } catch (err) {
      console.error("Error fetching SEPA mandates:", err);
    } finally {
      setLoadingSepaMandates(false);
    }
  }

  async function fetchTenniscampAnmeldungen() {
    if (!authUser?.accountId) return;
    setLoadingTenniscampAnmeldungen(true);
    try {
      const { data, error } = await supabase
        .from("tenniscamp_anmeldungen")
        .select("*")
        .in("account_id", [authUser.accountId, "public"])
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching Tenniscamp-Anmeldungen:", error);
        return;
      }
      setTenniscampAnmeldungen(data || []);
    } catch (err) {
      console.error("Error fetching Tenniscamp-Anmeldungen:", err);
    } finally {
      setLoadingTenniscampAnmeldungen(false);
    }
  }

  async function updateTenniscampStatus(anmeldungId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from("tenniscamp_anmeldungen")
        .update({ status: newStatus })
        .eq("id", anmeldungId);

      if (error) {
        console.error("Error updating Tenniscamp status:", error);
        return;
      }
      fetchTenniscampAnmeldungen();
    } catch (err) {
      console.error("Error updating Tenniscamp status:", err);
    }
  }

  async function deleteTenniscampAnmeldung(anmeldungId: string) {
    if (!window.confirm("Möchten Sie diese Anmeldung wirklich löschen?")) return;
    try {
      const { error } = await supabase
        .from("tenniscamp_anmeldungen")
        .delete()
        .eq("id", anmeldungId);

      if (error) {
        console.error("Error deleting Tenniscamp-Anmeldung:", error);
        return;
      }
      fetchTenniscampAnmeldungen();
    } catch (err) {
      console.error("Error deleting Tenniscamp-Anmeldung:", err);
    }
  }

  async function fetchProbetrainingAnfragen() {
    if (!authUser?.accountId) return;
    setLoadingProbetrainingAnfragen(true);
    try {
      const { data, error } = await supabase
        .from("probetraining_anfragen")
        .select("*")
        .in("account_id", [authUser.accountId, "public"])
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching Probetraining-Anfragen:", error);
        return;
      }
      setProbetrainingAnfragen(data || []);
    } catch (err) {
      console.error("Error fetching Probetraining-Anfragen:", err);
    } finally {
      setLoadingProbetrainingAnfragen(false);
    }
  }

  async function updateProbetrainingStatus(anfragenId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from("probetraining_anfragen")
        .update({ status: newStatus })
        .eq("id", anfragenId);

      if (error) {
        console.error("Error updating Probetraining status:", error);
        return;
      }
      fetchProbetrainingAnfragen();
    } catch (err) {
      console.error("Error updating Probetraining status:", err);
    }
  }

  async function deleteProbetrainingAnfrage(anfragenId: string) {
    if (!window.confirm("Möchten Sie diese Anfrage wirklich löschen?")) return;
    try {
      const { error } = await supabase
        .from("probetraining_anfragen")
        .delete()
        .eq("id", anfragenId);

      if (error) {
        console.error("Error deleting Probetraining-Anfrage:", error);
        return;
      }
      fetchProbetrainingAnfragen();
    } catch (err) {
      console.error("Error deleting Probetraining-Anfrage:", err);
    }
  }

  async function fetchKennlerntennisAnfragen() {
    if (!authUser?.accountId) return;
    setLoadingKennlerntennisAnfragen(true);
    try {
      const { data, error } = await supabase
        .from("kennlerntennis_anfragen")
        .select("*")
        .eq("account_id", authUser.accountId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching Kennlerntennis-Anfragen:", error);
        return;
      }
      setKennlerntennisAnfragen(data || []);
    } catch (err) {
      console.error("Error fetching Kennlerntennis-Anfragen:", err);
    } finally {
      setLoadingKennlerntennisAnfragen(false);
    }
  }

  async function updateKennlerntennisStatus(anfragenId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from("kennlerntennis_anfragen")
        .update({ status: newStatus })
        .eq("id", anfragenId);

      if (error) {
        console.error("Error updating Kennlerntennis status:", error);
        return;
      }
      fetchKennlerntennisAnfragen();
    } catch (err) {
      console.error("Error updating Kennlerntennis status:", err);
    }
  }

  async function deleteKennlerntennisAnfrage(anfragenId: string) {
    if (!window.confirm("Möchten Sie diese Anfrage wirklich löschen?")) return;
    try {
      const { error } = await supabase
        .from("kennlerntennis_anfragen")
        .delete()
        .eq("id", anfragenId);

      if (error) {
        console.error("Error deleting Kennlerntennis-Anfrage:", error);
        return;
      }
      fetchKennlerntennisAnfragen();
    } catch (err) {
      console.error("Error deleting Kennlerntennis-Anfrage:", err);
    }
  }

  async function updateSepaMandateStatus(mandateId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from("sepa_mandates")
        .update({ status: newStatus })
        .eq("id", mandateId);

      if (error) {
        console.error("Error updating SEPA mandate status:", error);
        return;
      }

      setSepaMandates((prev) =>
        prev.map((m) => (m.id === mandateId ? { ...m, status: newStatus } : m))
      );
    } catch (err) {
      console.error("Error updating SEPA mandate status:", err);
    }
  }

  async function deleteSepaMandate(mandateId: string) {
    if (!window.confirm("Möchten Sie dieses SEPA-Mandat wirklich löschen?")) {
      return;
    }
    try {
      const { error } = await supabase
        .from("sepa_mandates")
        .delete()
        .eq("id", mandateId);

      if (error) {
        console.error("Error deleting SEPA mandate:", error);
        return;
      }

      setSepaMandates((prev) => prev.filter((m) => m.id !== mandateId));
      setExpandedSepaMandateId(null);
    } catch (err) {
      console.error("Error deleting SEPA mandate:", err);
    }
  }

  async function updateRequestStatus(requestId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from("registration_requests")
        .update({ status: newStatus })
        .eq("id", requestId);

      if (error) {
        console.error("Error updating status:", error);
        return;
      }

      setRegistrationRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      console.error("Error updating status:", err);
    }
  }

  async function deleteRegistrationRequest(requestId: string) {
    try {
      const { error } = await supabase
        .from("registration_requests")
        .delete()
        .eq("id", requestId);

      if (error) {
        console.error("Error deleting request:", error);
        return;
      }

      setRegistrationRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error("Error deleting request:", err);
    }
  }

  // Generisches Soft-Archiv für Formular-Anfragen (Supabase-Tabellen mit Spalte "archiviert")
  async function setAnfrageArchiviert(
    table: string,
    id: string,
    archiviert: boolean,
    refetch: () => void
  ) {
    try {
      const { error } = await supabase.from(table).update({ archiviert }).eq("id", id);
      if (error) {
        console.error("Fehler beim Archivieren:", error);
        return;
      }
      refetch();
    } catch (err) {
      console.error("Fehler beim Archivieren:", err);
    }
  }

  // Spontane Stunden CRUD
  async function fetchSpontaneStunden() {
    if (!authUser?.accountId) return;
    setLoadingSpontaneStunden(true);
    try {
      const { data, error } = await supabase
        .from("spontane_stunden")
        .select("*")
        .eq("account_id", authUser.accountId)
        .order("datum", { ascending: true });

      if (error) {
        console.error("Error fetching spontane stunden:", error);
        return;
      }

      const mapped: SpontaneStunde[] = (data || []).map((row: {
        id: string;
        datum: string;
        uhrzeit_von: string;
        uhrzeit_bis: string;
        trainer_id: string;
        tarif_id?: string;
        custom_preis_pro_stunde?: number;
        status: string;
        anlage: string;
        veroeffentlicht: boolean;
        buchung?: SpontaneStundeBuchung;
        training_id?: string;
      }) => ({
        id: row.id,
        datum: row.datum,
        uhrzeitVon: row.uhrzeit_von,
        uhrzeitBis: row.uhrzeit_bis,
        trainerId: row.trainer_id,
        tarifId: row.tarif_id,
        customPreisProStunde: row.custom_preis_pro_stunde,
        status: row.status as "offen" | "gebucht",
        anlage: row.anlage as "Wedding" | "Britz",
        veroeffentlicht: row.veroeffentlicht,
        buchung: row.buchung,
        trainingId: row.training_id,
      }));
      setSpontaneStunden(mapped);
    } catch (err) {
      console.error("Error fetching spontane stunden:", err);
    } finally {
      setLoadingSpontaneStunden(false);
    }
  }

  async function createSpontaneStunde() {
    if (!authUser?.accountId) return;
    if (!spontanTrainerId) {
      alert("Bitte Trainer auswählen");
      return;
    }

    const neu: Omit<SpontaneStunde, "id"> = {
      datum: spontanDatum,
      uhrzeitVon: spontanVon,
      uhrzeitBis: spontanBis,
      trainerId: spontanTrainerId,
      tarifId: spontanTarifId || undefined,
      customPreisProStunde: spontanCustomPreis === "" ? undefined : spontanCustomPreis,
      status: "offen",
      anlage: spontanAnlage,
      veroeffentlicht: spontanVeroeffentlicht,
    };

    try {
      const { data, error } = await supabase
        .from("spontane_stunden")
        .insert({
          account_id: authUser.accountId,
          datum: neu.datum,
          uhrzeit_von: neu.uhrzeitVon,
          uhrzeit_bis: neu.uhrzeitBis,
          trainer_id: neu.trainerId,
          tarif_id: neu.tarifId || null,
          custom_preis_pro_stunde: neu.customPreisProStunde ?? null,
          status: neu.status,
          anlage: neu.anlage,
          veroeffentlicht: neu.veroeffentlicht,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating spontane stunde:", error);
        alert("Fehler beim Erstellen: " + error.message);
        return;
      }

      const created: SpontaneStunde = {
        id: data.id,
        datum: data.datum,
        uhrzeitVon: data.uhrzeit_von,
        uhrzeitBis: data.uhrzeit_bis,
        trainerId: data.trainer_id,
        tarifId: data.tarif_id,
        customPreisProStunde: data.custom_preis_pro_stunde,
        status: data.status,
        anlage: data.anlage,
        veroeffentlicht: data.veroeffentlicht,
        buchung: data.buchung,
        trainingId: data.training_id,
      };
      setSpontaneStunden((prev) => [...prev, created]);
      resetSpontanForm();
    } catch (err) {
      console.error("Error creating spontane stunde:", err);
    }
  }

  async function createSpontaneStundeFromTraining(
    trainingId: string,
    trainerId: string,
    datum: string,
    von: string,
    bis: string,
    tarifId?: string,
    customPreis?: number,
    anlage: "Wedding" | "Britz" = "Wedding"
  ) {
    if (!authUser?.accountId) return;

    // Optimistisch sofort in den State, damit die Kalenderkarte ohne kurzes
    // blaues "Privat"-Aufblitzen direkt als "Spontan · frei" rendert.
    const tempId = `temp-${trainingId}`;
    setSpontaneStunden((prev) => [
      ...prev,
      {
        id: tempId,
        datum,
        uhrzeitVon: von,
        uhrzeitBis: bis,
        trainerId,
        tarifId,
        customPreisProStunde: customPreis,
        status: "offen",
        anlage,
        veroeffentlicht: true,
        trainingId,
      },
    ]);

    try {
      const { data, error } = await supabase
        .from("spontane_stunden")
        .insert({
          account_id: authUser.accountId,
          datum,
          uhrzeit_von: von,
          uhrzeit_bis: bis,
          trainer_id: trainerId,
          tarif_id: tarifId || null,
          custom_preis_pro_stunde: customPreis ?? null,
          status: "offen",
          anlage,
          veroeffentlicht: true,
          training_id: trainingId,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating spontane stunde from training:", error);
        setSpontaneStunden((prev) => prev.filter((s) => s.id !== tempId));
        return;
      }

      const created: SpontaneStunde = {
        id: data.id,
        datum: data.datum,
        uhrzeitVon: data.uhrzeit_von,
        uhrzeitBis: data.uhrzeit_bis,
        trainerId: data.trainer_id,
        tarifId: data.tarif_id,
        customPreisProStunde: data.custom_preis_pro_stunde,
        status: data.status,
        anlage: data.anlage,
        veroeffentlicht: data.veroeffentlicht,
        buchung: data.buchung,
        trainingId: data.training_id,
      };
      // Temp-Eintrag ersetzen; falls ein paralleler Fetch ihn schon entfernt
      // hat, die echte Zeile anhängen (sofern nicht ebenfalls schon geladen).
      setSpontaneStunden((prev) => {
        if (prev.some((s) => s.id === tempId)) {
          return prev.map((s) => (s.id === tempId ? created : s));
        }
        if (prev.some((s) => s.id === created.id)) return prev;
        return [...prev, created];
      });
    } catch (err) {
      console.error("Error creating spontane stunde from training:", err);
      setSpontaneStunden((prev) => prev.filter((s) => s.id !== tempId));
    }
  }

  async function updateSpontaneStunde() {
    if (!editingSpontanId || !authUser?.accountId) return;

    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .update({
          datum: spontanDatum,
          uhrzeit_von: spontanVon,
          uhrzeit_bis: spontanBis,
          trainer_id: spontanTrainerId,
          tarif_id: spontanTarifId || null,
          custom_preis_pro_stunde: spontanCustomPreis === "" ? null : spontanCustomPreis,
          anlage: spontanAnlage,
          veroeffentlicht: spontanVeroeffentlicht,
        })
        .eq("id", editingSpontanId);

      if (error) {
        console.error("Error updating spontane stunde:", error);
        alert("Fehler beim Aktualisieren: " + error.message);
        return;
      }

      setSpontaneStunden((prev) =>
        prev.map((s) =>
          s.id === editingSpontanId
            ? {
                ...s,
                datum: spontanDatum,
                uhrzeitVon: spontanVon,
                uhrzeitBis: spontanBis,
                trainerId: spontanTrainerId,
                tarifId: spontanTarifId || undefined,
                customPreisProStunde: spontanCustomPreis === "" ? undefined : spontanCustomPreis,
                anlage: spontanAnlage,
                veroeffentlicht: spontanVeroeffentlicht,
              }
            : s
        )
      );

      // Gegenrichtung: Hängt an diesem Slot ein Kalender-Training (verknüpfte
      // „Spontan · frei"-Variante), ziehen wir das Training mit – sonst würde der
      // Reconcile-Effekt die gerade gemachte Slot-Änderung beim nächsten Laden
      // wieder auf die alten Trainingswerte zurücksetzen.
      const editedSlot = spontaneStunden.find((s) => s.id === editingSpontanId);
      if (editedSlot?.trainingId) {
        const linkedTrainingId = editedSlot.trainingId;
        setTrainings((prev) =>
          prev.map((t) =>
            t.id === linkedTrainingId
              ? {
                  ...t,
                  datum: spontanDatum,
                  uhrzeitVon: spontanVon,
                  uhrzeitBis: spontanBis,
                  trainerId: spontanTrainerId,
                  tarifId: spontanTarifId || undefined,
                  customPreisProStunde: spontanCustomPreis === "" ? undefined : spontanCustomPreis,
                  anlage: spontanAnlage,
                }
              : t
          )
        );
      }

      resetSpontanForm();
      setSpontanFormOpen(false);
    } catch (err) {
      console.error("Error updating spontane stunde:", err);
    }
  }

  async function deleteSpontaneStunde(id: string) {
    if (!window.confirm("Möchten Sie diese spontane Stunde wirklich löschen?")) {
      return;
    }
    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting spontane stunde:", error);
        return;
      }

      // Verknüpftes Training aus dem Kalender entfernen
      const spontanStunde = spontaneStunden.find((s) => s.id === id);
      if (spontanStunde?.trainingId) {
        setTrainings((prev) => prev.filter((t) => t.id !== spontanStunde.trainingId));
      }

      setSpontaneStunden((prev) => prev.filter((s) => s.id !== id));
      if (editingSpontanId === id) {
        resetSpontanForm();
      }
    } catch (err) {
      console.error("Error deleting spontane stunde:", err);
    }
  }

  // Gebuchte spontane Stunde wieder freigeben: Slot wird erneut buchbar,
  // das verknüpfte Training fliegt aus dem Kalender, der Spieler bekommt
  // eine Info-E-Mail (keine 48-h-Frist - Admin darf jederzeit freigeben).
  async function freigebenSpontaneStunde(s: SpontaneStunde) {
    if (!s.buchung) return;
    const buchung = s.buchung;
    const von = s.uhrzeitVon.slice(0, 5);
    const bis = s.uhrzeitBis.slice(0, 5);
    const datumFormatted = new Date(s.datum + "T12:00:00").toLocaleDateString("de-DE", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    if (
      !window.confirm(
        `Buchung von ${buchung.name} am ${datumFormatted}, ${von}–${bis} Uhr stornieren und den Termin wieder freigeben?\n\n${buchung.name} erhält eine Info-E-Mail an ${buchung.email}.`
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .update({ status: "offen", buchung: null, training_id: null, veroeffentlicht: true })
        .eq("id", s.id);

      if (error) {
        console.error("Error releasing spontane stunde:", error);
        alert("Fehler beim Freigeben: " + error.message);
        return;
      }

      // Verknüpftes Training aus dem Kalender entfernen
      if (s.trainingId) {
        setTrainings((prev) => prev.filter((t) => t.id !== s.trainingId));
      }
      setSpontaneStunden((prev) =>
        prev.map((x) =>
          x.id === s.id
            ? { ...x, status: "offen", buchung: undefined, trainingId: undefined, veroeffentlicht: true }
            : x
        )
      );

      // Info-E-Mail an den Spieler
      const ort = s.anlage === "Britz" ? "Britz" : "BSC Rehberge, Wedding";
      const mailText = `Hallo ${buchung.name},\n\nIhre Buchung für das Training in den Sommerferien musste leider storniert werden:\n\nTermin: ${datumFormatted}\nUhrzeit: ${von} – ${bis} Uhr\nOrt: ${ort}\n\nEs wird selbstverständlich nichts per SEPA-Lastschrift eingezogen.\n\nDer Termin steht wieder zur Buchung frei - gerne können Sie sich einen neuen Termin auf unserer Website aussuchen.\n\nBei Fragen erreichen Sie uns unter tennisabisz@gmail.com.\n\nSportliche Grüße,\nTennisschule A bis Z`;
      const mailHtml = `<!DOCTYPE html>
<html>
<body style="margin:0; padding:0; background:#faf6f2; font-family: Arial, Helvetica, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#faf6f2; padding:24px 0;">
    <tr><td align="center">
      <table role="presentation" width="560" cellspacing="0" cellpadding="0" style="background:#ffffff; border-radius:8px; overflow:hidden; max-width:560px; width:100%;">
        <tr><td style="background:#c96442; padding:20px 28px;">
          <span style="color:#ffffff; font-size:18px; font-weight:bold;">Tennisschule A bis Z</span>
        </td></tr>
        <tr><td style="padding:28px;">
          <p style="margin:0 0 16px; color:#2d2a26; font-size:15px;">Hallo ${buchung.name},</p>
          <p style="margin:0 0 18px; color:#2d2a26; font-size:15px; line-height:1.6;">
            Ihre Buchung für das <strong>Training in den Sommerferien</strong> musste leider storniert werden:
          </p>
          <table role="presentation" cellspacing="0" cellpadding="0" style="margin:0 0 18px; background:#faf6f2; border-left:4px solid #c96442; width:100%;">
            <tr><td style="padding:14px 18px;">
              <p style="margin:0 0 6px; color:#2d2a26; font-size:15px;"><strong>Termin:</strong> ${datumFormatted}</p>
              <p style="margin:0 0 6px; color:#2d2a26; font-size:15px;"><strong>Uhrzeit:</strong> ${von} – ${bis} Uhr</p>
              <p style="margin:0; color:#2d2a26; font-size:15px;"><strong>Ort:</strong> ${ort}</p>
            </td></tr>
          </table>
          <p style="margin:0 0 16px; color:#2d2a26; font-size:15px; line-height:1.6;">
            Es wird selbstverständlich <strong>nichts per SEPA-Lastschrift eingezogen</strong>.
            Der Termin steht wieder zur Buchung frei – gerne können Sie sich einen neuen Termin auf unserer Website aussuchen.
          </p>
          <p style="margin:0 0 24px; color:#2d2a26; font-size:15px; line-height:1.6;">
            Bei Fragen erreichen Sie uns unter <a href="mailto:tennisabisz@gmail.com" style="color:#c96442;">tennisabisz@gmail.com</a>.
          </p>
          <p style="margin:0; color:#2d2a26; font-size:15px;">Sportliche Grüße<br>Tennisschule A bis Z</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

      try {
        const resp = await fetch("/api/send-newsletter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: [buchung.email],
            subject: `Ihr Training am ${datumFormatted} wurde storniert`,
            body: mailText,
            html: mailHtml,
            fromName: "Tennisschule A bis Z",
          }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        alert(`Termin freigegeben. ${buchung.name} wurde per E-Mail informiert (${buchung.email}).`);
      } catch (mailErr) {
        console.error("Freigabe-E-Mail fehlgeschlagen:", mailErr);
        alert(
          `Der Termin wurde freigegeben, aber die Info-E-Mail konnte nicht versendet werden.\nBitte informieren Sie ${buchung.name} manuell: ${buchung.email}`
        );
      }
    } catch (err) {
      console.error("Error releasing spontane stunde:", err);
      alert("Fehler beim Freigeben. Bitte versuchen Sie es erneut.");
    }
  }

  async function toggleSpontanVeroeffentlicht(id: string, current: boolean) {
    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .update({ veroeffentlicht: !current })
        .eq("id", id);

      if (error) {
        console.error("Error toggling veroeffentlicht:", error);
        return;
      }

      setSpontaneStunden((prev) =>
        prev.map((s) => (s.id === id ? { ...s, veroeffentlicht: !current } : s))
      );
    } catch (err) {
      console.error("Error toggling veroeffentlicht:", err);
    }
  }

  function startEditSpontaneStunde(s: SpontaneStunde) {
    setEditingSpontanId(s.id);
    setSpontanDatum(s.datum);
    setSpontanVon(s.uhrzeitVon);
    setSpontanBis(s.uhrzeitBis);
    setSpontanTrainerId(s.trainerId);
    setSpontanTarifId(s.tarifId || "");
    setSpontanCustomPreis(s.customPreisProStunde ?? "");
    setSpontanAnlage(s.anlage);
    setSpontanVeroeffentlicht(s.veroeffentlicht);
    setSpontanFormOpen(true);
  }

  function resetSpontanForm() {
    setEditingSpontanId(null);
    setSpontanDatum(todayISO());
    setSpontanVon("14:00");
    setSpontanBis("15:00");
    setSpontanTrainerId(trainers[0]?.id ?? "");
    setSpontanTarifId("");
    setSpontanCustomPreis("");
    setSpontanAnlage("Wedding");
    setSpontanVeroeffentlicht(false);
  }

  // Offenen Spontan-Slot direkt aus dem Kalender heraus bearbeiten (Zeit/Trainer/Datum)
  function openCalendarSpontanEdit(s: SpontaneStunde) {
    if (isTrainer) return;
    setCalSpontanEdit(s);
    setCalSpontanDatum(s.datum);
    setCalSpontanVon(s.uhrzeitVon.slice(0, 5));
    setCalSpontanBis(s.uhrzeitBis.slice(0, 5));
    setCalSpontanTrainerId(s.trainerId);
  }

  async function saveCalendarSpontanEdit() {
    const s = calSpontanEdit;
    if (!s || isTrainer) return;
    if (!calSpontanDatum || !calSpontanVon || !calSpontanBis || !calSpontanTrainerId) {
      alert("Bitte Datum, Uhrzeit und Trainer ausfüllen.");
      return;
    }
    if (calSpontanVon >= calSpontanBis) {
      alert("Die Endzeit muss nach der Startzeit liegen.");
      return;
    }
    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .update({
          datum: calSpontanDatum,
          uhrzeit_von: calSpontanVon,
          uhrzeit_bis: calSpontanBis,
          trainer_id: calSpontanTrainerId,
        })
        .eq("id", s.id);
      if (error) {
        console.error("Error updating spontane stunde (calendar):", error);
        alert("Fehler beim Speichern: " + error.message);
        return;
      }
      setSpontaneStunden((prev) =>
        prev.map((x) =>
          x.id === s.id
            ? { ...x, datum: calSpontanDatum, uhrzeitVon: calSpontanVon, uhrzeitBis: calSpontanBis, trainerId: calSpontanTrainerId }
            : x
        )
      );
      // Verknüpftes (noch offenes) Training im Kalender synchron halten
      if (s.trainingId) {
        setTrainings((prev) =>
          prev.map((t) =>
            t.id === s.trainingId
              ? { ...t, datum: calSpontanDatum, uhrzeitVon: calSpontanVon, uhrzeitBis: calSpontanBis, trainerId: calSpontanTrainerId }
              : t
          )
        );
      }
      setCalSpontanEdit(null);
    } catch (err) {
      console.error("Error updating spontane stunde (calendar):", err);
      alert("Fehler beim Speichern. Bitte erneut versuchen.");
    }
  }

  // Wird ein Training, das aus einer spontanen Stunde entstanden ist, im Kalender
  // bearbeitet (Trainer/Datum/Zeit/Tarif/Preis/Anlage), schreiben wir die Änderung
  // auf die verknüpfte spontane_stunden-Zeile zurück – sonst zeigt der Spontan-Tab
  // veraltete Daten (z.B. den alten Trainer). Status/Buchung/Veröffentlichung
  // bleiben unangetastet.
  async function syncSpontaneStundeFromTrainingEdit(
    trainingId: string,
    fields: {
      datum: string;
      uhrzeitVon: string;
      uhrzeitBis: string;
      trainerId?: string;
      tarifId?: string;
      customPreisProStunde?: number;
      anlage?: string;
    }
  ) {
    if (!authUser?.accountId) return;
    const linked = spontaneStunden.find((s) => s.trainingId === trainingId);
    if (!linked) return;
    const anlage: "Wedding" | "Britz" =
      fields.anlage === "Britz" || fields.anlage === "Wedding" ? fields.anlage : linked.anlage;
    const trainerId = fields.trainerId ?? linked.trainerId;
    try {
      const { error } = await supabase
        .from("spontane_stunden")
        .update({
          datum: fields.datum,
          uhrzeit_von: fields.uhrzeitVon,
          uhrzeit_bis: fields.uhrzeitBis,
          trainer_id: trainerId,
          tarif_id: fields.tarifId || null,
          custom_preis_pro_stunde: fields.customPreisProStunde ?? null,
          anlage,
        })
        .eq("id", linked.id);
      if (error) {
        console.error("Error syncing spontane stunde from training edit:", error);
        return;
      }
      setSpontaneStunden((prev) =>
        prev.map((s) =>
          s.id === linked.id
            ? {
                ...s,
                datum: fields.datum,
                uhrzeitVon: fields.uhrzeitVon,
                uhrzeitBis: fields.uhrzeitBis,
                trainerId,
                tarifId: fields.tarifId || undefined,
                customPreisProStunde: fields.customPreisProStunde,
                anlage,
              }
            : s
        )
      );
    } catch (err) {
      console.error("Error syncing spontane stunde from training edit:", err);
    }
  }

  async function uebernehmenSpontanBuchung(s: SpontaneStunde) {
    if (!s.buchung) return;

    const { name, email, telefon } = s.buchung;

    // Name splitten
    const parts = name.trim().split(/\s+/);
    const vorname = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0] || "Unbekannt";
    const nachname = parts.length > 1 ? parts[parts.length - 1] : undefined;

    // Prüfe ob Spieler mit gleicher Email existiert
    let spielerId: string;
    const existingSpieler = spieler.find(
      (sp) => sp.kontaktEmail?.toLowerCase() === email.toLowerCase()
    );

    if (existingSpieler) {
      spielerId = existingSpieler.id;
    } else {
      // Neuen Spieler erstellen
      const neuerSpieler: Spieler = {
        id: uid(),
        vorname,
        nachname,
        kontaktEmail: email,
        kontaktTelefon: telefon,
        notizen: "Spontanbuchung",
      };
      setSpieler((prev) => [...prev, neuerSpieler]);
      spielerId = neuerSpieler.id;
    }

    // Wenn Training bereits existiert (z.B. über Kalender erstellt), Spieler hinzufügen
    if (s.trainingId) {
      setTrainings((prev) =>
        prev.map((t) => {
          if (t.id !== s.trainingId) return t;
          if (t.spielerIds.includes(spielerId)) return t;
          return { ...t, spielerIds: [...t.spielerIds, spielerId], isSpontanBuchung: true };
        })
      );
      alert(`"${name}" wurde zum bestehenden Training hinzugefügt!`);
      return;
    }

    // Neues Training erstellen
    const trainingId = uid();
    const neuesTraining: Training = {
      id: trainingId,
      trainerId: s.trainerId,
      datum: s.datum,
      uhrzeitVon: s.uhrzeitVon.slice(0, 5),
      uhrzeitBis: s.uhrzeitBis.slice(0, 5),
      spielerIds: [spielerId],
      tarifId: s.tarifId,
      customPreisProStunde: s.customPreisProStunde,
      status: "geplant",
      anlage: s.anlage,
      isSpontanBuchung: true,
    };
    setTrainings((prev) => [...prev, neuesTraining]);

    // Spontane Stunde mit Training-ID aktualisieren
    try {
      await supabase
        .from("spontane_stunden")
        .update({ training_id: trainingId })
        .eq("id", s.id);
    } catch (err) {
      console.error("Error updating training_id:", err);
    }

    // Lokalen State aktualisieren
    setSpontaneStunden((prev) =>
      prev.map((item) =>
        item.id === s.id ? { ...item, trainingId } : item
      )
    );

    alert(`Training für "${name}" wurde erstellt!`);
  }

  function adoptPlayerFromRequest(req: RegistrationRequest) {
    let vorname = (req.trainee_vorname || "").trim();
    let nachname = (req.trainee_nachname || "").trim();

    if (!vorname && !nachname) {
      // Legacy-Fallback: req.name splitten (letztes Wort = Nachname)
      const parts = (req.name || "").trim().split(/\s+/);
      if (parts.length === 1) {
        vorname = parts[0];
      } else if (parts.length > 1) {
        nachname = parts.pop() || "";
        vorname = parts.join(" ");
      }
    }

    // Aus der Anmeldung ableitbare Profildaten (KEINE Bank-/Mandatsdaten – die
    // kommen separat über Formulare → SEPA).
    const email = (req.email || "").trim();
    const telefon = (req.telefon || "").trim();
    const adresse = getRegistrationKontaktAdresse(req);
    const kontaktName = isRegistrationAbweichend(req) ? getRegistrationKontaktName(req) : "";
    const anlageLabel: "" | "Wedding" | "Britz" =
      req.anlage === "Wedding" || req.anlage === "Britz" ? req.anlage : "";

    // Existiert bereits ein Spieler mit exakt diesem Namen? Dann die Anmeldedaten
    // mit dem vorhandenen Profil abgleichen und dieses vervollständigen, statt eine
    // Dublette anzulegen. Anmeldedaten haben dabei Vorrang; leere Felder werden
    // ergänzt, vorhandene Werte werden nur überschrieben, wenn die Anmeldung einen
    // Wert liefert.
    const fullLower = `${vorname} ${nachname}`.toLowerCase().trim();
    const existing = fullLower
      ? spieler.find((s) => `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullLower)
      : undefined;

    if (existing) {
      if (
        !window.confirm(
          `Es gibt bereits einen Spieler „${existing.vorname} ${existing.nachname || ""}".\n\n` +
            `Die Anmeldedaten (E-Mail, Telefon, Adresse, Kontaktperson, Anlage) mit dem ` +
            `vorhandenen Profil abgleichen und dieses vervollständigen?\n\n` +
            `Vorhandene Werte werden durch die Anmeldedaten ersetzt, leere Felder ergänzt. ` +
            `Bank-/Mandatsdaten bleiben unberührt.`
        )
      ) {
        return;
      }
      const prevLabels = existing.labels ?? [];
      const mergedLabels =
        anlageLabel && !prevLabels.includes(anlageLabel)
          ? [...prevLabels, anlageLabel]
          : prevLabels;
      setSpieler((prev) =>
        prev.map((s) =>
          s.id === existing.id
            ? {
                ...s,
                kontaktEmail: email || s.kontaktEmail,
                kontaktTelefon: telefon || s.kontaktTelefon,
                rechnungsAdresse: adresse || s.rechnungsAdresse,
                abweichenderEmpfaenger: kontaktName ? true : s.abweichenderEmpfaenger,
                empfaengerName: kontaktName || s.empfaengerName,
                labels: mergedLabels.length > 0 ? mergedLabels : s.labels,
              }
            : s
        )
      );
      updateRequestStatus(req.id, "erledigt");
      alert(
        `Anmeldedaten in „${existing.vorname} ${existing.nachname || ""}" übernommen und das Profil vervollständigt.`
      );
      return;
    }

    // Kein vorhandener Spieler: wie bisher ein neues Spieler-Formular vorbefüllen.
    setSpielerVorname(vorname);
    setSpielerNachname(nachname);
    setSpielerEmail(req.email);
    setSpielerTelefon(req.telefon || "");
    setSpielerRechnung(adresse);
    if (kontaktName) {
      setSpielerAbweichenderEmpfaenger(true);
      setSpielerEmpfaengerName(kontaktName);
    } else {
      setSpielerAbweichenderEmpfaenger(false);
      setSpielerEmpfaengerName("");
    }
    setSpielerNotizen(""); // Nachricht wird nicht als Notiz übernommen

    // Labels vorbereiten
    const newLabels: string[] = [];
    if (anlageLabel) {
      newLabels.push(anlageLabel); // "Wedding" oder "Britz"
    }
    setSpielerLabels(newLabels);

    // UI-State setzen
    setVerwaltungTab("spieler");
    setShowSpielerForm(true);
    setEditingSpielerId(null);
    setSpielerError(null);

    // Nach oben scrollen
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Aus einer Tenniscamp-Anmeldung einen Spieler anlegen – inkl. SEPA-Mandat
  // (IBAN, Mandatsreferenz, Unterschriftsdatum, abweichender Empfänger bei
  // Kindern). Füllt das Spieler-Formular vor, damit man vor dem Speichern
  // noch prüfen kann.
  function adoptPlayerFromTenniscamp(anmeldung: TenniscampAnmeldung) {
    setSpielerVorname((anmeldung.teilnehmer_vorname || "").trim());
    setSpielerNachname((anmeldung.teilnehmer_nachname || "").trim());
    setSpielerEmail(anmeldung.email || "");
    setSpielerZusaetzlicheEmails([]);
    setSpielerNeueEmail("");
    setSpielerTelefon(anmeldung.telefon || "");
    // Rechnungsadresse aus der Anmeldung zusammensetzen
    const adresse = [
      (anmeldung.strasse || "").trim(),
      `${(anmeldung.plz || "").trim()} ${(anmeldung.ort || "").trim()}`.trim(),
    ].filter(Boolean).join(", ");
    setSpielerRechnung(adresse);
    setSpielerNotizen("");

    // SEPA-Mandat aus der Anmeldung übernehmen
    setSpielerIban(anmeldung.iban || "");
    setSpielerBankname("");
    setSpielerMandatsreferenz(anmeldung.mandatsreferenz || "");
    // Das SEPA-Mandat wurde bei der Anmeldung erteilt -> Anmeldedatum als Unterschriftsdatum
    setSpielerUnterschriftsdatum((anmeldung.created_at || "").slice(0, 10));
    setSpielerSepaSequenz("RCUR");
    setSpielerSepaLastschriftart("CORE");

    // Abweichender Rechnungsempfänger (Zahlungspflichtiger, z.B. Eltern beim Kindercamp)
    const zahlerName = `${(anmeldung.zahlungspflichtiger_vorname || "").trim()} ${(anmeldung.zahlungspflichtiger_nachname || "").trim()}`.trim();
    if (zahlerName) {
      setSpielerAbweichenderEmpfaenger(true);
      setSpielerEmpfaengerName(zahlerName);
    } else {
      setSpielerAbweichenderEmpfaenger(false);
      setSpielerEmpfaengerName("");
    }

    // Label "Tenniscamp" zur Filterung
    setSpielerLabels(["Tenniscamp"]);
    setNewLabelInput("");

    // UI-State: zum Spieler-Formular wechseln
    setTab("verwaltung");
    setVerwaltungTab("spieler");
    setShowSpielerForm(true);
    setEditingSpielerId(null);
    setSpielerError(null);

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Aus einem eingegangenen SEPA-Mandat (Formulare → SEPA) die Daten direkt
  // unter einem Spieler speichern – exakt wie der Kontaktbuch-CSV-Import:
  // Wird ein vorhandener Spieler über den Namen gefunden, werden dessen
  // Bank-/Mandatsdaten aktualisiert; sonst wird ein neuer Spieler angelegt.
  function adoptPlayerFromSepaMandate(mandate: SepaMandate) {
    const vorname = (mandate.vorname || "").trim();
    const nachname = (mandate.nachname || "").trim();
    if (!vorname) {
      alert("Das Mandat enthält keinen Vornamen.");
      return;
    }

    const iban = normalizeIban(mandate.iban || "");
    const mandatsreferenz = (mandate.mandatsreferenz || "").replace(/[,\s]+$/g, "").trim();
    const unterschriftsdatum = toIsoDate(mandate.unterschriftsdatum || "");
    const email = (mandate.email || "").trim();
    const telefon = (mandate.telefon || "").trim();
    const adresse = [
      (mandate.strasse || "").trim(),
      `${(mandate.plz || "").trim()} ${(mandate.ort || "").trim()}`.trim(),
    ].filter(Boolean).join(", ");
    const empfaenger = mandate.ist_kind ? (mandate.elternteil_name || "").trim() : "";
    const anlageLabel: "" | "Wedding" | "Britz" =
      mandate.anlage === "Wedding" || mandate.anlage === "Britz" ? mandate.anlage : "";

    const fullLower = `${vorname} ${nachname}`.toLowerCase().trim();
    const idxFound = spieler.findIndex(
      (s) => `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullLower
    );

    if (idxFound >= 0) {
      const existing = spieler[idxFound];
      if (
        !window.confirm(
          `Daten in den vorhandenen Spieler „${existing.vorname} ${existing.nachname || ""}" übernehmen?\n\n` +
            `IBAN, Mandatsreferenz, Unterschriftsdatum, Adresse, E-Mail und Telefon werden aktualisiert.`
        )
      ) {
        return;
      }
      const prevLabels = existing.labels ?? [];
      const mergedLabels =
        anlageLabel && !prevLabels.includes(anlageLabel)
          ? [...prevLabels, anlageLabel]
          : prevLabels;
      setSpieler((prev) =>
        prev.map((s) =>
          s.id === existing.id
            ? {
                ...s,
                iban: iban || s.iban,
                mandatsreferenz: mandatsreferenz || s.mandatsreferenz,
                unterschriftsdatum: unterschriftsdatum || s.unterschriftsdatum,
                kontaktEmail: email || s.kontaktEmail,
                kontaktTelefon: telefon || s.kontaktTelefon,
                rechnungsAdresse: adresse || s.rechnungsAdresse,
                abweichenderEmpfaenger: empfaenger ? true : s.abweichenderEmpfaenger,
                empfaengerName: empfaenger || s.empfaengerName,
                labels: mergedLabels.length > 0 ? mergedLabels : s.labels,
                sepaSequenz: s.sepaSequenz ?? "RCUR",
                sepaLastschriftart: s.sepaLastschriftart ?? "CORE",
              }
            : s
        )
      );
      updateSepaMandateStatus(mandate.id, "zugeordnet");
      alert(`Daten in „${existing.vorname} ${existing.nachname || ""}" übernommen.`);
    } else {
      if (
        !window.confirm(
          `Kein vorhandener Spieler mit dem Namen „${vorname} ${nachname}".\n\n` +
            `Neuen Spieler mit diesen Mandatsdaten anlegen?`
        )
      ) {
        return;
      }
      const neu: Spieler = {
        id: uid(),
        vorname,
        nachname: nachname || undefined,
        iban: iban || undefined,
        mandatsreferenz: mandatsreferenz || undefined,
        unterschriftsdatum: unterschriftsdatum || undefined,
        kontaktEmail: email || undefined,
        kontaktTelefon: telefon || undefined,
        rechnungsAdresse: adresse || undefined,
        abweichenderEmpfaenger: empfaenger ? true : undefined,
        empfaengerName: empfaenger || undefined,
        labels: anlageLabel ? [anlageLabel] : undefined,
        sepaSequenz: "RCUR",
        sepaLastschriftart: "CORE",
      };
      setSpieler((prev) => [...prev, neu]);
      updateSepaMandateStatus(mandate.id, "zugeordnet");
      alert(`Neuer Spieler „${vorname} ${nachname}" angelegt.`);
    }
  }

  function adoptMultiplePlayersFromRequests() {
    const selectedReqs = registrationRequests.filter(r => selectedRequestIds.has(r.id));
    if (selectedReqs.length === 0) return;

    let addedCount = 0;
    let skippedCount = 0;

    selectedReqs.forEach((req) => {
      let vorname = (req.trainee_vorname || "").trim();
      let nachname = (req.trainee_nachname || "").trim();

      if (!vorname && !nachname) {
        // Legacy-Fallback
        const parts = (req.name || "").trim().split(/\s+/);
        if (parts.length === 1) {
          vorname = parts[0];
        } else if (parts.length > 1) {
          nachname = parts.pop() || "";
          vorname = parts.join(" ");
        }
      }

      // Duplikatscheck
      const fullNameLower = `${vorname} ${nachname}`.toLowerCase().trim();
      const duplicate = spieler.find((s) => {
        const existingFullName = `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim();
        return existingFullName === fullNameLower;
      });

      if (duplicate) {
        skippedCount++;
        return;
      }

      // Labels vorbereiten
      const newLabels: string[] = [];
      if (req.anlage) {
        newLabels.push(req.anlage);
      }

      const neu: Spieler = {
        id: uid(),
        vorname,
        nachname: nachname || undefined,
        kontaktEmail: req.email || undefined,
        kontaktTelefon: req.telefon || undefined,
        labels: newLabels.length > 0 ? newLabels : undefined,
      };

      setSpieler((prev) => [...prev, neu]);
      addedCount++;
    });

    // Dialog schließen und Auswahl aufheben
    setShowAdoptConfirmDialog(false);
    setSelectedRequestIds(new Set());

    // Feedback anzeigen
    if (skippedCount > 0) {
      alert(`${addedCount} Spieler übernommen. ${skippedCount} übersprungen (bereits vorhanden).`);
    } else {
      alert(`${addedCount} Spieler erfolgreich übernommen!`);
    }
  }

  function autoSelectTarif(spielerCount: number) {
    const tarifList = tarife;
    const nameLC = (t: Tarif) => t.name.toLowerCase();
    if (spielerCount === 1) {
      const found = tarifList.find(t => nameLC(t).includes("einzel"));
      if (found) setTTarifId(found.id);
    } else if (spielerCount === 2) {
      const found = tarifList.find(t => nameLC(t).includes("2er") && t.preisProStunde === 25);
      if (found) setTTarifId(found.id);
    } else if (spielerCount === 4) {
      const found = tarifList.find(t => t.abrechnung === "monatlich" && t.preisProStunde === 60);
      if (found) setTTarifId(found.id);
    }
    // Bei 3 Spielern: nichts automatisch auswählen
  }

  function toggleSpielerPick(id: string) {
    setTSpielerIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Beim Tenniscamp keinen Tarif automatisch wählen – Camp-Teilnehmer werden
      // nicht über die Monatsabrechnung, sondern nur per Camp-SEPA abgerechnet.
      if (!tIsTenniscamp) autoSelectTarif(next.length);
      return next;
    });
  }





  const trainingPreisGesamt = useCallback((t: Training) => {
    if (t.isPrivat || t.isTenniscamp) return 0;
    const cfg = getPreisConfig(t, tarifById);
    if (!cfg) return 0;

    if (cfg.abrechnung === "monatlich") return 0;

    const mins = durationMin(t.uhrzeitVon, t.uhrzeitBis);
    const basis = cfg.preisProStunde * (mins / 60);

    if (cfg.abrechnung === "proSpieler") {
      return basis * t.spielerIds.length;
    }
    return basis;
  }, [tarifById]);

  const priceFuerSpieler = useCallback((t: Training) => {
    if (t.isPrivat || t.isTenniscamp) return 0;
    if (t.status === "abgesagt" && t.cancelFee !== undefined) return t.cancelFee;
    const cfg = getPreisConfig(t, tarifById);
    if (!cfg) return 0;

    if (cfg.abrechnung === "monatlich") return 0;

    const plannedMins = durationMin(t.uhrzeitVon, t.uhrzeitBis);
    const actualMins = (t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < plannedMins)
      ? t.actualMinutes : plannedMins;
    const basis = cfg.preisProStunde * (actualMins / 60);

    if (cfg.abrechnung === "proSpieler") return basis;
    const n = Math.max(1, t.spielerIds.length);
    return basis / n;
  }, [tarifById]);

  // Honorar für ein Training. Mit overrideTrainerId wird gezielt das Honorar für
  // einen bestimmten Trainer berechnet (z.B. je Trainer beim Tenniscamp) – ohne
  // Vertretungs-/Manuell-Anpassungs-Logik, die auf den einzelnen Trainer zielt.
  const trainerHonorarFuerTraining = useCallback((t: Training, overrideTrainerId?: string) => {
    if (t.isPrivat) return 0;
    if (t.status === "abgesagt") return 0;
    if (!overrideTrainerId && typeof trainerHonorarAnpassungen[t.id] === "number") {
      return trainerHonorarAnpassungen[t.id];
    }
    // Wenn eine Vertretung existiert, den Vertretungstrainer für Honorar verwenden
    const vertretung = overrideTrainerId ? undefined : vertretungen.find(v => v.trainingId === t.id);
    const tid = overrideTrainerId || vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
    const trainer = trainerById.get(tid);
    const rate = trainer?.stundensatz ?? 0;
    const plannedMins = durationMin(t.uhrzeitVon, t.uhrzeitBis);
    const actualMins = (t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < plannedMins) ? t.actualMinutes : plannedMins;
    return round2(rate * (actualMins / 60));
  }, [vertretungen, trainerById, defaultTrainerId, trainerHonorarAnpassungen]);

  // Effektive Trainer eines Trainings: beim Camp mit mehreren Trainern alle,
  // sonst der (Einzel-)Trainer. Basis für Honorar-Anzeige und -Berechnung.
  const campTrainerIdsOf = useCallback((t: Training): string[] => {
    if (t.isTenniscamp && t.trainerIds && t.trainerIds.length > 0) return t.trainerIds;
    return t.trainerId ? [t.trainerId] : [];
  }, []);

  // Gesamt-Honorar eines Trainings über alle beteiligten Trainer (Camp = Summe).
  const trainerHonorarGesamtFuerTraining = useCallback((t: Training): number => {
    const ids = campTrainerIdsOf(t);
    if (ids.length <= 1) return trainerHonorarFuerTraining(t);
    return round2(ids.reduce((s, id) => s + trainerHonorarFuerTraining(t, id), 0));
  }, [campTrainerIdsOf, trainerHonorarFuerTraining]);

  function fillTrainingFromSelected(t: Training) {
    if (isTrainer) return;
    // Wenn eine Vertretung existiert, den Vertretungstrainer übernehmen
    const vertretung = vertretungen.find(v => v.trainingId === t.id);
    const effectiveTrainerId = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
    setTTrainerId(effectiveTrainerId);
    setTDatum(t.datum);
    setTVon(t.uhrzeitVon);
    setTBis(t.uhrzeitBis);
    setTTarifId(t.tarifId ?? "");
    setTStatus(t.status);
    setTActualMinutes(t.actualMinutes ? String(t.actualMinutes) : "");
    setTNotiz(t.notiz ?? "");
    setTSpielerIds(t.spielerIds);
    setSelectedTrainingId(t.id);
    setRepeatWeekly(false);
    setApplySerieScope("nurDieses");
    setTCustomPreisProStunde(
      typeof t.customPreisProStunde === "number" ? t.customPreisProStunde : ""
    );
    setTCustomAbrechnung(t.customAbrechnung ?? "proTraining");
    setTAnlage(t.anlage ?? "Wedding");
    setTIsPrivat(t.isPrivat ?? false);
    setTIsTenniscamp(t.isTenniscamp ?? false);
    setTCampGebuehr(typeof t.campGebuehr === "number" ? t.campGebuehr : "");
    setTCampTrainerIds(
      t.trainerIds && t.trainerIds.length > 0
        ? t.trainerIds
        : (t.trainerId ? [t.trainerId] : [])
    );
    setTCampBisDatum(t.datum);
    setTab("training");
  }

  function resetTrainingForm() {
    setSelectedTrainingId(null);
    setTTrainerId(defaultTrainerId);
    setTDatum(todayISO());
    setTVon("16:00");
    setTBis("17:00");
    setTStatus("geplant");
    setTActualMinutes("");
    setTNotiz("");
    setSpielerSuche("");
    setTSpielerIds([]);
    setRepeatWeekly(true);
    setRepeatUntil("2026-07-12");
    setRepeatPeriods([{ von: todayISO(), bis: "2026-07-12" }, { von: "2026-08-24", bis: "2026-09-30" }]);
    setApplySerieScope("nurDieses");
    setTTarifId("");
    setTCustomPreisProStunde("");
    setTCustomAbrechnung("proTraining");
    setTAnlage("Wedding");
    setTIsPrivat(false);
    setTIsKurzfristig(false);
    setTIsTenniscamp(false);
    setTCampGebuehr("");
    setTCampTrainerIds([]);
    setTCampBisDatum(todayISO());
  }

  // Beim Löschen eines Trainings den verknüpften Spontan-Slot wieder freigeben:
  // zukünftige Termine werden wieder online buchbar, vergangene Slots gelöscht.
  function releaseLinkedSpontaneStunden(trainingIds: Set<string>) {
    trainingIds.forEach((tid) => {
      const linked = spontaneStunden.find((s) => s.trainingId === tid);
      if (!linked) return;
      if (linked.datum >= todayISO()) {
        supabase
          .from("spontane_stunden")
          .update({ status: "offen", buchung: null, training_id: null, veroeffentlicht: true })
          .eq("id", linked.id)
          .then(() => {});
        setSpontaneStunden((prev) =>
          prev.map((s) =>
            s.id === linked.id
              ? { ...s, status: "offen" as const, buchung: undefined, trainingId: undefined, veroeffentlicht: true }
              : s
          )
        );
      } else {
        supabase.from("spontane_stunden").delete().eq("id", linked.id).then(() => {});
        setSpontaneStunden((prev) => prev.filter((s) => s.id !== linked.id));
      }
    });
  }

  function deleteTraining(id: string) {
    if (isTrainer) return;
    const existing = trainings.find((t) => t.id === id);
    if (!existing) return;

    saveUndoSnapshot("Training gelöscht");

    // Bestimme betroffene Trainings (Serie "ab heute" oder einzeln)
    let affectedTrainings: Training[];
    if (existing.serieId && applySerieScope === "abHeute") {
      const sid = existing.serieId;
      const cutoff = existing.datum;
      affectedTrainings = trainings.filter((t) => t.serieId === sid && t.datum >= cutoff);
    } else {
      affectedTrainings = [existing];
    }

    // Erstattung erfragen, sobald ein betroffenes Gruppentraining einen Preis hat
    // -- unabhängig von Abrechnungsart und Status (siehe needsRefundDialogOnDelete).
    if (affectedTrainings.some(needsRefundDialogOnDelete)) {
      openCancelDialog(affectedTrainings, 'delete');
      return;
    }

    const idsToDelete = new Set(affectedTrainings.map((t) => t.id));

    // Verknüpfte spontane Stunden wieder freigeben
    releaseLinkedSpontaneStunden(idsToDelete);

    revertCancelRefundsForDeletedTrainings(affectedTrainings);
    setTrainings((prev) => prev.filter((t) => !idsToDelete.has(t.id)));

    if (selectedTrainingId && idsToDelete.has(selectedTrainingId)) {
      resetTrainingForm();
    }
  }

  // Beim Löschen eines Trainings darf NUR die Erstattung zurückgenommen werden,
  // die dieses Training selbst verursacht hat -- also die cancelFee einer Absage
  // bei monatlichem Tarif. Früher wurde stattdessen der komplette Monats-Key des
  // Spielers gelöscht: damit fielen auch die Erstattungen anderer Absagen und
  // manuelle Korrekturen desselben Monats weg (die "Erstattung Absage"-Zeile blieb
  // sichtbar, das Geld war aber wieder auf der Rechnung -> "Weiterer Aufschlag").
  function revertCancelRefundsForDeletedTrainings(deletedTrainings: Training[]) {
    setMonthlyAdjustments((prev) => {
      const next = { ...prev };
      let changed = false;
      deletedTrainings.forEach((t) => {
        if (t.status !== "abgesagt") return;
        const refund = t.cancelFee ?? 0;
        if (refund <= 0) return;
        // Nur monatliche Tarife schreiben überhaupt in monthlyAdjustments;
        // bei proTraining/proSpieler ist cancelFee der Restbetrag am Training.
        const cfg = getPreisConfig(t, tarifById);
        if (cfg?.abrechnung !== "monatlich") return;
        const monat = t.datum.substring(0, 7);
        t.spielerIds.forEach((pid) => {
          const key = `${monat}__${pid}`;
          // Kein Key = nichts zurückzunehmen. Sonst würde aus einer bereits
          // entfernten Erstattung ein Aufschlag entstehen.
          if (!(key in next)) return;
          const value = round2((next[key] ?? 0) + refund);
          if (value === 0) delete next[key];
          else next[key] = value;
          changed = true;
        });
      });
      return changed ? next : prev;
    });
  }

  // Löschen entfernt den Termin komplett aus der Abrechnung. Erstattet werden soll
  // aber nur `refundPerPlayer` -- der einbehaltene Rest wird deshalb wieder
  // aufgeschlagen (refundBasePerPlayer minus Erstattung). "Ohne Erstattung"
  // (refundPerPlayer = 0) berechnet den Termin damit voll weiter, volle Erstattung
  // lässt nichts stehen.
  function applyRetentionForDeletedTrainings(trainingsList: Training[], refundPerPlayer: number) {
    setMonthlyAdjustments((prev) => {
      const next = { ...prev };
      let changed = false;
      trainingsList.forEach((t) => {
        const retention = round2(Math.max(0, refundBasePerPlayer(t) - refundPerPlayer));
        if (retention === 0) return;
        const monat = t.datum.substring(0, 7);
        t.spielerIds.forEach((pid) => {
          const key = `${monat}__${pid}`;
          const value = round2((next[key] ?? 0) + retention);
          if (value === 0) delete next[key];
          else next[key] = value;
          changed = true;
        });
      });
      return changed ? next : prev;
    });
  }

  function executeDeleteTrainings(trainingsList: Training[]) {
    saveUndoSnapshot(`${trainingsList.length} Training(s) gelöscht`);
    const idsToDelete = new Set(trainingsList.map((t) => t.id));

    // Verknüpfte spontane Stunden wieder freigeben
    releaseLinkedSpontaneStunden(idsToDelete);

    revertCancelRefundsForDeletedTrainings(trainingsList);
    setTrainings((prev) => prev.filter((t) => !idsToDelete.has(t.id)));
    if (selectedTrainingId && idsToDelete.has(selectedTrainingId)) {
      resetTrainingForm();
    }
  }

  // cancelFee-Bedeutung je Tarif:
  //  - monatlich: gespeicherter Erstattungsbetrag (der Termin bleibt über die
  //    cancelFee in der Abo-Zeile stehen, die Erstattung läuft über
  //    monthlyAdjustments)
  //  - proTraining/proSpieler: Restbetrag, den der Spieler für die abgesagte
  //    Einheit zahlt
  // Ohne Erstattung (refundPerPlayer = 0) bleibt cancelFee leer -- dann wird der
  // Termin gar nicht berechnet.
  function cancelFeeForTraining(t: Training, refundPerPlayer: number): number | undefined {
    if (refundPerPlayer <= 0) return undefined;
    const cfg = getPreisConfig(t, tarifById);
    if (cfg?.abrechnung === "monatlich") return refundPerPlayer;
    const rest = round2(Math.max(0, fullPricePerPlayer(t) - refundPerPlayer));
    return rest > 0 ? rest : undefined;
  }

  function executeCancelTrainings(trainingsList: Training[], refundPerPlayer?: number) {
    // cancelFee pro Training bestimmen: in einer Mehrfachauswahl können Termine
    // mit unterschiedlichen Tarifen stecken.
    const feeById = new Map<string, number | undefined>();
    trainingsList.forEach((t) => {
      feeById.set(t.id, refundPerPlayer !== undefined ? cancelFeeForTraining(t, refundPerPlayer) : undefined);
    });
    setTrainings((prev) =>
      prev.map((t) => {
        if (!feeById.has(t.id)) return t;
        const fee = feeById.get(t.id);
        return {
          ...t,
          status: "abgesagt" as TrainingStatus,
          ...(fee !== undefined && fee > 0 ? { cancelFee: fee } : {}),
        };
      })
    );
  }

  // Absage mehrerer Trainings: für Gruppentrainings mit Preis wird die Erstattung
  // erfragt (gleiche Regel wie bei der Einzel-Absage im Training-Tab), alle
  // übrigen werden direkt abgesagt.
  function cancelTrainingsWithRefundQuestion(trainingsList: Training[]) {
    const mitFrage = trainingsList.filter(needsRefundDialogOnCancel);
    const ohneFrage = trainingsList.filter((t) => !needsRefundDialogOnCancel(t));
    if (ohneFrage.length > 0) executeCancelTrainings(ohneFrage);
    if (mitFrage.length > 0) openCancelDialog(mitFrage, 'cancel');
  }

  function calcPerTrainingPrice(t: Training): number {
    const cfg = getPreisConfig(t, tarifById);
    if (!cfg) return 0;
    if (cfg.abrechnung === "monatlich") {
      const weekday = new Date(t.datum + "T12:00:00").getDay();
      const total = weekdayOccurrencesInMonth(t.datum.substring(0, 7), weekday);
      return round2(cfg.preisProStunde / (total || 1));
    }
    return round2(priceFuerSpieler(t));
  }

  // Voller Preis pro Spieler für einen Termin -- ohne eine evtl. schon gebuchte
  // Absagegebühr, damit die Prozent-Buttons im Dialog den echten Wert zeigen
  // (priceFuerSpieler liefert bei "abgesagt" sonst die alte cancelFee zurück).
  function fullPricePerPlayer(t: Training): number {
    if (t.isPrivat || t.isTenniscamp) return 0;
    const cfg = getPreisConfig(t, tarifById);
    if (!cfg) return 0;
    if (cfg.abrechnung === "monatlich") return calcPerTrainingPrice(t);
    return round2(priceFuerSpieler({ ...t, status: "geplant", cancelFee: undefined }));
  }

  // Basisbetrag für Erstattung und Einbehalt beim Löschen -- der Betrag, um den
  // es für diesen Termin pro Spieler geht:
  //  - monatlich: der Anteil an der Monatspauschale (auch bei geplanten
  //    Terminen, die Pauschale ist unabhängig vom Status geschuldet)
  //  - abgesagt: die gebuchte Absagegebühr bzw. Erstattung -- mehr steht für den
  //    Termin nicht auf der Rechnung
  //  - geplant/durchgeführt: der volle Terminpreis
  function refundBasePerPlayer(t: Training): number {
    if (t.isPrivat || t.isTenniscamp) return 0;
    const cfg = getPreisConfig(t, tarifById);
    if (!cfg) return 0;
    if (cfg.abrechnung === "monatlich") return calcPerTrainingPrice(t);
    if (t.status === "abgesagt") return round2(t.cancelFee ?? 0);
    return fullPricePerPlayer(t);
  }

  // Einheitliche Regel für die Erstattungsfrage: ab 2 Spielern und sobald der
  // Termin einen Preis hat -- bei jeder Abrechnungsart und in jedem Status.
  // Einzeltrainings (1 Spieler) werden bewusst ohne Rückfrage gelöscht/abgesagt.
  function needsRefundDialogOnDelete(t: Training): boolean {
    return t.spielerIds.length > 1 && refundBasePerPlayer(t) > 0;
  }

  // Beim Absagen zählt der volle Terminpreis: auch bei einem noch geplanten
  // Termin ist zu entscheiden, ob eine Absagegebühr berechnet wird. Bereits
  // abgesagte Termine bleiben außen vor -- sonst würde eine zweite Absage die
  // schon gebuchte Erstattung ein zweites Mal abziehen.
  function needsRefundDialogOnCancel(t: Training): boolean {
    return t.status !== "abgesagt" && t.spielerIds.length > 1 && fullPricePerPlayer(t) > 0;
  }

  function openCancelDialog(affectedTrainings: Training[], action: 'cancel' | 'delete', fromSaveTraining?: boolean) {
    // Referenzbetrag = der höchste Betrag, um den es bei den betroffenen Terminen
    // geht (in einer Serie können einzelne Termine 0 sein, z.B. noch nicht
    // berechnete Einzelpreis-Termine).
    const reference = round2(
      affectedTrainings.reduce(
        (max, t) => Math.max(max, action === 'delete' ? refundBasePerPlayer(t) : fullPricePerPlayer(t)),
        0
      )
    );
    setCancelTrainingDialog({ trainings: affectedTrainings, action, fromSaveTraining, fullPricePerTraining: reference });
    // Löschen: der Termin fällt komplett weg -> Vorschlag "voll erstatten".
    // Absage: der Termin bleibt bestehen -> Vorschlag "halbe Stunde erstatten".
    const vorschlag = action === 'delete' ? reference : round2(reference / 2);
    setCancelAdjustmentAmount(vorschlag > 0 ? String(vorschlag) : "0");
  }

  function closeCancelDialog() {
    setCancelTrainingDialog(null);
    setCancelAdjustmentAmount("0");
  }

  // Anpassung PRO TRAINING und Spieler buchen. Jede Absage schreibt ihre eigene
  // cancelFee ans Training und erzeugt in der Abrechnung eine eigene
  // "Erstattung Absage"-Zeile -- die Summe der Anpassungen muss dazu passen.
  // (Früher wurde pro Monat nur einmal gebucht: bei zwei Absagen im selben Monat
  // erstattete die App nur eine davon.)
  function applyAdjustmentsForTrainings(trainingsList: Training[], amountPerPlayer: number) {
    setMonthlyAdjustments((prev) => {
      const next = { ...prev };
      trainingsList.forEach((training) => {
        if (!training.datum || training.datum.length < 7) return;
        const monat = training.datum.substring(0, 7);
        if (!/^\d{4}-\d{2}$/.test(monat)) return;
        training.spielerIds.forEach((spielerId) => {
          const key = `${monat}__${spielerId}`;
          const value = round2((next[key] ?? 0) + amountPerPlayer);
          if (value === 0) delete next[key];
          else next[key] = value;
        });
      });
      return next;
    });
  }

  function handleCancelDialogConfirm(withAdjustment: boolean) {
    if (!cancelTrainingDialog) return;

    const { trainings: affectedTrainings, action, fromSaveTraining } = cancelTrainingDialog;
    const erstattung = withAdjustment ? (parseFloat(cancelAdjustmentAmount) || 0) : 0;

    if (action === 'delete') {
      // Reihenfolge wichtig: executeDeleteTrainings nimmt zuerst eine evtl. schon
      // gebuchte Absage-Erstattung dieses Trainings zurück, danach wird der
      // einbehaltene Rest aufgeschlagen (siehe applyRetentionForDeletedTrainings).
      executeDeleteTrainings(affectedTrainings);
      applyRetentionForDeletedTrainings(affectedTrainings, erstattung);
      closeCancelDialog();
      return;
    }

    // Absage bei monatlichem Tarif: Erstattung explizit buchen. Der Termin bleibt
    // über die cancelFee in der Abo-Zeile stehen, deshalb muss das Geld über
    // monthlyAdjustments zurück. Bei proTraining/proSpieler steckt die Erstattung
    // dagegen im Restbetrag am Training selbst (cancelFeeForTraining).
    if (erstattung > 0) {
      const monatliche = affectedTrainings.filter(
        (t) => getPreisConfig(t, tarifById)?.abrechnung === "monatlich"
      );
      if (monatliche.length > 0) applyAdjustmentsForTrainings(monatliche, -erstattung);
    }

    if (fromSaveTraining) {
      // Wenn vom Training-Tab aufgerufen, saveTraining mit skipCancelCheck aufrufen
      const first = affectedTrainings[0];
      closeCancelDialog();
      saveTraining(true, first ? cancelFeeForTraining(first, erstattung) : undefined);
      return;
    }

    executeCancelTrainings(affectedTrainings, erstattung);
    closeCancelDialog();
  }

  function toggleTrainingSelection(id: string) {
    setSelectedTrainingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function clearTrainingSelection() {
    setSelectedTrainingIds([]);
  }

  function batchUpdateStatusForSelected(newStatus: TrainingStatus) {
    if (isTrainer) return;
    if (selectedTrainingIds.length === 0) return;

    // Bei Status "abgesagt": Benachrichtigungs-Dialog anzeigen
    if (newStatus === "abgesagt") {
      const trainingsToCancel = trainings.filter((t) => selectedTrainingIds.includes(t.id));

      // Prüfen ob Spieler mit E-Mail vorhanden sind
      const hasPlayersWithEmail = trainingsToCancel.some((t) =>
        t.spielerIds.some((id) => spielerById.get(id)?.kontaktEmail)
      );

      if (hasPlayersWithEmail) {
        // Dialog anzeigen - Standardtext generieren
        const trainingDetails = trainingsToCancel.map((t) => {
          const [y, m, d] = t.datum.split("-");
          const germanDate = d && m && y ? `${d}.${m}.${y}` : t.datum;
          return { datum: germanDate, uhrzeit: `${t.uhrzeitVon} - ${t.uhrzeitBis}` };
        });

        const defaultSubject = trainingsToCancel.length === 1
          ? `Training am ${trainingDetails[0].datum} abgesagt`
          : `Trainingsabsage`;

        const defaultBody = `Hallo [Name],

leider kann das Training heute krankheitsbedingt nicht stattfinden. Es tut uns leid für die Kurzfristigkeit. Die Trainingsgebühr wird selbstverständlich nicht berechnet.

Nächste Woche geht es wieder regulär weiter.

Sportliche Grüße
Tennisschule A bis Z`;

        setCancelNotifySubject(defaultSubject);
        setCancelNotifyBody(defaultBody);
        setCancelNotifyDialog({
          trainings: trainingsToCancel,
          onConfirm: () => {
            cancelTrainingsWithRefundQuestion(trainingsToCancel);
          }
        });
        clearTrainingSelection();
        return;
      }

      // Keine Spieler mit E-Mail - direkt absagen (Erstattungsfrage bleibt)
      cancelTrainingsWithRefundQuestion(trainingsToCancel);
      clearTrainingSelection();
      return;
    }

    setTrainings((prev) =>
      prev.map((t) =>
        selectedTrainingIds.includes(t.id) ? { ...t, status: newStatus } : t
      )
    );
    if (newStatus === "durchgefuehrt") {
      selectedTrainingIds.forEach((id) => {
        triggerDonePulse(id);
      });
    }
    clearTrainingSelection();
  }

  function batchSetDurchgefuehrtUndBarBezahlt() {
    if (isTrainer) return;
    if (selectedTrainingIds.length === 0) return;
    
    setTrainings((prev) =>
      prev.map((t) =>
        selectedTrainingIds.includes(t.id)
          ? { ...t, status: "durchgefuehrt", barBezahlt: true }
          : t
      )
    );
    // Kein automatisches Setzen von payments - der Status wird über das Dropdown gesteuert
    
    selectedTrainingIds.forEach((id) => {
      triggerDonePulse(id);
    });
    clearTrainingSelection();
  }

  function batchDeleteSelectedTrainings() {
    if (isTrainer) return;
    if (selectedTrainingIds.length === 0) return;

    const selected = trainings.filter((t) => selectedTrainingIds.includes(t.id));
    // Für Gruppentrainings mit Geldwirkung die Erstattung erfragen, alle übrigen
    // direkt löschen -- gleiche Regel wie beim Löschen eines einzelnen Trainings.
    const mitFrage = selected.filter(needsRefundDialogOnDelete);
    const ohneFrage = selected.filter((t) => !needsRefundDialogOnDelete(t));

    if (ohneFrage.length > 0) executeDeleteTrainings(ohneFrage);
    if (mitFrage.length > 0) openCancelDialog(mitFrage, 'delete');
    clearTrainingSelection();
  }

  function batchChangeTrainerForSelected() {
    if (isTrainer) return;
    if (selectedTrainingIds.length === 0) return;
    const tid = batchTrainerId || defaultTrainerId;
    if (!tid) return;
    setTrainings((prev) =>
      prev.map((t) =>
        selectedTrainingIds.includes(t.id) ? { ...t, trainerId: tid } : t
      )
    );
    clearTrainingSelection();
  }

  function triggerDonePulse(trainingId: string) {
    setDoneFlashId(trainingId);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => {
      setDoneFlashId((prev) => (prev === trainingId ? null : prev));
    }, 650);

    const el = document.querySelector(
      `[data-training-id="${trainingId}"]`
    ) as HTMLElement | null;
    if (el) {
      el.animate(
        [
          { transform: "scale(1)", filter: "brightness(1)" },
          { transform: "scale(1.06)", filter: "brightness(1.15)" },
          { transform: "scale(1)", filter: "brightness(1)" },
        ],
        { duration: 650, easing: "ease-out" }
      );
    }
  }

  function markTrainingDone(trainingId: string) {
    let changed = false;

    setTrainings((prev) =>
      prev.map((t) => {
        if (t.id !== trainingId) return t;
        if (t.status !== "geplant") return t;
        changed = true;
        return { ...t, status: "durchgefuehrt" };
      })
    );

    if (changed) triggerDonePulse(trainingId);
  }

  function markTrainingDoneAndBarBezahlt(trainingId: string) {
    if (isTrainer) return;
    
    const training = trainings.find((t) => t.id === trainingId);
    if (!training || training.status !== "geplant") return;
    
    setTrainings((prev) =>
      prev.map((t) => {
        if (t.id !== trainingId) return t;
        if (t.status !== "geplant") return t;
        return { ...t, status: "durchgefuehrt", barBezahlt: true };
      })
    );
    // Kein automatisches Setzen von payments - der Status wird über das Dropdown gesteuert

    triggerDonePulse(trainingId);
  }

  function goToToday() {
    const t = todayISO();
    setWeekAnchor(t);
    
    // Immer den aktuellen Wochentag setzen
    const d = new Date(t + "T12:00:00");
    const idx = (d.getDay() + 6) % 7;
    setDayIndex(idx);
  }

  function handleCalendarEventClick(t: Training, e: React.MouseEvent) {
    // Wenn Long-Press gerade ausgelöst wurde, nicht auch noch den Klick verarbeiten
    if (longPressTriggered) {
      setLongPressTriggered(false);
      return;
    }
    
    // Strg+Klick (Windows/Linux) oder Cmd+Klick (Mac) für Mehrfachauswahl
    if ((e.ctrlKey || e.metaKey) && !isTrainer) {
      e.preventDefault();
      e.stopPropagation();
      toggleTrainingSelection(t.id);
      return;
    }
    
    if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
    clickTimerRef.current = window.setTimeout(() => {
      fillTrainingFromSelected(t);
      clickTimerRef.current = null;
    }, 220);
  }
  
  function handleCalendarEventTouchStart(t: Training) {
    if (isTrainer) return;
    
    // Long-Press Timer starten (500ms)
    setLongPressTriggered(false);
    longPressTimerRef.current = window.setTimeout(() => {
      setLongPressTriggered(true);
      toggleTrainingSelection(t.id);
      // Vibration für haptisches Feedback (falls unterstützt)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  }
  
  function handleCalendarEventTouchEnd() {
    // Long-Press Timer abbrechen wenn Touch endet vor Ablauf
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }
  
  function handleCalendarEventTouchMove() {
    // Long-Press abbrechen bei Bewegung
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  function handleCalendarEventDoubleClick(t: Training) {
    if (isTrainer) return;
    if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
    clickTimerRef.current = null;
    markTrainingDone(t.id);
  }

  function saveTraining(skipCancelCheck?: boolean, pendingCancelFee?: number) {
    if (isTrainer) return;
    const hasTarif = !!tTarifId;
    const customPreis =
      !tTarifId &&
      typeof tCustomPreisProStunde === "number" &&
      tCustomPreisProStunde > 0
        ? tCustomPreisProStunde
        : undefined;

    if (!tDatum || !tVon || !tBis || (!tIsPrivat && !tIsKurzfristig && !tIsTenniscamp && tSpielerIds.length === 0)) return;
    const mins = durationMin(tVon, tBis);
    if (mins <= 0) return;
    if (!tIsPrivat && !tIsKurzfristig && !tIsTenniscamp && !hasTarif && !customPreis) return;
    const trainerIdForSave = tTrainerId || defaultTrainerId;
    if (!trainerIdForSave) return;

    // Tenniscamp: über einen Zeitraum für jeden Tag ein (spielerloses) Training anlegen.
    // Keine Spieler-Abrechnung – der Trainer wird pro Tag mit seinem Stundensatz vergütet.
    if (tIsTenniscamp && !selectedTrainingId) {
      const bis = tCampBisDatum && tCampBisDatum >= tDatum ? tCampBisDatum : tDatum;
      const serieId = uid();
      const created: Training[] = [];
      // Alle Camp-Trainer (jeder bekommt seinen Stundenlohn); erster ist der primäre Trainer.
      const campTrainerIds = tCampTrainerIds.length > 0 ? tCampTrainerIds : [trainerIdForSave];
      let d = tDatum;
      while (d <= bis) {
        created.push({
          id: uid(),
          datum: d,
          uhrzeitVon: tVon,
          uhrzeitBis: tBis,
          trainerId: campTrainerIds[0],
          trainerIds: campTrainerIds,
          spielerIds: tSpielerIds,
          status: tStatus,
          notiz: tNotiz.trim() || undefined,
          serieId,
          anlage: tAnlage,
          isTenniscamp: true,
          campGebuehr: typeof tCampGebuehr === "number" && tCampGebuehr > 0 ? tCampGebuehr : undefined,
        });
        d = addDaysISO(d, 1);
      }
      if (created.length === 0) return;
      saveUndoSnapshot("Tenniscamp angelegt");
      setTrainings((prev) => [...prev, ...created]);
      resetTrainingForm();
      setTab("kalender");
      return;
    }

    const existing = selectedTrainingId
      ? trainings.find((x) => x.id === selectedTrainingId)
      : undefined;

    if (selectedTrainingId && existing) {
      if (tStatus !== "abgesagt") {
        saveUndoSnapshot("Training geändert");
      }

      // Prüfen ob Status von "abgesagt" auf "geplant" geändert wird - Rücknahme des Abzugs anbieten
      if (
        !skipCancelCheck &&
        existing.status === "abgesagt" &&
        tStatus === "geplant"
      ) {
        // Nur fragen, wenn GENAU DIESES Training selbst eine monatliche Erstattung
        // verursacht hat. Bei monatlichem Tarif wird die cancelFee zugleich von
        // monthlyAdjustments abgezogen, daher ist die cancelFee des Trainings das
        // eindeutige Signal. Andere Absagen im selben Monat (z.B. ein separates
        // Gruppentraining) dürfen NICHT mitgezählt werden – sonst fragt ein
        // reaktiviertes Einzeltraining nach der Rücknahme einer fremden Erstattung.
        const cfg = getPreisConfig(existing, tarifById);
        const isMonatlich = cfg?.abrechnung === "monatlich";
        const refundPerPlayer = existing.cancelFee ?? 0;

        if (isMonatlich && refundPerPlayer > 0) {
          const monat = existing.datum.substring(0, 7);
          setReverseAdjustmentDialog({
            training: existing,
            refundPerPlayer,
            onConfirm: (reverseAdjustment) => {
              if (reverseAdjustment) {
                // Erstattung dieses Trainings rückgängig machen (+cancelFee pro Spieler).
                // Funktionales Update: der Dialog kann länger offen stehen, ein
                // Snapshot aus dem Render-Closure wäre womöglich veraltet.
                setMonthlyAdjustments((prev) => {
                  const next = { ...prev };
                  existing.spielerIds.forEach((spielerId) => {
                    const key = `${monat}__${spielerId}`;
                    const value = round2((next[key] ?? 0) + refundPerPlayer);
                    if (value === 0) delete next[key];
                    else next[key] = value;
                  });
                  return next;
                });
              }
              // Training auf geplant setzen
              saveTraining(true);
            }
          });
          return;
        }
      }

      // Prüfen ob Status auf "abgesagt" geändert wird - Benachrichtigungs-Dialog anzeigen
      if (
        !skipCancelCheck &&
        existing.status !== "abgesagt" &&
        tStatus === "abgesagt"
      ) {
        const trainingForDialog: Training = {
          ...existing,
          trainerId: trainerIdForSave,
          datum: tDatum,
          uhrzeitVon: tVon,
          uhrzeitBis: tBis,
          tarifId: hasTarif ? tTarifId : undefined,
          spielerIds: tSpielerIds,
          // Bewusst der BISHERIGE Status (hier immer != "abgesagt", siehe Bedingung
          // oben) und nicht tStatus: needsRefundDialogOnCancel schließt bereits
          // abgesagte Termine aus, damit eine zweite Absage die Erstattung nicht
          // doppelt bucht. Mit tStatus ("abgesagt") hätte sich die Erstattungsfrage
          // hier immer selbst blockiert. Gespeichert wird ohnehin über saveTraining,
          // dieses Objekt dient nur den Dialogen.
          status: existing.status,
          notiz: tNotiz.trim() || undefined,
          customPreisProStunde: customPreis,
          customAbrechnung: !hasTarif ? tCustomAbrechnung : undefined,
        };

        // Prüfen ob Spieler mit E-Mail vorhanden sind
        const hasPlayersWithEmail = tSpielerIds.some((id) => spielerById.get(id)?.kontaktEmail);

        if (hasPlayersWithEmail) {
          // Standardtext generieren
          const [y, m, d] = tDatum.split("-");
          const germanDate = d && m && y ? `${d}.${m}.${y}` : tDatum;

          const defaultSubject = `Training am ${germanDate} abgesagt`;
          const defaultBody = `Hallo [Name],

leider kann das Training heute krankheitsbedingt nicht stattfinden. Es tut uns leid für die Kurzfristigkeit. Die Trainingsgebühr wird selbstverständlich nicht berechnet.

Nächste Woche geht es wieder regulär weiter.

Sportliche Grüße
Tennisschule A bis Z`;

          setCancelNotifySubject(defaultSubject);
          setCancelNotifyBody(defaultBody);
          // Benachrichtigungs-Dialog anzeigen
          setCancelNotifyDialog({
            trainings: [trainingForDialog],
            onConfirm: () => {
              // Nach Bestätigung: Bei Gruppentraining mit Preis Abrechnungs-Dialog öffnen
              if (needsRefundDialogOnCancel(trainingForDialog)) {
                openCancelDialog([trainingForDialog], 'cancel', true);
              } else {
                // Einzeltraining / ohne Preis: direkt absagen
                saveTraining(true);
              }
            }
          });
          return;
        }

        // Keine Spieler mit E-Mail - Bei Gruppentraining mit Preis Abrechnungs-Dialog öffnen
        if (needsRefundDialogOnCancel(trainingForDialog)) {
          openCancelDialog([trainingForDialog], 'cancel', true);
          return;
        }
      }

      // Vertretungs-Logik prüfen
      const existingVertretung = vertretungen.find(v => v.trainingId === selectedTrainingId);
      const effectiveExistingTrainerId = existingVertretung?.vertretungTrainerId || existing.trainerId || defaultTrainerId;
      
      // Hat sich der Trainer gegenüber dem *aktuell angezeigten* (effektiven) Trainer geändert?
      const hasTrainerChanged = effectiveExistingTrainerId !== trainerIdForSave;

      // Bestimme die Trainer-ID für das Update
      // Wenn eine Vertretung existiert UND der Trainer im Formular nicht geändert wurde:
      // Behalte den ORIGINALEN Trainer bei (nicht den Vertretungstrainer in das Training-Objekt schreiben)
      let finalTrainerId = trainerIdForSave;
      if (existingVertretung && !hasTrainerChanged) {
        finalTrainerId = existing.trainerId || defaultTrainerId;
      }

      const payload: Training = {
        ...existing,
        trainerId: finalTrainerId,
        datum: tDatum,
        uhrzeitVon: tVon,
        uhrzeitBis: tBis,
        tarifId: hasTarif ? tTarifId : undefined,
        spielerIds: tSpielerIds,
        status: tStatus,
        notiz: tNotiz.trim() || undefined,
        customPreisProStunde: customPreis,
        customAbrechnung: !hasTarif ? tCustomAbrechnung : undefined,
        anlage: tAnlage,
        isPrivat: tIsPrivat || undefined,
        isTenniscamp: tIsTenniscamp || undefined,
        trainerIds: tIsTenniscamp && tCampTrainerIds.length > 0 ? tCampTrainerIds : undefined,
        campGebuehr: tIsTenniscamp && typeof tCampGebuehr === "number" && tCampGebuehr > 0 ? tCampGebuehr : undefined,
        actualMinutes: tStatus === "durchgefuehrt" && tActualMinutes !== "" ? (parseInt(tActualMinutes) || undefined) : undefined,
        cancelFee: tStatus === "abgesagt" ? (pendingCancelFee ?? existing.cancelFee) : undefined,
      };

      if (existing.serieId && applySerieScope === "abHeute") {
        const sid = existing.serieId;
        setTrainings((prev) =>
          prev.map((x) => {
            if (!x.serieId || x.serieId !== sid) return x;
            if (x.datum < existing.datum) return x;
            
            // Delta-Logik für Serie: Nur ändern was sich geändert hat
            // Besonders wichtig für Trainer: Nur überschreiben wenn explizit geändert
            
            return {
              ...x,
              // Trainer nur ändern wenn er explizit geändert wurde, sonst den jeweiligen Original-Trainer behalten
              trainerId: hasTrainerChanged ? payload.trainerId : x.trainerId, 
              
              uhrzeitVon: payload.uhrzeitVon,
              uhrzeitBis: payload.uhrzeitBis,
              tarifId: payload.tarifId,
              spielerIds: payload.spielerIds,
              status: payload.status,
              notiz: payload.notiz,
              customPreisProStunde: payload.customPreisProStunde,
              customAbrechnung: payload.customAbrechnung,
              anlage: payload.anlage,
              isPrivat: payload.isPrivat,
              isTenniscamp: payload.isTenniscamp,
            };
          })
        );
      } else {
        setTrainings((prev) =>
          prev.map((x) => (x.id === selectedTrainingId ? payload : x))
        );
      }

      // Falls dieses Training aus einer spontanen Stunde stammt: verknüpfte
      // spontane_stunden-Zeile mitziehen, damit der Spontan-Tab konsistent bleibt.
      syncSpontaneStundeFromTrainingEdit(selectedTrainingId, {
        datum: payload.datum,
        uhrzeitVon: payload.uhrzeitVon,
        uhrzeitBis: payload.uhrzeitBis,
        trainerId: payload.trainerId,
        tarifId: payload.tarifId,
        customPreisProStunde: payload.customPreisProStunde,
        anlage: payload.anlage,
      });

      // Tenniscamp: Teilnehmer + Camp-Gebühr + Trainer gelten fürs ganze Camp –
      // auf alle Tage der Serie spiegeln (unabhängig vom Serien-Scope).
      if (existing.isTenniscamp && existing.serieId) {
        const campSid = existing.serieId;
        const campG = typeof tCampGebuehr === "number" && tCampGebuehr > 0 ? tCampGebuehr : undefined;
        const campTrainers = tCampTrainerIds.length > 0 ? tCampTrainerIds : (existing.trainerId ? [existing.trainerId] : []);
        setTrainings((prev) =>
          prev.map((x) =>
            x.serieId === campSid
              ? {
                  ...x,
                  spielerIds: tSpielerIds,
                  campGebuehr: campG,
                  trainerIds: campTrainers.length > 0 ? campTrainers : undefined,
                  trainerId: campTrainers[0] ?? x.trainerId,
                }
              : x
          )
        );
      }

      // Vertretung löschen wenn Datum oder Trainer geändert wurde.
      // Eine reine Uhrzeit-Änderung (gleicher Tag, gleicher Vertretungstrainer)
      // hebt die Vertretung NICHT auf – sonst würde fälschlich der Originaltrainer wieder gesetzt.
      const datumChanged = existing.datum !== tDatum;

      if (datumChanged || hasTrainerChanged) {
        if (existing.serieId && applySerieScope === "abHeute") {
          // Bei Serienänderung alle betroffenen Trainings
          const serieTrainingIds = trainings
            .filter((x) => x.serieId === existing.serieId && x.datum >= existing.datum)
            .map((x) => x.id);
          setVertretungen((prev) => prev.filter((v) => !serieTrainingIds.includes(v.trainingId)));
        } else {
          setVertretungen((prev) => prev.filter((v) => v.trainingId !== selectedTrainingId));
        }
      }

      resetTrainingForm();
      setTab("kalender");
      return;
    }

    if (repeatWeekly) {
      const periods = repeatPeriods.length > 0
        ? repeatPeriods.filter(p => p.von && p.bis && p.bis >= p.von)
        : [{ von: tDatum, bis: repeatUntil }];

      if (periods.length === 0) return;

      const serieId = uid();
      const created: Training[] = [];

      for (const period of periods) {
        let d = period.von;
        // Align to the same weekday as tDatum
        const startDay = new Date(tDatum + "T00:00:00").getDay();
        const periodDay = new Date(d + "T00:00:00").getDay();
        let diff = startDay - periodDay;
        if (diff < 0) diff += 7;
        if (diff > 0) d = addDaysISO(d, diff);

        while (d <= period.bis) {
          created.push({
            id: uid(),
            datum: d,
            uhrzeitVon: tVon,
            uhrzeitBis: tBis,
            trainerId: trainerIdForSave,
            tarifId: hasTarif ? tTarifId : undefined,
            spielerIds: tSpielerIds,
            status: tStatus,
            notiz: tNotiz.trim() || undefined,
            serieId,
            customPreisProStunde: customPreis,
            customAbrechnung: !hasTarif ? tCustomAbrechnung : undefined,
            anlage: tAnlage,
            isPrivat: tIsPrivat || undefined,
          });
          d = addDaysISO(d, 7);
        }
      }

      setTrainings((prev) => [...prev, ...created]);
      resetTrainingForm();
      setTab("kalender");
      return;
    }

    const newTrainingId = uid();

    setTrainings((prev) => [
      ...prev,
      {
        id: newTrainingId,
        trainerId: trainerIdForSave,
        datum: tDatum,
        uhrzeitVon: tVon,
        uhrzeitBis: tBis,
        tarifId: hasTarif ? tTarifId : undefined,
        spielerIds: tSpielerIds,
        status: tStatus,
        notiz: tNotiz.trim() || undefined,
        customPreisProStunde: customPreis,
        customAbrechnung: !hasTarif ? tCustomAbrechnung : undefined,
        anlage: tAnlage,
        isPrivat: tIsPrivat || undefined,
        isTenniscamp: tIsTenniscamp || undefined,
        isSpontanBuchung: tIsKurzfristig || undefined,
      },
    ]);

    if (tIsKurzfristig && authUser?.accountId) {
      createSpontaneStundeFromTraining(newTrainingId, trainerIdForSave, tDatum, tVon, tBis, hasTarif ? tTarifId : undefined, customPreis, tAnlage as "Wedding" | "Britz");
    }

    resetTrainingForm();
    setTab("kalender");
  }

  const preisVorschau = useMemo(() => {
    if (tSpielerIds.length === 0) return 0;

    const hasTarif = !!tTarifId;
    const customPreis =
      !tTarifId &&
      typeof tCustomPreisProStunde === "number" &&
      tCustomPreisProStunde > 0
        ? tCustomPreisProStunde
        : undefined;

    if (!hasTarif && !customPreis) return 0;

    const fake: Training = {
      id: "x",
      trainerId: tTrainerId || defaultTrainerId,
      datum: tDatum,
      uhrzeitVon: tVon,
      uhrzeitBis: tBis,
      tarifId: hasTarif ? tTarifId : undefined,
      spielerIds: tSpielerIds,
      status: tStatus,
      notiz: tNotiz || undefined,
      customPreisProStunde: customPreis,
      customAbrechnung: !hasTarif ? tCustomAbrechnung : undefined,
    };

    return trainingPreisGesamt(fake);
  }, [
    tDatum,
    tVon,
    tBis,
    tTarifId,
    tSpielerIds,
    tStatus,
    tNotiz,
    tCustomPreisProStunde,
    tCustomAbrechnung,
    tTrainerId,
    defaultTrainerId,
    trainingPreisGesamt,
  ]);

  const trainingsInMonth = useMemo(
    () =>
      trainings
        .filter((t) => t.datum.startsWith(abrechnungMonat))
        .filter((t) => t.status === "durchgefuehrt" || (t.status === "abgesagt" && (t.cancelFee ?? 0) > 0))
        .filter((t) => !t.isPrivat)
        .filter((t) => {
          if (abrechnungTrainerFilter === "alle") return true;
          // Tenniscamp mit mehreren Trainern zählt für jeden beteiligten Trainer;
          // sonst Vertretungstrainer berücksichtigen.
          return trainingGehoertZuTrainer(t, abrechnungTrainerFilter);
        })
        .sort((a, b) =>
          (a.datum + a.uhrzeitVon).localeCompare(b.datum + b.uhrzeitVon)
        ),
    [trainings, abrechnungMonat, abrechnungTrainerFilter, trainingGehoertZuTrainer]
  );

  // Berechne für jeden Spieler, an welchen Wochentagen er wiederkehrende Trainings hat
  const spielerWochentage = useMemo(() => {
    const result = new Map<string, Set<number>>();
    trainingsInMonth.forEach((t) => {
      if (t.serieId) {
        const d = new Date(t.datum);
        const wochentag = (d.getDay() + 6) % 7; // 0=Montag, 6=Sonntag
        t.spielerIds.forEach((pid) => {
          if (!result.has(pid)) {
            result.set(pid, new Set());
          }
          result.get(pid)!.add(wochentag);
        });
      }
    });
    return result;
  }, [trainingsInMonth]);

  const trainingsForAbrechnung = useMemo(() => {
    let filtered = trainingsInMonth;

    if (abrechnungTab === "trainer") {
      filtered = filtered.filter((t) => t.status !== "abgesagt");
      if (abrechnungFilter === "bezahlt") {
        filtered = filtered.filter((t) => honorarBezahltImFilter(t));
      } else if (abrechnungFilter === "offen") {
        filtered = filtered.filter((t) => !honorarBezahltImFilter(t));
      } else if (abrechnungFilter === "bar") {
        filtered = filtered.filter((t) => t.barBezahlt);
      }
    } else {
      if (abrechnungFilter === "bar") {
        filtered = filtered.filter((t) => t.barBezahlt);
      }
    }

    if (abrechnungTab === "spieler" && abrechnungSpielerSuche.trim()) {
      const q = abrechnungSpielerSuche.trim().toLowerCase();
      filtered = filtered.filter((t) =>
        t.spielerIds.some((sid) => {
          const s = spielerById.get(sid);
          return (
            s &&
            (s.vorname.toLowerCase().includes(q) ||
              (s.nachname ?? "").toLowerCase().includes(q) ||
              (s.kontaktEmail ?? "").toLowerCase().includes(q))
          );
        })
      );
    }

    return filtered;
  }, [
    trainingsInMonth,
    abrechnungFilter,
    abrechnungTab,
    honorarBezahltImFilter,
    abrechnungSpielerSuche,
    spielerById,
  ]);

  const abrechnung = useMemo(() => {
    // 5c: Erweiterte Struktur für Bar/Nicht-Bar Unterscheidung
    const perSpieler = new Map<string, { 
      name: string; 
      sum: number; 
      countsBar: Map<number, number>;      // Beträge für bar bezahlte Trainings
      countsNichtBar: Map<number, number>; // Beträge für nicht bar bezahlte Trainings
    }>();
    // Ermittle die gesuchten Spieler-IDs bei aktiver Suche
    const searchQuery = abrechnungSpielerSuche.trim().toLowerCase();
    const searchedSpielerIds = searchQuery
      ? spieler
          .filter(
            (s) =>
              s.vorname.toLowerCase().includes(searchQuery) ||
              (s.nachname ?? "").toLowerCase().includes(searchQuery) ||
              (s.kontaktEmail ?? "").toLowerCase().includes(searchQuery)
          )
          .map((s) => s.id)
      : null; // null bedeutet keine Filterung

    const addShare = (pid: string, name: string, amount: number, isBar: boolean) => {
      const share = round2(amount);
      let entry = perSpieler.get(pid);
      if (!entry) {
        entry = { 
          name, 
          sum: 0, 
          countsBar: new Map<number, number>(),
          countsNichtBar: new Map<number, number>()
        };
        perSpieler.set(pid, entry);
      }
      entry.sum = round2(entry.sum + share);
      
      // 5c: Getrennte Zählung für Bar/Nicht-Bar
      if (isBar) {
        entry.countsBar.set(share, (entry.countsBar.get(share) ?? 0) + 1);
      } else {
        entry.countsNichtBar.set(share, (entry.countsNichtBar.get(share) ?? 0) + 1);
      }
    };

    // Für monatliche Tarife: Zähle die abzurechnenden Trainings pro Slot (Zähler für Anteilsberechnung).
    // WICHTIG: exakt dieselbe Grundmenge wie die Detailansicht verwenden (trainingsForAbrechnung),
    // also nur durchgeführte + abgesagte-mit-Gebühr. Baut man den Zähler stattdessen aus dem rohen
    // trainings-Array, werden noch *geplante* (nicht stattgefundene) Termine mitgezählt und die
    // Listensumme fällt höher aus als in der Detailansicht (siehe Anteils-Berechnung unten).
    const monthlySlotCounts = new Map<string, Map<string, number>>();
    const monthlyHasBar = new Map<string, boolean>();

    trainingsForAbrechnung
      .forEach((t) => {
        const cfg = getPreisConfig(t, tarifById);
        if (!cfg || cfg.abrechnung !== "monatlich") return;
        const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
        const weekday = new Date(t.datum + "T12:00:00").getDay();
        t.spielerIds.forEach((pid) => {
          if (searchedSpielerIds && !searchedSpielerIds.includes(pid)) return;
          const key = `${pid}__${tarifKey}`;
          const slotKey = `${weekday}_${t.uhrzeitVon}_${t.uhrzeitBis}`;
          const slotCounts = monthlySlotCounts.get(key) ?? new Map<string, number>();
          const plannedMins = durationMin(t.uhrzeitVon, t.uhrzeitBis);
          const ratio = (t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < plannedMins) ? t.actualMinutes / plannedMins : 1;
          slotCounts.set(slotKey, (slotCounts.get(slotKey) ?? 0) + ratio);
          monthlySlotCounts.set(key, slotCounts);
        });
      });

    // Bar-Status aus durchgeführten Trainings tracken
    trainingsForAbrechnung.forEach((t) => {
      const cfg = getPreisConfig(t, tarifById);
      if (!cfg || cfg.abrechnung !== "monatlich") return;
      const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
      t.spielerIds.forEach((pid) => {
        if (searchedSpielerIds && !searchedSpielerIds.includes(pid)) return;
        if (t.barBezahlt) monthlyHasBar.set(`${pid}__${tarifKey}`, true);
      });
    });

    // Jetzt die Abrechnung durchführen
    const monthlyProcessed = new Set<string>(); // Um doppelte Verarbeitung zu vermeiden

    trainingsForAbrechnung.forEach((t) => {
      const cfg = getPreisConfig(t, tarifById);
      if (!cfg) return;

      if (cfg.abrechnung === "monatlich") {
        const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
        t.spielerIds.forEach((pid) => {
          // Bei aktiver Suche nur gesuchte Spieler berücksichtigen
          if (searchedSpielerIds && !searchedSpielerIds.includes(pid)) return;
          
          const processKey = `${pid}__${tarifKey}`;
          if (monthlyProcessed.has(processKey)) return;
          monthlyProcessed.add(processKey);
          
          const name = getSpielerFullName(pid);
          const slotCounts = monthlySlotCounts.get(processKey);
          let totalAmount = 0;
          if (slotCounts) {
            slotCounts.forEach((actualCount, slotKey) => {
              const wd = parseInt(slotKey.split("_")[0]);
              const possible = weekdayOccurrencesInMonth(abrechnungMonat, wd);
              totalAmount += cfg.preisProStunde * (actualCount / (possible || 1));
            });
            totalAmount = round2(totalAmount);
          } else {
            totalAmount = cfg.preisProStunde;
          }
          const isBar = monthlyHasBar.get(processKey) ?? false;
          addShare(pid, name, totalAmount, isBar);
        });
        return;
      }

      const share = priceFuerSpieler(t);
      const isBar = t.barBezahlt === true;
      t.spielerIds.forEach((pid) => {
        // Bei aktiver Suche nur gesuchte Spieler berücksichtigen
        if (searchedSpielerIds && !searchedSpielerIds.includes(pid)) return;
        
        const name = getSpielerFullName(pid);
        addShare(pid, name, share, isBar);
      });
    });

    const spielerRows = Array.from(perSpieler.entries())
      .map(([id, v]) => {
        // 5c: Getrennte Breakdowns für Bar und Nicht-Bar
        const breakdownBar = Array.from(v.countsBar.entries())
          .map(([amount, count]) => ({
            amount,
            count,
            subtotal: round2(amount * count),
            isBar: true,
          }))
          .sort((a, b) => b.amount - a.amount);

        const breakdownNichtBar = Array.from(v.countsNichtBar.entries())
          .map(([amount, count]) => ({
            amount,
            count,
            subtotal: round2(amount * count),
            isBar: false,
          }))
          .sort((a, b) => b.amount - a.amount);

        // Kombiniertes Breakdown für Kompatibilität (ohne isBar Info)
        const allCounts = new Map<number, number>();
        v.countsBar.forEach((count, amount) => {
          allCounts.set(amount, (allCounts.get(amount) ?? 0) + count);
        });
        v.countsNichtBar.forEach((count, amount) => {
          allCounts.set(amount, (allCounts.get(amount) ?? 0) + count);
        });
        const breakdown = Array.from(allCounts.entries())
          .map(([amount, count]) => ({
            amount,
            count,
            subtotal: round2(amount * count),
          }))
          .sort((a, b) => b.amount - a.amount);

        return {
          id,
          name: v.name,
          sum: round2(v.sum),
          breakdown,
          breakdownBar,
          breakdownNichtBar,
        };
      })
      .sort((a, b) => b.sum - a.sum);

    const total = round2(spielerRows.reduce((sum, r) => sum + r.sum, 0));

    // Bar-Total aus den gefilterten spielerRows berechnen (nicht aus trainingsInMonth)
    // damit es korrekt auf den ausgewählten Spieler gefiltert ist
    let barTotal = 0;
    spielerRows.forEach((row) => {
      row.breakdownBar.forEach((item) => {
        barTotal = round2(barTotal + item.subtotal);
      });
    });

    // totalMitBar ist jetzt identisch mit total, da barTotal bereits in total enthalten ist
    // (die spielerRows enthalten bereits alle Beträge inkl. bar)
    // Für die korrekte Anzeige: total ist die Gesamtsumme, barTotal zeigt nur den Bar-Anteil
    const totalMitBar = total; // Bar ist bereits in total enthalten

    return { total, spielerRows, barTotal, totalMitBar };
  }, [
    trainingsForAbrechnung,
    spieler,
    priceFuerSpieler,
    tarifById,
    abrechnungSpielerSuche,
    getSpielerFullName,
    abrechnungMonat,
  ]);

  const abrechnungTrainer = useMemo(() => {
    type TrainerAbrechnungSummary = {
      name: string;
      sum: number;
      trainings: number;
      honorar: number;
      honorarBezahlt: number;
      honorarOffen: number;
    };

    const perTrainer = new Map<string, TrainerAbrechnungSummary>();
    // Für monatliche Tarife: Zähle geplante+durchgeführte Trainings pro Slot
    const monthlyTrainerSlotCounts = new Map<string, Map<string, number>>(); // key: `${tid}__${pid}__${tarifKey}`, inner key: "weekday_timeFrom_timeTo"

    // Alle Trainings im Monat (geplant + durchgeführt, NICHT abgesagt) für Slot-Zählung
    trainings
      .filter((t) => t.datum.startsWith(abrechnungMonat) && t.status !== "abgesagt" && !t.isPrivat)
      .forEach((t) => {
        const cfg = getPreisConfig(t, tarifById);
        if (!cfg || cfg.abrechnung !== "monatlich") return;
        const vertretung = vertretungen.find(v => v.trainingId === t.id);
        const tid = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
        const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
        const weekday = new Date(t.datum + "T12:00:00").getDay();
        t.spielerIds.forEach((pid) => {
          const key = `${tid}__${pid}__${tarifKey}`;
          const slotKey = `${weekday}_${t.uhrzeitVon}_${t.uhrzeitBis}`;
          const slotCounts = monthlyTrainerSlotCounts.get(key) ?? new Map<string, number>();
          slotCounts.set(slotKey, (slotCounts.get(slotKey) ?? 0) + 1);
          monthlyTrainerSlotCounts.set(key, slotCounts);
        });
      });

    const monthlyTrainerProcessed = new Set<string>();

    trainingsForAbrechnung.forEach((t) => {
      // Vertretungstrainer berücksichtigen
      const vertretung = vertretungen.find(v => v.trainingId === t.id);
      const tid = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
      const name = trainerById.get(tid)?.name ?? "Trainer";
      const cfg = getPreisConfig(t, tarifById);
      // Tenniscamp hat keinen Tarif (cfg === null), der Trainer wird trotzdem vergütet.
      if (!cfg && !t.isTenniscamp) return;

      // Tenniscamp mit mehreren Trainern: jeder Trainer bekommt seinen eigenen
      // Stundenlohn für die Camp-Stunden. Keine Spieler-Abrechnung (sum bleibt 0).
      if (t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1) {
        // Bei aktivem Trainerfilter nur den gefilterten Trainer gutschreiben,
        // sonst tauchen die anderen Camp-Trainer als Teil-Zeilen auf.
        const campTrainerIdsToPay = abrechnungTrainerFilter === "alle"
          ? t.trainerIds
          : t.trainerIds.filter((id) => id === abrechnungTrainerFilter);
        campTrainerIdsToPay.forEach((campTid) => {
          const campHonorar = trainerHonorarFuerTraining(t, campTid);
          const campEntry =
            perTrainer.get(campTid) ?? {
              name: trainerById.get(campTid)?.name ?? "Trainer",
              sum: 0,
              trainings: 0,
              honorar: 0,
              honorarBezahlt: 0,
              honorarOffen: 0,
            };
          campEntry.trainings += 1;
          campEntry.honorar = round2(campEntry.honorar + campHonorar);
          // Bezahlt-Haken gilt pro Camp-Trainer, nicht fuer den ganzen Termin.
          if (honorarBezahltFuer(t, campTid)) {
            campEntry.honorarBezahlt = round2(campEntry.honorarBezahlt + campHonorar);
          } else {
            campEntry.honorarOffen = round2(campEntry.honorarOffen + campHonorar);
          }
          perTrainer.set(campTid, campEntry);
        });
        return;
      }

      const honorar = trainerHonorarFuerTraining(t);
      let entry =
        perTrainer.get(tid) ?? {
          name,
          sum: 0,
          trainings: 0,
          honorar: 0,
          honorarBezahlt: 0,
          honorarOffen: 0,
        };

      if (cfg && cfg.abrechnung === "monatlich") {
        const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
        t.spielerIds.forEach((pid) => {
          const processKey = `${tid}__${pid}__${tarifKey}`;
          if (monthlyTrainerProcessed.has(processKey)) return;
          monthlyTrainerProcessed.add(processKey);

          const slotCounts = monthlyTrainerSlotCounts.get(processKey);
          let slotSum = 0;
          if (slotCounts) {
            slotCounts.forEach((actualCount, slotKey) => {
              const wd = parseInt(slotKey.split("_")[0]);
              const possible = weekdayOccurrencesInMonth(abrechnungMonat, wd);
              slotSum += cfg.preisProStunde * (actualCount / (possible || 1));
            });
            slotSum = round2(slotSum);
          } else {
            slotSum = cfg.preisProStunde;
          }
          entry.sum = round2(entry.sum + slotSum);
        });
      } else {
        const amount = round2(trainingPreisGesamt(t));
        entry.sum = round2(entry.sum + amount);
      }

      entry.trainings += 1;
      entry.honorar = round2(entry.honorar + honorar);

      const paid = honorarBezahltFuer(t, tid);
      if (paid) {
        entry.honorarBezahlt = round2(entry.honorarBezahlt + honorar);
      } else {
        entry.honorarOffen = round2(entry.honorarOffen + honorar);
      }

      perTrainer.set(tid, entry);
    });

    // Zuschläge/Abzüge einrechnen — nur für den aktuell gefilterten Trainer (oder alle, wenn "alle")
    trainers.forEach((trainer) => {
      if (abrechnungTrainerFilter !== "alle" && trainer.id !== abrechnungTrainerFilter) return;
      const key = `${abrechnungMonat}__${trainer.id}`;
      const zuschlaege = trainerZuschlaege[key] ?? [];
      if (zuschlaege.length === 0) return;
      const zuschlagSum = round2(zuschlaege.reduce((acc, z) => acc + z.betrag, 0));
      let entry = perTrainer.get(trainer.id);
      if (!entry) {
        entry = { name: trainer.name, sum: 0, trainings: 0, honorar: 0, honorarBezahlt: 0, honorarOffen: 0 };
      }
      entry.honorar = round2(entry.honorar + zuschlagSum);
      entry.honorarOffen = round2(entry.honorarOffen + zuschlagSum);
      perTrainer.set(trainer.id, entry);
    });

    const rows = Array.from(perTrainer.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.sum - a.sum);

    const total = round2(rows.reduce((acc, r) => acc + r.sum, 0));
    const totalHonorar = round2(rows.reduce((acc, r) => acc + r.honorar, 0));
    const totalHonorarBezahlt = round2(
      rows.reduce((acc, r) => acc + r.honorarBezahlt, 0)
    );
    const totalHonorarOffen = round2(
      rows.reduce((acc, r) => acc + r.honorarOffen, 0)
    );

    return {
      total,
      rows,
      totalHonorar,
      totalHonorarBezahlt,
      totalHonorarOffen,
    };
  }, [
    defaultTrainerId,
    trainerById,
    trainers,
    trainings,
    trainingsForAbrechnung,
    tarifById,
    trainerHonorarFuerTraining,
    trainingPreisGesamt,
    vertretungen,
    honorarBezahltFuer,
    trainerZuschlaege,
    abrechnungMonat,
    abrechnungTrainerFilter,
  ]);

  function togglePaidForPlayer(monat: string, spielerId: string) {
    if (isTrainer) return;
    const key = paymentKey(monat, spielerId);
    setPayments((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }



  function closePayConfirm() {
    setPayConfirm(null);
  }

  function confirmPay() {
    if (!payConfirm) return;
    togglePaidForPlayer(payConfirm.monat, payConfirm.spielerId);
    setPayConfirm(null);
  }

  function toggleTrainerPaid(trainingId: string) {
    if (isTrainer) return;
    setTrainerPayments((prev) => ({
      ...prev,
      [trainingId]: !prev[trainingId],
    }));
  }

  function toggleBarBezahlt(trainingId: string) {
    if (isTrainer) return;
    
    // Finde das Training um zu prüfen ob wir auf true oder false setzen
    const training = trainings.find((t) => t.id === trainingId);
    if (!training) return;
    
    const newBarBezahlt = !training.barBezahlt;
    
    setTrainings((prev) =>
      prev.map((t) =>
        t.id === trainingId ? { ...t, barBezahlt: newBarBezahlt } : t
      )
    );
    // Kein automatisches Setzen von payments - der Status wird über das Dropdown gesteuert
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setAuthUser(null);
    setInitialSynced(false);
    setProfileFinished(false);
  }

  // Hilfsfunktion: Berechnet die Bar-Summe für einen Spieler im Monat
  const getSumBarForSpieler = useCallback(
    (spielerId: string) => {
      let sumBar = 0;
      
      // Für monatliche Tarife: Tracke ob ein monatlicher Betrag bereits gezählt wurde
      const monthlyProcessedForBar = new Set<string>();
      
      trainingsInMonth.forEach((t) => {
        if (!t.barBezahlt) return;
        if (!t.spielerIds.includes(spielerId)) return;
        
        const cfg = getPreisConfig(t, tarifById);
        if (!cfg) return;
        
        if (cfg.abrechnung === "monatlich") {
          // Bei monatlichen Tarifen: Nur einmal pro Tarif+Spieler zählen
          const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
          const processKey = `${spielerId}__${tarifKey}`;
          if (monthlyProcessedForBar.has(processKey)) return;
          monthlyProcessedForBar.add(processKey);
          
          // Für monatliche Tarife: Anzahl wöchentlicher Trainings ermitteln
          const weekdays = new Set<string>();
          trainingsInMonth.forEach((t2) => {
            if (!t2.spielerIds.includes(spielerId)) return;
            const cfg2 = getPreisConfig(t2, tarifById);
            if (!cfg2 || cfg2.abrechnung !== "monatlich") return;
            const tarifKey2 = t2.tarifId || `custom-${cfg2.preisProStunde}`;
            if (tarifKey2 !== tarifKey) return;
            const trainingDate = new Date(t2.datum + "T12:00:00");
            // Zähle Trainings nach Wochentag UND Uhrzeit
            weekdays.add(`${trainingDate.getDay()}_${t2.uhrzeitVon}_${t2.uhrzeitBis}`);
          });
          
          sumBar = round2(sumBar + cfg.preisProStunde * weekdays.size);
        } else {
          // Für normale Tarife: Anteil pro Spieler berechnen
          const share = priceFuerSpieler(t);
          sumBar = round2(sumBar + share);
        }
      });
      
      return sumBar;
    },
    [trainingsInMonth, tarifById, priceFuerSpieler]
  );

  // Bar/Nicht-Bar Trainings für Admin-Ansicht (wenn ein Trainer gefiltert ist)
  const adminTrainerTrainings = useMemo(() => {
    if (abrechnungTrainerFilter === "alle") return [];
    return trainingsForAbrechnung.filter((t) =>
      trainingGehoertZuTrainer(t, abrechnungTrainerFilter)
    );
  }, [trainingsForAbrechnung, abrechnungTrainerFilter, trainingGehoertZuTrainer]);

  const adminBarTrainings = useMemo(() => adminTrainerTrainings.filter((t) => t.barBezahlt), [adminTrainerTrainings]);
  const adminNichtBarTrainings = useMemo(() => adminTrainerTrainings.filter((t) => !t.barBezahlt), [adminTrainerTrainings]);

  const kalenderTrainersWithFutureTrainings = useMemo(() => {
    const heute = todayISO();
    const aktiveTrainerIds = new Set<string>();
    for (const t of trainings) {
      if (t.datum < heute) continue;
      if (t.status === "abgesagt") continue;
      const vertretung = vertretungen.find(v => v.trainingId === t.id);
      const tid = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
      if (tid) aktiveTrainerIds.add(tid);
    }
    return trainers.filter(tr => aktiveTrainerIds.has(tr.id));
  }, [trainings, trainers, vertretungen, defaultTrainerId]);

  // Bar-Abrechnung Key für Trainer
  const trainerBarSettledKey = useCallback((month: string, trainerId: string) => `${month}__${trainerId}__bar`, []);

    if (authLoading || profileLoading || !initialSynced) {
    return (
      <div className="container">
        <div className="card" style={{ marginTop: 60 }}>
          Synchronisiere Daten mit der Cloud ...
        </div>
      </div>
    );
  }


  if (!authUser) {
    return <AuthScreen />;
  }

  // Hilfsfunktion: Ermittle Status für einen Spieler
  const getSpielerStatus = (spielerId: string, sum: number): "komplett_bar" | "teilweise_bar" | "komplett_abgerechnet" | "offen" | "keine_trainings" => {
    const key = paymentKey(abrechnungMonat, spielerId);
    const paymentsFlag = payments[key] ?? false;
    const sumBarSpieler = getSumBarForSpieler(spielerId);
    const sumTotalSpieler = sum;
    
    if (sumTotalSpieler === 0) return "keine_trainings";
    if (sumBarSpieler === sumTotalSpieler) return "komplett_bar";
    if (sumBarSpieler > 0 && sumBarSpieler < sumTotalSpieler) {
      return paymentsFlag ? "komplett_abgerechnet" : "teilweise_bar";
    }
    return paymentsFlag ? "komplett_abgerechnet" : "offen";
  };

  // Hilfsfunktion um den Anpassungsbetrag eines Spielers zu ermitteln
  const getAdjustmentForSpieler = (spielerId: string): number => {
    const adjustmentKey = `${abrechnungMonat}__${spielerId}`;
    return monthlyAdjustments[adjustmentKey] ?? 0;
  };

  const getAdjustedSum = (spielerId: string, baseSum: number): number => {
    const adjustment = getAdjustmentForSpieler(spielerId);
    // Anpassung zur Basissumme addieren (Anpassungen sind i.d.R. negativ bei Absagen)
    return round2(baseSum + adjustment);
  };

  const filteredSpielerRowsForMonth = abrechnung.spielerRows.filter((r) => {
    const adjustedSum = getAdjustedSum(r.id, r.sum);
    const hasManualAdjustment = (monthlyAdjustments[`${abrechnungMonat}__${r.id}`] ?? 0) !== 0;
    if (adjustedSum <= 0 && !hasManualAdjustment) {
      return false;
    }
    const status = getSpielerStatus(r.id, adjustedSum);
    const isBezahlt = status === "komplett_bar" || status === "komplett_abgerechnet";

    // Tagesfilter prüfen
    if (abrechnungTagFilter !== "alle") {
      const tagNum = parseInt(abrechnungTagFilter, 10);
      const spielerTage = spielerWochentage.get(r.id);
      if (!spielerTage || !spielerTage.has(tagNum)) {
        return false;
      }
    }

    // Abgebucht-Filter prüfen
    if (abrechnungAbgebuchtFilter !== "alle") {
      const istAbgebucht = wirdAbgebucht[`${abrechnungMonat}__${r.id}`] ?? false;
      if (abrechnungAbgebuchtFilter === "abgebucht" && !istAbgebucht) return false;
      if (abrechnungAbgebuchtFilter === "nicht_abgebucht" && istAbgebucht) return false;
    }

    if (abrechnungFilter === "alle") return true;
    if (abrechnungFilter === "bezahlt") return isBezahlt;
    if (abrechnungFilter === "offen") return !isBezahlt;
    if (abrechnungFilter === "bar") return status === "komplett_bar" || status === "teilweise_bar";
    return true;
  });

  const sumBezahlt = round2(
    abrechnung.spielerRows.reduce((acc, r) => {
      const adjustedSum = getAdjustedSum(r.id, r.sum);
      const status = getSpielerStatus(r.id, adjustedSum);
      const isBezahlt = status === "komplett_bar" || status === "komplett_abgerechnet";
      return acc + (isBezahlt ? adjustedSum : 0);
    }, 0)
  );

  const sumOffen = round2(
    abrechnung.spielerRows.reduce((acc, r) => {
      const adjustedSum = getAdjustedSum(r.id, r.sum);
      const status = getSpielerStatus(r.id, adjustedSum);
      const isBezahlt = status === "komplett_bar" || status === "komplett_abgerechnet";
      return acc + (!isBezahlt ? adjustedSum : 0);
    }, 0)
  );

  const gefilterteTrainerRow = abrechnungTrainerFilter !== "alle"
    ? abrechnungTrainer.rows.find((r) => r.id === abrechnungTrainerFilter)
    : undefined;
  const trainerHonorarBezahltTotal = gefilterteTrainerRow
    ? gefilterteTrainerRow.honorarBezahlt
    : abrechnungTrainer.totalHonorarBezahlt;
  const trainerHonorarOffenTotal = gefilterteTrainerRow
    ? gefilterteTrainerRow.honorarOffen
    : abrechnungTrainer.totalHonorarOffen;

  const eigeneTrainerRow = abrechnungTrainer.rows.find(
    (r) => r.id === ownTrainerId
  );

  const eigenerHonorarBezahlt = eigeneTrainerRow?.honorarBezahlt ?? 0;
  const eigenerHonorarOffen = eigeneTrainerRow?.honorarOffen ?? 0;

  const rueckzahlungTrainerOffen = round2(
    trainingsForAbrechnung.reduce((acc, t) => {
      // Vertretungstrainer berücksichtigen
      const vertretung = vertretungen.find(v => v.trainingId === t.id);
      const tid = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
      // Berücksichtige den Trainerfilter: wenn "alle" gewählt ist, alle Trainer einbeziehen
      if (abrechnungTrainerFilter !== "alle" && tid !== abrechnungTrainerFilter) return acc;
      if (!t.barBezahlt) return acc;
      const cfg = getPreisConfig(t, tarifById);
      if (!cfg || cfg.abrechnung === "monatlich") return acc;
      const priceNum = round2(trainingPreisGesamt(t));
      const honorarNum = trainerHonorarFuerTraining(t);
      const diff = round2(priceNum - honorarNum);
      return diff > 0 ? acc + diff : acc;
    }, 0)
  );

  // Effektive Rückzahlung (0 wenn bar-abgerechnet)
  const effectiveRueckzahlung = abrechnungTrainerFilter !== "alle" &&
    trainerBarSettled[trainerBarSettledKey(abrechnungMonat, abrechnungTrainerFilter)]
    ? 0
    : rueckzahlungTrainerOffen;

  const eigeneTrainingsImMonat = trainings.filter((t) => {
    if (t.status !== "durchgefuehrt") return false;
    if (!t.datum.startsWith(abrechnungMonat)) return false;
    // Tenniscamp mit mehreren Trainern zählt für jeden beteiligten Trainer;
    // sonst Vertretungstrainer berücksichtigen.
    return trainingGehoertZuTrainer(t, ownTrainerId);
  });

  // Privatstunden werden als eigene Kategorie geführt und NICHT in Bar/Nicht-bar mitgezählt
  const privatTrainings = eigeneTrainingsImMonat.filter((t) => t.isPrivat);
  const nichtBarTrainings = eigeneTrainingsImMonat.filter(
    (t) => !t.barBezahlt && !t.isPrivat
  );
  const barTrainings = eigeneTrainingsImMonat.filter(
    (t) => t.barBezahlt && !t.isPrivat
  );

  return (
    <>
      <div className="appShell">
        <header className="mobileTopBar">
          <button
            className="iconButton"
            onClick={() => setIsSideNavOpen((v) => !v)}
            aria-label="Navigation öffnen"
          >
            <span className="iconBar" />
            <span className="iconBar" />
            <span className="iconBar" />
          </button>

          <div className="mobileTopTitle">
            <div className="mobileTopMain">Tennistrainer Planung</div>
            <div className="mobileTopSub">
              {roleLabel} · {tab === "kalender" ? kalenderTrainerFilterLabel : trainerFilterLabel}
            </div>
          </div>
        </header>

        <aside className={`sideNav ${isSideNavOpen ? "sideNavOpen" : ""}`}>
          <div className="sideNavHeader">
            <div className="sideTitle">Tennistrainer Planung</div>
          </div>

          <span className="pill sideRolePill">
            Rolle: <strong>{roleLabel}</strong>
          </span>

          {isTrainer && ownTrainerId && trainerById.get(ownTrainerId)?.notiz && (
            <div className="card cardInset" style={{ margin: "12px 0", padding: 12 }}>
              <strong>Notiz:</strong>
              <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
                {trainerById.get(ownTrainerId)?.notiz}
              </div>
            </div>
          )}

          <nav className="sideTabs">
            {visibleTabs.map((t) => (
              <button
                key={t}
                className={`tabBtn sideTabBtn ${
                  tab === t ? "tabBtnActive" : ""
                }`}
                onClick={() => {
                  setTab(t);
                  setIsSideNavOpen(false);
                }}
              >
                {t === "kalender" && "Kalender"}
                {t === "training" && "Training"}
                {t === "verwaltung" && "Verwaltung"}
                {t === "formulare" && (
                  <>
                    Formulare
                    {(registrationRequests.filter(r => r.status !== "erledigt" && !r.archiviert).length + sepaMandates.filter(m => (m.status || "neu") === "neu" && !m.archiviert).length) > 0 && (
                      <span style={{
                        marginLeft: 6,
                        background: "var(--danger)",
                        color: "white",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700
                      }}>
                        {registrationRequests.filter(r => r.status !== "erledigt" && !r.archiviert).length + sepaMandates.filter(m => (m.status || "neu") === "neu" && !m.archiviert).length}
                      </span>
                    )}
                  </>
                )}
                {t === "abrechnung" && "Abrechnung"}
                {t === "weiteres" && "Weiteres"}
              </button>
            ))}
          </nav>

          <button className="btn btnGhost sideLogout" onClick={handleLogout}>
            Logout ({authUser.email ?? "ohne Email"})
          </button>
        </aside>

        {isSideNavOpen && (
          <div
            className="sideNavOverlay"
            onClick={() => setIsSideNavOpen(false)}
          />
        )}

        <main className="mainArea">
          <div className="container">
            <div className="header">
              <div className="hTitle">
                <h1>Tennistrainer Planung</h1>
              </div>
            </div>

            {tab === "kalender" && (
              <div className="card">
                {/* Modal: offenen Spontan-Slot direkt aus dem Kalender bearbeiten */}
                {calSpontanEdit && (
                  <div
                    onClick={() => setCalSpontanEdit(null)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                      padding: 16,
                    }}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: "#ffffff",
                        borderRadius: 12,
                        padding: 20,
                        width: "100%",
                        maxWidth: 380,
                        boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                      }}
                    >
                      <h3 style={{ margin: "0 0 14px" }}>Spontan-Slot bearbeiten</h3>
                      <div className="field" style={{ marginBottom: 10 }}>
                        <label>Datum</label>
                        <input type="date" value={calSpontanDatum} onChange={(e) => setCalSpontanDatum(e.target.value)} />
                      </div>
                      <div className="row" style={{ marginBottom: 10 }}>
                        <div className="field" style={{ flex: 1 }}>
                          <label>Von</label>
                          <input type="time" value={calSpontanVon} onChange={(e) => setCalSpontanVon(e.target.value)} />
                        </div>
                        <div className="field" style={{ flex: 1 }}>
                          <label>Bis</label>
                          <input type="time" value={calSpontanBis} onChange={(e) => setCalSpontanBis(e.target.value)} />
                        </div>
                      </div>
                      <div className="field" style={{ marginBottom: 16 }}>
                        <label>Trainer</label>
                        <select value={calSpontanTrainerId} onChange={(e) => setCalSpontanTrainerId(e.target.value)}>
                          <option value="">Trainer auswählen...</option>
                          {trainers.map((tr) => (
                            <option key={tr.id} value={tr.id}>{tr.name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <button
                          className="btn micro btnWarn"
                          onClick={() => {
                            const id = calSpontanEdit.id;
                            setCalSpontanEdit(null);
                            deleteSpontaneStunde(id);
                          }}
                        >
                          Löschen
                        </button>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="btn btnGhost" onClick={() => setCalSpontanEdit(null)}>Abbrechen</button>
                          <button className="btn" onClick={saveCalendarSpontanEdit}>Speichern</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Erinnerung: vergangene Trainings, die noch nicht abgeschlossen wurden – gruppiert nach Trainer */}
                {offeneVergangeneTrainings.length > 0 && (() => {
                  const gruppen: { id: string; name: string; items: Training[] }[] = [];
                  offeneVergangeneTrainings.forEach((t) => {
                    const vertretung = vertretungen.find((v) => v.trainingId === t.id);
                    const effId = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
                    const name = trainerById.get(effId)?.name ?? "Ohne Trainer";
                    let g = gruppen.find((x) => x.id === effId);
                    if (!g) { g = { id: effId, name, items: [] }; gruppen.push(g); }
                    g.items.push(t);
                  });
                  gruppen.sort((a, b) => a.name.localeCompare(b.name, "de"));

                  return (
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid var(--border, #e5e7eb)",
                        borderLeft: "4px solid #f59e0b",
                        borderRadius: 10,
                        padding: "14px 16px",
                        marginBottom: 16,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#92400e", marginBottom: 12 }}>
                        Noch nicht abgeschlossen ({offeneVergangeneTrainings.length})
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: 320, overflowY: "auto" }}>
                        {gruppen.map((g) => (
                          <div key={g.id}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "var(--muted-fg, #6b7280)",
                                paddingBottom: 6,
                                marginBottom: 8,
                                borderBottom: "1px solid var(--border, #e5e7eb)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>{g.name}</span>
                              <span style={{ fontWeight: 500 }}>{g.items.length}</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {g.items.map((t) => {
                                const namen = t.spielerIds.map((id) => getSpielerDisplayName(id)).join(", ") || (t.isPrivat ? "Privat" : t.isTenniscamp ? "Tenniscamp" : "—");
                                const datumKurz = new Date(t.datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
                                return (
                                  <div key={t.id} className="openTrainingRow">
                                    <button
                                      onClick={() => {
                                        setWeekAnchor(t.datum);
                                        const d = new Date(t.datum + "T12:00:00");
                                        setDayIndex((d.getDay() + 6) % 7);
                                      }}
                                      title="Im Kalender anzeigen"
                                      className="openTrainingInfo"
                                    >
                                      <strong className="openTrainingDate">
                                        {datumKurz} · {t.uhrzeitVon.slice(0, 5)}–{t.uhrzeitBis.slice(0, 5)}
                                      </strong>
                                      <span className="openTrainingNames">
                                        {namen}
                                      </span>
                                    </button>
                                    {!isTrainer && (
                                      <div className="openTrainingActions">
                                        <button
                                          className="btn micro"
                                          style={{ background: "#22c55e" }}
                                          onClick={() => markTrainingDone(t.id)}
                                          title="Als durchgeführt abschließen"
                                        >
                                          Abschließen
                                        </button>
                                        <button
                                          className="btn micro"
                                          style={{ background: "#0d9488" }}
                                          onClick={() => markTrainingDoneAndBarBezahlt(t.id)}
                                          title="Als durchgeführt abschließen und bar bezahlt markieren"
                                        >
                                          Bar
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Oberer Bereich: Einstellungen (collapsed auf Mobile) */}
                <div className="calendarSettings">
                  <div className="row">
                    <div className="field" style={{ minWidth: 220 }}>
                      <label>Woche springen</label>
                      <input
                        type="date"
                        value={weekAnchor}
                        onChange={(e) => setWeekAnchor(e.target.value)}
                      />
                    </div>

                    {!isTrainer && kalenderTrainersWithFutureTrainings.length > 1 && (
                      <div className="field" style={{ minWidth: 200, position: "relative" }}>
                        <label>Trainer Filter</label>
                        <button
                          type="button"
                          className="dropdownToggle"
                          onClick={() => setShowTrainerDropdown(!showTrainerDropdown)}
                        >
                          <span className="dropdownLabel">
                            {kalenderTrainerFilter.length === 0
                              ? "Alle Trainer"
                              : kalenderTrainerFilter.length === 1
                                ? trainerById.get(kalenderTrainerFilter[0])?.name
                                : `${kalenderTrainerFilter.length} Trainer`}
                          </span>
                          <span className="dropdownArrow">▼</span>
                        </button>
                        {showTrainerDropdown && (
                          <div className="dropdownMenu">
                            {kalenderTrainersWithFutureTrainings.map((tr) => (
                              <label key={tr.id} className="dropdownItem">
                                <input
                                  type="checkbox"
                                  checked={kalenderTrainerFilter.includes(tr.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setKalenderTrainerFilter([...kalenderTrainerFilter, tr.id]);
                                    } else {
                                      setKalenderTrainerFilter(kalenderTrainerFilter.filter(id => id !== tr.id));
                                    }
                                  }}
                                />
                                {tr.name}
                              </label>
                            ))}
                            {kalenderTrainerFilter.length > 0 && (
                              <button
                                type="button"
                                className="dropdownReset"
                                onClick={() => setKalenderTrainerFilter([])}
                              >
                                Auswahl zurücksetzen
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="field" style={{ minWidth: 140 }}>
                      <label>Anlage</label>
                      <select
                        value={kalenderAnlageFilter}
                        onChange={(e) => setKalenderAnlageFilter(e.target.value as "alle" | "Wedding" | "Britz")}
                      >
                        <option value="alle">Alle Anlagen</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Britz">Britz</option>
                      </select>
                    </div>

                    {!isTrainer && (
                      <span className="pill">
                        Trainer gesamt: <strong>{trainers.length}</strong>
                      </span>
                    )}

                    {!isTrainer && (
                      <button
                        className="btn"
                        onClick={() => {
                          resetTrainingForm();
                          setTab("training");
                        }}
                      >
                        Neues Training
                      </button>
                    )}
                  </div>
                </div>

                {/* Batch-Aktionen für ausgewählte Trainings */}
                {!isTrainer && selectedTrainingIds.length > 0 && (
                  <div className="card cardInset" style={{ marginBottom: 12, marginTop: 12 }}>
                    <div
                      className="row"
                      style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}
                    >
                      <span className="pill">
                        Ausgewählte Trainings:{" "}
                        <strong>{selectedTrainingIds.length}</strong>
                      </span>
                      <ActionMenu label="Status setzen" buttonClassName="btn micro" menuWidth={230}>
                      <button
                        className="btn micro"
                        onClick={() =>
                          batchUpdateStatusForSelected("durchgefuehrt")
                        }
                      >
                        Alle durchgeführt
                      </button>
                      <button
                        className="btn micro"
                        style={{
                          backgroundColor: "#8b5cf6",
                          borderColor: "#8b5cf6",
                        }}
                        onClick={batchSetDurchgefuehrtUndBarBezahlt}
                      >
                        Alle durchgeführt + bar
                      </button>
                      <button
                        className="btn micro"
                        onClick={() =>
                          batchUpdateStatusForSelected("geplant")
                        }
                      >
                        Alle geplant
                      </button>
                      <button
                        className="btn micro"
                        style={{
                          backgroundColor: "#ef4444",
                          borderColor: "#ef4444",
                        }}
                        onClick={() =>
                          batchUpdateStatusForSelected("abgesagt")
                        }
                      >
                        Alle abgesagt
                      </button>
                      </ActionMenu>
                      <button
                        className="btn micro btnWarn"
                        onClick={batchDeleteSelectedTrainings}
                      >
                        Alle löschen
                      </button>
                      <div className="field" style={{ minWidth: 180 }}>
                        <label>Trainer für Auswahl</label>
                        <select
                          value={batchTrainerId}
                          onChange={(e) => setBatchTrainerId(e.target.value)}
                        >
                          <option value="">Standardtrainer</option>
                          {trainers.map((tr) => (
                            <option key={tr.id} value={tr.id}>
                              {tr.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        className="btn micro"
                        onClick={batchChangeTrainerForSelected}
                      >
                        Trainer übernehmen
                      </button>
                      <button
                        className="btn micro btnGhost"
                        onClick={clearTrainingSelection}
                      >
                        Auswahl aufheben
                      </button>
                    </div>
                  </div>
                )}

                {/* Kalender-Navigation - direkt über dem Grid */}
                <div className="calendarNavCompact">
                  <div className="calendarNavRow">
                    <button
                      className="navArrowBtn"
                      onClick={() => {
                        if (viewMode === "day") {
                          const newIndex = (dayIndex + 7 - 1) % 7;
                          setDayIndex(newIndex);
                          if (newIndex === 6) {
                            setWeekAnchor(addDaysISO(weekStart, -7));
                          }
                        } else {
                          setWeekAnchor(addDaysISO(weekStart, -7));
                        }
                      }}
                      aria-label="Vorheriger Zeitraum"
                    >
                      ‹
                    </button>
                    
                    <div className="calendarNavCenter">
                      <span className="calendarWeekLabel">
                        {viewMode === "day" 
                          ? formatShort(weekDays[dayIndex]) + " " + weekDays[dayIndex].split("-")[0]
                          : formatWeekRange(weekStart)
                        }
                      </span>
                      <div className="viewModeToggle">
                        <button
                          className={`viewModeBtn ${viewMode === "week" ? "viewModeBtnActive" : ""}`}
                          onClick={() => setViewMode("week")}
                        >
                          Woche
                        </button>
                        <button
                          className={`viewModeBtn ${viewMode === "day" ? "viewModeBtnActive" : ""}`}
                          onClick={() => setViewMode("day")}
                        >
                          Tag
                        </button>
                      </div>
                    </div>

                    <button
                      className="navArrowBtn"
                      onClick={() => {
                        if (viewMode === "day") {
                          const newIndex = (dayIndex + 1) % 7;
                          setDayIndex(newIndex);
                          if (newIndex === 0) {
                            setWeekAnchor(addDaysISO(weekStart, 7));
                          }
                        } else {
                          setWeekAnchor(addDaysISO(weekStart, 7));
                        }
                      }}
                      aria-label="Nächster Zeitraum"
                    >
                      ›
                    </button>
                  </div>
                  
                  <button className="todayBtnCompact" onClick={goToToday}>
                    Heute
                  </button>
                </div>

                {/* Swipe-Hinweis für Mobile */}
                <div className="swipeHint">
                  <span>← Wischen für Navigation →</span>
                </div>

                {/* Filter für Mobile */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "0 8px" }}>
                  {!isTrainer && kalenderTrainersWithFutureTrainings.length > 1 && (
                    <div className="mobileTrainerFilter" style={{ position: "relative" }}>
                      <button
                        type="button"
                        className="dropdownToggle"
                        onClick={() => setShowTrainerDropdown(!showTrainerDropdown)}
                      >
                        <span className="dropdownLabel">
                          {kalenderTrainerFilter.length === 0
                            ? "Alle Trainer"
                            : kalenderTrainerFilter.length === 1
                              ? trainerById.get(kalenderTrainerFilter[0])?.name
                              : `${kalenderTrainerFilter.length} Trainer`}
                        </span>
                        <span className="dropdownArrow">▼</span>
                      </button>
                      {showTrainerDropdown && (
                        <div className="dropdownMenu">
                          {kalenderTrainersWithFutureTrainings.map((tr) => (
                            <label key={tr.id} className="dropdownItem">
                              <input
                                type="checkbox"
                                checked={kalenderTrainerFilter.includes(tr.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setKalenderTrainerFilter([...kalenderTrainerFilter, tr.id]);
                                  } else {
                                    setKalenderTrainerFilter(kalenderTrainerFilter.filter(id => id !== tr.id));
                                  }
                                }}
                              />
                              {tr.name}
                            </label>
                          ))}
                          {kalenderTrainerFilter.length > 0 && (
                            <button
                              type="button"
                              className="dropdownReset"
                              onClick={() => setKalenderTrainerFilter([])}
                            >
                              Auswahl zurücksetzen
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <select
                    className="mobileAnlageFilter"
                    value={kalenderAnlageFilter}
                    onChange={(e) => setKalenderAnlageFilter(e.target.value as "alle" | "Wedding" | "Britz")}
                    style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border, #d1d5db)", background: "var(--bg, #fff)" }}
                  >
                    <option value="alle">Alle Anlagen</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Britz">Britz</option>
                  </select>
                </div>

                <div 
                  className={`kgrid ${viewMode === "day" ? "kgridDay" : ""}`}
                  onTouchStart={calendarSwipeHandlers.onTouchStart}
                  onTouchMove={calendarSwipeHandlers.onTouchMove}
                  onTouchEnd={calendarSwipeHandlers.onTouchEnd}
                >
                  <div className="kHead">
                    <div className="kHeadCell">Zeit</div>
                    {(viewMode === "week" ? weekDays : [weekDays[dayIndex]]).map(
                      (d) => (
                        <div key={d} className={`kHeadCell${d === todayISO() ? " kHeadToday" : ""}`}>
                          {formatShort(d)}
                        </div>
                      )
                    )}
                  </div>

                  <div className="kBody">
                    <div className="kTimeCol">
                      {hours.map((h) => (
                        <div key={h} className="kTime">
                          {pad2(h)}:00
                        </div>
                      ))}
                    </div>

                    {(viewMode === "week" ? weekDays : [weekDays[dayIndex]]).map(
                      (day) => {
                        const dayEvents = trainingsInWeek.filter(
                          (t) => t.datum === day
                        );
                        const dayGhosts = offeneSpontanInWeek.filter((s) => s.datum === day);
                        const startMin = 7 * 60;

                        // Trainings UND offene Spontan-Platzhalter gemeinsam auf Überlappung
                        // gruppieren, damit gleichzeitige Angebote (z.B. zwei Trainer parallel)
                        // nebeneinander statt übereinander dargestellt werden.
                        const layoutItems: { key: string; von: number; bis: number }[] = [
                          ...dayEvents.map((t) => ({
                            key: t.id,
                            von: toMinutes(t.uhrzeitVon),
                            bis: toMinutes(t.uhrzeitBis),
                          })),
                          ...dayGhosts.map((s) => ({
                            key: `spontan-${s.id}`,
                            von: toMinutes(s.uhrzeitVon),
                            bis: toMinutes(s.uhrzeitBis),
                          })),
                        ].sort((a, b) => a.von - b.von || a.bis - b.bis);

                        const groupedItems: { key: string; von: number; bis: number }[][] = [];
                        layoutItems.forEach((item) => {
                          let placed = false;
                          for (const group of groupedItems) {
                            if (group.some((o) => item.von < o.bis && item.bis > o.von)) {
                              group.push(item);
                              placed = true;
                              break;
                            }
                          }
                          if (!placed) groupedItems.push([item]);
                        });
                        const layoutByKey = new Map<string, { size: number; index: number }>();
                        groupedItems.forEach((g) =>
                          g.forEach((item, i) => layoutByKey.set(item.key, { size: g.length, index: i }))
                        );

                        const isToday = day === todayISO();

                        return (
                          <div key={day} className={`kDayCol${isToday ? " kDayToday" : ""}`}>
                            {hours.map((h) => (
                              <div
                                key={h}
                                className="kHourLine kHourLineClickable"
                                onClick={() => {
                                  if (isTrainer) return;
                                  resetTrainingForm();
                                  setTDatum(day);
                                  setTCampBisDatum(day);
                                  setRepeatPeriods([{ von: day, bis: "2026-07-12" }, { von: "2026-08-24", bis: "2026-09-30" }]);
                                  setTVon(`${pad2(h)}:00`);
                                  setTBis(`${pad2(h + 1)}:00`);
                                  if (kalenderTrainerFilter.length === 1) {
                                    setTTrainerId(kalenderTrainerFilter[0]);
                                  }
                                  if (kalenderAnlageFilter !== "alle") {
                                    setTAnlage(kalenderAnlageFilter);
                                  }
                                  setTab("training");
                                }}
                              />
                            ))}

                            {/* Offene Spontan-Slots als gestrichelte Platzhalter (klick-durchlässig, hinter echten Trainings) */}
                            {dayGhosts
                              .map((s) => {
                                const top = Math.max(0, (toMinutes(s.uhrzeitVon) - startMin) / 60) * 40;
                                const height = Math.max(26, ((toMinutes(s.uhrzeitBis) - toMinutes(s.uhrzeitVon)) / 60) * 40);
                                const anlage = s.anlage ?? "Wedding";
                                const trainerName = trainerById.get(s.trainerId)?.name ?? "Trainer";
                                const von = s.uhrzeitVon.slice(0, 5);
                                const bis = s.uhrzeitBis.slice(0, 5);
                                const lay = layoutByKey.get(`spontan-${s.id}`) ?? { size: 1, index: 0 };
                                return (
                                  <div
                                    key={`spontan-${s.id}`}
                                    className="kEvent"
                                    style={{
                                      top,
                                      height,
                                      width: `${100 / lay.size}%`,
                                      left: `${(lay.index * 100) / lay.size}%`,
                                      background:
                                        "repeating-linear-gradient(135deg, rgba(234,179,8,0.18) 0px, rgba(234,179,8,0.18) 6px, rgba(234,179,8,0.05) 6px, rgba(234,179,8,0.05) 12px)",
                                      border: "1px dashed #ca8a04",
                                      borderLeft: "3px solid #eab308",
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      overflow: "hidden",
                                      padding: "6px 8px",
                                      gap: 6,
                                      pointerEvents: isTrainer ? "none" : "auto",
                                      cursor: isTrainer ? "default" : "pointer",
                                    }}
                                    onClick={isTrainer ? undefined : () => openCalendarSpontanEdit(s)}
                                    title={isTrainer
                                      ? `Freier Spontan-Slot\nZeit: ${von}–${bis}\nAnlage: ${anlage}\nTrainer: ${trainerName}\nNoch nicht gebucht`
                                      : `Freier Spontan-Slot bearbeiten\nZeit: ${von}–${bis}\nTrainer: ${trainerName}`}
                                  >
                                    <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                          color: "#854d0e",
                                        }}
                                      >
                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Spontan · frei</span>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                          opacity: 0.75,
                                        }}
                                      >
                                        {von}–{bis}
                                        {isTrainer
                                          ? ` · ${anlage}`
                                          : trainers.length > 1
                                          ? ` · ${anlage} | ${trainerName}`
                                          : ` · ${anlage}`}
                                      </div>
                                    </div>
                                    {!isTrainer && kalenderAnlageFilter === "alle" && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          background: anlage === "Britz" ? "#f59e0b" : "#2563eb",
                                          color: "white",
                                          padding: "1px 3px",
                                          borderRadius: 2,
                                          flex: "0 0 auto",
                                        }}
                                      >
                                        {anlage === "Britz" ? "B" : "W"}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}

                            {dayEvents.map((t) => {
                              const top =
                                Math.max(
                                  0,
                                  (toMinutes(t.uhrzeitVon) - startMin) / 60
                                ) * 40;
                              const height = Math.max(
                                26,
                                ((toMinutes(t.uhrzeitBis) -
                                  toMinutes(t.uhrzeitVon)) /
                                  60) *
                                  40
                              );
                              const tarif = t.tarifId
                                ? tarifById.get(t.tarifId)
                                : undefined;
                              const abrechnungLabel = (a: string) => a === "proSpieler" ? "pro Spieler" : a === "monatlich" ? "monatlich" : "pro Training";
                              const ta = tarif
                                ? tarif.abrechnung === "monatlich"
                                  ? `${tarif.name} (monatlich ${tarif.preisProStunde} EUR)`
                                  : `${tarif.name} (${tarif.preisProStunde} EUR/Std, ${abrechnungLabel(tarif.abrechnung)})`
                                : t.customPreisProStunde
                                ? `Individuell (${t.customPreisProStunde} EUR/Std, ${abrechnungLabel(t.customAbrechnung || "proTraining")})`
                                : "Tarif";
                              const sp = t.spielerIds
                                .map(
                                  (id) =>
                                    getSpielerDisplayName(id)
                                )
                                .join(", ");
                              const trainerName =
                                t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1
                                  ? t.trainerIds
                                      .map((id) => trainerById.get(id)?.name ?? "Trainer")
                                      .join(", ")
                                  : trainerById.get(
                                      t.trainerId ?? defaultTrainerId
                                    )?.name ?? "Trainer";

                              // Vertretung prüfen
                              const trainingVertretung = vertretungen.find((v) => v.trainingId === t.id);
                              const vertretungTrainerObj = trainingVertretung?.vertretungTrainerId
                                ? trainerById.get(trainingVertretung.vertretungTrainerId)
                                : null;
                              const isVertretungOffen = trainingVertretung && !trainingVertretung.vertretungTrainerId;

                              const taLine = isTrainer
                                ? (t.anlage ?? "Wedding")
                                : trainers.length > 1
                                ? trainingVertretung
                                  ? isVertretungOffen
                                    ? "Vertretung offen"
                                    : `${vertretungTrainerObj?.name ?? "Vertretung"} (V)`
                                  : trainerName
                                : "";

                              const isDone = t.status === "durchgefuehrt";
                              const isCancel = t.status === "abgesagt";
                              const isPulse = doneFlashId === t.id;
                              const hasVertretung = !!trainingVertretung;

                              const isSelected = selectedTrainingIds.includes(
                                t.id
                              );

                              // Offener (noch nicht gebuchter) Spontan-Slot? Dann wie die
                              // gelben „Spontan · frei"-Platzhalter darstellen.
                              const istOffenerSpontanSlot = offeneSpontanTrainingIds.has(t.id);

                              // Farbschema: dezente Hintergründe mit farbigem linken Rand
                              const accentColor = istOffenerSpontanSlot && !isSelected
                                ? "#eab308"
                                : t.isTenniscamp && !isSelected
                                ? isDone ? "#16a34a" : isCancel ? "#ef4444" : "#f59e0b"
                                : t.isPrivat
                                ? isDone ? "#22c55e" : isCancel ? "#ef4444" : "#3b82f6"
                                : hasVertretung
                                ? isVertretungOffen ? "#dc2626" : "#22c55e"
                                : isSelected
                                ? "#8b5cf6"
                                : isDone
                                ? "#22c55e"
                                : isCancel
                                ? "#ef4444"
                                : "#3b82f6";
                              const bg = isSelected
                                ? "rgba(139, 92, 246, 0.18)"
                                : istOffenerSpontanSlot
                                ? "repeating-linear-gradient(135deg, rgba(234,179,8,0.18) 0px, rgba(234,179,8,0.18) 6px, rgba(234,179,8,0.05) 6px, rgba(234,179,8,0.05) 12px)"
                                : isDone
                                ? "rgba(34, 197, 94, 0.14)"
                                : isCancel
                                ? "rgba(239, 68, 68, 0.10)"
                                : t.isTenniscamp
                                ? "rgba(245, 158, 11, 0.16)"
                                : t.isPrivat
                                ? "rgba(59, 130, 246, 0.10)"
                                : "rgba(59, 130, 246, 0.12)";
                              const border = accentColor;

                              // Position bei Überlappung (gemeinsame Gruppierung mit offenen Spontan-Slots)
                              const lay = layoutByKey.get(t.id) ?? { size: 1, index: 0 };
                              const widthPercent = 100 / lay.size;
                              const leftPercent = lay.index * widthPercent;

                              return (
                                <div
                                  key={t.id}
                                  data-training-id={t.id}
                                  className="kEvent"
                                  style={{
                                    top,
                                    height,
                                    width: `${widthPercent}%`,
                                    left: `${leftPercent}%`,
                                    background: t.isPrivat && !istOffenerSpontanSlot
                                      ? isDone
                                        ? `repeating-linear-gradient(135deg, rgba(34,197,94,0.18) 0px, rgba(34,197,94,0.18) 6px, rgba(34,197,94,0.05) 6px, rgba(34,197,94,0.05) 12px)`
                                        : isCancel
                                        ? `repeating-linear-gradient(135deg, rgba(239,68,68,0.14) 0px, rgba(239,68,68,0.14) 6px, rgba(239,68,68,0.04) 6px, rgba(239,68,68,0.04) 12px)`
                                        : `repeating-linear-gradient(135deg, rgba(59,130,246,0.15) 0px, rgba(59,130,246,0.15) 6px, rgba(59,130,246,0.04) 6px, rgba(59,130,246,0.04) 12px)`
                                      : bg,
                                    borderLeft: `3px solid ${border}`,
                                    borderTop: istOffenerSpontanSlot ? "1px dashed #ca8a04" : "none",
                                    borderRight: istOffenerSpontanSlot ? "1px dashed #ca8a04" : "none",
                                    borderBottom: istOffenerSpontanSlot ? "1px dashed #ca8a04" : "none",
                                    opacity: isCancel ? 0.8 : 1,
                                    transform: isPulse
                                      ? "scale(1.05)"
                                      : undefined,
                                    filter: isPulse
                                      ? "brightness(1.12)"
                                      : undefined,
                                    transition:
                                      "transform 150ms ease, filter 150ms ease, background-color 150ms ease",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    overflow: "hidden",
                                    padding: "6px 8px",
                                    gap: 6,
                                  }}
                                  onClick={(e) => {
                                    if (istOffenerSpontanSlot && !isTrainer) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const slot = spontaneStunden.find((x) => x.trainingId === t.id && x.status === "offen");
                                      if (slot) openCalendarSpontanEdit(slot);
                                      return;
                                    }
                                    handleCalendarEventClick(t, e);
                                  }}
                                  onDoubleClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (istOffenerSpontanSlot) return;
                                    handleCalendarEventDoubleClick(t);
                                  }}
                                  onTouchStart={() => handleCalendarEventTouchStart(t)}
                                  onTouchEnd={handleCalendarEventTouchEnd}
                                  onTouchMove={handleCalendarEventTouchMove}
                                  title={`${t.isTenniscamp ? "Tenniscamp" : `Spieler: ${sp}`}\nZeit: ${t.uhrzeitVon} bis ${
                                    t.uhrzeitBis
                                  }${
                                    isTrainer ? "" : `\nTarif: ${ta}`
                                  }\nTrainer: ${trainerName}${
                                    hasVertretung
                                      ? `\nVertretung: ${isVertretungOffen ? "offen" : vertretungTrainerObj?.name ?? "Vertretung"}`
                                      : ""
                                  }\nStatus: ${statusLabel(
                                    t.status
                                  )}${t.isPrivat ? "\nPrivat (keine Abrechnung)" : ""}${t.isTenniscamp && !isTrainer ? "\nTenniscamp – keine Spieler-Abrechnung, Trainer normal vergütet" : ""}`}
                                >
                                  <div
                                    style={{
                                      flex: "1 1 auto",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                        color: istOffenerSpontanSlot ? "#854d0e" : undefined,
                                      }}
                                    >
                                      {t.spielerIds.length > 0 && !istOffenerSpontanSlot && !t.isTenniscamp && (
                                        <span style={{
                                          fontSize: 9,
                                          fontWeight: 700,
                                          background: "rgba(0,0,0,0.12)",
                                          color: "rgba(0,0,0,0.6)",
                                          borderRadius: 3,
                                          padding: "0 4px",
                                          lineHeight: "16px",
                                          flexShrink: 0,
                                        }}>
                                          {t.spielerIds.length}
                                        </span>
                                      )}
                                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{istOffenerSpontanSlot ? "Spontan · frei" : (t.isTenniscamp ? "Tenniscamp" : (sp || "Privat"))}</span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        opacity: istOffenerSpontanSlot ? 0.75 : 0.7,
                                      }}
                                    >
                                      {t.uhrzeitVon}–{t.uhrzeitBis}{istOffenerSpontanSlot ? ` · ${t.anlage ?? "Wedding"}` : (taLine ? ` · ${taLine}` : "")}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: "0 0 auto" }}>
                                    {!isTrainer && kalenderAnlageFilter === "alle" && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          background: (t.anlage ?? "Wedding") === "Britz" ? "#f59e0b" : "#2563eb",
                                          color: "white",
                                          padding: "1px 3px",
                                          borderRadius: 2,
                                        }}
                                      >
                                        {(t.anlage ?? "Wedding") === "Britz" ? "B" : "W"}
                                      </span>
                                    )}
                                    {t.isTenniscamp && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          background: "#d97706",
                                          color: "white",
                                          padding: "1px 3px",
                                          borderRadius: 2,
                                        }}
                                      >
                                        Camp
                                      </span>
                                    )}
                                    {t.isSpontanBuchung && !istOffenerSpontanSlot && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          background: "#eab308",
                                          color: "white",
                                          padding: "1px 3px",
                                          borderRadius: 2,
                                        }}
                                      >
                                        S
                                      </span>
                                    )}
                                    {hasVertretung && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          background: isVertretungOffen ? "#dc2626" : "#22c55e",
                                          color: "white",
                                          padding: "1px 3px",
                                          borderRadius: 2,
                                        }}
                                      >
                                        V
                                      </span>
                                    )}
                                    {!istOffenerSpontanSlot && (
                                      <div
                                        style={{
                                          width: 14,
                                          height: 14,
                                          borderRadius: "999px",
                                          border: "2px solid white",
                                          boxShadow:
                                            "0 0 0 1px rgba(15,23,42,0.15)",
                                          backgroundColor: statusDotColor(t.status),
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                <div style={{ height: 12 }} />
              </div>
            )}

            {tab === "training" &&
              (isTrainer ? (
                <div className="card">
                  <h2>Nur Lesen für Trainer</h2>
                  <p className="muted">
                    Trainings können nur vom Hauptaccount angelegt oder
                    bearbeitet werden.
                  </p>
                </div>
              ) : (
                <div className="grid2">
                  <div className="card">
                    <h2>
                      {selectedTrainingId
                        ? "Training bearbeiten"
                        : "Training anlegen"}
                    </h2>
                    <div className="formSectionTitle">Termin</div>
                    <div className="row">
                      <div className="field">
                        <label>Datum</label>
                        <input
                          type="date"
                          value={tDatum}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTDatum(v);
                            // Tenniscamp: Enddatum mitziehen, damit der Zeitraum nie auf einen Tag kollabiert
                            if (tCampBisDatum < v) setTCampBisDatum(v);
                          }}
                        />
                      </div>
                      <div className="field">
                        <label>Von</label>
                        <input
                          type="time"
                          value={tVon}
                          onChange={(e) => {
                            const newVon = e.target.value;
                            setTVon(newVon);
                            const vonMinutes = toMinutes(newVon);
                            const bisMinutes = vonMinutes + 60;
                            const bisH = Math.floor(bisMinutes / 60);
                            const bisM = bisMinutes % 60;
                            setTBis(`${pad2(bisH)}:${pad2(bisM)}`);
                          }}
                        />
                      </div>
                      <div className="field">
                        <label>Bis</label>
                        <input
                          type="time"
                          value={tBis}
                          onChange={(e) => setTBis(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="formSectionTitle">Zuordnung &amp; Art</div>
                    <div className="row">
                      <div className="field">
                        <label>Trainer</label>
                        <select
                          value={tTrainerId}
                          disabled={isTrainer}
                          onChange={(e) => setTTrainerId(e.target.value)}
                        >
                          {trainerOptionsForSelect.map((tr) => (
                            <option key={tr.id} value={tr.id}>
                              {tr.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label>Anlage</label>
                        <select
                          value={tAnlage}
                          onChange={(e) => setTAnlage(e.target.value)}
                        >
                          <option value="Wedding">Wedding</option>
                          <option value="Britz">Britz</option>
                        </select>
                      </div>
                      <label className="pill" style={{ cursor: "pointer", alignSelf: "end", marginBottom: 6 }}>
                        <input
                          type="checkbox"
                          checked={tIsPrivat}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setTIsPrivat(checked);
                            if (checked) {
                              // Privattraining hat keine Abrechnung -> Tarif/Preis zuruecksetzen
                              setTIsTenniscamp(false);
                              setTTarifId("");
                              setTCustomPreisProStunde("");
                            }
                          }}
                          style={{ marginRight: 8 }}
                        />
                        Privat
                      </label>
                      <label className="pill" style={{ cursor: "pointer", alignSelf: "end", marginBottom: 6, background: tIsKurzfristig ? "rgba(234, 179, 8, 0.15)" : undefined }}>
                        <input
                          type="checkbox"
                          checked={tIsKurzfristig}
                          onChange={(e) => {
                            setTIsKurzfristig(e.target.checked);
                            if (e.target.checked) {
                              setTIsTenniscamp(false);
                              setRepeatWeekly(false);
                              setRepeatPeriods([]);
                            }
                          }}
                          style={{ marginRight: 8 }}
                        />
                        Kurzfristiges Training
                      </label>
                      <label className="pill" style={{ cursor: "pointer", alignSelf: "end", marginBottom: 6, background: tIsTenniscamp ? "rgba(20, 184, 166, 0.15)" : undefined }}>
                        <input
                          type="checkbox"
                          checked={tIsTenniscamp}
                          disabled={!!selectedTrainingId}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setTIsTenniscamp(checked);
                            if (checked) {
                              // Tenniscamp: keine Spieler-Abrechnung, kein Tarif, läuft über einen Zeitraum
                              setTIsPrivat(false);
                              setTIsKurzfristig(false);
                              setTTarifId("");
                              setTCustomPreisProStunde("");
                              setTSpielerIds([]);
                              setTCampGebuehr("");
                              // Camp mit dem aktuell gewählten Trainer vorbelegen
                              setTCampTrainerIds(tTrainerId ? [tTrainerId] : []);
                              setRepeatWeekly(false);
                              setRepeatPeriods([]);
                              if (!tCampBisDatum || tCampBisDatum < tDatum) setTCampBisDatum(tDatum);
                            }
                          }}
                          style={{ marginRight: 8 }}
                        />
                        Tenniscamp
                      </label>
                    </div>

                    <div className="formSectionTitle">Abrechnung</div>
                    <div className="row">
                      <div className="field">
                        <label>Tarif (optional)</label>
                        <select
                          value={tTarifId}
                          disabled={tIsPrivat || tIsTenniscamp}
                          onChange={(e) => setTTarifId(e.target.value)}
                        >
                          <option value="">
                            Kein Tarif ausgewählt
                          </option>
                          {tarife.filter((t) => !t.archiviert || t.id === tTarifId).map((t) => {
                            const beschreibung =
                              t.abrechnung === "monatlich"
                                ? `${t.preisProStunde} EUR monatlich`
                                : `${t.preisProStunde} EUR/Std. – ${
                                    t.abrechnung === "proSpieler"
                                      ? "pro Spieler"
                                      : "pro Training"
                                  }`;
                            return (
                              <option key={t.id} value={t.id}>
                                {t.name} ({beschreibung})
                              </option>
                            );
                          })}
                        </select>
                        <div className="muted">
                          {tIsTenniscamp
                            ? "Beim Tenniscamp nicht relevant – es gibt keine Spieler-Abrechnung. Der Trainer wird normal mit seinem Stundensatz vergütet."
                            : tIsPrivat
                            ? "Bei Privattraining nicht relevant – es findet keine Abrechnung statt."
                            : "Entweder einen Tarif auswählen oder unten einen individuellen Preis pro Stunde eingeben."}
                        </div>
                      </div>

                      <div className="field">
                        <label>Status</label>
                        <select
                          value={tStatus}
                          onChange={(e) =>
                            setTStatus(e.target.value as TrainingStatus)
                          }
                        >
                          <option value="geplant">Geplant</option>
                          <option value="durchgefuehrt">Durchgeführt</option>
                          <option value="abgesagt">Abgesagt</option>
                        </select>
                      </div>
                      {tStatus === "durchgefuehrt" && (
                        <div className="field">
                          <label>Tatsächliche Dauer (Min)</label>
                          <input
                            type="number"
                            placeholder={String(durationMin(tVon, tBis))}
                            value={tActualMinutes}
                            onChange={(e) => setTActualMinutes(e.target.value)}
                            min={1}
                            max={durationMin(tVon, tBis)}
                          />
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="field">
                        <label>Individueller Preis pro Stunde</label>
                        <input
                          type="number"
                          value={
                            tCustomPreisProStunde === ""
                              ? ""
                              : tCustomPreisProStunde
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") {
                              setTCustomPreisProStunde("");
                            } else {
                              const n = Number(v);
                              setTCustomPreisProStunde(
                                Number.isFinite(n) ? n : ""
                              );
                            }
                          }}
                          placeholder="z.B. 60"
                          disabled={!!tTarifId || tIsPrivat || tIsTenniscamp}
                        />
                        {!!tTarifId && <div className="muted" style={{ fontSize: 12 }}>Durch Tarif überschrieben</div>}
                      </div>
                      <div className="field">
                        <label>Individuelle Abrechnung</label>
                        <select
                          value={tCustomAbrechnung}
                          onChange={(e) =>
                            setTCustomAbrechnung(
                              e.target.value as "proTraining" | "proSpieler"
                            )
                          }
                          disabled={!!tTarifId || tIsPrivat || tIsTenniscamp}
                        >
                          <option value="proTraining">Pro Training</option>
                          <option value="proSpieler">Pro Spieler</option>
                        </select>
                        {!!tTarifId && <div className="muted" style={{ fontSize: 12 }}>Durch Tarif überschrieben</div>}
                      </div>
                    </div>

                    <div className="row">
                      <div className="field" style={{ minWidth: 260 }}>
                        <label>Notiz</label>
                        <input
                          value={tNotiz}
                          onChange={(e) => setTNotiz(e.target.value)}
                          placeholder="optional"
                        />
                      </div>
                    </div>

                    <div style={{ height: 10 }} />

                    {tIsTenniscamp && !selectedTrainingId && (() => {
                      const bisOk = tCampBisDatum && tCampBisDatum >= tDatum;
                      const dayCount = bisOk
                        ? Math.floor((Date.parse(tCampBisDatum) - Date.parse(tDatum)) / 86400000) + 1
                        : 0;
                      const hoursPerDay = durationMin(tVon, tBis) / 60;
                      const campTrainers = tCampTrainerIds.length > 0 ? tCampTrainerIds : [tTrainerId || defaultTrainerId];
                      const honorar = round2(
                        campTrainers.reduce((s, id) => s + (trainerById.get(id)?.stundensatz ?? 0) * dayCount * hoursPerDay, 0)
                      );
                      const trainerNamen = campTrainers.map((id) => trainerById.get(id)?.name ?? "Trainer").join(", ");
                      const ersterTagLabel = (() => {
                        const [yy, mm, dd] = tDatum.split("-");
                        return dd && mm && yy ? `${dd}.${mm}.${yy}` : tDatum;
                      })();
                      return (
                        <div className="card cardInset">
                          <h2>Tenniscamp-Zeitraum</h2>
                          <div className="row" style={{ alignItems: "end" }}>
                            <span className="pill">
                              Erster Tag: <strong>{ersterTagLabel}</strong>
                              <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>(oben „Datum")</span>
                            </span>
                            <div className="field" style={{ minWidth: 180 }}>
                              <label>Letzter Tag</label>
                              <input
                                type="date"
                                value={tCampBisDatum}
                                min={tDatum}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setTCampBisDatum(v < tDatum ? tDatum : v);
                                }}
                              />
                            </div>
                            <span className="pill">
                              Stunden/Tag: <strong>{tVon}–{tBis}</strong>
                            </span>
                          </div>
                          <div className="muted" style={{ marginTop: 6 }}>
                            {bisOk
                              ? `Es werden ${dayCount} Trainingstag${dayCount === 1 ? "" : "e"} (${ersterTagLabel} – ${(() => { const [yy, mm, dd] = tCampBisDatum.split("-"); return dd && mm && yy ? `${dd}.${mm}.${yy}` : tCampBisDatum; })()}, ${tVon}–${tBis}, ${hoursPerDay.toLocaleString("de-DE")} Std./Tag) für ${trainerNamen} angelegt. Spieler werden nicht abgerechnet – Trainer-Honorar gesamt ≈ ${euro(honorar)} (${campTrainers.length} Trainer, je normaler Stundenlohn).`
                              : "Bitte einen gültigen Zeitraum wählen (Letzter Tag darf nicht vor dem Ersten Tag liegen)."}
                          </div>
                        </div>
                      );
                    })()}

                    {!selectedTrainingId && !tIsKurzfristig && !tIsTenniscamp && (
                      <div className="card cardInset">
                        <h2>Wiederholung</h2>
                        <div className="row">
                          <label
                            className="pill"
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              type="checkbox"
                              checked={repeatWeekly}
                              onChange={(e) =>
                                setRepeatWeekly(e.target.checked)
                              }
                              style={{ marginRight: 8 }}
                            />
                            Wöchentlich wiederholen
                          </label>
                          {repeatPeriods.length === 0 && (
                            <div className="field" style={{ minWidth: 220 }}>
                              <label>Bis Datum</label>
                              <input
                                type="date"
                                value={repeatUntil}
                                onChange={(e) =>
                                  setRepeatUntil(e.target.value)
                                }
                                disabled={!repeatWeekly}
                              />
                            </div>
                          )}
                          <span className="pill">
                            Trainer: <strong>{selectedTrainerName}</strong>
                          </span>
                        </div>

                        {repeatWeekly && repeatPeriods.length > 0 && (
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                            {repeatPeriods.map((period, idx) => (
                              <div key={idx} className="row" style={{ alignItems: "end" }}>
                                <div className="field" style={{ minWidth: 180 }}>
                                  <label>Zeitraum {idx + 1} – Von</label>
                                  <input
                                    type="date"
                                    value={period.von}
                                    onChange={(e) => {
                                      const updated = [...repeatPeriods];
                                      updated[idx] = { ...updated[idx], von: e.target.value };
                                      setRepeatPeriods(updated);
                                    }}
                                  />
                                </div>
                                <div className="field" style={{ minWidth: 180 }}>
                                  <label>Bis</label>
                                  <input
                                    type="date"
                                    value={period.bis}
                                    onChange={(e) => {
                                      const updated = [...repeatPeriods];
                                      updated[idx] = { ...updated[idx], bis: e.target.value };
                                      setRepeatPeriods(updated);
                                    }}
                                  />
                                </div>
                                <button
                                  className="btn btnWarn"
                                  style={{ padding: "6px 12px", minWidth: "auto" }}
                                  onClick={() => {
                                    setRepeatPeriods(repeatPeriods.filter((_, i) => i !== idx));
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {repeatWeekly && (
                          <div style={{ marginTop: 8 }}>
                            <button
                              className="btn btnGhost"
                              style={{ padding: "4px 12px", fontSize: 13 }}
                              onClick={() => {
                                if (repeatPeriods.length === 1 && repeatPeriods[0].bis === "2026-07-12") {
                                  // Zweiten Zeitraum (nach Sommerferien) hinzufügen
                                  setRepeatPeriods([...repeatPeriods, { von: "2026-08-24", bis: "2026-09-30" }]);
                                } else {
                                  const lastPeriod = repeatPeriods[repeatPeriods.length - 1];
                                  const newVon = lastPeriod ? addDaysISO(lastPeriod.bis, 1) : tDatum;
                                  const newBis = addDaysISO(newVon, 90);
                                  setRepeatPeriods([...repeatPeriods, { von: newVon, bis: newBis }]);
                                }
                              }}
                            >
                              + Zeitraum hinzufügen
                            </button>
                          </div>
                        )}

                        <div className="muted" style={{ marginTop: 6 }}>
                          {repeatPeriods.length > 0
                            ? `Trainings werden wöchentlich in ${repeatPeriods.length} Zeiträumen angelegt (z.B. Ferien auslassen).`
                            : "Wenn aktiv: Es werden alle Termine wöchentlich bis zum Bis Datum angelegt."}
                        </div>
                      </div>
                    )}

                    {selectedTrainingId &&
                      (() => {
                        const ex = trainings.find(
                          (x) => x.id === selectedTrainingId
                        );
                        if (!ex?.serieId) return null;
                        return (
                          <div className="card cardInset">
                            <h2>Serie bearbeiten</h2>
                            <div className="row">
                              <div className="field">
                                <label>Änderungen anwenden</label>
                                <select
                                  value={applySerieScope}
                                  onChange={(e) =>
                                    setApplySerieScope(
                                      e.target.value as
                                        | "nurDieses"
                                        | "abHeute"
                                    )
                                  }
                                >
                                  <option value="nurDieses">
                                    Nur diesen Termin
                                  </option>
                                  <option value="abHeute">
                                    Alle Termine der Serie ab diesem Datum
                                  </option>
                                </select>
                              </div>
                              <span className="pill">
                                Serie:{" "}
                                <strong>{ex.serieId.slice(0, 8)}</strong>
                              </span>
                            </div>
                            <div className="muted">
                              „Ab diesem Datum" übernimmt alle Änderungen für zukünftige Serientermine. „Löschen" entfernt alle zukünftigen Termine der Serie.
                            </div>
                          </div>
                        );
                      })()}

                    <div style={{ height: 10 }} />

                    <div className="row">
                      <button className="btn" onClick={() => saveTraining()}>
                        {selectedTrainingId
                          ? "Änderungen speichern"
                          : "Training speichern"}
                      </button>
                      <button
                        className="btn btnGhost"
                        onClick={() => {
                          resetTrainingForm();
                          setTab("kalender");
                        }}
                      >
                        Zurück zum Kalender
                      </button>
                      {selectedTrainingId && (
                        <>
                          <div style={{ flex: 1 }} />
                          <button
                            className="btn btnWarn"
                            onClick={() => deleteTraining(selectedTrainingId)}
                          >
                            Training löschen
                          </button>
                        </>
                      )}
                      {tSpielerIds.length > 0 && tSpielerIds.some(id => spielerById.get(id)?.kontaktEmail) && (
                        <ActionMenu
                          label="E-Mail senden"
                          buttonClassName="btn"
                          buttonStyle={{ backgroundColor: "#0e7490", borderColor: "#0e7490" }}
                          menuWidth={260}
                        >
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "#0e7490",
                            borderColor: "#0e7490",
                          }}
                          onClick={() => {
                            const trainerName = trainerById.get(tTrainerId)?.name ?? "Trainer";
                            const datum = new Date(tDatum + "T12:00:00");
                            const wochentag = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][datum.getDay()];
                            const trainerTelMap: Record<string, string> = {
                              sascha: "0157 73584431",
                              konsti: "0173 7255920",
                              marc: "01511 6227911",
                              jesper: "0172 3104772",
                              henri: "0163 9757063",
                              leon: "0176 62029303",
                              ramon: "0176 56793299",
                            };
                            const trainerTel = trainerTelMap[trainerName.trim().toLowerCase()] ?? "";
                            const trainerKontaktZeile = trainerTel
                              ? `Trainer: ${trainerName}\nTelefon: ${trainerTel}`
                              : `Trainer: ${trainerName}`;

                            setTrainingInfoEmailSubject(`Trainer-Kontakt bei Regen`);
                            setTrainingInfoIncludeSepa(false);
                            setTrainingInfoIncludeProbe(false);
                            setTrainingInfoIncludeMitglied(false);
                            setTrainingInfoIncludeErwachsene(false);
                            setTrainingInfoIncludeBeitragsordnung(false);
                            setTrainingInfoIncludeProbetraining(false);
                            setTrainingInfoEmailBody(
`Hallo {SPIELERNAME},

bei unsicherem Wetter (z.B. Regen) kannst du deinen Trainer direkt erreichen, um zu erfahren, ob die Plätze bespielbar sind und das Training stattfindet.

${trainerKontaktZeile}

Tag: ${wochentag}
Uhrzeit: ${tVon} - ${tBis} Uhr

Grundsätzlich gilt: Falls keine Absage erfolgt, wird von Stunde zu Stunde entschieden, ob das Training möglich ist. Bei einer kompletten Sperre der Plätze erhalten alle Schüler eine E-Mail zur Trainingsabsage.`
                            );
                            setTrainingInfoExcluded([]);
                            setShowTrainingInfoEmail(true);
                          }}
                        >
                          Trainer-Tel bei Regen
                        </button>
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "#047857",
                            borderColor: "#047857",
                          }}
                          onClick={() => {
                            const trainerName = trainerById.get(tTrainerId)?.name ?? "Trainer";
                            const datum = new Date(tDatum + "T12:00:00");
                            const wochentag = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][datum.getDay()];
                            const trainerTelMap: Record<string, string> = {
                              sascha: "0157 73584431",
                              konsti: "0173 7255920",
                              marc: "01511 6227911",
                              jesper: "0172 3104772",
                              henri: "0163 9757063",
                              leon: "0176 62029303",
                              ramon: "0176 56793299",
                            };
                            const trainerTel = trainerTelMap[trainerName.trim().toLowerCase()] ?? "";
                            const trainerKontaktZeile = trainerTel
                              ? `Trainer: ${trainerName}\nTelefon: ${trainerTel}`
                              : `Trainer: ${trainerName}`;
                            const sepaLink = tAnlage === "Britz"
                              ? `${window.location.origin}/sepa-britz`
                              : `${window.location.origin}/sepa`;
                            const tarif = tTarifId ? tarifById.get(tTarifId) : undefined;
                            const tarifInfo = tarif
                              ? `\nTarif: ${tarif.name} (${tarif.preisProStunde.toFixed(2).replace(".", ",")} EUR${tarif.abrechnung === "monatlich" ? " monatlich" : tarif.abrechnung === "proSpieler" ? " pro Spieler" : " pro Training"})`
                              : tCustomPreisProStunde
                              ? `\nPreis: ${Number(tCustomPreisProStunde).toFixed(2).replace(".", ",")} EUR pro Stunde`
                              : "";

                            const selectedTraining = selectedTrainingId ? trainings.find(t => t.id === selectedTrainingId) : undefined;
                            let startdatum = tDatum;
                            if (selectedTraining?.serieId) {
                              const serieTermine = trainings.filter(t => t.serieId === selectedTraining.serieId).map(t => t.datum).sort();
                              if (serieTermine.length > 0) startdatum = serieTermine[0];
                            }
                            const startdatumFormatted = new Date(startdatum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });

                            setTrainingInfoEmailSubject(`Trainingsbestätigung`);
                            setTrainingInfoIncludeSepa(true);
                            setTrainingInfoIncludeProbe(false);
                            setTrainingInfoIncludeMitglied(false);
                            setTrainingInfoIncludeErwachsene(false);
                            setTrainingInfoIncludeBeitragsordnung(false);
                            setTrainingInfoIncludeProbetraining(false);
                            setTrainingInfoEmailBody(
`Hallo {SPIELERNAME},

hiermit bestätige ich dein Training mit allen wichtigen Infos:

Tag: ${wochentag}
Uhrzeit: ${tVon} - ${tBis} Uhr
${tAnlage === "Wedding"
  ? `Anlage: BSC Rehberge 1945 e.V. Abt. Tennis
Sambesistraße 11, 13351 Berlin-Wedding
Eingangscode: 7788`
  : `Anlage: ${tAnlage}`}
${trainerKontaktZeile}
Teilnehmer: {ANDERE_TEILNEHMER}${tarifInfo}
Startdatum: ${startdatumFormatted}

Bitte beachte: Spezielle Sandplatzschuhe und ein eigener Tennisschläger sind verpflichtend.

Für die Abrechnung erteile uns bitte vor dem ersten Training ein SEPA-Lastschriftmandat:
${sepaLink}

Solltest du dies schon in einer vorherigen Saison erledigt haben, so kann dieses wieder benutzt werden und eine neue Erteilung ist nicht nötig.

Bei unsicherem Wetter (z.B. Regen) kannst du deinen Trainer direkt unter der oben genannten Nummer erreichen, um zu erfahren, ob die Plätze bespielbar sind. Bei einer kompletten Sperre der Plätze erhalten alle Schüler eine E-Mail zur Trainingsabsage.

Solltest du Fragen haben, antworte bitte auf diese E-Mail.`
                            );
                            setTrainingInfoExcluded([]);
                            setShowTrainingInfoEmail(true);
                          }}
                        >
                          Trainingsbestätigung
                        </button>
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "#ea580c",
                            borderColor: "#ea580c",
                          }}
                          onClick={() => {
                            const campLink = `${window.location.origin}/tenniscamp`;
                            setTrainingInfoEmailSubject(`Einladung: Tenniscamps in den Sommerferien 2026`);
                            setTrainingInfoIncludeSepa(false);
                            setTrainingInfoIncludeProbe(false);
                            setTrainingInfoIncludeMitglied(false);
                            setTrainingInfoIncludeErwachsene(false);
                            setTrainingInfoIncludeBeitragsordnung(false);
                            setTrainingInfoIncludeProbetraining(false);
                            setTrainingInfoEmailBody(
`Hallo {SPIELERNAME},

die Sommerferien stehen vor der Tür – und auch in diesem Jahr bieten wir am BSC Rehberge unsere beliebten Tenniscamps an! Wir würden uns freuen, dich dabei zu haben.

Es gibt zwei Wochen zur Auswahl:
1. Ferienwoche: 13. - 17. Juli 2026
Letzte Ferienwoche: 17. - 21. August 2026

Kindercamp: täglich 10:00 - 15:00 Uhr (inkl. Mittagessen) – 270 €
Erwachsenencamp: täglich 18:00 - 20:00 Uhr – 140 €

Wichtig beim Erwachsenencamp: Die Plätze sind besonders begrenzt – wir können nur die ersten 12 Anmeldungen annehmen, um ein intensives Tenniserlebnis zu ermöglichen.

Die Teilnehmer werden nach Spielstärke in Gruppen eingeteilt – passend für jedes Niveau. Eine ganze Woche voller Tennis, Spaß und Bewegung auf unserer Anlage:

BSC Rehberge
Sambesistraße 11, 13351 Berlin-Wedding

Mitzubringen: Sandplatzschuhe (Pflicht), Wasserflasche und ein Tennisschläger (Ausleihe möglich).

Hier kannst du dich direkt anmelden:
${campLink}

Sichere dir am besten frühzeitig deinen Platz. Bei Fragen antworte einfach auf diese E-Mail.

Wir freuen uns auf dich!`
                            );
                            setTrainingInfoExcluded([]);
                            setShowTrainingInfoEmail(true);
                          }}
                        >
                          Tenniscamp-Einladung
                        </button>
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "#0891b2",
                            borderColor: "#0891b2",
                          }}
                          onClick={() => {
                            // Letztes reguläres Training = passender Wochentag dieses Trainings
                            // in der Woche vom Montag, 6. Juli 2026.
                            const weekday = new Date(tDatum + "T12:00:00").getDay(); // 0=So..6=Sa
                            const letztesTrainingISO = addDaysISO("2026-07-06", (weekday + 6) % 7);
                            const letztesTraining = new Date(letztesTrainingISO + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
                            // Erstes reguläres Training nach den Ferien = gleicher Wochentag in der
                            // Woche vom Montag, 24. August 2026 (Trainingsstart nach der Sommerpause).
                            const erstesTrainingISO = addDaysISO("2026-08-24", (weekday + 6) % 7);
                            const erstesTraining = new Date(erstesTrainingISO + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
                            const sommerLink = tAnlage === "Britz"
                              ? `${window.location.origin}/britz-sommertraining`
                              : `${window.location.origin}/wedding-sommertraining`;
                            setTrainingInfoEmailSubject(`Letztes Training vor den Sommerferien & Sommertraining`);
                            setTrainingInfoIncludeSepa(false);
                            setTrainingInfoIncludeProbe(false);
                            setTrainingInfoIncludeMitglied(false);
                            setTrainingInfoIncludeErwachsene(false);
                            setTrainingInfoIncludeBeitragsordnung(false);
                            setTrainingInfoIncludeProbetraining(false);
                            setTrainingInfoEmailBody(
`Hallo {SPIELERNAME},

die Sommerferien stehen vor der Tür! Dein letztes reguläres Training vor den Ferien findet am ${letztesTraining} statt.

In den Sommerferien pausiert das reguläre Training, da viele von euch verreist sind. Dein erstes reguläres Training nach den Ferien findet am ${erstesTraining} statt.

Aufs Tennis verzichten musst du deshalb aber nicht: Du kannst jederzeit ein Training für dich allein oder zusammen mit weiteren Personen bei einem unserer Trainer buchen – ganz flexibel nach deinen Wünschen.

Hier kannst du dein Sommertraining buchen:
${sommerLink}

Aktuell sind dort erst einige Termine eingestellt – nach und nach kommen weitere dazu. Schau daher am besten regelmäßig vorbei, um einen passenden Termin zu finden.

Wir wünschen dir eine schöne, erholsame Ferienzeit und freuen uns darauf, dich danach wieder fit und erholt auf dem Platz zu begrüßen!`
                            );
                            setTrainingInfoExcluded([]);
                            setShowTrainingInfoEmail(true);
                          }}
                        >
                          Sommerferien-Info
                        </button>
                        </ActionMenu>
                      )}
                      {selectedTrainingId && tStatus === "geplant" && (
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "#8b5cf6",
                            borderColor: "#8b5cf6",
                          }}
                          onClick={() => {
                            markTrainingDoneAndBarBezahlt(selectedTrainingId);
                            resetTrainingForm();
                            setTab("kalender");
                          }}
                        >
                          Durchgeführt + bar
                        </button>
                      )}
                      {selectedTrainingId && tStatus === "durchgefuehrt" && (
                        <button
                          className="btn"
                          style={{
                            backgroundColor: trainings.find(t => t.id === selectedTrainingId)?.barBezahlt ? "#f97316" : "#8b5cf6",
                            borderColor: trainings.find(t => t.id === selectedTrainingId)?.barBezahlt ? "#f97316" : "#8b5cf6",
                          }}
                          onClick={() => toggleBarBezahlt(selectedTrainingId)}
                        >
                          {trainings.find(t => t.id === selectedTrainingId)?.barBezahlt
                            ? "Bar-Zahlung zurücknehmen"
                            : "Als bar bezahlt markieren"}
                        </button>
                      )}
                      {!tIsTenniscamp && (
                        <span className="pill">
                          Preis Vorschau:{" "}
                          <strong>{euro(preisVorschau)}</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="card">
                    <h2>{tIsTenniscamp ? "Teilnehmer" : "Spieler auswählen"}</h2>
                    <div className="row">
                      <div className="field" style={{ flex: 1 }}>
                        <label>{tIsTenniscamp ? "Teilnehmer hinzufügen" : "Spieler hinzufügen"}</label>
                        <input
                          value={spielerSuche}
                          onChange={(e) => setSpielerSuche(e.target.value)}
                          placeholder="Suche nach Name oder Email..."
                        />
                      </div>
                      <span className="pill">
                        Ausgewählt:{" "}
                        <strong>{tSpielerIds.length}</strong>
                      </span>
                    </div>

                    {/* Dropdown-Liste nur wenn Suche aktiv */}
                    {spielerSuche.trim() && (
                      <div style={{ 
                        maxHeight: 200, 
                        overflowY: "auto", 
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-md)",
                        marginTop: 8,
                        background: "var(--bg-card)"
                      }}>
                        {filteredSpielerForPick
                          .filter((s) => !tSpielerIds.includes(s.id))
                          .slice()
                          .sort((a, b) => getFullName(a).localeCompare(getFullName(b)))
                          .map((s) => (
                          <div
                            key={s.id}
                            style={{
                              padding: "10px 14px",
                              cursor: "pointer",
                              borderBottom: "1px solid var(--border-light)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                            onClick={() => {
                              toggleSpielerPick(s.id);
                              setSpielerSuche("");
                            }}
                          >
                            <div>
                              <strong>{getFullName(s)}</strong>
                              {s.kontaktEmail && (
                                <span className="muted" style={{ marginLeft: 8 }}>
                                  {s.kontaktEmail}
                                </span>
                              )}
                            </div>
                            <span style={{ color: "var(--primary)", fontSize: 12 }}>
                              + Hinzufügen
                            </span>
                          </div>
                        ))}
                        {filteredSpielerForPick.filter((s) => !tSpielerIds.includes(s.id)).length === 0 && (
                          <div style={{ padding: "10px 14px", color: "var(--text-muted)" }}>
                            Keine Spieler gefunden
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dropdown ohne Suche - alle verfügbaren Spieler */}
                    {!spielerSuche.trim() && (
                      <div className="field" style={{ marginTop: 8 }}>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              toggleSpielerPick(e.target.value);
                            }
                          }}
                          style={{ width: "100%" }}
                        >
                          <option value="">{tIsTenniscamp ? "Teilnehmer auswählen..." : "Spieler auswählen..."}</option>
                          {spieler
                            .filter((s) => !tSpielerIds.includes(s.id) && !s.archiviert)
                            .slice()
                            .sort((a, b) => getFullName(a).localeCompare(getFullName(b)))
                            .map((s) => (
                            <option key={s.id} value={s.id}>
                              {getFullName(s)}{s.kontaktEmail ? ` (${s.kontaktEmail})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Ausgewählte Spieler anzeigen */}
                    {tSpielerIds.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div className="muted" style={{ marginBottom: 8 }}>Ausgewählte Spieler:</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {tSpielerIds.map((id) => {
                            const s = spielerById.get(id);
                            if (!s) return null;
                            return (
                              <span
                                key={id}
                                className="pill"
                                style={{ 
                                  display: "inline-flex", 
                                  alignItems: "center", 
                                  gap: 6,
                                  background: "rgba(34, 197, 94, 0.15)",
                                  color: "#15803d"
                                }}
                              >
                                {getDisplayName(s)}
                                <button
                                  type="button"
                                  onClick={() => toggleSpielerPick(id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    fontSize: 14,
                                    lineHeight: 1,
                                    color: "#991b1b"
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {tIsTenniscamp && (
                    <div className="card">
                      <h2>Trainer im Camp</h2>
                      <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                        Alle ausgewählten Trainer führen das Camp durch und bekommen ihren
                        normalen Stundenlohn für die Camp-Stunden.
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {trainers.map((tr) => {
                          const checked = tCampTrainerIds.includes(tr.id);
                          return (
                            <label
                              key={tr.id}
                              className="pill"
                              style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                background: checked ? "rgba(20, 184, 166, 0.15)" : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setTCampTrainerIds((prev) =>
                                    prev.includes(tr.id)
                                      ? prev.filter((x) => x !== tr.id)
                                      : [...prev, tr.id]
                                  )
                                }
                              />
                              {tr.name}
                              <span className="muted" style={{ fontSize: 12 }}>
                                {euro(tr.stundensatz ?? 0)}/Std.
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {tCampTrainerIds.length === 0 && (
                        <div className="muted" style={{ marginTop: 8, fontSize: 13, color: "#dc2626" }}>
                          Kein Trainer ausgewählt – es wird der oben im Formular gewählte Trainer verwendet.
                        </div>
                      )}
                    </div>
                  )}

                  {tIsTenniscamp && (() => {
                    const gebuehr = typeof tCampGebuehr === "number" ? tCampGebuehr : 0;
                    const teilnehmer = tSpielerIds.length;
                    const bereit = tSpielerIds.filter((id) => {
                      const sp = spielerById.get(id);
                      return !!(sp?.iban && sp?.mandatsreferenz && sp?.unterschriftsdatum);
                    }).length;
                    const summe = round2(teilnehmer * gebuehr);
                    const campTitel = tNotiz.trim() || (() => {
                      const [yy, mm, dd] = tDatum.split("-");
                      return dd && mm && yy ? `${dd}.${mm}.${yy}` : tDatum;
                    })();
                    return (
                      <div className="card">
                        <h2>Camp-Gebühr &amp; SEPA-Lastschrift</h2>
                        <div className="row" style={{ alignItems: "end" }}>
                          <div className="field" style={{ minWidth: 220 }}>
                            <label>Camp-Preis pro Teilnehmer (EUR)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={tCampGebuehr === "" ? "" : tCampGebuehr}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "") { setTCampGebuehr(""); return; }
                                const n = Number(v);
                                setTCampGebuehr(Number.isFinite(n) ? n : "");
                              }}
                              placeholder="z.B. 120"
                            />
                          </div>
                          <span className="pill">Teilnehmer: <strong>{teilnehmer}</strong></span>
                          <span className="pill">Summe: <strong>{euro(summe)}</strong></span>
                        </div>
                        <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                          Fester Beitrag fürs ganze Camp – <strong>nicht</strong> Teil der Monatsabrechnung.
                          Für die Lastschrift braucht jeder Teilnehmer IBAN, Mandatsreferenz und
                          Unterschriftsdatum ({bereit}/{teilnehmer} vollständig). Beträge sind im
                          nächsten Schritt pro Teilnehmer anpassbar.
                        </div>
                        <div className="row" style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="btn"
                            disabled={teilnehmer === 0}
                            onClick={() => openCampSepaModal(tSpielerIds, gebuehr, campTitel)}
                          >
                            SEPA-XML erstellen
                          </button>
                          {teilnehmer === 0 && (
                            <span className="muted" style={{ alignSelf: "center", fontSize: 13 }}>
                              Zuerst oben Teilnehmer hinzufügen.
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}

            {tab === "verwaltung" && !isTrainer && (
              <>
                <div className="subTabs">
                  <button
                    className={`tabBtn ${
                      verwaltungTab === "trainer" ? "tabBtnActive" : ""
                    }`}
                    onClick={() => setVerwaltungTab("trainer")}
                  >
                    Trainer
                  </button>
                  <button
                    className={`tabBtn ${
                      verwaltungTab === "spieler" ? "tabBtnActive" : ""
                    }`}
                    onClick={() => setVerwaltungTab("spieler")}
                  >
                    Spieler
                  </button>
                  <button
                    className={`tabBtn ${
                      verwaltungTab === "tarife" ? "tabBtnActive" : ""
                    }`}
                    onClick={() => setVerwaltungTab("tarife")}
                  >
                    Tarife
                  </button>
                  <button
                    className={`tabBtn ${
                      verwaltungTab === "newsletter" ? "tabBtnActive" : ""
                    }`}
                    onClick={() => setVerwaltungTab("newsletter")}
                  >
                    Newsletter
                  </button>
                </div>

                <div style={{ height: 12 }} />

                {verwaltungTab === "trainer" && (
                  <div className="card">
                    <h2>Trainer verwalten</h2>
                    
                    <ul className="list">
                      {trainers.map((t) => (
                        <li key={t.id} className="listItem">
                          <div>
                            <strong>{t.name}</strong>
                            {t.email && (
                              <div className="muted">{t.email}</div>
                            )}
                            <div className="muted">
                              Honorar: {euro(t.stundensatz ?? 0)} pro Stunde
                            </div>
                          </div>
                          <div className="smallActions">
                            <button
                              className="btn micro btnGhost"
                              onClick={() => {
                                startEditTrainer(t);
                                setShowTrainerForm(true);
                              }}
                            >
                              Bearbeiten
                            </button>
                            {trainers.length > 1 && (
                              <button
                                className="btn micro btnWarn"
                                onClick={() => deleteTrainer(t.id)}
                              >
                                Löschen
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>

                    {!showTrainerForm && !editingTrainerId && (
                      <div style={{ marginTop: 16 }}>
                        <button
                          className="btn"
                          onClick={() => setShowTrainerForm(true)}
                        >
                          Neuen Trainer hinzufügen
                        </button>
                      </div>
                    )}

                    {(showTrainerForm || editingTrainerId) && (
                      <div className="card cardInset" style={{ marginTop: 16 }}>
                        <h3>{editingTrainerId ? "Trainer bearbeiten" : "Neuen Trainer hinzufügen"}</h3>
                        <div className="fieldGrid">
                          <div className="field">
                            <label>Name</label>
                            <input
                              value={trainerName}
                              onChange={(e) => setTrainerName(e.target.value)}
                              placeholder="z.B. Jesper"
                            />
                          </div>
                          <div className="field">
                            <label>Nachname</label>
                            <input
                              value={trainerNachname}
                              onChange={(e) => setTrainerNachname(e.target.value)}
                              placeholder="Mustermann"
                            />
                          </div>
                          <div className="field">
                            <label>Email</label>
                            <input
                              value={trainerEmail}
                              onChange={(e) => setTrainerEmail(e.target.value)}
                              placeholder="z.B. trainer@example.com"
                            />
                          </div>
                          <div className="field">
                            <label>Telefon</label>
                            <input
                              type="tel"
                              value={trainerTelefon}
                              onChange={(e) => setTrainerTelefon(e.target.value)}
                              placeholder="z.B. 0176 1234567"
                            />
                          </div>
                          <div className="field">
                            <label>Stundensatz Trainer Honorar</label>
                            <input
                              type="number"
                              value={
                                trainerStundensatz === ""
                                  ? ""
                                  : trainerStundensatz
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "") {
                                  setTrainerStundensatz("");
                                } else {
                                  const n = Number(v);
                                  setTrainerStundensatz(
                                    Number.isFinite(n) ? n : ""
                                  );
                                }
                              }}
                              placeholder="z.B. 20"
                            />
                          </div>
                        </div>
                        <div className="field" style={{ marginTop: 8 }}>
                          <label>Notiz für Trainer</label>
                          <textarea
                            rows={3}
                            value={trainerNotiz}
                            onChange={(e) => setTrainerNotiz(e.target.value)}
                            placeholder="Interne Notiz für diesen Trainer..."
                          />
                        </div>
                        <div className="field" style={{ marginTop: 8 }}>
                          <label>Rechnungsadresse des Trainers</label>
                          <textarea
                            rows={3}
                            value={trainerAdresse}
                            onChange={(e) => setTrainerAdresse(e.target.value)}
                            placeholder="Max Mustermann&#10;Musterstraße 123&#10;12345 Berlin"
                          />
                        </div>
                        <div className="row" style={{ marginTop: 8 }}>
                          <div className="field">
                            <label>IBAN</label>
                            <input
                              value={trainerIban}
                              onChange={(e) => setTrainerIban(e.target.value)}
                              placeholder="DE89 3704 0044 0532 0130 00"
                            />
                          </div>
                          <div className="field">
                            <label>Steuernummer</label>
                            <input
                              value={trainerUstIdNr}
                              onChange={(e) => setTrainerUstIdNr(e.target.value)}
                              placeholder="123/456/78901"
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={trainerKleinunternehmer}
                              onChange={(e) => setTrainerKleinunternehmer(e.target.checked)}
                            />
                            <span>Kleinunternehmerregelung (keine MwSt.)</span>
                          </label>
                        </div>

                        <div className="row stickyActions" style={{ marginTop: 12, background: "var(--bg-inset)" }}>
                          <button
                            className="btn"
                            onClick={() => {
                              if (editingTrainerId) {
                                saveTrainer();
                              } else {
                                addTrainer();
                              }
                              setShowTrainerForm(false);
                            }}
                          >
                            {editingTrainerId
                              ? "Trainer speichern"
                              : "Trainer hinzufügen"}
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => {
                              setEditingTrainerId(null);
                              setTrainerName("");
                              setTrainerNachname("");
                              setTrainerEmail("");
                              setTrainerTelefon("");
                              setTrainerStundensatz(0);
                              setTrainerNotiz("");
                              setTrainerAdresse("");
                              setTrainerIban("");
                              setTrainerUstIdNr("");
                              setTrainerKleinunternehmer(false);
                              setShowTrainerForm(false);
                            }}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {verwaltungTab === "spieler" && (
                  <div className="card">
                    <h2>Spieler verwalten</h2>

                    <div className="row" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                      <div className="field" style={{ flex: 1, minWidth: 150 }}>
                        <label>Suche</label>
                        <input
                          value={verwaltungSpielerSuche}
                          onChange={(e) => setVerwaltungSpielerSuche(e.target.value)}
                          placeholder="Name oder Email suchen..."
                        />
                      </div>
                      <div className="field" style={{ minWidth: 120 }}>
                        <label>Label</label>
                        <select
                          value={verwaltungLabelFilter}
                          onChange={(e) => setVerwaltungLabelFilter(e.target.value)}
                        >
                          <option value="alle">Alle Labels</option>
                          <option value="ohne">Ohne Label</option>
                          {allLabels.map((label) => (
                            <option key={label} value={label}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <span className="pill" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
                        {verwaltungZeigeArchiv ? "Archiviert" : "Aktiv"}:{" "}
                        <strong>
                          {verwaltungZeigeArchiv
                            ? spieler.filter((s) => s.archiviert).length
                            : spieler.filter((s) => !s.archiviert).length}
                        </strong>
                      </span>
                      <button
                        className={verwaltungZeigeArchiv ? "btn" : "btn btnGhost"}
                        style={{ alignSelf: "flex-end", marginBottom: 4 }}
                        onClick={() => setVerwaltungZeigeArchiv((v) => !v)}
                      >
                        {verwaltungZeigeArchiv
                          ? "← Aktive Spieler"
                          : `Archiv (${spieler.filter((s) => s.archiviert).length})`}
                      </button>
                      {!showSpielerForm && !editingSpielerId && (
                        <>
                          <button
                            className="btn"
                            onClick={() => {
                              setSpielerError(null);
                              setShowSpielerForm(true);
                            }}
                          >
                            Neuen Spieler hinzufügen
                          </button>
                          <label
                            className="btn btnGhost"
                            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                          >
                            Kontaktbuch importieren
                            <input
                              type="file"
                              accept=".csv,text/csv"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleKontaktbuchFileSelect(file);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        </>
                      )}
                    </div>

                    {(showSpielerForm || editingSpielerId) && (
                      <div className="card cardInset" style={{ marginBottom: 16 }}>
                        <h3>{editingSpielerId ? "Spieler bearbeiten" : "Neuen Spieler hinzufügen"}</h3>
                        
                        {spielerError && (
                          <div style={{
                            backgroundColor: "#fee2e2",
                            border: "1px solid #dc2626",
                            borderRadius: "var(--radius-md)",
                            padding: "12px 16px",
                            marginBottom: 12,
                            color: "#991b1b",
                            fontWeight: 500
                          }}>
                            {spielerError}
                          </div>
                        )}
                        
                        <div className="fieldGrid">
                          <div className="field">
                            <label>Vorname</label>
                            <input
                              value={spielerVorname}
                              onChange={(e) => {
                                setSpielerVorname(e.target.value);
                                setSpielerError(null);
                              }}
                              placeholder="Vorname"
                            />
                          </div>
                          <div className="field">
                            <label>Nachname</label>
                            <input
                              value={spielerNachname}
                              onChange={(e) => {
                                setSpielerNachname(e.target.value);
                                setSpielerError(null);
                              }}
                              placeholder="Nachname (optional)"
                            />
                          </div>
                          <div className="field">
                            <label>Email</label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <input
                                value={spielerEmail}
                                onChange={(e) => {
                                  setSpielerEmail(e.target.value);
                                  setSpielerError(null);
                                }}
                                placeholder="Kontakt Email"
                                style={{ flex: 1 }}
                              />
                              {spielerEmail && (
                                <button
                                  type="button"
                                  title="E-Mail senden"
                                  onClick={() => {
                                    const name = `${spielerVorname}${spielerNachname ? " " + spielerNachname : ""}`;
                                    const allEmails = [spielerEmail, ...spielerZusaetzlicheEmails].filter(Boolean);
                                    setNewsletterExtraEmails(prev => {
                                      const existingEmails = new Set(prev.map(e => e.email));
                                      const newEntries = allEmails
                                        .filter(email => !existingEmails.has(email))
                                        .map(email => ({ email, name }));
                                      return [...prev, ...newEntries];
                                    });
                                    setNewsletterSubject("Anfrage zum Tennistraining");
                                    setNewsletterLabelFilter("keine");
                                    setTab("verwaltung");
                                    setVerwaltungTab("newsletter");
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 12px",
                                    height: 36,
                                    borderRadius: "var(--radius-md)",
                                    background: "var(--primary)",
                                    color: "#fff",
                                    border: "none",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Mail
                                </button>
                              )}
                            </div>
                            {/* Zusätzliche E-Mail-Adressen */}
                            {spielerZusaetzlicheEmails.length > 0 && (
                              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {spielerZusaetzlicheEmails.map((email, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      padding: "4px 8px",
                                      background: "var(--surface)",
                                      borderRadius: "var(--radius-sm)",
                                      fontSize: 13,
                                    }}
                                  >
                                    {email}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSpielerZusaetzlicheEmails(prev => prev.filter((_, i) => i !== idx));
                                      }}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: 0,
                                        color: "var(--danger)",
                                        fontSize: 14,
                                        lineHeight: 1,
                                      }}
                                      title="Entfernen"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
                              <input
                                value={spielerNeueEmail}
                                onChange={(e) => setSpielerNeueEmail(e.target.value)}
                                placeholder="Weitere E-Mail hinzufügen"
                                style={{ flex: 1, fontSize: 13 }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const email = spielerNeueEmail.trim();
                                    if (!email) return;
                                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                                    if (email === spielerEmail.trim()) return;
                                    if (spielerZusaetzlicheEmails.includes(email)) return;
                                    setSpielerZusaetzlicheEmails(prev => [...prev, email]);
                                    setSpielerNeueEmail("");
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const email = spielerNeueEmail.trim();
                                  if (!email) return;
                                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                                  if (email === spielerEmail.trim()) return;
                                  if (spielerZusaetzlicheEmails.includes(email)) return;
                                  setSpielerZusaetzlicheEmails(prev => [...prev, email]);
                                  setSpielerNeueEmail("");
                                }}
                                style={{
                                  padding: "4px 10px",
                                  fontSize: 13,
                                  background: "var(--surface)",
                                  border: "1px solid var(--border)",
                                  borderRadius: "var(--radius-sm)",
                                  cursor: "pointer",
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div className="field">
                            <label>Telefon</label>
                            <input
                              value={spielerTelefon}
                              onChange={(e) => setSpielerTelefon(e.target.value)}
                              placeholder="Telefon"
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="field" style={{ minWidth: 260 }}>
                            <label>Rechnungsadresse</label>
                            <input
                              value={spielerRechnung}
                              onChange={(e) => setSpielerRechnung(e.target.value)}
                              placeholder="optional"
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="field" style={{ minWidth: 260 }}>
                            <label>Notizen</label>
                            <input
                              value={spielerNotizen}
                              onChange={(e) => setSpielerNotizen(e.target.value)}
                              placeholder="optional"
                            />
                          </div>
                        </div>

                        <h4 style={{ marginTop: 20, marginBottom: 12, color: "var(--text-muted)" }}>Standort</h4>
                        <div className="row">
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="standort"
                              checked={spielerLabels.includes("Wedding")}
                              onChange={() => {
                                const newLabels = spielerLabels.filter(l => l !== "Britz" && l !== "Wedding");
                                newLabels.push("Wedding");
                                setSpielerLabels(newLabels);
                              }}
                            />
                            Wedding
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginLeft: 16 }}>
                            <input
                              type="radio"
                              name="standort"
                              checked={spielerLabels.includes("Britz")}
                              onChange={() => {
                                const newLabels = spielerLabels.filter(l => l !== "Britz" && l !== "Wedding");
                                newLabels.push("Britz");
                                setSpielerLabels(newLabels);
                              }}
                            />
                            Britz
                          </label>
                        </div>

                        <h4 style={{ marginTop: 20, marginBottom: 12, color: "var(--text-muted)" }}>Labels (für Newsletter)</h4>
                        <div className="row" style={{ alignItems: "flex-start" }}>
                          <div className="field" style={{ flex: 1 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                              {spielerLabels.map((label, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    background: "var(--primary)",
                                    color: "#fff",
                                    padding: "4px 10px",
                                    borderRadius: 12,
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  {label}
                                  <button
                                    type="button"
                                    onClick={() => setSpielerLabels(spielerLabels.filter((_, i) => i !== idx))}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#fff",
                                      cursor: "pointer",
                                      padding: 0,
                                      fontSize: 14,
                                      lineHeight: 1,
                                    }}
                                  >
                                    &times;
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <input
                                value={newLabelInput}
                                onChange={(e) => setNewLabelInput(e.target.value)}
                                placeholder="Neues Label eingeben..."
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const label = newLabelInput.trim();
                                    if (label && !spielerLabels.includes(label)) {
                                      setSpielerLabels([...spielerLabels, label]);
                                      setNewLabelInput("");
                                    }
                                  }
                                }}
                                list="available-labels"
                                style={{ flex: 1 }}
                              />
                              <datalist id="available-labels">
                                {allLabels.filter(l => !spielerLabels.includes(l)).map((label) => (
                                  <option key={label} value={label} />
                                ))}
                              </datalist>
                              <button
                                type="button"
                                className="btn btnGhost"
                                onClick={() => {
                                  const label = newLabelInput.trim();
                                  if (label && !spielerLabels.includes(label)) {
                                    setSpielerLabels([...spielerLabels, label]);
                                    setNewLabelInput("");
                                  }
                                }}
                              >
                                Hinzufügen
                              </button>
                            </div>
                          </div>
                        </div>

                        <h4 style={{ marginTop: 20, marginBottom: 12, color: "var(--text-muted)" }}>SEPA-Lastschrift Daten</h4>
                        <div className="row">
                          <div className="field" style={{ minWidth: 280 }}>
                            <label>IBAN</label>
                            <input
                              value={spielerIban}
                              onChange={(e) => setSpielerIban(e.target.value)}
                              placeholder="DE89 3704 0044 0532 0130 00"
                            />
                          </div>
                          <div className="field" style={{ minWidth: 200 }}>
                            <label>Bankname</label>
                            <input
                              value={spielerBankname}
                              onChange={(e) => setSpielerBankname(e.target.value)}
                              placeholder="z.B. Commerzbank Berlin"
                            />
                          </div>
                          <div className="field" style={{ minWidth: 180 }}>
                            <label>Mandatsreferenz</label>
                            <input
                              value={spielerMandatsreferenz}
                              onChange={(e) => setSpielerMandatsreferenz(e.target.value)}
                              placeholder="z.B. MANDAT-001"
                            />
                          </div>
                          <div className="field" style={{ minWidth: 160 }}>
                            <label>Unterschriftsdatum</label>
                            <input
                              type="date"
                              value={spielerUnterschriftsdatum}
                              onChange={(e) => setSpielerUnterschriftsdatum(e.target.value)}
                            />
                          </div>
                          <div className="field" style={{ minWidth: 140 }}>
                            <label>Sequenz</label>
                            <select
                              value={spielerSepaSequenz}
                              onChange={(e) => setSpielerSepaSequenz(e.target.value as SepaSequenz)}
                            >
                              <option value="RCUR">RCUR (wiederkehrend)</option>
                              <option value="FRST">FRST (erstmalig)</option>
                              <option value="OOFF">OOFF (einmalig)</option>
                              <option value="FNAL">FNAL (letztmalig)</option>
                            </select>
                          </div>
                          <div className="field" style={{ minWidth: 140 }}>
                            <label>Lastschriftart</label>
                            <select
                              value={spielerSepaLastschriftart}
                              onChange={(e) => setSpielerSepaLastschriftart(e.target.value as SepaLastschriftart)}
                            >
                              <option value="CORE">CORE (Basis)</option>
                              <option value="B2B">B2B (Firmen)</option>
                            </select>
                          </div>
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                          Gläubiger-ID: {GLAEUBIGER_ID} (global)
                        </div>

                        <h4 style={{ marginTop: 20, marginBottom: 4, color: "var(--text-muted)" }}>Rechnungsempfänger / Kontoinhaber</h4>
                        <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
                          Standard: der Spieler selbst. Bei Kindern: hier den Eltern-Namen eintragen — der erscheint dann als Kontoinhaber im SEPA-XML.
                        </p>
                        <div className="row" style={{ alignItems: "center" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={spielerAbweichenderEmpfaenger}
                              onChange={(e) => setSpielerAbweichenderEmpfaenger(e.target.checked)}
                            />
                            Anderer Kontoinhaber als der Spieler (z.B. Eltern bei Kindern)
                          </label>
                        </div>
                        {spielerAbweichenderEmpfaenger && (
                          <div className="row" style={{ marginTop: 12 }}>
                            <div className="field" style={{ minWidth: 280 }}>
                              <label>Vor- und Nachname des Rechnungsempfängers / Kontoinhabers</label>
                              <input
                                value={spielerEmpfaengerName}
                                onChange={(e) => setSpielerEmpfaengerName(e.target.value)}
                                placeholder="z.B. Maria Müller (Mama von …)"
                              />
                            </div>
                          </div>
                        )}

                        <div className="row stickyActions" style={{ marginTop: 20, background: "var(--bg-inset)" }}>
                          <button
                            className="btn"
                            onClick={() => {
                              if (editingSpielerId) {
                                saveSpieler();
                              } else {
                                addSpieler();
                              }
                            }}
                          >
                            {editingSpielerId
                              ? "Spieler speichern"
                              : "Spieler hinzufügen"}
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => {
                              setEditingSpielerId(null);
                              setSpielerVorname("");
                              setSpielerNachname("");
                              setSpielerEmail("");
                              setSpielerTelefon("");
                              setSpielerRechnung("");
                              setSpielerNotizen("");
                              setSpielerIban("");
                              setSpielerBankname("");
                              setSpielerMandatsreferenz("");
                              setSpielerUnterschriftsdatum("");
                              setSpielerSepaSequenz("RCUR");
                              setSpielerSepaLastschriftart("CORE");
                              setSpielerAbweichenderEmpfaenger(false);
                              setSpielerEmpfaengerName("");
                              setSpielerError(null);
                              setShowSpielerForm(false);
                            }}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}

                    <ul className="list">
                      {spieler
                        .slice()
                        .filter((s) => {
                          // Archiv-Umschalter: entweder aktive oder archivierte Spieler
                          if (verwaltungZeigeArchiv ? !s.archiviert : !!s.archiviert) return false;
                          // Label-Filter
                          if (verwaltungLabelFilter === "ohne") {
                            if (s.labels && s.labels.length > 0) return false;
                          } else if (verwaltungLabelFilter !== "alle") {
                            if (!s.labels?.includes(verwaltungLabelFilter)) return false;
                          }
                          // Suche
                          const q = verwaltungSpielerSuche.trim().toLowerCase();
                          if (!q) return true;
                          return (
                            s.vorname.toLowerCase().includes(q) ||
                            (s.nachname ?? "").toLowerCase().includes(q) ||
                            (s.kontaktEmail ?? "").toLowerCase().includes(q) ||
                            (s.kontaktTelefon ?? "").toLowerCase().includes(q)
                          );
                        })
                        .sort((a, b) => getFullName(a).localeCompare(getFullName(b)))
                        .map((s) => (
                        <li key={s.id} className="listItem" style={s.archiviert ? { opacity: 0.6 } : undefined}>
                          <div>
                            <strong>{getFullName(s)}</strong>
                            {s.archiviert && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  background: "var(--bg-inset)",
                                  color: "var(--text-muted)",
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                  fontSize: 11,
                                }}
                              >
                                Archiviert
                              </span>
                            )}
                            <div className="muted">
                              {s.kontaktEmail ?? ""}
                              {s.kontaktTelefon
                                ? `, ${s.kontaktTelefon}`
                                : ""}
                            </div>
                            {s.rechnungsAdresse && (
                              <div className="muted">
                                Rechnungsadresse: {s.rechnungsAdresse}
                              </div>
                            )}
                            {s.notizen && (
                              <div className="muted">{s.notizen}</div>
                            )}
                            {s.labels && s.labels.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                                {s.labels.map((label, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      background: "var(--primary)",
                                      color: "#fff",
                                      padding: "2px 8px",
                                      borderRadius: 10,
                                      fontSize: 11,
                                    }}
                                  >
                                    {label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="smallActions">
                            {s.archiviert ? (
                              <>
                                <button
                                  className="btn micro"
                                  onClick={() => unarchiveSpieler(s.id)}
                                >
                                  Reaktivieren
                                </button>
                                <button
                                  className="btn micro btnWarn"
                                  onClick={() => deleteSpieler(s.id)}
                                >
                                  Endgültig löschen
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => {
                                    startEditSpieler(s);
                                    setSpielerError(null);
                                    setShowSpielerForm(true);
                                  }}
                                >
                                  Bearbeiten
                                </button>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => archiveSpieler(s.id)}
                                >
                                  Archivieren
                                </button>
                                <button
                                  className="btn micro btnWarn"
                                  onClick={() => deleteSpieler(s.id)}
                                >
                                  Löschen
                                </button>
                              </>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {verwaltungTab === "tarife" && (
                  <div className="card">
                    <h2>Tarife verwalten</h2>

                    <div className="row" style={{ marginBottom: 12, alignItems: "center", gap: 8 }}>
                      <span className="pill">
                        {tarifZeigeArchiv ? "Archiviert" : "Aktiv"}:{" "}
                        <strong>
                          {tarife.filter((t) => (tarifZeigeArchiv ? t.archiviert : !t.archiviert)).length}
                        </strong>
                      </span>
                      <button
                        className="btn btnGhost micro"
                        onClick={() => setTarifZeigeArchiv((v) => !v)}
                      >
                        {tarifZeigeArchiv
                          ? "← Aktive Tarife"
                          : `Archiv (${tarife.filter((t) => t.archiviert).length})`}
                      </button>
                    </div>

                    <ul className="list">
                      {tarife
                        .filter((t) => (tarifZeigeArchiv ? t.archiviert : !t.archiviert))
                        .map((t) => (
                        <li key={t.id} className="listItem" style={t.archiviert ? { opacity: 0.6 } : undefined}>
                          <div>
                            <strong>{t.name}</strong>
                            {t.archiviert && (
                              <span style={{ marginLeft: 8, background: "var(--bg-inset)", color: "var(--text-muted)", padding: "2px 8px", borderRadius: 10, fontSize: 11 }}>
                                Archiviert
                              </span>
                            )}
                            <div className="muted">
                              {t.abrechnung === "monatlich"
                                ? `${t.preisProStunde} EUR monatlich`
                                : `${t.preisProStunde} EUR pro Stunde, ${
                                    t.abrechnung === "proSpieler"
                                      ? "pro Spieler"
                                      : "pro Training"
                                  }`}
                            </div>
                            {t.beschreibung && (
                              <div className="muted">{t.beschreibung}</div>
                            )}
                          </div>
                          <div className="smallActions">
                            {t.archiviert ? (
                              <>
                                <button
                                  className="btn micro"
                                  onClick={() => unarchiveTarif(t.id)}
                                >
                                  Reaktivieren
                                </button>
                                <button
                                  className="btn micro btnWarn"
                                  onClick={() => deleteTarif(t.id)}
                                >
                                  Endgültig löschen
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => {
                                    startEditTarif(t);
                                    setShowTarifForm(true);
                                  }}
                                >
                                  Bearbeiten
                                </button>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => archiveTarif(t.id)}
                                >
                                  Archivieren
                                </button>
                                <button
                                  className="btn micro btnWarn"
                                  onClick={() => deleteTarif(t.id)}
                                >
                                  Löschen
                                </button>
                              </>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>

                    {!showTarifForm && !editingTarifId && (
                      <div style={{ marginTop: 16 }}>
                        <button
                          className="btn"
                          onClick={() => setShowTarifForm(true)}
                        >
                          Neuen Tarif hinzufügen
                        </button>
                      </div>
                    )}

                    {(showTarifForm || editingTarifId) && (
                      <div className="card cardInset" style={{ marginTop: 16 }}>
                        <h3>{editingTarifId ? "Tarif bearbeiten" : "Neuen Tarif hinzufügen"}</h3>
                        <div className="row">
                          <div className="field">
                            <label>Name</label>
                            <input
                              value={tarifName}
                              onChange={(e) => setTarifName(e.target.value)}
                              placeholder="z.B. Gruppentraining"
                            />
                          </div>
                          <div className="field">
                            <label>Preis pro Stunde</label>
                            <input
                              type="number"
                              value={tarifPreisProStunde}
                              onChange={(e) =>
                                setTarifPreisProStunde(
                                  Number(e.target.value) || 0
                                )
                              }
                            />
                          </div>
                          <div className="field">
                            <label>Abrechnung</label>
                            <select
                              value={tarifAbrechnung}
                              onChange={(e) =>
                                setTarifAbrechnung(
                                  e.target.value as
                                    | "proTraining"
                                    | "proSpieler"
                                    | "monatlich"
                                )
                              }
                            >
                              <option value="proTraining">Pro Training</option>
                              <option value="proSpieler">Pro Spieler</option>
                              <option value="monatlich">Monatlich</option>
                            </select>
                          </div>
                        </div>

                        <div className="row">
                          <div className="field" style={{ minWidth: 260 }}>
                            <label>Beschreibung</label>
                            <input
                              value={tarifBeschreibung}
                              onChange={(e) =>
                                setTarifBeschreibung(e.target.value)
                              }
                              placeholder="optional"
                            />
                          </div>
                        </div>

                        <div className="row stickyActions" style={{ background: "var(--bg-inset)" }}>
                          <button
                            className="btn"
                            onClick={() => {
                              if (editingTarifId) {
                                saveTarif();
                              } else {
                                addTarif();
                              }
                              setShowTarifForm(false);
                            }}
                          >
                            {editingTarifId
                              ? "Tarif speichern"
                              : "Tarif hinzufügen"}
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => {
                              setEditingTarifId(null);
                              setTarifName("");
                              setTarifPreisProStunde(60);
                              setTarifAbrechnung("proTraining");
                              setTarifBeschreibung("");
                              setShowTarifForm(false);
                            }}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </>
            )}

            {tab === "formulare" && !isTrainer && (
              <>
                <div className="card">
                  <h2>Formulare</h2>

                  {/* Sub-Tabs für Formulare */}
                    <div className="tabBar" style={{ marginBottom: 20 }}>
                      <button
                        className={`tabBtn ${formulareTab === "anmeldung" ? "tabBtnActive" : ""}`}
                        onClick={() => setFormulareTab("anmeldung")}
                      >
                        Anmeldung
                        {registrationRequests.filter(r => r.status !== "erledigt" && !r.archiviert).length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            background: "var(--danger)",
                            color: "white",
                            borderRadius: "50%",
                            width: 18,
                            height: 18,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11
                          }}>
                            {registrationRequests.filter(r => r.status !== "erledigt" && !r.archiviert).length}
                          </span>
                        )}
                      </button>
                      <button
                        className={`tabBtn ${formulareTab === "sepa" ? "tabBtnActive" : ""}`}
                        onClick={() => setFormulareTab("sepa")}
                      >
                        SEPA-Mandat
                        {sepaMandates.filter(m => (m.status || "neu") === "neu" && !m.archiviert).length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            background: "var(--primary)",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "2px 6px",
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {sepaMandates.filter(m => (m.status || "neu") === "neu" && !m.archiviert).length}
                          </span>
                        )}
                      </button>
                      <button
                        className={`tabBtn ${formulareTab === "tenniscamp" ? "tabBtnActive" : ""}`}
                        onClick={() => setFormulareTab("tenniscamp")}
                      >
                        Tenniscamp
                        {tenniscampAnmeldungen.filter(a => a.status === "neu").length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            background: "#22c55e",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "2px 6px",
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {tenniscampAnmeldungen.filter(a => a.status === "neu").length}
                          </span>
                        )}
                      </button>
                      <button
                        className={`tabBtn ${formulareTab === "probetraining" ? "tabBtnActive" : ""}`}
                        onClick={() => setFormulareTab("probetraining")}
                      >
                        Probetraining
                        {probetrainingAnfragen.filter(a => a.status === "offen" && !a.archiviert).length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            background: "#f59e0b",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "2px 6px",
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {probetrainingAnfragen.filter(a => a.status === "offen" && !a.archiviert).length}
                          </span>
                        )}
                      </button>
                      <button
                        className={`tabBtn ${formulareTab === "kennlerntennis" ? "tabBtnActive" : ""}`}
                        onClick={() => setFormulareTab("kennlerntennis")}
                      >
                        Kennenlerntennis
                        {kennlerntennisAnfragen.filter(a => a.status === "offen" && !a.archiviert).length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            background: "#3b82f6",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "2px 6px",
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {kennlerntennisAnfragen.filter(a => a.status === "offen" && !a.archiviert).length}
                          </span>
                        )}
                      </button>
                    </div>

                    <div className="row" style={{ margin: "0 0 12px", alignItems: "center", gap: 8 }}>
                      <button
                        className="btn micro btnGhost"
                        onClick={() => setFormZeigeArchiv((v) => !v)}
                      >
                        {formZeigeArchiv ? "← Aktive Anfragen" : "Archiv anzeigen"}
                      </button>
                      {formZeigeArchiv && (
                        <span className="pill" style={{ background: "var(--bg-inset)", color: "var(--text-muted)" }}>
                          Archiv-Ansicht — archivierte Anfragen
                        </span>
                      )}
                    </div>

                    {/* Anmeldung Tab */}
                    {formulareTab === "anmeldung" && (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <p className="muted" style={{ marginBottom: 8 }}>
                            <strong>Anmeldung Wedding:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/anmeldung-wedding
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/anmeldung-wedding`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                          <p className="muted" style={{ marginBottom: 8 }}>
                            <strong>Anmeldung Britz:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/anmeldung-britz
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/anmeldung-britz`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                        </div>

                        <div style={{ marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Suche</label>
                            <input
                              type="text"
                              placeholder="Name suchen..."
                              value={anmeldungNameSuche}
                              onChange={(e) => setAnmeldungNameSuche(e.target.value)}
                              style={{ padding: "4px 8px", width: 150 }}
                            />
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Anlage</label>
                            <select
                              value={anmeldungAnlageFilter}
                              onChange={(e) => setAnmeldungAnlageFilter(e.target.value as "alle" | "Wedding" | "Britz")}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle</option>
                              <option value="Wedding">Wedding</option>
                              <option value="Britz">Britz</option>
                            </select>
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Verfügbar am</label>
                            <select
                              value={anmeldungTagFilter}
                              onChange={(e) => setAnmeldungTagFilter(e.target.value as typeof anmeldungTagFilter)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle Tage</option>
                              <option value="montag">Montag</option>
                              <option value="dienstag">Dienstag</option>
                              <option value="mittwoch">Mittwoch</option>
                              <option value="donnerstag">Donnerstag</option>
                              <option value="freitag">Freitag</option>
                              <option value="samstag">Samstag</option>
                              <option value="sonntag">Sonntag</option>
                            </select>
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Status</label>
                            <select
                              value={anmeldungStatusFilter}
                              onChange={(e) => setAnmeldungStatusFilter(e.target.value as typeof anmeldungStatusFilter)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle</option>
                              <option value="offen">Offen</option>
                              <option value="erledigt">Erledigt</option>
                            </select>
                          </div>
                        </div>

                        {/* Auswahl-Toolbar */}
                        {registrationRequests.length > 0 && (
                          <div style={{
                            marginBottom: 16,
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            padding: "8px 12px",
                            background: "var(--bg-inset)",
                            borderRadius: 8
                          }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const filtered = registrationRequests.filter(r => {
                                    if (anmeldungAnlageFilter !== "alle" && r.anlage !== anmeldungAnlageFilter) return false;
                                    if (anmeldungNameSuche && !r.name.toLowerCase().includes(anmeldungNameSuche.toLowerCase())) return false;
                                    if (anmeldungTagFilter !== "alle" && r.verfuegbarkeit) {
                                      const tagWert = r.verfuegbarkeit[anmeldungTagFilter];
                                      if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                                    }
                                    if (anmeldungStatusFilter === "offen" && r.status === "erledigt") return false;
                                    if (anmeldungStatusFilter === "erledigt" && r.status !== "erledigt") return false;
                                    return true;
                                  });
                                  return filtered.length > 0 && filtered.every(r => selectedRequestIds.has(r.id));
                                })()}
                                onChange={(e) => {
                                  const filtered = registrationRequests.filter(r => {
                                    if (anmeldungAnlageFilter !== "alle" && r.anlage !== anmeldungAnlageFilter) return false;
                                    if (anmeldungNameSuche && !r.name.toLowerCase().includes(anmeldungNameSuche.toLowerCase())) return false;
                                    if (anmeldungTagFilter !== "alle" && r.verfuegbarkeit) {
                                      const tagWert = r.verfuegbarkeit[anmeldungTagFilter];
                                      if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                                    }
                                    if (anmeldungStatusFilter === "offen" && r.status === "erledigt") return false;
                                    if (anmeldungStatusFilter === "erledigt" && r.status !== "erledigt") return false;
                                    return true;
                                  });
                                  if (e.target.checked) {
                                    setSelectedRequestIds(new Set([...Array.from(selectedRequestIds), ...filtered.map(r => r.id)]));
                                  } else {
                                    const newSet = new Set(selectedRequestIds);
                                    filtered.forEach(r => newSet.delete(r.id));
                                    setSelectedRequestIds(newSet);
                                  }
                                }}
                              />
                              <span style={{ fontSize: 13 }}>Alle auswählen</span>
                            </label>
                            {selectedRequestIds.size > 0 && (
                              <>
                                <span className="muted" style={{ fontSize: 13 }}>
                                  {selectedRequestIds.size} ausgewählt
                                </span>
                                <ActionMenu label="Aktionen" buttonClassName="btn micro" menuWidth={240}>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={async () => {
                                    const selectedReqs = registrationRequests.filter(r => selectedRequestIds.has(r.id));
                                    if (selectedReqs.length === 0) return;

                                    const generateCardHTML = (req: typeof selectedReqs[0]) => {
                                      const trainingsartText = req.trainingsart === "einzel"
                                        ? "Einzeltraining"
                                        : req.trainingsart === "gruppe"
                                        ? "Gruppentraining"
                                        : req.trainingsart === "beides"
                                        ? "Beides"
                                        : "-";
                                      const erfahrungText = req.erfahrungslevel === "anfaenger"
                                        ? "Anfänger"
                                        : req.erfahrungslevel === "fortgeschritten"
                                        ? "Fortgeschritten"
                                        : req.erfahrungslevel === "profi"
                                        ? "Profi"
                                        : "-";
                                      const verfuegbarkeitRows = req.verfuegbarkeit ? [
                                        req.verfuegbarkeit.montag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Mo</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.montag)}</td></tr>` : "",
                                        req.verfuegbarkeit.dienstag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Di</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.dienstag)}</td></tr>` : "",
                                        req.verfuegbarkeit.mittwoch ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Mi</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.mittwoch)}</td></tr>` : "",
                                        req.verfuegbarkeit.donnerstag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Do</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.donnerstag)}</td></tr>` : "",
                                        req.verfuegbarkeit.freitag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Fr</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.freitag)}</td></tr>` : "",
                                        req.verfuegbarkeit.samstag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">Sa</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.samstag)}</td></tr>` : "",
                                        req.verfuegbarkeit.sonntag ? `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">So</td><td style="padding:1px 0;">${escapeHtml(req.verfuegbarkeit.sonntag)}</td></tr>` : "",
                                      ].filter(Boolean).join("") : "";

                                      const traineeName = getRegistrationTraineeName(req);
                                      const kontaktName = getRegistrationKontaktName(req);
                                      const adresse = getRegistrationKontaktAdresse(req);
                                      const abweichend = isRegistrationAbweichend(req);
                                      const kontaktBlock = abweichend
                                        ? `<div class="info-item"><label>Kontakt</label><span>${escapeHtml(kontaktName)}</span></div>`
                                        : "";
                                      const adresseBlock = adresse
                                        ? `<div class="info-item"><label>Adresse</label><span style="font-size:7pt;">${escapeHtml(adresse)}</span></div>`
                                        : "";

                                      return `
                                        <div class="card">
                                          <div class="header">
                                            <p class="name">${escapeHtml(traineeName)}</p>
                                            ${req.anlage ? `<span class="anlage" style="background:${req.anlage === "Britz" ? "#f59e0b" : "#2563eb"};">${escapeHtml(req.anlage)}</span>` : ""}
                                          </div>
                                          <div class="info-grid">
                                            <div class="info-item"><label>Telefon</label><span>${escapeHtml(req.telefon) || "-"}</span></div>
                                            <div class="info-item"><label>E-Mail</label><span style="font-size:7pt;word-break:break-all;">${escapeHtml(req.email)}</span></div>
                                            <div class="info-item"><label>Alter</label><span>${req.alter_jahre ? escapeHtml(req.alter_jahre) + " J." : "-"}</span></div>
                                            <div class="info-item"><label>Art</label><span>${trainingsartText}</span></div>
                                            <div class="info-item"><label>Level</label><span>${erfahrungText}</span></div>
                                            <div class="info-item"><label>Pro Woche</label><span>${req.trainings_pro_woche ? escapeHtml(req.trainings_pro_woche) + "x" : "-"}</span></div>
                                            ${kontaktBlock}
                                            ${adresseBlock}
                                          </div>
                                          ${verfuegbarkeitRows ? `<div class="verfuegbarkeit"><h4>Verfügbarkeit</h4><table>${verfuegbarkeitRows}</table></div>` : ""}
                                          ${req.nachricht ? `<div class="nachricht"><label>Nachricht</label><span>${escapeHtml(req.nachricht)}</span></div>` : ""}
                                          ${req.gruppenwuensche ? `<div class="nachricht"><label>Gruppenwünsche</label><span>${escapeHtml(req.gruppenwuensche)}</span></div>` : ""}
                                          <div class="footer">Anmeldung vom ${new Date(req.created_at).toLocaleDateString("de-DE")}</div>
                                        </div>
                                      `;
                                    };

                                    // Karten in 4er-Gruppen aufteilen
                                    const cardGroups: typeof selectedReqs[] = [];
                                    for (let i = 0; i < selectedReqs.length; i += 4) {
                                      cardGroups.push(selectedReqs.slice(i, i + 4));
                                    }

                                    const cardsHTML = `
                                      <div style="font-family: Arial, sans-serif;">
                                        <style>
                                          .print-page {
                                            width: 190mm;
                                            display: grid;
                                            grid-template-columns: 1fr 1fr;
                                            gap: 4mm;
                                            margin-bottom: 10mm;
                                          }
                                          .card {
                                            border: 1px solid #ccc;
                                            border-radius: 3px;
                                            padding: 3mm;
                                            box-sizing: border-box;
                                            font-size: 8pt;
                                            height: 134mm;
                                            overflow: hidden;
                                          }
                                          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #ddd; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
                                          .name { font-size: 10pt; font-weight: bold; margin: 0; }
                                          .anlage { color: white; padding: 1px 5px; border-radius: 2px; font-size: 7pt; }
                                          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5mm; margin-bottom: 1.5mm; }
                                          .info-item label { font-size: 6pt; color: #666; display: block; }
                                          .info-item span { font-size: 8pt; }
                                          .verfuegbarkeit { margin-top: 1mm; }
                                          .verfuegbarkeit h4 { font-size: 7pt; margin: 0 0 0.5mm 0; color: #666; }
                                          .verfuegbarkeit table { font-size: 7pt; border-collapse: collapse; }
                                          .nachricht { margin-top: 1.5mm; padding-top: 1.5mm; border-top: 1px dashed #ddd; }
                                          .nachricht label { font-size: 6pt; color: #666; display: block; margin-bottom: 0.5mm; }
                                          .nachricht span { font-size: 7pt; display: block; white-space: pre-wrap; }
                                          .footer { font-size: 6pt; color: #999; margin-top: 1mm; text-align: right; }
                                        </style>
                                        ${cardGroups.map(group => `
                                          <div class="print-page">
                                            ${group.map(generateCardHTML).join("")}
                                          </div>
                                        `).join("")}
                                      </div>
                                    `;

                                    const html2pdf = (await import('html2pdf.js')).default;
                                    const container = document.createElement('div');
                                    container.innerHTML = cardsHTML;
                                    document.body.appendChild(container);

                                    await html2pdf()
                                      .set({
                                        margin: 10,
                                        filename: `Anmeldungen_${new Date().toISOString().split('T')[0]}.pdf`,
                                        html2canvas: { scale: 2 },
                                        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
                                      })
                                      .from(container.firstElementChild as HTMLElement)
                                      .save();

                                    document.body.removeChild(container);
                                  }}
                                >
                                  Ausgewählte drucken
                                </button>
                                <button
                                  className="btn micro"
                                  style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                                  onClick={() => setShowAdoptConfirmDialog(true)}
                                >
                                  Als Spieler übernehmen
                                </button>
                                <button
                                  className="btn micro"
                                  style={{ backgroundColor: "#22c55e", borderColor: "#22c55e" }}
                                  onClick={async () => {
                                    const ids = Array.from(selectedRequestIds);
                                    for (const id of ids) {
                                      await updateRequestStatus(id, "erledigt");
                                    }
                                    setSelectedRequestIds(new Set());
                                  }}
                                >
                                  Als erledigt markieren
                                </button>
                                <button
                                  className="btn micro"
                                  style={{ backgroundColor: "#f59e0b", borderColor: "#f59e0b" }}
                                  onClick={async () => {
                                    const ids = Array.from(selectedRequestIds);
                                    for (const id of ids) {
                                      await updateRequestStatus(id, "offen");
                                    }
                                    setSelectedRequestIds(new Set());
                                  }}
                                >
                                  Als offen markieren
                                </button>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => setSelectedRequestIds(new Set())}
                                >
                                  Auswahl aufheben
                                </button>
                                </ActionMenu>
                              </>
                            )}
                          </div>
                        )}

                        {loadingRequests ? (
                          <p className="muted">Laden...</p>
                        ) : registrationRequests.filter(r => {
                          if (formZeigeArchiv ? !r.archiviert : !!r.archiviert) return false;
                          // Anlage Filter
                          if (anmeldungAnlageFilter !== "alle" && r.anlage !== anmeldungAnlageFilter) return false;
                          // Name Suche
                          if (anmeldungNameSuche && !r.name.toLowerCase().includes(anmeldungNameSuche.toLowerCase())) return false;
                          // Tag Filter - prüfe ob an diesem Tag eine gültige Zeit eingetragen ist (nicht leer und nicht "nicht verfügbar")
                          if (anmeldungTagFilter !== "alle" && r.verfuegbarkeit) {
                            const tagWert = r.verfuegbarkeit[anmeldungTagFilter];
                            if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                          }
                          // Status Filter
                          if (anmeldungStatusFilter === "offen" && r.status === "erledigt") return false;
                          if (anmeldungStatusFilter === "erledigt" && r.status !== "erledigt") return false;
                          return true;
                        }).length === 0 ? (
                          <p className="muted">Keine Anmeldungen für diesen Filter.</p>
                        ) : (
                          <ul className="list">
                            {registrationRequests.filter(r => {
                              if (formZeigeArchiv ? !r.archiviert : !!r.archiviert) return false;
                              if (anmeldungAnlageFilter !== "alle" && r.anlage !== anmeldungAnlageFilter) return false;
                              if (anmeldungNameSuche && !r.name.toLowerCase().includes(anmeldungNameSuche.toLowerCase())) return false;
                              if (anmeldungTagFilter !== "alle" && r.verfuegbarkeit) {
                                const tagWert = r.verfuegbarkeit[anmeldungTagFilter];
                                if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                              }
                              if (anmeldungStatusFilter === "offen" && r.status === "erledigt") return false;
                              if (anmeldungStatusFilter === "erledigt" && r.status !== "erledigt") return false;
                              return true;
                            }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((req) => (
                              <li key={req.id} className="listItem" style={{ flexDirection: "column", alignItems: "stretch", padding: expandedRequestId === req.id ? undefined : "8px 12px" }}>
                                <div
                                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                  onClick={() => setExpandedRequestId(expandedRequestId === req.id ? null : req.id)}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedRequestIds.has(req.id)}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const newSet = new Set(selectedRequestIds);
                                        if (e.target.checked) {
                                          newSet.add(req.id);
                                        } else {
                                          newSet.delete(req.id);
                                        }
                                        setSelectedRequestIds(newSet);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <span style={{ fontWeight: 500 }}>{getRegistrationTraineeName(req)}</span>
                                        {req.alter_jahre && (
                                          <span className="muted" style={{ fontSize: 13 }}>
                                            {req.alter_jahre}J
                                          </span>
                                        )}
                                        {req.anlage && (
                                          <span style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: req.anlage === "Britz" ? "var(--warning)" : "var(--primary)",
                                            color: req.anlage === "Britz" ? "#000" : "#fff",
                                            padding: "2px 6px",
                                            borderRadius: 4,
                                            minWidth: 16,
                                            textAlign: "center"
                                          }}>
                                            {req.anlage === "Britz" ? "B" : "W"}
                                          </span>
                                        )}
                                      </div>
                                      {isRegistrationAbweichend(req) && (
                                        <span className="muted" style={{ fontSize: 12 }}>
                                          Kontakt: {getRegistrationKontaktName(req)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.2s", transform: expandedRequestId === req.id ? "rotate(90deg)" : "rotate(0deg)" }}>
                                    ▶
                                  </span>
                                </div>

                                {expandedRequestId === req.id && (
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                                    {(() => {
                                      const kontaktName = getRegistrationKontaktName(req);
                                      const adresse = getRegistrationKontaktAdresse(req);
                                      const abweichend = isRegistrationAbweichend(req);
                                      if (!abweichend && !adresse) return null;
                                      return (
                                        <div style={{ marginBottom: 12, padding: 10, background: "var(--bg-subtle, #f7f7f8)", borderRadius: 6 }}>
                                          <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                                            {abweichend ? "Kontaktperson / Rechnungsempfänger" : "Kontaktperson (= Spieler/in)"}
                                          </div>
                                          {abweichend && kontaktName && (
                                            <div style={{ fontWeight: 500 }}>{kontaktName}</div>
                                          )}
                                          {adresse && (
                                            <div style={{ fontSize: 13 }}>{adresse}</div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="muted">{req.email}</span>
                                        <button
                                          type="button"
                                          title="E-Mail senden"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setNewsletterExtraEmails(prev =>
                                              prev.some(em => em.email === req.email)
                                                ? prev
                                                : [...prev, { email: req.email, name: getRegistrationKontaktName(req) }]
                                            );
                                            setNewsletterSubject("Anfrage zum Tennistraining");
                                            setNewsletterLabelFilter("keine");
                                            setTab("verwaltung");
                                            setVerwaltungTab("newsletter");
                                          }}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "2px 8px",
                                            height: 22,
                                            borderRadius: 4,
                                            background: "var(--primary)",
                                            color: "#fff",
                                            border: "none",
                                            cursor: "pointer",
                                            fontSize: 11,
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          Mail
                                        </button>
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="muted" style={{ fontSize: 12 }}>
                                          {new Date(req.created_at).toLocaleDateString("de-DE", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                          })}
                                        </span>
                                        <span
                                          className="pill"
                                          style={{
                                            background: req.status === "erledigt" ? "var(--success)" : "var(--danger)",
                                            color: "white",
                                            fontSize: 12,
                                            padding: "4px 10px"
                                          }}
                                        >
                                          {req.status === "erledigt" ? "Erledigt" : "Offen"}
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                                      {req.telefon && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Telefon</div>
                                          <div>{req.telefon}</div>
                                        </div>
                                      )}
                                      {req.alter_jahre && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Alter</div>
                                          <div>{req.alter_jahre} Jahre</div>
                                        </div>
                                      )}
                                      {req.trainingsart && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Trainingsart</div>
                                          <div>
                                            {req.trainingsart === "einzel"
                                              ? "Einzeltraining"
                                              : req.trainingsart === "gruppe"
                                              ? "Gruppentraining"
                                              : "Beides möglich"}
                                          </div>
                                        </div>
                                      )}
                                      {req.trainings_pro_woche && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Trainings pro Woche</div>
                                          <div>{req.trainings_pro_woche}x</div>
                                        </div>
                                      )}
                                      {req.erfahrungslevel && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Erfahrungslevel</div>
                                          <div>
                                            {req.erfahrungslevel === "anfaenger"
                                              ? "Anfänger"
                                              : req.erfahrungslevel === "fortgeschritten"
                                              ? "Fortgeschritten"
                                              : "Profi"}
                                          </div>
                                        </div>
                                      )}
                                      {req.ist_vereinsmitglied != null && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Vereinsmitglied</div>
                                          <div>{req.ist_vereinsmitglied ? "Ja" : "Nein"}</div>
                                        </div>
                                      )}
                                    </div>
                                    {req.verfuegbarkeit && (
                                      <div style={{ marginBottom: 12 }}>
                                        <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Verfügbarkeit</div>
                                        <table className="verfuegbarkeitTable" style={{ fontSize: 13 }}>
                                          <tbody>
                                            {req.verfuegbarkeit.montag && (
                                              <tr><td>Montag</td><td>{req.verfuegbarkeit.montag}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.dienstag && (
                                              <tr><td>Dienstag</td><td>{req.verfuegbarkeit.dienstag}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.mittwoch && (
                                              <tr><td>Mittwoch</td><td>{req.verfuegbarkeit.mittwoch}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.donnerstag && (
                                              <tr><td>Donnerstag</td><td>{req.verfuegbarkeit.donnerstag}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.freitag && (
                                              <tr><td>Freitag</td><td>{req.verfuegbarkeit.freitag}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.samstag && (
                                              <tr><td>Samstag</td><td>{req.verfuegbarkeit.samstag}</td></tr>
                                            )}
                                            {req.verfuegbarkeit.sonntag && (
                                              <tr><td>Sonntag</td><td>{req.verfuegbarkeit.sonntag}</td></tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {req.nachricht && (
                                      <div style={{ marginBottom: 12 }}>
                                        <div className="muted" style={{ fontSize: 11 }}>Nachricht</div>
                                        <div style={{ whiteSpace: "pre-wrap" }}>{req.nachricht}</div>
                                      </div>
                                    )}
                                    {req.gruppenwuensche && (
                                      <div style={{ marginBottom: 12 }}>
                                        <div className="muted" style={{ fontSize: 11 }}>Gruppenwünsche</div>
                                        <div style={{ whiteSpace: "pre-wrap" }}>{req.gruppenwuensche}</div>
                                      </div>
                                    )}
                                    <div className="smallActions">
                                      <button
                                        className="btn micro"
                                        style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                                        onClick={() => adoptPlayerFromRequest(req)}
                                        title="Gibt es bereits einen Spieler mit diesem Namen, werden die Anmeldedaten mit dem vorhandenen Profil abgeglichen und dieses vervollständigt. Sonst wird ein neuer Spieler angelegt."
                                      >
                                        Als Spieler übernehmen
                                      </button>
                                      <select
                                        value={req.status === "erledigt" ? "erledigt" : "offen"}
                                        onChange={(e) => updateRequestStatus(req.id, e.target.value)}
                                        style={{ padding: "6px 10px", borderRadius: 8 }}
                                      >
                                        <option value="offen">Offen</option>
                                        <option value="erledigt">Erledigt</option>
                                      </select>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={async () => {
                                          const trainingsartText = req.trainingsart === "einzel"
                                            ? "Einzeltraining"
                                            : req.trainingsart === "gruppe"
                                            ? "Gruppentraining"
                                            : req.trainingsart === "beides"
                                            ? "Beides"
                                            : "-";
                                          const erfahrungText = req.erfahrungslevel === "anfaenger"
                                            ? "Anfänger"
                                            : req.erfahrungslevel === "fortgeschritten"
                                            ? "Fortgeschritten"
                                            : req.erfahrungslevel === "profi"
                                            ? "Profi"
                                            : "-";

                                          const verfuegbarkeitRows = req.verfuegbarkeit ? [
                                            req.verfuegbarkeit.montag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Mo</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.montag)}</td></tr>` : "",
                                            req.verfuegbarkeit.dienstag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Di</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.dienstag)}</td></tr>` : "",
                                            req.verfuegbarkeit.mittwoch ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Mi</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.mittwoch)}</td></tr>` : "",
                                            req.verfuegbarkeit.donnerstag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Do</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.donnerstag)}</td></tr>` : "",
                                            req.verfuegbarkeit.freitag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Fr</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.freitag)}</td></tr>` : "",
                                            req.verfuegbarkeit.samstag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">Sa</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.samstag)}</td></tr>` : "",
                                            req.verfuegbarkeit.sonntag ? `<tr><td style="padding:2px 8px 2px 0;font-weight:500;">So</td><td style="padding:2px 0;">${escapeHtml(req.verfuegbarkeit.sonntag)}</td></tr>` : "",
                                          ].filter(Boolean).join("") : "";

                                          const cardHTML = `
                                            <!DOCTYPE html>
                                            <html>
                                            <head>
                                              <style>
                                                @page { size: 95mm 140mm; margin: 0; }
                                                body {
                                                  font-family: Arial, sans-serif;
                                                  margin: 0;
                                                  padding: 3mm;
                                                  box-sizing: border-box;
                                                }
                                                .card {
                                                  width: 89mm;
                                                  height: 134mm;
                                                  border: 1px solid #ccc;
                                                  border-radius: 3px;
                                                  padding: 3mm;
                                                  box-sizing: border-box;
                                                  font-size: 8pt;
                                                  overflow: hidden;
                                                }
                                                .header {
                                                  display: flex;
                                                  justify-content: space-between;
                                                  align-items: flex-start;
                                                  border-bottom: 1px solid #ddd;
                                                  padding-bottom: 2mm;
                                                  margin-bottom: 2mm;
                                                }
                                                .name { font-size: 10pt; font-weight: bold; margin: 0; }
                                                .anlage {
                                                  background: ${req.anlage === "Britz" ? "#f59e0b" : "#2563eb"};
                                                  color: white;
                                                  padding: 2px 6px;
                                                  border-radius: 3px;
                                                  font-size: 8pt;
                                                }
                                                .info-grid {
                                                  display: grid;
                                                  grid-template-columns: 1fr 1fr;
                                                  gap: 2mm;
                                                  margin-bottom: 2mm;
                                                }
                                                .info-item label { font-size: 7pt; color: #666; display: block; }
                                                .info-item span { font-size: 9pt; }
                                                .verfuegbarkeit { margin-top: 2mm; }
                                                .verfuegbarkeit h4 { font-size: 8pt; margin: 0 0 1mm 0; color: #666; }
                                                .verfuegbarkeit table { font-size: 8pt; border-collapse: collapse; }
                                                .nachricht {
                                                  margin-top: 2mm;
                                                  font-size: 8pt;
                                                  color: #333;
                                                  border-top: 1px dashed #ddd;
                                                  padding-top: 2mm;
                                                }
                                                .footer { font-size: 7pt; color: #999; margin-top: 2mm; text-align: right; }
                                              </style>
                                            </head>
                                            <body>
                                              <div class="card">
                                                <div class="header">
                                                  <p class="name">${escapeHtml(getRegistrationTraineeName(req))}</p>
                                                  ${req.anlage ? `<span class="anlage">${escapeHtml(req.anlage)}</span>` : ""}
                                                </div>
                                                <div class="info-grid">
                                                  <div class="info-item">
                                                    <label>Telefon</label>
                                                    <span>${escapeHtml(req.telefon) || "-"}</span>
                                                  </div>
                                                  <div class="info-item">
                                                    <label>E-Mail</label>
                                                    <span>${escapeHtml(req.email)}</span>
                                                  </div>
                                                  <div class="info-item">
                                                    <label>Alter</label>
                                                    <span>${req.alter_jahre ? escapeHtml(req.alter_jahre) + " Jahre" : "-"}</span>
                                                  </div>
                                                  <div class="info-item">
                                                    <label>Trainingsart</label>
                                                    <span>${trainingsartText}</span>
                                                  </div>
                                                  <div class="info-item">
                                                    <label>Level</label>
                                                    <span>${erfahrungText}</span>
                                                  </div>
                                                  <div class="info-item">
                                                    <label>Pro Woche</label>
                                                    <span>${req.trainings_pro_woche ? escapeHtml(req.trainings_pro_woche) + "x" : "-"}</span>
                                                  </div>
                                                  ${isRegistrationAbweichend(req) ? `<div class="info-item"><label>Kontakt</label><span>${escapeHtml(getRegistrationKontaktName(req))}</span></div>` : ""}
                                                  ${getRegistrationKontaktAdresse(req) ? `<div class="info-item"><label>Adresse</label><span>${escapeHtml(getRegistrationKontaktAdresse(req))}</span></div>` : ""}
                                                </div>
                                                ${verfuegbarkeitRows ? `
                                                  <div class="verfuegbarkeit">
                                                    <h4>Verfügbarkeit</h4>
                                                    <table>${verfuegbarkeitRows}</table>
                                                  </div>
                                                ` : ""}
                                                ${req.nachricht ? `<div class="nachricht"><strong>Nachricht:</strong> ${escapeHtml(req.nachricht)}</div>` : ""}
                                                ${req.gruppenwuensche ? `<div class="nachricht"><strong>Gruppenwünsche:</strong> ${escapeHtml(req.gruppenwuensche)}</div>` : ""}
                                                <div class="footer">Anmeldung vom ${new Date(req.created_at).toLocaleDateString("de-DE")}</div>
                                              </div>
                                            </body>
                                            </html>
                                          `;

                                          const html2pdf = (await import('html2pdf.js')).default;
                                          const container = document.createElement('div');
                                          container.innerHTML = cardHTML;
                                          document.body.appendChild(container);
                                          const cardEl = container.querySelector('.card') as HTMLElement;

                                          await html2pdf()
                                            .set({
                                              margin: 0,
                                              filename: `Anmeldung_${getRegistrationTraineeName(req).replace(/\s+/g, "_") || "Anmeldung"}.pdf`,
                                              html2canvas: { scale: 2, useCORS: true },
                                              jsPDF: { unit: 'mm', format: [95, 140], orientation: 'portrait' }
                                            })
                                            .from(cardEl)
                                            .save();

                                          document.body.removeChild(container);
                                        }}
                                      >
                                        Drucken
                                      </button>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={() => setAnfrageArchiviert("registration_requests", req.id, !req.archiviert, fetchRegistrationRequests)}
                                      >
                                        {req.archiviert ? "Reaktivieren" : "Archivieren"}
                                      </button>
                                      <button
                                        className="btn micro btnWarn"
                                        onClick={() => {
                                          if (window.confirm("Anmeldung wirklich löschen?")) {
                                            deleteRegistrationRequest(req.id);
                                          }
                                        }}
                                      >
                                        Löschen
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}

                    {/* SEPA-Mandat Tab */}
                    {formulareTab === "sepa" && (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <p className="muted" style={{ marginBottom: 8 }}>
                            <strong>SEPA-Mandat Wedding:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/sepa
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/sepa`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                          <p className="muted">
                            <strong>SEPA-Mandat Britz:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/sepa-britz
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/sepa-britz`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                        </div>

                        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                          <input
                            type="text"
                            placeholder="Suche nach Name, E-Mail, IBAN..."
                            value={sepaMandateSearch}
                            onChange={(e) => setSepaMandateSearch(e.target.value)}
                            style={{ flex: 1, padding: "8px 12px", fontSize: 14 }}
                          />
                          <button
                            className="btn micro btnGhost"
                            onClick={() => setSepaMandateSortDesc(prev => !prev)}
                            title="Nach Datum sortieren"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {sepaMandateSortDesc ? "↓ Neueste zuerst" : "↑ Älteste zuerst"}
                          </button>
                        </div>

                        {loadingSepaMandates ? (
                          <p className="muted">Lade SEPA-Mandate...</p>
                        ) : sepaMandates.length === 0 ? (
                          <div style={{
                            textAlign: "center",
                            padding: "40px 20px",
                            color: "var(--text-muted)"
                          }}>
                            <div>Noch keine SEPA-Mandate eingegangen.</div>
                          </div>
                        ) : (
                          <ul className="simpleList">
                            {sepaMandates.filter((m) => {
                              if (formZeigeArchiv ? !m.archiviert : !!m.archiviert) return false;
                              if (!sepaMandateSearch.trim()) return true;
                              const q = sepaMandateSearch.toLowerCase();
                              return (
                                `${m.vorname} ${m.nachname}`.toLowerCase().includes(q) ||
                                m.email.toLowerCase().includes(q) ||
                                (m.telefon || "").toLowerCase().includes(q) ||
                                m.iban.toLowerCase().includes(q) ||
                                (m.elternteil_name || "").toLowerCase().includes(q) ||
                                m.mandatsreferenz.toLowerCase().includes(q)
                              );
                            }).sort((a, b) => {
                              const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                              return sepaMandateSortDesc ? -diff : diff;
                            }).map((mandate) => (
                              <li key={mandate.id} className="listItem" style={{ flexDirection: "column", alignItems: "stretch" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div
                                    style={{ flex: 1, cursor: "pointer" }}
                                    onClick={() => setExpandedSepaMandateId(
                                      expandedSepaMandateId === mandate.id ? null : mandate.id
                                    )}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <strong>{mandate.vorname} {mandate.nachname}</strong>
                                      {mandate.ist_kind && (
                                        <span style={{
                                          fontSize: 11,
                                          background: "var(--warning)",
                                          color: "#000",
                                          padding: "2px 6px",
                                          borderRadius: 4
                                        }}>
                                          Kind
                                        </span>
                                      )}
                                      {mandate.anlage && (
                                        <span style={{
                                          fontSize: 11,
                                          background: mandate.anlage === "Britz" ? "#8b5cf6" : "#3b82f6",
                                          color: "#fff",
                                          padding: "2px 6px",
                                          borderRadius: 4
                                        }}>
                                          {mandate.anlage}
                                        </span>
                                      )}
                                      <span style={{
                                        fontSize: 11,
                                        background: (mandate.status || "neu") === "neu" ? "var(--primary)" :
                                                   (mandate.status || "neu") === "zugeordnet" ? "var(--success)" : "var(--text-muted)",
                                        color: "#fff",
                                        padding: "2px 6px",
                                        borderRadius: 4
                                      }}>
                                        {(mandate.status || "neu") === "neu" ? "Neu" :
                                         (mandate.status || "neu") === "zugeordnet" ? "Zugeordnet" : mandate.status}
                                      </span>
                                    </div>
                                    <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                                      {mandate.email} • Mandat vom {new Date(mandate.unterschriftsdatum).toLocaleDateString("de-DE")}
                                    </div>
                                  </div>
                                </div>

                                {expandedSepaMandateId === mandate.id && (
                                  <div style={{
                                    marginTop: 12,
                                    padding: 12,
                                    background: "var(--bg-inset)",
                                    borderRadius: 8,
                                    fontSize: 13
                                  }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                                      <div><strong>Spieler:</strong> {mandate.vorname} {mandate.nachname}</div>
                                      {mandate.ist_kind && mandate.elternteil_name && (
                                        <div><strong>Rechnungsempfänger (Eltern):</strong> {mandate.elternteil_name}</div>
                                      )}
                                      <div><strong>E-Mail:</strong> {mandate.email}</div>
                                      {mandate.telefon && (
                                        <div><strong>Telefon:</strong> <a href={`tel:${mandate.telefon}`}>{mandate.telefon}</a></div>
                                      )}
                                      <div><strong>IBAN:</strong> <span style={{ fontFamily: "monospace" }}>{mandate.iban.replace(/(.{4})/g, "$1 ").trim()}</span></div>
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <strong>Adresse:</strong> {mandate.strasse}, {mandate.plz} {mandate.ort}
                                      </div>
                                      <div><strong>Mandatsreferenz:</strong> {mandate.mandatsreferenz}</div>
                                      <div><strong>Unterschrieben:</strong> {new Date(mandate.unterschriftsdatum).toLocaleDateString("de-DE")}</div>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                      <button
                                        className="btn micro"
                                        title="Bank- und Mandatsdaten unter einem vorhandenen Spieler speichern (wie der Kontaktbuch-Import) – oder neuen Spieler anlegen"
                                        onClick={() => adoptPlayerFromSepaMandate(mandate)}
                                      >
                                        In Spieler übernehmen
                                      </button>
                                      <select
                                        value={mandate.status || "neu"}
                                        onChange={(e) => updateSepaMandateStatus(mandate.id, e.target.value)}
                                        style={{ fontSize: 13, padding: "4px 8px" }}
                                      >
                                        <option value="neu">Neu</option>
                                        <option value="zugeordnet">Zugeordnet</option>
                                        <option value="erledigt">Erledigt</option>
                                      </select>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={() => setAnfrageArchiviert("sepa_mandates", mandate.id, !mandate.archiviert, fetchSepaMandates)}
                                      >
                                        {mandate.archiviert ? "Reaktivieren" : "Archivieren"}
                                      </button>
                                      <button
                                        className="btn micro btnWarn"
                                        onClick={() => deleteSepaMandate(mandate.id)}
                                      >
                                        Löschen
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}

                    {/* Tenniscamp Tab */}
                    {formulareTab === "tenniscamp" && (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <p className="muted" style={{ marginBottom: 12 }}>
                            <strong>Tenniscamp-Anmeldung:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/tenniscamp
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/tenniscamp`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                          <p className="muted" style={{ marginBottom: 12 }}>
                            <strong>Tenniscamp-Info:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/tenniscamp-info
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/tenniscamp-info`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                        </div>

                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Suche</label>
                            <input
                              type="text"
                              placeholder="Name suchen..."
                              value={tenniscampNameSuche}
                              onChange={(e) => setTenniscampNameSuche(e.target.value)}
                              style={{ padding: "4px 8px", width: 150 }}
                            />
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Camp</label>
                            <select
                              value={tenniscampCampFilter}
                              onChange={(e) => setTenniscampCampFilter(e.target.value)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle Camps</option>
                              {tenniscampCampOptions.map(([id, label]) => (
                                <option key={id} value={id}>{label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Typ</label>
                            <select
                              value={tenniscampTypFilter}
                              onChange={(e) => setTenniscampTypFilter(e.target.value as typeof tenniscampTypFilter)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle</option>
                              <option value="kind">Kind</option>
                              <option value="erwachsene">Erwachsen</option>
                            </select>
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Status</label>
                            <select
                              value={tenniscampStatusFilter}
                              onChange={(e) => setTenniscampStatusFilter(e.target.value as typeof tenniscampStatusFilter)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle</option>
                              <option value="offen">Offen</option>
                              <option value="storniert">Storniert</option>
                            </select>
                          </div>
                          {(tenniscampNameSuche || tenniscampCampFilter !== "alle" || tenniscampTypFilter !== "alle" || tenniscampStatusFilter !== "offen") && (
                            <button
                              className="btn micro btnGhost"
                              onClick={() => {
                                setTenniscampNameSuche("");
                                setTenniscampCampFilter("alle");
                                setTenniscampTypFilter("alle");
                                setTenniscampStatusFilter("offen");
                              }}
                            >
                              Filter zurücksetzen
                            </button>
                          )}
                        </div>

                        {loadingTenniscampAnmeldungen ? (
                          <p className="muted">Laden...</p>
                        ) : filteredTenniscampAnmeldungen.length === 0 ? (
                          <p className="muted">Keine Tenniscamp-Anmeldungen für diesen Filter.</p>
                        ) : (
                          <>
                          {/* Aktionsleiste: Mehrfachauswahl -> Newsletter */}
                          {(() => {
                            const auswaehlbar = filteredTenniscampAnmeldungen.filter(a => a.email);
                            const allSelected = auswaehlbar.length > 0 && auswaehlbar.every(a => selectedTenniscampIds.has(a.id));
                            return (
                              <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", background: "var(--bg-inset)", borderRadius: 8 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={() => {
                                      if (allSelected) {
                                        setSelectedTenniscampIds(new Set());
                                      } else {
                                        setSelectedTenniscampIds(new Set(auswaehlbar.map(a => a.id)));
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: 13 }}>Alle auswählen</span>
                                </label>
                                {selectedTenniscampIds.size > 0 && (
                                  <>
                                    <span className="muted" style={{ fontSize: 13 }}>
                                      {selectedTenniscampIds.size} ausgewählt
                                    </span>
                                    <button
                                      className="btn micro"
                                      style={{ background: "var(--primary)", color: "#fff" }}
                                      onClick={() => {
                                        const selected = tenniscampAnmeldungen.filter(a => selectedTenniscampIds.has(a.id) && a.email);
                                        const newExtras = selected
                                          .filter(a => !newsletterExtraEmails.some(em => em.email === a.email))
                                          .map(a => ({ email: a.email, name: `${a.teilnehmer_vorname} ${a.teilnehmer_nachname}` }));
                                        setNewsletterExtraEmails(prev => [...prev, ...newExtras]);
                                        setNewsletterLabelFilter("keine");
                                        setSelectedTenniscampIds(new Set());
                                        setTab("verwaltung");
                                        setVerwaltungTab("newsletter");
                                      }}
                                    >
                                      Newsletter senden
                                    </button>
                                    <button
                                      className="btn micro btnGhost"
                                      onClick={() => setSelectedTenniscampIds(new Set())}
                                    >
                                      Auswahl aufheben
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          <ul className="list">
                            {filteredTenniscampAnmeldungen.map((anmeldung) => (
                              <li key={anmeldung.id} className="listItem" style={{ flexDirection: "column", alignItems: "stretch" }}>
                                <div
                                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                  onClick={() => setExpandedTenniscampId(expandedTenniscampId === anmeldung.id ? null : anmeldung.id)}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedTenniscampIds.has(anmeldung.id)}
                                      disabled={!anmeldung.email}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => {
                                        setSelectedTenniscampIds(prev => {
                                          const next = new Set(prev);
                                          if (next.has(anmeldung.id)) next.delete(anmeldung.id);
                                          else next.add(anmeldung.id);
                                          return next;
                                        });
                                      }}
                                      style={{ cursor: anmeldung.email ? "pointer" : "not-allowed" }}
                                    />
                                    <span style={{ fontWeight: 500 }}>
                                      {anmeldung.teilnehmer_vorname} {anmeldung.teilnehmer_nachname}
                                    </span>
                                    <span className="muted" style={{ fontSize: 13 }}>
                                      {anmeldung.alter}J
                                    </span>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: anmeldung.status === "neu" ? "var(--danger)" : anmeldung.status === "bestaetigt" ? "var(--success)" : "var(--text-muted)",
                                      color: "#fff",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                    }}>
                                      {anmeldung.status === "neu" ? "Neu" : anmeldung.status === "bestaetigt" ? "Bestätigt" : anmeldung.status}
                                    </span>
                                    <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.2s", transform: expandedTenniscampId === anmeldung.id ? "rotate(90deg)" : "rotate(0deg)" }}>
                                      ▶
                                    </span>
                                  </div>
                                </div>

                                {expandedTenniscampId === anmeldung.id && (
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Camp</div>
                                        <div style={{ fontWeight: 500 }}>{anmeldung.camp_label}</div>
                                      </div>
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Zeitraum</div>
                                        <div>{anmeldung.camp_dates}</div>
                                      </div>
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>E-Mail</div>
                                        <div>{anmeldung.email}</div>
                                      </div>
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Telefon</div>
                                        <div>{anmeldung.telefon}</div>
                                      </div>
                                      {anmeldung.zahlungspflichtiger_vorname && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                          <div className="muted" style={{ fontSize: 11 }}>Zahlungspflichtiger</div>
                                          <div>{anmeldung.zahlungspflichtiger_vorname} {anmeldung.zahlungspflichtiger_nachname}</div>
                                        </div>
                                      )}
                                      {(anmeldung.strasse || anmeldung.plz || anmeldung.ort) && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                          <div className="muted" style={{ fontSize: 11 }}>Anschrift</div>
                                          <div>{[anmeldung.strasse, `${anmeldung.plz || ""} ${anmeldung.ort || ""}`.trim()].filter(Boolean).join(", ")}</div>
                                        </div>
                                      )}
                                      {anmeldung.niveau && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Niveau</div>
                                          <div style={{ fontWeight: 500 }}>{anmeldung.niveau}</div>
                                        </div>
                                      )}
                                      {anmeldung.spielstand_beschreibung && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                          <div className="muted" style={{ fontSize: 11 }}>Spielstand-Beschreibung</div>
                                          <div style={{ background: "var(--bg-inset)", padding: 8, borderRadius: 4, fontSize: 13 }}>{anmeldung.spielstand_beschreibung}</div>
                                        </div>
                                      )}
                                      {anmeldung.mitglied != null && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Mitglied BSC Rehberge</div>
                                          <div style={{ fontWeight: 600, color: anmeldung.mitglied ? "var(--success)" : "var(--danger)" }}>
                                            {anmeldung.mitglied ? "Ja" : "Nein"}
                                          </div>
                                        </div>
                                      )}
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <div className="muted" style={{ fontSize: 11 }}>IBAN</div>
                                        <div style={{ fontFamily: "monospace" }}>{anmeldung.iban.replace(/(.{4})/g, "$1 ").trim()}</div>
                                      </div>
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <div className="muted" style={{ fontSize: 11 }}>Mandatsreferenz</div>
                                        <div style={{ fontFamily: "monospace", fontWeight: 600, color: "var(--primary)" }}>{anmeldung.mandatsreferenz}</div>
                                      </div>
                                      {anmeldung.bemerkungen && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                          <div className="muted" style={{ fontSize: 11 }}>Bemerkungen</div>
                                          <div style={{ background: "var(--bg-inset)", padding: 8, borderRadius: 4, fontSize: 13 }}>{anmeldung.bemerkungen}</div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
                                      Angemeldet am: {new Date(anmeldung.created_at).toLocaleDateString("de-DE", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit"
                                      })}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                      <select
                                        value={anmeldung.status}
                                        onChange={(e) => updateTenniscampStatus(anmeldung.id, e.target.value)}
                                        style={{ fontSize: 13, padding: "4px 8px" }}
                                      >
                                        <option value="neu">Neu</option>
                                        <option value="bestaetigt">Bestätigt</option>
                                        <option value="storniert">Storniert</option>
                                      </select>
                                      <button
                                        className="btn micro"
                                        style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                                        onClick={() => adoptPlayerFromTenniscamp(anmeldung)}
                                        title="Spieler inkl. SEPA-Mandat aus dieser Anmeldung anlegen"
                                      >
                                        Als Spieler übernehmen
                                      </button>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={() => {
                                          setNewsletterExtraEmails(prev =>
                                            prev.some(em => em.email === anmeldung.email)
                                              ? prev
                                              : [...prev, { email: anmeldung.email, name: `${anmeldung.teilnehmer_vorname} ${anmeldung.teilnehmer_nachname}` }]
                                          );
                                          setNewsletterSubject(`Tenniscamp ${anmeldung.camp_label}`);
                                          setNewsletterLabelFilter("keine");
                                          setTab("verwaltung");
                                          setVerwaltungTab("newsletter");
                                        }}
                                      >
                                        E-Mail senden
                                      </button>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={() => setAnfrageArchiviert("tenniscamp_anmeldungen", anmeldung.id, !anmeldung.archiviert, fetchTenniscampAnmeldungen)}
                                      >
                                        {anmeldung.archiviert ? "Reaktivieren" : "Archivieren"}
                                      </button>
                                      <button
                                        className="btn micro btnWarn"
                                        onClick={() => deleteTenniscampAnmeldung(anmeldung.id)}
                                      >
                                        Löschen
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                          </>
                        )}
                      </>
                    )}

                    {/* Probetraining Tab */}
                    {formulareTab === "probetraining" && (
                      <>

                        {loadingProbetrainingAnfragen ? (
                          <p className="muted">Laden...</p>
                        ) : probetrainingAnfragen.length === 0 ? (
                          <p className="muted">Keine Probetraining-Anfragen vorhanden.</p>
                        ) : (
                          <>
                          {/* Filter */}
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
                            <div className="field" style={{ margin: 0 }}>
                              <label>Suche</label>
                              <input
                                type="text"
                                placeholder="Name suchen..."
                                value={probetrainingNameSuche}
                                onChange={(e) => setProbetrainingNameSuche(e.target.value)}
                                style={{ padding: "4px 8px", width: 150 }}
                              />
                            </div>
                            <div className="field" style={{ margin: 0 }}>
                              <label>Anlage</label>
                              <select
                                value={probetrainingAnlageFilter}
                                onChange={(e) => setProbetrainingAnlageFilter(e.target.value as "alle" | "Wedding" | "Britz")}
                                style={{ padding: "4px 8px" }}
                              >
                                <option value="alle">Alle</option>
                                <option value="Wedding">Wedding</option>
                                <option value="Britz">Britz</option>
                              </select>
                            </div>
                            <div className="field" style={{ margin: 0 }}>
                              <label>Verfügbar am</label>
                              <select
                                value={probetrainingTagFilter}
                                onChange={(e) => setProbetrainingTagFilter(e.target.value as typeof probetrainingTagFilter)}
                                style={{ padding: "4px 8px" }}
                              >
                                <option value="alle">Alle Tage</option>
                                <option value="montag">Montag</option>
                                <option value="dienstag">Dienstag</option>
                                <option value="mittwoch">Mittwoch</option>
                                <option value="donnerstag">Donnerstag</option>
                                <option value="freitag">Freitag</option>
                                <option value="samstag">Samstag</option>
                                <option value="sonntag">Sonntag</option>
                              </select>
                            </div>
                            <div className="field" style={{ margin: 0 }}>
                              <label>Status</label>
                              <select
                                value={probetrainingStatusFilter}
                                onChange={(e) => setProbetrainingStatusFilter(e.target.value)}
                                style={{ padding: "4px 8px" }}
                              >
                                <option value="alle">Alle</option>
                                <option value="offen">Offen</option>
                                <option value="geantwortet">Geantwortet / Warte auf E-Mail</option>
                                <option value="probetraining_ausstehend">Probetraining ausstehend</option>
                                <option value="erledigt">Erledigt</option>
                              </select>
                            </div>
                          </div>

                          {/* Aktionsleiste */}
                          {(() => {
                            const filtered = probetrainingAnfragen.filter(a => {
                              if (probetrainingAnlageFilter !== "alle" && a.anlage !== probetrainingAnlageFilter) return false;
                              if (probetrainingNameSuche && !`${a.vorname} ${a.nachname}`.toLowerCase().includes(probetrainingNameSuche.toLowerCase())) return false;
                              if (probetrainingTagFilter !== "alle" && a.verfuegbarkeit) {
                                const tagWert = (a.verfuegbarkeit as Record<string, string>)[probetrainingTagFilter];
                                if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                              }
                              if (probetrainingStatusFilter !== "alle" && a.status !== probetrainingStatusFilter) return false;
                              return true;
                            });
                            const allSelected = filtered.length > 0 && filtered.every(a => selectedProbetrainingIds.has(a.id));
                            return (
                              <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", background: "var(--bg-inset)", borderRadius: 8 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={() => {
                                      if (allSelected) {
                                        setSelectedProbetrainingIds(new Set());
                                      } else {
                                        setSelectedProbetrainingIds(new Set(filtered.map(a => a.id)));
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: 13 }}>Alle auswählen</span>
                                </label>
                                {selectedProbetrainingIds.size > 0 && (
                                  <>
                                  <span className="muted" style={{ fontSize: 13 }}>
                                    {selectedProbetrainingIds.size} ausgewählt
                                  </span>
                                  <ActionMenu label="Aktionen" buttonClassName="btn micro" menuWidth={240}>
                                  <button
                                    className="btn micro"
                                    style={{ background: "var(--primary)", color: "#fff" }}
                                    onClick={() => {
                                      const selected = probetrainingAnfragen.filter(a => selectedProbetrainingIds.has(a.id) && a.email);
                                      const newExtras = selected
                                        .filter(a => !newsletterExtraEmails.some(em => em.email === a.email))
                                        .map(a => ({ email: a.email!, name: `${a.vorname} ${a.nachname}` }));
                                      setNewsletterExtraEmails(prev => [...prev, ...newExtras]);
                                      setNewsletterSubject("Anfrage zum Probetraining");
                                      setNewsletterLabelFilter("keine");
                                      setSelectedProbetrainingIds(new Set());
                                      setTab("verwaltung");
                                      setVerwaltungTab("newsletter");
                                    }}
                                  >
                                    Newsletter senden
                                  </button>
                                  <button
                                    className="btn micro btnGhost"
                                    onClick={async () => {
                                      const selectedAnfragen = probetrainingAnfragen.filter(a => selectedProbetrainingIds.has(a.id));
                                      if (selectedAnfragen.length === 0) return;

                                      const generateProbeCardHTML = (a: ProbetrainingAnfrage) => {
                                        const spielstandText = a.spielstand === "anfaenger" ? "Anfänger" : a.spielstand === "fortgeschritten" ? "Fortgeschritten" : "Wettkampf";
                                        const trainingsartText = a.trainingsart === "einzel" ? "Einzeltraining" : a.trainingsart === "gruppe" ? "Gruppentraining" : a.trainingsart === "beides" ? "Beides" : "-";
                                        const verfuegbarkeitRows = a.verfuegbarkeit ? ["montag","dienstag","mittwoch","donnerstag","freitag","samstag","sonntag"]
                                          .filter(tag => {
                                            const zeit = (a.verfuegbarkeit as Record<string, string>)?.[tag];
                                            return zeit && zeit !== "nicht verfügbar";
                                          })
                                          .map(tag => `<tr><td style="padding:1px 6px 1px 0;font-weight:500;">${tag.slice(0,2).toUpperCase()}</td><td style="padding:1px 0;">${escapeHtml((a.verfuegbarkeit as Record<string, string>)[tag])}</td></tr>`)
                                          .join("") : "";

                                        return `
                                          <div class="card">
                                            <div class="header">
                                              <p class="name">${escapeHtml(a.vorname)} ${escapeHtml(a.nachname)}</p>
                                              ${a.anlage ? `<span class="anlage" style="background:${a.anlage === "Britz" ? "#f59e0b" : "#2563eb"};">${escapeHtml(a.anlage)}</span>` : ""}
                                            </div>
                                            <div class="info-grid">
                                              <div class="info-item"><label>Telefon</label><span>${a.telefon ? escapeHtml(a.telefon) : "-"}</span></div>
                                              <div class="info-item"><label>E-Mail</label><span style="font-size:7pt;word-break:break-all;">${a.email ? escapeHtml(a.email) : "-"}</span></div>
                                              <div class="info-item"><label>Alter</label><span>${a.alter} J.</span></div>
                                              <div class="info-item"><label>Spielstand</label><span>${spielstandText}</span></div>
                                              <div class="info-item"><label>Art</label><span>${trainingsartText}</span></div>
                                              <div class="info-item"><label>Mitglied</label><span>${a.ist_vereinsmitglied ? "Ja" : "Nein"}</span></div>
                                            </div>
                                            ${a.spielstaerke_beschreibung ? `<div class="nachricht"><label>Beschreibung</label><span>${escapeHtml(a.spielstaerke_beschreibung)}</span></div>` : ""}
                                            ${verfuegbarkeitRows ? `<div class="verfuegbarkeit"><h4>Verfügbarkeit</h4><table>${verfuegbarkeitRows}</table></div>` : ""}
                                            <div class="footer">Anfrage vom ${new Date(a.created_at).toLocaleDateString("de-DE")}</div>
                                          </div>
                                        `;
                                      };

                                      const cardGroups: typeof selectedAnfragen[] = [];
                                      for (let i = 0; i < selectedAnfragen.length; i += 4) {
                                        cardGroups.push(selectedAnfragen.slice(i, i + 4));
                                      }

                                      const cardsHTML = `
                                        <div style="font-family: Arial, sans-serif;">
                                          <style>
                                            .print-page {
                                              width: 190mm;
                                              display: grid;
                                              grid-template-columns: 1fr 1fr;
                                              gap: 4mm;
                                              margin-bottom: 10mm;
                                            }
                                            .card {
                                              border: 1px solid #ccc;
                                              border-radius: 3px;
                                              padding: 3mm;
                                              box-sizing: border-box;
                                              font-size: 8pt;
                                              height: 134mm;
                                              overflow: hidden;
                                            }
                                            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #ddd; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
                                            .name { font-size: 10pt; font-weight: bold; margin: 0; }
                                            .anlage { color: white; padding: 1px 5px; border-radius: 2px; font-size: 7pt; }
                                            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5mm; margin-bottom: 1.5mm; }
                                            .info-item label { font-size: 6pt; color: #666; display: block; }
                                            .info-item span { font-size: 8pt; }
                                            .verfuegbarkeit { margin-top: 1mm; }
                                            .verfuegbarkeit h4 { font-size: 7pt; margin: 0 0 0.5mm 0; color: #666; }
                                            .verfuegbarkeit table { font-size: 7pt; border-collapse: collapse; }
                                            .nachricht { margin-top: 1.5mm; padding-top: 1.5mm; border-top: 1px dashed #ddd; }
                                            .nachricht label { font-size: 6pt; color: #666; display: block; margin-bottom: 0.5mm; }
                                            .nachricht span { font-size: 7pt; display: block; white-space: pre-wrap; }
                                            .footer { font-size: 6pt; color: #999; margin-top: 1mm; text-align: right; }
                                          </style>
                                          ${cardGroups.map(group => `
                                            <div class="print-page">
                                              ${group.map(generateProbeCardHTML).join("")}
                                            </div>
                                          `).join("")}
                                        </div>
                                      `;

                                      const html2pdf = (await import('html2pdf.js')).default;
                                      const container = document.createElement('div');
                                      container.innerHTML = cardsHTML;
                                      document.body.appendChild(container);

                                      await html2pdf()
                                        .set({
                                          margin: 10,
                                          filename: `Probetraining_${new Date().toISOString().split('T')[0]}.pdf`,
                                          html2canvas: { scale: 2 },
                                          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
                                        })
                                        .from(container.firstElementChild as HTMLElement)
                                        .save();

                                      document.body.removeChild(container);
                                    }}
                                  >
                                    Ausgewählte drucken
                                  </button>
                                  <button
                                    className="btn micro btnGhost"
                                    onClick={() => setSelectedProbetrainingIds(new Set())}
                                  >
                                    Auswahl aufheben
                                  </button>
                                  </ActionMenu>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          <ul className="list">
                            {probetrainingAnfragen.filter(a => {
                              if (formZeigeArchiv ? !a.archiviert : !!a.archiviert) return false;
                              if (probetrainingAnlageFilter !== "alle" && a.anlage !== probetrainingAnlageFilter) return false;
                              if (probetrainingNameSuche && !`${a.vorname} ${a.nachname}`.toLowerCase().includes(probetrainingNameSuche.toLowerCase())) return false;
                              if (probetrainingTagFilter !== "alle" && a.verfuegbarkeit) {
                                const tagWert = (a.verfuegbarkeit as Record<string, string>)[probetrainingTagFilter];
                                if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                              }
                              if (probetrainingStatusFilter !== "alle" && a.status !== probetrainingStatusFilter) return false;
                              return true;
                            }).length === 0 ? (
                              <p className="muted">Keine Anfragen für diesen Filter.</p>
                            ) : probetrainingAnfragen.filter(a => {
                              if (formZeigeArchiv ? !a.archiviert : !!a.archiviert) return false;
                              if (probetrainingAnlageFilter !== "alle" && a.anlage !== probetrainingAnlageFilter) return false;
                              if (probetrainingNameSuche && !`${a.vorname} ${a.nachname}`.toLowerCase().includes(probetrainingNameSuche.toLowerCase())) return false;
                              if (probetrainingTagFilter !== "alle" && a.verfuegbarkeit) {
                                const tagWert = (a.verfuegbarkeit as Record<string, string>)[probetrainingTagFilter];
                                if (!tagWert || tagWert === "" || tagWert.toLowerCase() === "nicht verfügbar") return false;
                              }
                              if (probetrainingStatusFilter !== "alle" && a.status !== probetrainingStatusFilter) return false;
                              return true;
                            }).map((anfrage) => (
                              <li key={anfrage.id} className="listItem" style={{ flexDirection: "column", alignItems: "stretch", padding: expandedProbetrainingId === anfrage.id ? undefined : "8px 12px" }}>
                                <div
                                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                  onClick={() => setExpandedProbetrainingId(expandedProbetrainingId === anfrage.id ? null : anfrage.id)}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedProbetrainingIds.has(anfrage.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => {
                                        setSelectedProbetrainingIds(prev => {
                                          const next = new Set(prev);
                                          if (next.has(anfrage.id)) next.delete(anfrage.id);
                                          else next.add(anfrage.id);
                                          return next;
                                        });
                                      }}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <span style={{ fontWeight: 500 }}>
                                      {anfrage.vorname} {anfrage.nachname}
                                    </span>
                                    <span className="muted" style={{ fontSize: 13 }}>
                                      {anfrage.alter}J
                                    </span>
                                    <span style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: anfrage.spielstand === "anfaenger" ? "#8b5cf6" : anfrage.spielstand === "fortgeschritten" ? "#06b6d4" : "#22c55e",
                                      color: "#fff",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                    }}>
                                      {anfrage.spielstand === "anfaenger" ? "Anfänger" : anfrage.spielstand === "fortgeschritten" ? "Fortgeschritten" : "Wettkampf"}
                                    </span>
                                    {anfrage.anlage && (
                                      <span style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: anfrage.anlage === "Britz" ? "var(--warning)" : "var(--primary)",
                                        color: anfrage.anlage === "Britz" ? "#000" : "#fff",
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        minWidth: 16,
                                        textAlign: "center"
                                      }}>
                                        {anfrage.anlage === "Britz" ? "B" : "W"}
                                      </span>
                                    )}
                                  </div>
                                  <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.2s", transform: expandedProbetrainingId === anfrage.id ? "rotate(90deg)" : "rotate(0deg)" }}>
                                    ▶
                                  </span>
                                </div>

                                {expandedProbetrainingId === anfrage.id && (
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="muted">{anfrage.email || "-"}</span>
                                        {anfrage.email && (
                                          <button
                                            type="button"
                                            title="E-Mail senden"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setNewsletterExtraEmails(prev =>
                                                prev.some(em => em.email === anfrage.email)
                                                  ? prev
                                                  : [...prev, { email: anfrage.email!, name: `${anfrage.vorname} ${anfrage.nachname}` }]
                                              );
                                              setNewsletterSubject("Anfrage zum Probetraining");
                                              setNewsletterLabelFilter("keine");
                                              setTab("verwaltung");
                                              setVerwaltungTab("newsletter");
                                            }}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              padding: "2px 8px",
                                              height: 22,
                                              borderRadius: 4,
                                              background: "var(--primary)",
                                              color: "#fff",
                                              border: "none",
                                              cursor: "pointer",
                                              fontSize: 11,
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            Mail
                                          </button>
                                        )}
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="muted" style={{ fontSize: 12 }}>
                                          {new Date(anfrage.created_at).toLocaleDateString("de-DE", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                          })}
                                        </span>
                                        <span
                                          className="pill"
                                          style={{
                                            background: anfrage.status === "erledigt" ? "var(--success)" : anfrage.status === "geantwortet" ? "#8b5cf6" : anfrage.status === "probetraining_ausstehend" ? "#06b6d4" : "var(--warning)",
                                            color: "white",
                                            fontSize: 12,
                                            padding: "4px 10px"
                                          }}
                                        >
                                          {anfrage.status === "geantwortet" ? "Geantwortet / Warte auf E-Mail" : anfrage.status === "probetraining_ausstehend" ? "Probetraining ausstehend" : anfrage.status === "erledigt" ? "Erledigt" : "Offen"}
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                                      {anfrage.telefon && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Telefon</div>
                                          <div>{anfrage.telefon}</div>
                                        </div>
                                      )}
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Alter</div>
                                        <div>{anfrage.alter} Jahre</div>
                                      </div>
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Spielstand</div>
                                        <div>{anfrage.spielstand === "anfaenger" ? "Anfänger" : anfrage.spielstand === "fortgeschritten" ? "Fortgeschritten" : "Wettkampf"}</div>
                                      </div>
                                      {anfrage.spielstaerke_beschreibung && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                          <div className="muted" style={{ fontSize: 11 }}>Spielstärke Beschreibung</div>
                                          <div>{anfrage.spielstaerke_beschreibung}</div>
                                        </div>
                                      )}
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Tennis gespielt</div>
                                        <div>{anfrage.hat_tennis_gespielt ? "Ja" : "Nein"}</div>
                                      </div>
                                      {anfrage.trainingsart && (
                                        <div>
                                          <div className="muted" style={{ fontSize: 11 }}>Trainingsart</div>
                                          <div>{anfrage.trainingsart === "einzel" ? "Einzeltraining" : anfrage.trainingsart === "gruppe" ? "Gruppentraining" : "Beides möglich"}</div>
                                        </div>
                                      )}
                                      <div>
                                        <div className="muted" style={{ fontSize: 11 }}>Vereinsmitglied</div>
                                        <div>{anfrage.ist_vereinsmitglied ? "Ja" : "Nein"}</div>
                                      </div>
                                    </div>
                                    {anfrage.verfuegbarkeit && (
                                      <div style={{ marginBottom: 12 }}>
                                        <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Verfügbarkeit</div>
                                        <table className="verfuegbarkeitTable" style={{ fontSize: 13 }}>
                                          <tbody>
                                            {["montag", "dienstag", "mittwoch", "donnerstag", "freitag", "samstag", "sonntag"]
                                              .filter(tag => {
                                                const zeit = (anfrage.verfuegbarkeit as Record<string, string>)?.[tag];
                                                return zeit && zeit !== "nicht verfügbar";
                                              })
                                              .map(tag => (
                                                <tr key={tag}><td>{tag.charAt(0).toUpperCase() + tag.slice(1)}</td><td>{(anfrage.verfuegbarkeit as Record<string, string>)[tag]}</td></tr>
                                              ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                      <select
                                        value={anfrage.status}
                                        onChange={(e) => updateProbetrainingStatus(anfrage.id, e.target.value)}
                                        style={{ fontSize: 13, padding: "6px 10px" }}
                                      >
                                        <option value="offen">Offen</option>
                                        <option value="geantwortet">Geantwortet / Warte auf E-Mail</option>
                                        <option value="probetraining_ausstehend">Probetraining ausstehend</option>
                                        <option value="erledigt">Erledigt</option>
                                      </select>
                                      <button
                                        className="btn micro"
                                        style={{ background: "var(--primary)", color: "#fff" }}
                                        onClick={() => {
                                          const fullName = `${anfrage.vorname} ${anfrage.nachname}`.toLowerCase().trim();
                                          const duplicate = spieler.find(s =>
                                            `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullName
                                          );
                                          if (duplicate) {
                                            alert(`Spieler "${anfrage.vorname} ${anfrage.nachname}" existiert bereits.`);
                                            return;
                                          }
                                          const neu: Spieler = {
                                            id: uid(),
                                            vorname: anfrage.vorname,
                                            nachname: anfrage.nachname || undefined,
                                            kontaktEmail: anfrage.email || undefined,
                                            kontaktTelefon: anfrage.telefon || undefined,
                                            notizen: [
                                              `Spielstand: ${anfrage.spielstand === "anfaenger" ? "Anfänger" : anfrage.spielstand === "fortgeschritten" ? "Fortgeschritten" : "Wettkampf"}`,
                                              anfrage.spielstaerke_beschreibung ? `Beschreibung: ${anfrage.spielstaerke_beschreibung}` : "",
                                              `Alter: ${anfrage.alter}`,
                                              anfrage.trainingsart ? `Trainingsart: ${anfrage.trainingsart === "einzel" ? "Einzeltraining" : anfrage.trainingsart === "gruppe" ? "Gruppentraining" : "Beides"}` : "",
                                            ].filter(Boolean).join("\n") || undefined,
                                          };
                                          setSpieler(prev => [...prev, neu]);
                                          updateProbetrainingStatus(anfrage.id, "erledigt");
                                          alert(`"${anfrage.vorname} ${anfrage.nachname}" wurde als Spieler übernommen.`);
                                        }}
                                      >
                                        Spieler übernehmen
                                      </button>
                                      <button
                                        className="btn micro btnGhost"
                                        onClick={() => setAnfrageArchiviert("probetraining_anfragen", anfrage.id, !anfrage.archiviert, fetchProbetrainingAnfragen)}
                                      >
                                        {anfrage.archiviert ? "Reaktivieren" : "Archivieren"}
                                      </button>
                                      <button
                                        className="btn micro btnWarn"
                                        onClick={() => deleteProbetrainingAnfrage(anfrage.id)}
                                      >
                                        Löschen
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                          </>
                        )}
                      </>
                    )}

                    {/* Kennlerntennis Tab */}
                    {formulareTab === "kennlerntennis" && (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <p className="muted" style={{ marginBottom: 12 }}>
                            <strong>Kennenlerntennis-Formular:</strong>{" "}
                            <code style={{
                              background: "var(--bg-inset)",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 13,
                            }}>
                              {window.location.origin}/kennenlerntennis
                            </code>
                            <button
                              className="btn micro btnGhost"
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/kennenlerntennis`
                                );
                              }}
                            >
                              Kopieren
                            </button>
                          </p>
                          <p className="muted" style={{ fontSize: 13 }}>
                            Termin: <strong>20.09.2026 um 16 Uhr</strong> (30.08.2026 geschlossen){" "}
                            (TC Blau-Weiß Britz) &ndash;{" "}
                            <span style={{ color: "var(--success)", fontWeight: 600 }}>
                              Anmeldung offen
                            </span>
                          </p>
                          <p className="muted" style={{ fontSize: 12 }}>
                            Anmelder bekommen automatisch eine Bestätigungsmail, eine Kopie geht an
                            tennisabisz@gmail.com. Termine ändern oder Anmeldung schließen:
                            TERMINE bzw. ANMELDUNG_GESCHLOSSEN in KennlerntennisForm.tsx.
                            Der alte Link /kennlerntennis funktioniert weiterhin.
                          </p>
                        </div>

                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
                          <div className="field" style={{ margin: 0 }}>
                            <label>Status</label>
                            <select
                              value={kennlerntennisStatusFilter}
                              onChange={(e) => setKennlerntennisStatusFilter(e.target.value)}
                              style={{ padding: "4px 8px" }}
                            >
                              <option value="alle">Alle</option>
                              <option value="offen">Offen</option>
                              <option value="erledigt">Erledigt</option>
                            </select>
                          </div>
                          <button
                            className="btn micro"
                            style={{ marginLeft: "auto" }}
                            disabled={kennlerntennisAnfragen.filter(a => {
                              if (kennlerntennisStatusFilter === "offen" && a.status === "erledigt") return false;
                              if (kennlerntennisStatusFilter === "erledigt" && a.status !== "erledigt") return false;
                              return true;
                            }).length === 0}
                            onClick={async () => {
                              const exportList = kennlerntennisAnfragen.filter(a => {
                                if (kennlerntennisStatusFilter === "offen" && a.status === "erledigt") return false;
                                if (kennlerntennisStatusFilter === "erledigt" && a.status !== "erledigt") return false;
                                return true;
                              });
                              if (exportList.length === 0) return;

                              const statusLabel =
                                kennlerntennisStatusFilter === "offen" ? "Offen" :
                                kennlerntennisStatusFilter === "erledigt" ? "Erledigt" :
                                "Alle";

                              const tableHTML = `
                                <html>
                                <head>
                                  <style>
                                    body { font-family: Arial, sans-serif; padding: 20px; }
                                    h1 { font-size: 18px; margin-bottom: 4px; }
                                    .sub { font-size: 12px; color: #666; margin-bottom: 16px; }
                                    table { width: 100%; border-collapse: collapse; }
                                    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 11px; vertical-align: top; }
                                    th { background-color: #f5f5f5; font-weight: bold; }
                                    tr:nth-child(even) { background-color: #fafafa; }
                                    .footer { margin-top: 20px; font-size: 11px; color: #666; }
                                  </style>
                                </head>
                                <body>
                                  <h1>Kennenlerntennis-Anfragen (${exportList.length})</h1>
                                  <div class="sub">Status-Filter: ${statusLabel}</div>
                                  <table>
                                    <thead>
                                      <tr>
                                        <th style="width: 30px;">#</th>
                                        <th>Name</th>
                                        <th style="width: 40px;">Alter</th>
                                        <th style="width: 110px;">Termin</th>
                                        <th>Spielstand</th>
                                        <th>E-Mail</th>
                                        <th>Telefon</th>
                                        <th style="width: 55px;">Mitglied</th>
                                        <th style="width: 70px;">Interesse weiterf.</th>
                                        <th style="width: 60px;">Status</th>
                                        <th style="width: 70px;">Datum</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      ${exportList.map((a, idx) => {
                                        const spielstandText =
                                          a.spielstand === "anfaenger" ? "Anfänger" :
                                          a.spielstand === "fortgeschritten" ? "Fortgeschritten" :
                                          "Turnierspieler";
                                        return `
                                          <tr>
                                            <td>${idx + 1}</td>
                                            <td>${escapeHtml(a.vorname)} ${escapeHtml(a.nachname)}</td>
                                            <td>${escapeHtml(a.alter)}</td>
                                            <td>${a.termin ? escapeHtml(a.termin) : "&ndash;"}</td>
                                            <td>${spielstandText}${a.spielstaerke_beschreibung ? `<br><span style="color:#888;font-size:9px;">${escapeHtml(a.spielstaerke_beschreibung)}</span>` : ""}</td>
                                            <td style="word-break: break-all;">${escapeHtml(a.email)}</td>
                                            <td>${escapeHtml(a.telefon)}</td>
                                            <td>${a.ist_vereinsmitglied ? "Ja" : "Nein"}</td>
                                            <td>${a.interesse_weiterfuehrend == null ? "&ndash;" : (a.interesse_weiterfuehrend ? "Ja" : "Nein")}</td>
                                            <td>${a.status === "erledigt" ? "Erledigt" : "Offen"}</td>
                                            <td>${new Date(a.created_at).toLocaleDateString("de-DE")}</td>
                                          </tr>
                                        `;
                                      }).join("")}
                                    </tbody>
                                  </table>
                                  <div class="footer">
                                    Erstellt am ${new Date().toLocaleDateString("de-DE")}
                                  </div>
                                </body>
                                </html>
                              `;

                              const html2pdf = (await import('html2pdf.js')).default;
                              const container = document.createElement('div');
                              container.innerHTML = tableHTML;
                              document.body.appendChild(container);

                              await html2pdf()
                                .set({
                                  margin: 10,
                                  filename: `Kennenlerntennis_${new Date().toISOString().split('T')[0]}.pdf`,
                                  html2canvas: { scale: 2 },
                                  jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' as const }
                                })
                                .from(container)
                                .save();

                              document.body.removeChild(container);
                            }}
                          >
                            PDF-Export
                          </button>
                        </div>

                        {loadingKennlerntennisAnfragen ? (
                          <p className="muted">Laden...</p>
                        ) : kennlerntennisAnfragen.length === 0 ? (
                          <p className="muted">Keine Kennenlerntennis-Anfragen vorhanden.</p>
                        ) : (
                          <ul className="list">
                            {kennlerntennisAnfragen
                              .filter(a => {
                                if (formZeigeArchiv ? !a.archiviert : !!a.archiviert) return false;
                                if (kennlerntennisStatusFilter === "offen" && a.status === "erledigt") return false;
                                if (kennlerntennisStatusFilter === "erledigt" && a.status !== "erledigt") return false;
                                return true;
                              })
                              .map(anfrage => {
                                const spielstandText =
                                  anfrage.spielstand === "anfaenger" ? "Anfänger" :
                                  anfrage.spielstand === "fortgeschritten" ? "Fortgeschritten" :
                                  "Turnierspieler";
                                return (
                                  <li key={anfrage.id} className="listItem" style={{ flexDirection: "column", alignItems: "stretch", padding: expandedKennlerntennisId === anfrage.id ? undefined : "8px 12px" }}>
                                    <div
                                      style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", width: "100%" }}
                                      onClick={() => setExpandedKennlerntennisId(expandedKennlerntennisId === anfrage.id ? null : anfrage.id)}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600 }}>
                                          {anfrage.vorname} {anfrage.nachname}
                                          {anfrage.status === "erledigt" && (
                                            <span style={{
                                              marginLeft: 8,
                                              background: "#d1fae5",
                                              color: "#065f46",
                                              padding: "2px 8px",
                                              borderRadius: 10,
                                              fontSize: 11,
                                              fontWeight: 600
                                            }}>Erledigt</span>
                                          )}
                                        </div>
                                        <div className="muted" style={{ fontSize: 12 }}>
                                          {anfrage.termin ? `${anfrage.termin} · ` : ""}{spielstandText} · {anfrage.alter} Jahre · {new Date(anfrage.created_at).toLocaleDateString("de-DE")}
                                        </div>
                                      </div>
                                      <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.2s", transform: expandedKennlerntennisId === anfrage.id ? "rotate(90deg)" : "rotate(0deg)" }}>
                                        ›
                                      </span>
                                    </div>

                                    {expandedKennlerntennisId === anfrage.id && (
                                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 14 }}>
                                          <div style={{ gridColumn: "1 / -1" }}>
                                            <strong>Termin:</strong> {anfrage.termin || "– (vor Einführung der Terminauswahl)"}
                                          </div>
                                          <div><strong>Vorname:</strong> {anfrage.vorname}</div>
                                          <div><strong>Nachname:</strong> {anfrage.nachname}</div>
                                          <div><strong>Alter:</strong> {anfrage.alter}</div>
                                          <div><strong>Spielstand:</strong> {spielstandText}</div>
                                          <div><strong>E-Mail:</strong> <a href={`mailto:${anfrage.email}`}>{anfrage.email}</a></div>
                                          <div><strong>Telefon:</strong> <a href={`tel:${anfrage.telefon}`}>{anfrage.telefon}</a></div>
                                          <div><strong>Vereinsmitglied:</strong> {anfrage.ist_vereinsmitglied ? "Ja" : "Nein"}</div>
                                          {anfrage.interesse_weiterfuehrend != null && (
                                            <div><strong>Interesse weiterführendes Training:</strong> {anfrage.interesse_weiterfuehrend ? "Ja" : "Nein"}</div>
                                          )}
                                        </div>
                                        {anfrage.spielstaerke_beschreibung && (
                                          <div style={{ marginTop: 12, fontSize: 14 }}>
                                            <strong>Beschreibung Spielstärke:</strong>
                                            <div style={{ marginTop: 4, padding: 8, background: "var(--bg-inset)", borderRadius: 6 }}>
                                              {anfrage.spielstaerke_beschreibung}
                                            </div>
                                          </div>
                                        )}
                                        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                          <label style={{ fontSize: 13 }}>Status:</label>
                                          <select
                                            value={anfrage.status}
                                            onChange={(e) => updateKennlerntennisStatus(anfrage.id, e.target.value)}
                                            style={{ padding: "4px 8px" }}
                                          >
                                            <option value="offen">Offen</option>
                                            <option value="erledigt">Erledigt</option>
                                          </select>
                                          {anfrage.email && (
                                            <button
                                              type="button"
                                              className="btn micro btnGhost"
                                              onClick={() => {
                                                setNewsletterExtraEmails(prev =>
                                                  prev.some(em => em.email === anfrage.email)
                                                    ? prev
                                                    : [...prev, { email: anfrage.email, name: `${anfrage.vorname} ${anfrage.nachname}` }]
                                                );
                                                setNewsletterSubject("Ihre Kennenlerntennis-Anfrage");
                                                setNewsletterLabelFilter("keine");
                                                setTab("verwaltung");
                                                setVerwaltungTab("newsletter");
                                              }}
                                              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                                            >
                                              <span aria-hidden="true">&#9993;</span>
                                              E-Mail Kontakt
                                            </button>
                                          )}
                                          <button
                                            className="btn micro btnGhost"
                                            style={{ marginLeft: "auto" }}
                                            onClick={() => setAnfrageArchiviert("kennlerntennis_anfragen", anfrage.id, !anfrage.archiviert, fetchKennlerntennisAnfragen)}
                                          >
                                            {anfrage.archiviert ? "Reaktivieren" : "Archivieren"}
                                          </button>
                                          <button
                                            className="btn micro btnWarn"
                                            onClick={() => deleteKennlerntennisAnfrage(anfrage.id)}
                                          >
                                            Löschen
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                          </ul>
                        )}
                      </>
                    )}
                </div>
              </>
            )}

            {tab === "verwaltung" && !isTrainer && verwaltungTab === "newsletter" && (
              <div className="card">
                <h2>Newsletter versenden</h2>

                    {newsletterSuccess && (
                      <div style={{
                        background: "#d1fae5",
                        border: "1px solid #10b981",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 16,
                        color: "#065f46"
                      }}>
                        Newsletter erfolgreich versendet!
                        <button
                          className="btn btnGhost"
                          style={{ marginLeft: 12, fontSize: 12 }}
                          onClick={() => setNewsletterSuccess(false)}
                        >
                          Schließen
                        </button>
                      </div>
                    )}

                    {newsletterError && (
                      <div style={{
                        background: "#fee2e2",
                        border: "1px solid #dc2626",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 16,
                        color: "#991b1b"
                      }}>
                        {newsletterError}
                        <button
                          className="btn btnGhost"
                          style={{ marginLeft: 12, fontSize: 12 }}
                          onClick={() => setNewsletterError(null)}
                        >
                          Schließen
                        </button>
                      </div>
                    )}

                    {/* Spieler-Suche und Auswahl */}
                    <div className="field" style={{ marginBottom: 16 }}>
                      <label>Einzelne Spieler suchen und hinzufügen</label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          value={newsletterPlayerSearch}
                          onChange={(e) => setNewsletterPlayerSearch(e.target.value)}
                          placeholder="Spielername eingeben..."
                        />
                        {newsletterPlayerSearch.trim() && (
                          <div style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#ffffff",
                            border: "1px solid var(--border)",
                            borderRadius: 8,
                            maxHeight: 200,
                            overflowY: "auto",
                            zIndex: 9999,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                          }}>
                            {spieler
                              .filter(s =>
                                s.kontaktEmail &&
                                !s.archiviert &&
                                (s.vorname.toLowerCase().includes(newsletterPlayerSearch.toLowerCase()) ||
                                  (s.nachname ?? "").toLowerCase().includes(newsletterPlayerSearch.toLowerCase())) &&
                                !newsletterSelectedPlayers.includes(s.id)
                              )
                              .slice(0, 10)
                              .map(s => (
                                <div
                                  key={s.id}
                                  style={{
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid var(--border)",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2
                                  }}
                                  onClick={() => {
                                    setNewsletterSelectedPlayers(prev => [...prev, s.id]);
                                    setNewsletterPlayerSearch("");
                                    setNewsletterLabelFilter("keine");
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-hover)")}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                >
                                  <strong style={{ display: "block" }}>{getFullName(s)}</strong>
                                  <span style={{ color: "var(--text-muted)", fontSize: 12, display: "block" }}>
                                    {s.kontaktEmail}
                                  </span>
                                </div>
                              ))}
                            {spieler.filter(s =>
                              s.kontaktEmail &&
                              !s.archiviert &&
                              (s.vorname.toLowerCase().includes(newsletterPlayerSearch.toLowerCase()) ||
                                (s.nachname ?? "").toLowerCase().includes(newsletterPlayerSearch.toLowerCase())) &&
                              !newsletterSelectedPlayers.includes(s.id)
                            ).length === 0 && (
                              <div style={{ padding: "10px 12px", color: "var(--text-muted)" }}>
                                Kein Spieler gefunden
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Freie E-Mail-Adresse: Empfaenger, die kein Spieler in der Verwaltung sind */}
                    <div className="field" style={{ marginBottom: 16 }}>
                      <label>E-Mail-Adressen direkt eingeben</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <input
                          type="text"
                          value={newsletterManualEmail}
                          onChange={(e) => setNewsletterManualEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addNewsletterManualEmail();
                            }
                          }}
                          placeholder="name@beispiel.de, zweite@beispiel.de, ..."
                          style={{ flex: "2 1 220px", minWidth: 0 }}
                        />
                        <input
                          type="text"
                          value={newsletterManualName}
                          onChange={(e) => setNewsletterManualName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addNewsletterManualEmail();
                            }
                          }}
                          placeholder="Name (optional)"
                          style={{ flex: "1 1 140px", minWidth: 0 }}
                        />
                        <button
                          type="button"
                          className="btn btnGhost"
                          disabled={!newsletterManualEmail.trim()}
                          onClick={addNewsletterManualEmail}
                        >
                          Hinzufügen
                        </button>
                      </div>
                      <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                        Für Empfänger, die nicht als Spieler angelegt sind. Mehrere Adressen auf
                        einmal einfügen geht &ndash; getrennt durch Komma, Semikolon, Leerzeichen
                        oder Zeilenumbruch, auch im Format <code>{"Name <mail@beispiel.de>"}</code>.
                        Das Namensfeld gilt nur für eine einzelne Adresse; ohne Namen bleibt der
                        Platzhalter <code>{"{NAME}"}</code> im Text leer.
                      </p>
                      {newsletterManualInfo && (
                        <p style={{ fontSize: 12, marginTop: 4, color: "var(--success)", fontWeight: 600 }}>
                          {newsletterManualInfo}
                        </p>
                      )}
                    </div>

                    {/* Ausgewählte Spieler anzeigen */}
                    {newsletterSelectedPlayers.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: "block", marginBottom: 8 }}>
                          Ausgewählte Spieler ({newsletterSelectedPlayers.length})
                        </label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {newsletterSelectedPlayers.map(id => {
                            const s = spielerById.get(id);
                            if (!s) return null;
                            return (
                              <span
                                key={id}
                                style={{
                                  background: "var(--primary)",
                                  color: "white",
                                  padding: "4px 10px",
                                  borderRadius: 16,
                                  fontSize: 13,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6
                                }}
                              >
                                {getFullName(s)}
                                <button
                                  type="button"
                                  onClick={() => setNewsletterSelectedPlayers(prev => prev.filter(pid => pid !== id))}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "white",
                                    cursor: "pointer",
                                    padding: 0,
                                    fontSize: 16,
                                    lineHeight: 1
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                          <button
                            type="button"
                            className="btn btnGhost"
                            style={{ fontSize: 12, padding: "4px 10px" }}
                            onClick={() => setNewsletterSelectedPlayers([])}
                          >
                            Alle entfernen
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Extra E-Mails anzeigen (nicht-Spieler) */}
                    {newsletterExtraEmails.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: "block", marginBottom: 8 }}>
                          Weitere Empfänger ({newsletterExtraEmails.length})
                        </label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {newsletterExtraEmails.map((item, idx) => (
                            <span
                              key={idx}
                              style={{
                                background: "#059669",
                                color: "white",
                                padding: "4px 10px",
                                borderRadius: 16,
                                fontSize: 13,
                                display: "flex",
                                alignItems: "center",
                                gap: 6
                              }}
                            >
                              {item.name || item.email}
                              <button
                                type="button"
                                onClick={() => setNewsletterExtraEmails(prev => prev.filter((_, i) => i !== idx))}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "white",
                                  cursor: "pointer",
                                  padding: 0,
                                  fontSize: 16,
                                  lineHeight: 1
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            className="btn btnGhost"
                            style={{ fontSize: 12, padding: "4px 10px" }}
                            onClick={() => setNewsletterExtraEmails([])}
                          >
                            Alle entfernen
                          </button>
                        </div>
                      </div>
                    )}

                    <div style={{
                      borderTop: "1px solid var(--border)",
                      paddingTop: 16,
                      marginBottom: 16,
                      display: (newsletterSelectedPlayers.length > 0 || newsletterExtraEmails.length > 0) ? "block" : "none"
                    }}>
                      <p className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                        — oder zusätzlich per Label filtern —
                      </p>
                    </div>

                    <div className="row" style={{ marginBottom: 16 }}>
                      <div className="field">
                        <label>Empfänger filtern nach Label</label>
                        <select
                          value={newsletterLabelFilter}
                          onChange={(e) => setNewsletterLabelFilter(e.target.value)}
                        >
                          <option value="alle">Alle Spieler mit E-Mail</option>
                          <option value="keine">Keine (nur ausgewählte Spieler)</option>
                          {allLabels.map((label) => (
                            <option key={label} value={label}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="field" style={{ alignSelf: "flex-end" }}>
                        <span style={{
                          background: "var(--bg-inset)",
                          padding: "8px 16px",
                          borderRadius: 8,
                          fontSize: 14
                        }}>
                          {getNewsletterRecipients().length + newsletterExtraEmails.length} Empfänger
                        </span>
                      </div>
                    </div>

                    {/* Empfänger-Vorschau */}
                    <div style={{ marginBottom: 24 }}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12
                      }}>
                        <label style={{ margin: 0 }}>
                          Empfänger-Vorschau ({getNewsletterRecipients().length + newsletterExtraEmails.length})
                        </label>
                        {newsletterExcludedPlayers.length > 0 && (
                          <button
                            type="button"
                            className="btn-text"
                            onClick={() => setNewsletterExcludedPlayers([])}
                            style={{ fontSize: 14 }}
                          >
                            Alle zurücksetzen
                          </button>
                        )}
                      </div>

                      <div style={{
                        background: "var(--bg-inset)",
                        borderRadius: 8,
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid var(--border-light)"
                      }}>
                        {(() => {
                          const recipients = getNewsletterRecipients();
                          if (recipients.length === 0 && newsletterExtraEmails.length === 0) {
                            return (
                              <div style={{
                                padding: 24,
                                textAlign: "center",
                                color: "var(--text-muted)"
                              }}>
                                Keine Empfänger ausgewählt...
                              </div>
                            );
                          }

                          // Alphabetisch sortieren
                          const sortedRecipients = [...recipients].sort((a, b) => {
                            const nameA = `${a.vorname} ${a.nachname || ""}`.trim();
                            const nameB = `${b.vorname} ${b.nachname || ""}`.trim();
                            return nameA.localeCompare(nameB);
                          });

                          return (
                            <>
                          {sortedRecipients.map((recipient, idx) => (
                            <div
                              key={recipient.id}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "12px 16px",
                                borderBottom: (idx < sortedRecipients.length - 1 || newsletterExtraEmails.length > 0)
                                  ? "1px solid var(--border-light)"
                                  : "none"
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                  {recipient.vorname} {recipient.nachname || ""}
                                </div>
                                <div style={{
                                  fontSize: 13,
                                  color: "var(--text-muted)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {[recipient.kontaktEmail, ...(recipient.zusaetzlicheEmails || [])].filter(Boolean).join(", ")}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setNewsletterExcludedPlayers(prev => [...prev, recipient.id]);
                                }}
                                style={{
                                  marginLeft: 12,
                                  padding: "4px 8px",
                                  background: "transparent",
                                  border: "1px solid var(--border-light)",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontSize: 18,
                                  lineHeight: 1,
                                  color: "var(--text-muted)"
                                }}
                                title="Empfänger entfernen"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {/* Frei eingetragene Adressen stehen in keiner Spielerliste - hier mit anzeigen */}
                          {newsletterExtraEmails.map((item, idx) => (
                            <div
                              key={`extra-${item.email}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "12px 16px",
                                borderBottom: idx < newsletterExtraEmails.length - 1
                                  ? "1px solid var(--border-light)"
                                  : "none"
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                  {item.name || item.email}
                                </div>
                                <div style={{
                                  fontSize: 13,
                                  color: "var(--text-muted)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {item.email}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setNewsletterExtraEmails(prev => prev.filter((_, i) => i !== idx))}
                                style={{
                                  marginLeft: 12,
                                  padding: "4px 8px",
                                  background: "transparent",
                                  border: "1px solid var(--border-light)",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontSize: 18,
                                  lineHeight: 1,
                                  color: "var(--text-muted)"
                                }}
                                title="Empfänger entfernen"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="field" style={{ marginBottom: 16 }}>
                      <label>Betreff *</label>
                      <input
                        value={newsletterSubject}
                        onChange={(e) => setNewsletterSubject(e.target.value)}
                        placeholder="E-Mail Betreff eingeben..."
                      />
                    </div>

                    <div className="field" style={{ marginBottom: 16 }}>
                      <label>Nachricht *</label>
                      <textarea
                        rows={10}
                        value={newsletterBody}
                        onChange={(e) => setNewsletterBody(e.target.value)}
                        placeholder="Ihre Nachricht hier eingeben..."
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                      <div style={{ marginTop: 4, padding: "6px 10px", background: "var(--bg-inset)", borderRadius: 4, fontSize: 12, color: "var(--text-muted)" }}>
                        Tipp: <code>{"{NAME}"}</code> wird automatisch durch den Vornamen des Empfängers ersetzt.
                      </div>
                      <div style={{ marginTop: 8, padding: "10px 12px", background: "var(--bg-subtle, #f3f4f6)", borderRadius: 6, fontSize: 13, color: "var(--text-muted, #6b7280)" }}>
                        <div style={{ whiteSpace: "pre-wrap" }}>{"Sportliche Grüße\n"}<select
                          value={newsletterAbsender}
                          onChange={(e) => setNewsletterAbsender(e.target.value as "Artur" | "Zlatan")}
                          style={{ fontSize: 13, padding: "2px 4px", margin: "2px 0" }}
                        >
                          <option value="Artur">Artur</option>
                          <option value="Zlatan">Zlatan</option>
                        </select>{"\nTennisschule A bis Z"}</div>
                        <img src="/logo.png" alt="Tennisschule A bis Z" style={{ width: 120, marginTop: 8, borderRadius: 6 }} />
                      </div>
                    </div>

                    <div className="stickyActions" style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <button
                        className="btn"
                        disabled={newsletterSending || !newsletterSubject.trim() || !newsletterBody.trim() || (getNewsletterRecipients().length === 0 && newsletterExtraEmails.length === 0)}
                        onClick={async () => {
                          const recipients = getNewsletterRecipients();
                          const extraEmails = newsletterExtraEmails;

                          // Empfänger-Liste mit Name + Emails aufbauen
                          const recipientList: { name: string; emails: string[] }[] = [
                            ...recipients.map(r => ({
                              name: r.vorname,
                              emails: [r.kontaktEmail, ...(r.zusaetzlicheEmails || [])].filter(Boolean) as string[]
                            })),
                            ...extraEmails.map(e => ({
                              name: e.name.split(" ")[0],
                              emails: [e.email]
                            }))
                          ];

                          const totalEmails = recipientList.reduce((sum, r) => sum + r.emails.length, 0);

                          if (totalEmails === 0) {
                            setNewsletterError("Keine Empfänger mit E-Mail-Adresse gefunden.");
                            return;
                          }

                          if (!window.confirm(`Newsletter an ${totalEmails} Empfänger senden?`)) {
                            return;
                          }

                          setNewsletterSending(true);
                          setNewsletterError(null);
                          setNewsletterSuccess(false);

                          try {
                            const bodyTemplate = newsletterBody.trim();
                            const footer = `\n\nSportliche Grüße\n${newsletterAbsender}\nTennisschule A bis Z`;
                            const htmlFooter = `<br><br>Sportliche Grüße<br>${newsletterAbsender}<br>Tennisschule A bis Z<br><br><img src="${window.location.origin}/logo.png" alt="Tennisschule A bis Z" style="width:180px;height:auto;border-radius:8px;" />`;
                            const hasPlaceholder = bodyTemplate.includes("{NAME}");

                            let totalSent = 0;
                            let totalFailed = 0;
                            const allErrors: string[] = [];

                            for (const recipient of recipientList) {
                              const personalBody = hasPlaceholder
                                ? bodyTemplate.replace(/\{NAME\}/g, recipient.name)
                                : bodyTemplate;
                              const personalHtml = personalBody.replace(/\n/g, "<br>") + htmlFooter;

                              const response = await fetch("/api/send-newsletter", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  to: recipient.emails,
                                  subject: newsletterSubject.trim(),
                                  body: personalBody + footer,
                                  html: personalHtml,
                                  fromName: "Tennisschule A bis Z"
                                })
                              });

                              if (response.ok) {
                                const result = await response.json();
                                totalSent += result.sent || 0;
                                totalFailed += result.failed || 0;
                                if (result.errors) allErrors.push(...result.errors);
                              } else {
                                const error = await response.json();
                                totalFailed += recipient.emails.length;
                                allErrors.push(error.message || "Fehler beim Versenden");
                              }
                            }

                            if (totalFailed > 0 && totalSent === 0) {
                              throw new Error(allErrors.join(", "));
                            }

                            setNewsletterSuccess(true);
                            setNewsletterSubject("");
                            setNewsletterBody("");
                            setNewsletterSelectedPlayers([]);
                            setNewsletterExcludedPlayers([]);
                            setNewsletterExtraEmails([]);
                            setNewsletterManualInfo(null);
                          } catch (err) {
                            setNewsletterError(err instanceof Error ? err.message : "Unbekannter Fehler");
                          } finally {
                            setNewsletterSending(false);
                          }
                        }}
                      >
                        {newsletterSending ? "Wird gesendet..." : "Newsletter senden"}
                      </button>

                      {getNewsletterRecipients().length === 0 && newsletterExtraEmails.length === 0 && (
                        <span className="muted">Keine Empfänger ausgewählt.</span>
                      )}
                    </div>

                    {allLabels.length === 0 && (
                      <p className="muted" style={{ marginTop: 20, fontSize: 13 }}>
                        Tipp: Sie können Spielern Labels zuweisen (z.B. "Erwachsene", "Kinder", "Anfänger")
                        um Newsletter gezielt an bestimmte Gruppen zu senden.
                      </p>
                    )}
              </div>
            )}


            {tab === "verwaltung" && isTrainer && (
              <div className="card">
                <h2>Kein Zugriff</h2>
                <p className="muted">
                  Die Verwaltung ist nur für den Hauptaccount verfügbar.
                </p>
              </div>
            )}

            {tab === "abrechnung" && (
              <div className="card">
                <h2>Abrechnung</h2>

                <div className="row">
                  <div className="field">
                    <label>Monat</label>
                    <input
                      type="month"
                      value={abrechnungMonat}
                      onChange={(e) => setAbrechnungMonat(e.target.value)}
                    />
                  </div>
                  {!isTrainer && (
                    <div className="field">
                      <label>Filter</label>
                      <select
                        value={abrechnungFilter}
                        onChange={(e) =>
                          setAbrechnungFilter(
                            e.target.value as AbrechnungFilter
                          )
                        }
                      >
                        <option value="alle">Alle</option>
                        <option value="bezahlt">Nur bezahlt</option>
                        <option value="offen">Nur offen</option>
                        <option value="bar">Nur bar bezahlt</option>
                      </select>
                    </div>
                  )}
                  {!isTrainer && trainers.length > 1 && (
                    <div className="field">
                      <label>Trainer</label>
                      <select
                        value={abrechnungTrainerFilter}
                        onChange={(e) =>
                          setAbrechnungTrainerFilter(e.target.value)
                        }
                      >
                        <option value="alle">Alle Trainer</option>
                        {trainers.filter((tr) => {
                          return trainings.some((t) =>
                            t.datum.startsWith(abrechnungMonat) &&
                            t.status === "durchgefuehrt" &&
                            !t.isPrivat &&
                            ((t.trainerId || defaultTrainerId) === tr.id ||
                              vertretungen.some(v => v.trainingId === t.id && v.vertretungTrainerId === tr.id))
                          );
                        }).map((tr) => {
                          const honorarDone = !!trainerMonthSettled[trainerMonthSettledKey(abrechnungMonat, tr.id)];
                          const barDone = !!trainerBarSettled[trainerBarSettledKey(abrechnungMonat, tr.id)];
                          const fullySettled = honorarDone && barDone;
                          return (
                            <option key={tr.id} value={tr.id}>
                              {fullySettled ? "\u2713 " : ""}{tr.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  {!isTrainer && abrechnungTab === "spieler" && (
                    <div className="field">
                      <label>Spieler Suche</label>
                      <input
                        value={abrechnungSpielerSuche}
                        onChange={(e) =>
                          setAbrechnungSpielerSuche(e.target.value)
                        }
                        placeholder="Name oder Email"
                      />
                    </div>
                  )}
                  {!isTrainer && abrechnungTab === "spieler" && (
                    <div className="field">
                      <label>Tag (wiederkehrend)</label>
                      <select
                        value={abrechnungTagFilter}
                        onChange={(e) => setAbrechnungTagFilter(e.target.value)}
                      >
                        <option value="alle">Alle Tage</option>
                        <option value="0">Montag</option>
                        <option value="1">Dienstag</option>
                        <option value="2">Mittwoch</option>
                        <option value="3">Donnerstag</option>
                        <option value="4">Freitag</option>
                        <option value="5">Samstag</option>
                        <option value="6">Sonntag</option>
                      </select>
                    </div>
                  )}
                  {!isTrainer && abrechnungTab === "spieler" && (
                    <div className="field">
                      <label>Abgebucht</label>
                      <select
                        value={abrechnungAbgebuchtFilter}
                        onChange={(e) => setAbrechnungAbgebuchtFilter(e.target.value)}
                      >
                        <option value="alle">Alle</option>
                        <option value="abgebucht">Abgebucht</option>
                        <option value="nicht_abgebucht">Nicht abgebucht</option>
                      </select>
                    </div>
                  )}
                </div>

                {!isTrainer && (
                  <div className="subTabs">
                    <button
                      className={`tabBtn ${
                        abrechnungTab === "spieler" ? "tabBtnActive" : ""
                      }`}
                      onClick={() => setAbrechnungTab("spieler")}
                    >
                      Spieler Abrechnung
                    </button>
                    <button
                      className={`tabBtn ${
                        abrechnungTab === "trainer" ? "tabBtnActive" : ""
                      }`}
                      onClick={() => setAbrechnungTab("trainer")}
                    >
                      Trainer Abrechnung
                    </button>
                  </div>
                )}

                {abrechnungTab === "spieler" && !isTrainer && (
                  <>
                    <div className="row" style={{ marginTop: 12 }}>
                      <span className="pill">
                        Umsatz gesamt:{" "}
                        <strong>{euro(abrechnung.total)}</strong>
                      </span>

                      {abrechnung.barTotal > 0 && (
                        <>
                          <span className="pill">
                            Bar bezahlt (Stunden):{" "}
                            <strong>{euro(abrechnung.barTotal)}</strong>
                          </span>
                          <span className="pill">
                            Umsatz inkl. Bar:{" "}
                            <strong>{euro(abrechnung.totalMitBar)}</strong>
                          </span>
                        </>
                      )}

                      <span className="pill">
                        Bereits bezahlt: <strong>{euro(sumBezahlt)}</strong>
                      </span>
                      <span className="pill">
                        Offen: <strong>{euro(sumOffen)}</strong>
                      </span>
                    </div>


                    <div style={{ height: 14 }} />
                    <div className="card cardInset">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <h2 style={{ margin: 0 }}>Summe pro Spieler</h2>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <span className="pill">
                            SEPA-Auswahl: <strong>{sepaExportSelection.size}</strong>
                          </span>
                          <ActionMenu label="SEPA / Export" buttonClassName="btn" menuWidth={260}>
                          <button
                            className="btn btnGhost"
                            onClick={() => {
                              const sel = new Set<string>();
                              filteredSpielerRowsForMonth.forEach((r) => {
                                const sp = spielerById.get(r.id);
                                const adjustedSum = getAdjustedSum(r.id, r.sum);
                                const sumBar = getSumBarForSpieler(r.id);
                                const restOffen = round2(adjustedSum - sumBar);
                                if (sp?.iban && sp.mandatsreferenz && sp.unterschriftsdatum && restOffen > 0) {
                                  sel.add(r.id);
                                }
                              });
                              setSepaExportSelection(sel);
                            }}
                          >
                            Alle abbuchbaren auswählen
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => setSepaExportSelection(new Set())}
                          >
                            Auswahl aufheben
                          </button>
                          <button
                            className="btn"
                            disabled={sepaExportSelection.size === 0}
                            onClick={() => setShowSepaExportModal(true)}
                          >
                            SEPA-XML exportieren
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => setShowBankImportModal(true)}
                            title="Commerzbank-CSV importieren und Spieler automatisch als abgerechnet markieren"
                          >
                            Bank-Umsätze importieren
                          </button>
                          </ActionMenu>
                        </div>
                      </div>
                      <div style={{ height: 8 }} />
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ width: 36 }}>SEPA</th>
                            <th>Spieler</th>
                            <th>Aufstellung</th>
                            <th>Summe</th>
                            <th>Status</th>
                            <th>Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSpielerRowsForMonth.map((r) => {
                            // 5c: Getrennte Aufstellung für Bar und Nicht-Bar
                            const barParts = r.breakdownBar
                              .map((b) => `${b.count} × ${euro(b.amount)} bar`)
                              .join(" + ");
                            const nichtBarParts = r.breakdownNichtBar
                              .map((b) => `${b.count} × ${euro(b.amount)}`)
                              .join(" + ");

                            // Anpassung für diesen Spieler/Monat
                            const adjustmentKey = `${abrechnungMonat}__${r.id}`;
                            const adjustment = getAdjustmentForSpieler(r.id);
                            const hasAdjustment = adjustment !== 0;

                            let breakdownText = "-";
                            if (barParts && nichtBarParts) {
                              // Gemischter Fall: beide Teile anzeigen
                              breakdownText = `${barParts} + ${nichtBarParts}`;
                            } else if (barParts) {
                              // Nur Bar
                              breakdownText = barParts;
                            } else if (nichtBarParts) {
                              // Nur Nicht-Bar
                              breakdownText = nichtBarParts;
                            }

                            // Anpassung zum Breakdown hinzufügen wenn vorhanden
                            if (hasAdjustment) {
                              const adjustmentStr = adjustment < 0
                                ? `${euro(adjustment)}`
                                : `+${euro(adjustment)}`;
                              if (breakdownText === "-") {
                                breakdownText = adjustmentStr;
                              } else {
                                breakdownText = `${breakdownText} ${adjustmentStr}`;
                              }
                            }

                            const key = paymentKey(abrechnungMonat, r.id);
                            const paymentsFlag = payments[key] ?? false;

                            // Berechne Bar-Summen
                            const sumBarSpieler = getSumBarForSpieler(r.id);
                            const sumTotalSpieler = r.sum;
                            const adjustedSum = getAdjustedSum(r.id, sumTotalSpieler);
                            const restOffen = round2(adjustedSum - sumBarSpieler);
                            
                            // Status-Logik gemäß Spezifikation (mit angepasster Summe):
                            // 1. "komplett bar": sumBarSpieler === adjustedSum && adjustedSum > 0
                            // 2. "teilweise bar bezahlt": 0 < sumBarSpieler < adjustedSum
                            // 3. "komplett abgerechnet": paymentsFlag === true && adjustedSum > sumBarSpieler
                            // 4. "offen": paymentsFlag === false && adjustedSum > sumBarSpieler

                            type SpielerStatus = "komplett_bar" | "teilweise_bar" | "komplett_abgerechnet" | "offen" | "keine_trainings";

                            let status: SpielerStatus;
                            if (adjustedSum === 0) {
                              status = "keine_trainings";
                            } else if (sumBarSpieler >= adjustedSum && adjustedSum > 0) {
                              status = "komplett_bar";
                            } else if (sumBarSpieler > 0 && sumBarSpieler < adjustedSum) {
                              // Teilweise bar - prüfe ob Rest abgerechnet wurde
                              if (paymentsFlag) {
                                status = "komplett_abgerechnet";
                              } else {
                                status = "teilweise_bar";
                              }
                            } else {
                              // Keine Bar-Zahlungen
                              if (paymentsFlag) {
                                status = "komplett_abgerechnet";
                              } else {
                                status = "offen";
                              }
                            }
                            
                            // Status-Badge Konfiguration
                            let statusLabel: string;
                            let statusClass: string;
                            let statusStyle: React.CSSProperties = {};
                            
                            switch (status) {
                              case "komplett_bar":
                                statusLabel = "komplett bar";
                                statusClass = "badge";
                                statusStyle = { backgroundColor: "#dc2626", color: "white" };
                                break;
                              case "teilweise_bar":
                                statusLabel = `teilw. bar (${euro(sumBarSpieler)})`;
                                statusClass = "badge";
                                statusStyle = { backgroundColor: "#f59e0b", color: "white" };
                                break;
                              case "komplett_abgerechnet":
                                statusLabel = "abgerechnet";
                                statusClass = "badge badgeOk";
                                break;
                              case "offen":
                                statusLabel = "offen";
                                statusClass = "badge";
                                break;
                              case "keine_trainings":
                                statusLabel = "keine Trainings";
                                statusClass = "badge";
                                statusStyle = { backgroundColor: "#9ca3af", color: "white" };
                                break;
                            }
                            
                            // Dropdown-Styling
                            let selectStyle: React.CSSProperties = {
                              padding: "4px 8px",
                              borderRadius: 6,
                              border: "1px solid #d1d5db",
                              fontSize: 13,
                              cursor: "pointer",
                              minWidth: 180,
                            };
                            
                            if (status === "komplett_bar") {
                              selectStyle = {
                                ...selectStyle,
                                backgroundColor: "#dc2626",
                                color: "white",
                                borderColor: "#dc2626",
                              };
                            } else if (status === "teilweise_bar") {
                              selectStyle = {
                                ...selectStyle,
                                backgroundColor: "#f59e0b",
                                color: "white",
                                borderColor: "#f59e0b",
                              };
                            } else if (status === "komplett_abgerechnet") {
                              selectStyle = {
                                ...selectStyle,
                                backgroundColor: "#16a34a",
                                color: "white",
                                borderColor: "#16a34a",
                              };
                            }

                            // Dropdown-Wert
                            const dropdownValue = status === "komplett_abgerechnet" ? "abgerechnet" : status;

                            const isEditingThis = editingAdjustment?.spielerId === r.id;

                            const sepaSpieler = spielerById.get(r.id);
                            const sepaReady = !!(sepaSpieler?.iban && sepaSpieler?.mandatsreferenz && sepaSpieler?.unterschriftsdatum);

                            const istAbgebuchtRow = wirdAbgebucht[`${abrechnungMonat}__${r.id}`] ?? false;
                            const rowBezahlt = status === "komplett_bar" || istAbgebuchtRow;

                            return (
                              <tr
                                key={r.id}
                                style={rowBezahlt ? { backgroundColor: "rgba(99, 102, 241, 0.12)" } : undefined}
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={sepaExportSelection.has(r.id)}
                                    disabled={!sepaReady || restOffen <= 0}
                                    title={!sepaReady ? "SEPA-Daten unvollständig (IBAN/Mandat/Datum)" : restOffen <= 0 ? "Nichts offen" : "Für SEPA-Export auswählen"}
                                    onChange={(e) => {
                                      setSepaExportSelection((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(r.id); else next.delete(r.id);
                                        return next;
                                      });
                                    }}
                                  />
                                </td>
                                <td>
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      color: "var(--primary)",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => setSelectedSpielerForDetail(r.id)}
                                  >
                                    {r.name}
                                  </span>
                                  {!sepaReady && (
                                    <span title="SEPA-Daten unvollständig" style={{ marginLeft: 6, color: "#dc2626", fontSize: 11 }}>⚠ SEPA</span>
                                  )}
                                </td>
                                <td>{breakdownText}</td>
                                <td>
                                  {isEditingThis ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                      <input
                                        type="text"
                                        style={{
                                          width: 80,
                                          padding: "4px 6px",
                                          fontSize: 13,
                                          border: "1px solid var(--primary)",
                                          borderRadius: 4,
                                        }}
                                        value={editingAdjustment.value}
                                        onChange={(e) => setEditingAdjustment({
                                          ...editingAdjustment,
                                          value: e.target.value,
                                        })}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            const desiredSum = parseFloat(editingAdjustment.value.replace(",", "."));
                                            if (!isNaN(desiredSum)) {
                                              // Benutzer gibt gewünschte Endsumme ein, wir berechnen das Delta
                                              const newAdjustment = round2(desiredSum - sumTotalSpieler);
                                              setMonthlyAdjustments((prev) => ({
                                                ...prev,
                                                [adjustmentKey]: newAdjustment,
                                              }));
                                            }
                                            setEditingAdjustment(null);
                                          }
                                          if (e.key === "Escape") {
                                            setEditingAdjustment(null);
                                          }
                                        }}
                                        onBlur={() => {
                                          const desiredSum = parseFloat(editingAdjustment.value.replace(",", "."));
                                          if (!isNaN(desiredSum)) {
                                            // Benutzer gibt gewünschte Endsumme ein, wir berechnen das Delta
                                            const newAdjustment = round2(desiredSum - sumTotalSpieler);
                                            setMonthlyAdjustments((prev) => ({
                                              ...prev,
                                              [adjustmentKey]: newAdjustment,
                                            }));
                                          }
                                          setEditingAdjustment(null);
                                        }}
                                        autoFocus
                                        placeholder={euro(sumTotalSpieler)}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      <span style={{ fontWeight: hasAdjustment ? 600 : 400 }}>
                                        {euro(adjustedSum)}
                                      </span>
                                      {hasAdjustment && (
                                        <button
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 2,
                                            fontSize: 12,
                                            color: "#9ca3af",
                                          }}
                                          onClick={() => {
                                            setMonthlyAdjustments((prev) => {
                                              const next = { ...prev };
                                              delete next[adjustmentKey];
                                              return next;
                                            });
                                          }}
                                          title="Zurücksetzen auf berechnet"
                                        >
                                          ✕
                                        </button>
                                      )}
                                      <button
                                        style={{
                                          background: "none",
                                          border: "none",
                                          cursor: "pointer",
                                          padding: 2,
                                          fontSize: 12,
                                          color: hasAdjustment ? "var(--primary)" : "#9ca3af",
                                        }}
                                        onClick={() => setEditingAdjustment({
                                          spielerId: r.id,
                                          value: String(adjustedSum),
                                        })}
                                        title="Summe anpassen"
                                      >
                                        ✎
                                      </button>
                                    </div>
                                  )}
                                  {status === "teilweise_bar" && (
                                    <div style={{ fontSize: 11, color: "#f59e0b" }}>
                                      (Rest: {euro(restOffen)})
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className={statusClass} style={statusStyle}>
                                    {statusLabel}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <select
                                      style={selectStyle}
                                      value={dropdownValue}
                                      onChange={(e) => {
                                        const newVal = e.target.value;
                                        if (newVal === "abgerechnet") {
                                          if (!paymentsFlag) {
                                            togglePaidForPlayer(abrechnungMonat, r.id);
                                          }
                                        } else if (newVal === "offen" || newVal === "teilweise_bar") {
                                          if (paymentsFlag) {
                                            togglePaidForPlayer(abrechnungMonat, r.id);
                                          }
                                        }
                                      }}
                                    >
                                      {status === "komplett_bar" && (
                                        <option value="komplett_bar">✓ Komplett bar bezahlt</option>
                                      )}
                                      {(status === "teilweise_bar") && (
                                        <option value="teilweise_bar">⚠ Teilweise bar ({euro(sumBarSpieler)})</option>
                                      )}
                                      <option value="abgerechnet">
                                        ✓ Komplett abgerechnet
                                      </option>
                                      <option value="offen">○ Offen</option>
                                    </select>
                                    <label style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      fontSize: 12,
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                      color: wirdAbgebucht[`${abrechnungMonat}__${r.id}`] ? "var(--primary)" : "var(--text-muted)"
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={wirdAbgebucht[`${abrechnungMonat}__${r.id}`] ?? false}
                                        onChange={(e) => {
                                          setWirdAbgebucht((prev) => ({
                                            ...prev,
                                            [`${abrechnungMonat}__${r.id}`]: e.target.checked,
                                          }));
                                        }}
                                        style={{ width: "auto" }}
                                      />
                                      abgebucht
                                    </label>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Detail-Ansicht für ausgewählten Spieler */}
                    {selectedSpielerForDetail && (() => {
                      const spielerData = spielerById.get(selectedSpielerForDetail);
                      const spielerName = spielerData ? getFullName(spielerData) : "Unbekannt";

                      // Trainings dieses Spielers im gewählten Monat
                      const spielerTrainings = trainingsForAbrechnung.filter(
                        (t) => t.spielerIds.includes(selectedSpielerForDetail)
                      );

                      // Sortiere nach Datum
                      const sortedTrainings = [...spielerTrainings].sort(
                        (a, b) => a.datum.localeCompare(b.datum)
                      );

                      // Pro-Training Anteilspreis berechnen (deckungsgleich mit der Abrechnungs-Logik)
                      const perTrainingPrice = new Map<string, number>();
                      sortedTrainings.forEach((t) => {
                        const cfg = getPreisConfig(t, tarifById);
                        if (!cfg) {
                          perTrainingPrice.set(t.id, 0);
                          return;
                        }
                        if (cfg.abrechnung === "monatlich") {
                          const trainingDate = new Date(t.datum + "T12:00:00");
                          const wd = trainingDate.getDay();
                          const possible = weekdayOccurrencesInMonth(abrechnungMonat, wd) || 1;
                          const plannedMins = durationMin(t.uhrzeitVon, t.uhrzeitBis);
                          const ratio = (t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < plannedMins)
                            ? t.actualMinutes / plannedMins : 1;
                          perTrainingPrice.set(t.id, round2(cfg.preisProStunde * ratio / possible));
                        } else {
                          perTrainingPrice.set(t.id, round2(priceFuerSpieler(t)));
                        }
                      });

                      // Aggregation: monatliche Tarife pro Slot (Tarif + Wochentag + Uhrzeit)
                      type MonthlySlotRow = {
                        tarifName: string;
                        preisProStunde: number;
                        weekday: number;
                        uhrzeitVon: string;
                        uhrzeitBis: string;
                        actualCount: number;
                        durchgefuehrtCount: number;
                        abgesagtCount: number;
                        possible: number;
                        sum: number;
                      };
                      const monthlySlotRows = new Map<string, MonthlySlotRow>();
                      const cancellationRefunds: { datum: string; uhrzeitVon: string; uhrzeitBis: string; refund: number; slotShare: number }[] = [];
                      let cancelRefundsTotal = 0;

                      sortedTrainings.forEach((t) => {
                        const cfg = getPreisConfig(t, tarifById);
                        if (cfg?.abrechnung !== "monatlich") return;
                        const tarifKey = t.tarifId || `custom-${cfg.preisProStunde}`;
                        const weekday = new Date(t.datum + "T12:00:00").getDay();
                        const slotKey = `${tarifKey}__${weekday}_${t.uhrzeitVon}_${t.uhrzeitBis}`;
                        if (!monthlySlotRows.has(slotKey)) {
                          const tarifData = t.tarifId ? tarifById.get(t.tarifId) : null;
                          monthlySlotRows.set(slotKey, {
                            tarifName: tarifData?.name || "Monatlich",
                            preisProStunde: cfg.preisProStunde,
                            weekday,
                            uhrzeitVon: t.uhrzeitVon,
                            uhrzeitBis: t.uhrzeitBis,
                            actualCount: 0,
                            durchgefuehrtCount: 0,
                            abgesagtCount: 0,
                            possible: weekdayOccurrencesInMonth(abrechnungMonat, weekday) || 1,
                            sum: 0,
                          });
                        }
                        const slot = monthlySlotRows.get(slotKey)!;
                        slot.actualCount += 1;
                        const slotShare = perTrainingPrice.get(t.id) ?? 0;
                        slot.sum = round2(slot.sum + slotShare);
                        if (t.status === "abgesagt") {
                          slot.abgesagtCount += 1;
                          if (typeof t.cancelFee === "number" && t.cancelFee > 0) {
                            cancellationRefunds.push({
                              datum: t.datum,
                              uhrzeitVon: t.uhrzeitVon,
                              uhrzeitBis: t.uhrzeitBis,
                              refund: t.cancelFee,
                              slotShare: round2(slotShare),
                            });
                            cancelRefundsTotal = round2(cancelRefundsTotal + t.cancelFee);
                          }
                        } else {
                          slot.durchgefuehrtCount += 1;
                        }
                      });

                      let monthlyTotal = 0;
                      monthlySlotRows.forEach((e) => { monthlyTotal = round2(monthlyTotal + e.sum); });

                      const wdLabels = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

                      // Nicht-monatliche Trainings: Einzeltrainings vs. Absagegebühren
                      let regularTotal = 0;
                      let cancelFeesTotal = 0;
                      sortedTrainings.forEach((t) => {
                        const cfg = getPreisConfig(t, tarifById);
                        if (cfg && cfg.abrechnung !== "monatlich") {
                          const share = perTrainingPrice.get(t.id) ?? 0;
                          if (t.status === "abgesagt") {
                            cancelFeesTotal = round2(cancelFeesTotal + share);
                          } else {
                            regularTotal = round2(regularTotal + share);
                          }
                        }
                      });

                      const baseSum = round2(monthlyTotal + regularTotal + cancelFeesTotal);
                      const adjustment = getAdjustmentForSpieler(selectedSpielerForDetail);
                      const adjustedSum = round2(baseSum + adjustment);
                      // Erstattungssumme aus den einzelnen abgesagten Trainings.
                      // Die manuelle monatliche Anpassung kann sowohl diese Erstattungen als auch
                      // weitere Korrekturen enthalten -> Rest ausweisen.
                      const totalCancellationRefund = cancelRefundsTotal > 0 ? -cancelRefundsTotal : 0;
                      const otherAdjustment = round2(adjustment - totalCancellationRefund);
                      const sumBarSpieler = getSumBarForSpieler(selectedSpielerForDetail);
                      const restOffenDetail = round2(adjustedSum - sumBarSpieler);
                      const paymentsFlag = payments[paymentKey(abrechnungMonat, selectedSpielerForDetail)] ?? false;
                      const istAbgebucht = wirdAbgebucht[`${abrechnungMonat}__${selectedSpielerForDetail}`] ?? false;

                      return (
                        <div
                          className="modalOverlay"
                          onClick={() => setSelectedSpielerForDetail(null)}
                        >
                          <div
                            className="modalCard"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: 700, maxHeight: "85vh", overflow: "auto" }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                              <h2 style={{ margin: 0 }}>
                                Trainings von {spielerName}
                              </h2>
                              <button
                                onClick={() => setSelectedSpielerForDetail(null)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: 24,
                                  cursor: "pointer",
                                  color: "#666",
                                }}
                              >
                                ×
                              </button>
                            </div>
                            <div className="muted" style={{ marginBottom: 12 }}>
                              {abrechnungMonat} • {sortedTrainings.length} Training{sortedTrainings.length !== 1 ? "s" : ""}
                            </div>

                            {sortedTrainings.length === 0 && adjustment === 0 && sumBarSpieler === 0 ? (
                              <p>Keine Trainings in diesem Monat.</p>
                            ) : (
                              <>
                                {sortedTrainings.length > 0 && (
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Datum</th>
                                      <th>Uhrzeit</th>
                                      <th>Trainer</th>
                                      <th>Preis</th>
                                      <th>Bar</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortedTrainings.map((t) => {
                                      const vertretung = vertretungen.find(v => v.trainingId === t.id);
                                      const trainerId = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
                                      const trainerName = trainerById.get(trainerId)?.name ?? "Unbekannt";
                                      const cfg = getPreisConfig(t, tarifById);
                                      const isMonthly = cfg?.abrechnung === "monatlich";
                                      const isAbgesagt = t.status === "abgesagt";
                                      const datum = new Date(t.datum);
                                      const wochentag = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][datum.getDay()];

                                      const preisAnzeige = perTrainingPrice.get(t.id) ?? 0;

                                      return (
                                        <tr key={t.id} style={isAbgesagt ? { color: "var(--text-muted)" } : undefined}>
                                          <td>{wochentag}, {t.datum.split("-").reverse().join(".")}</td>
                                          <td>{zeitspanneAnzeige(t)}</td>
                                          <td>
                                            {trainerName}
                                            {vertretung && (
                                              <span style={{ color: "#dc2626", marginLeft: 4 }} title="Vertretung">V</span>
                                            )}
                                          </td>
                                          <td>
                                            <span style={isAbgesagt && isMonthly ? { textDecoration: "line-through" } : undefined}>
                                              {euro(preisAnzeige ?? 0)}
                                            </span>
                                            {isMonthly && !isAbgesagt && (
                                              <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 6 }} title="anteilig aus monatlichem Tarif">
                                                · anteilig
                                              </span>
                                            )}
                                            {isMonthly && isAbgesagt && (
                                              <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 6 }} title="Slot zählt weiterhin im Monatstarif – Erstattung siehe Zusammenfassung">
                                                · im Tarif
                                              </span>
                                            )}
                                          </td>
                                          <td>{t.barBezahlt ? "Ja" : "Nein"}</td>
                                          <td>
                                            <span className={`badge ${t.status === "durchgefuehrt" ? "badgeOk" : isAbgesagt ? "badgeError" : ""}`}>
                                              {t.status === "durchgefuehrt" ? "durchgeführt" : isAbgesagt ? "abgesagt" : t.status}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                )}

                                {/* Zusammenfassung */}
                                <div style={{
                                  marginTop: 16,
                                  padding: 12,
                                  background: "var(--bg-inset)",
                                  borderRadius: "var(--radius-md)"
                                }}>
                                  {monthlySlotRows.size > 0 && (
                                    <div style={{ marginBottom: 4 }}>
                                      {Array.from(monthlySlotRows.entries()).map(([key, slot]) => {
                                        const perTermin = round2(slot.preisProStunde / slot.possible);
                                        return (
                                          <div key={key} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                            <span>
                                              {slot.tarifName} · {wdLabels[slot.weekday]} {slot.uhrzeitVon}–{slot.uhrzeitBis}
                                              <span style={{ color: "var(--text-muted)", fontSize: 12, marginLeft: 6 }}>
                                                ({euro(perTermin)}/Termin × {slot.actualCount} von {slot.possible})
                                              </span>
                                            </span>
                                            <strong>{euro(slot.sum)}</strong>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {regularTotal > 0 && (
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                      <span>Einzeltrainings</span>
                                      <strong>{euro(regularTotal)}</strong>
                                    </div>
                                  )}
                                  {cancelFeesTotal > 0 && (
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#b91c1c" }}>
                                      <span>Absagegebühren</span>
                                      <strong>{euro(cancelFeesTotal)}</strong>
                                    </div>
                                  )}
                                  {baseSum !== 0 && (
                                  <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    paddingTop: 6,
                                    marginTop: 6,
                                    borderTop: "1px solid var(--border)",
                                  }}>
                                    <span>Zwischensumme</span>
                                    <strong>{euro(baseSum)}</strong>
                                  </div>
                                  )}
                                  {cancellationRefunds.length > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                      {cancellationRefunds.map((c, idx) => {
                                        const [yy, mm, dd] = c.datum.split("-");
                                        const retention = round2(c.slotShare - c.refund);
                                        const partial = retention > 0;
                                        const labelStyle: React.CSSProperties = partial
                                          ? { fontWeight: 600 }
                                          : {};
                                        return (
                                          <div key={idx} style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginTop: 2,
                                            color: "#166534",
                                            ...labelStyle,
                                          }}>
                                            <span>
                                              Erstattung Absage {dd}.{mm}.{yy}, {c.uhrzeitVon}–{c.uhrzeitBis}
                                              {partial && (
                                                <span style={{ color: "#b45309", fontSize: 12, marginLeft: 6, fontWeight: 600 }}>
                                                  · Schule behält {euro(retention)}
                                                </span>
                                              )}
                                            </span>
                                            <strong>−{euro(c.refund)}</strong>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {otherAdjustment !== 0 && (
                                    <div style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginTop: 4,
                                      color: otherAdjustment < 0 ? "#166534" : "#b91c1c",
                                    }}>
                                      <span>{cancellationRefunds.length > 0
                                        ? (otherAdjustment < 0 ? "Weitere Erstattung / Anpassung" : "Weiterer Aufschlag / Anpassung")
                                        : (otherAdjustment < 0 ? "Erstattung / Anpassung" : "Aufschlag / Anpassung")}</span>
                                      <strong>{otherAdjustment < 0 ? "" : "+"}{euro(otherAdjustment)}</strong>
                                    </div>
                                  )}
                                  <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    borderTop: "2px solid var(--border)",
                                    paddingTop: 10,
                                    marginTop: 10,
                                    fontWeight: 700,
                                    fontSize: 16,
                                  }}>
                                    <span>Gesamt</span>
                                    <span style={{ color: "var(--primary)" }}>{euro(adjustedSum)}</span>
                                  </div>
                                  {sumBarSpieler > 0 && (
                                    <div style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginTop: 8,
                                      color: "#166534",
                                    }}>
                                      <span>Bar bezahlt</span>
                                      <strong>−{euro(sumBarSpieler)}</strong>
                                    </div>
                                  )}
                                  {adjustedSum > 0 && (
                                    <div style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginTop: 4,
                                      fontWeight: 600,
                                    }}>
                                      <span>Offen</span>
                                      <span style={{ color: restOffenDetail > 0 && !paymentsFlag ? "#b91c1c" : "#166534" }}>
                                        {paymentsFlag ? euro(0) : euro(Math.max(0, restOffenDetail))}
                                      </span>
                                    </div>
                                  )}
                                  <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {paymentsFlag && (
                                      <span className="badge badgeOk">Restbetrag abgerechnet</span>
                                    )}
                                    {istAbgebucht && (
                                      <span className="badge" style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}>
                                        Wird abgebucht
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                                {abrechnungTab === "trainer" && (
                  <>
                    {!isTrainer &&
                      !(
                        abrechnungTrainerFilter !== "alle" &&
                        (trainerById.get(abrechnungTrainerFilter)?.name ?? "")
                          .trim()
                          .toLowerCase() === "sascha"
                      ) && (
                        <div className="row" style={{ marginTop: 12 }}>
                          <span className="pill">
                            Honorar bezahlt:{" "}
                            <strong>{euro(trainerHonorarBezahltTotal)}</strong>
                          </span>
                          <span className="pill">
                            Honorar offen:{" "}
                            <strong>{euro(trainerHonorarOffenTotal)}</strong>
                          </span>
                          <span className="pill">
                            Rückzahlung an Schule (bar):{" "}
                            <strong>{euro(effectiveRueckzahlung)}</strong>
                          </span>
                        </div>
                      )}

                    {/* Bar/Nicht-Bar Stunden für Admin (wenn ein Trainer gefiltert) */}
                    {!isTrainer && abrechnungTrainerFilter !== "alle" && (
                      <>
                        <div style={{ height: 14 }} />
                        <div className="card cardInset">
                          <h2>Übersicht Stunden</h2>
                          <p className="muted" style={{ marginBottom: 8 }}>Klicke auf eine Zeile, um die Details anzuzeigen.</p>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Art</th>
                                <th>Anzahl</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                onClick={() => setAdminTrainerPaymentView(adminTrainerPaymentView === "nichtBar" ? "none" : "nichtBar")}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: adminTrainerPaymentView === "nichtBar" ? "var(--surface-hover)" : undefined
                                }}
                              >
                                <td>Nicht bar</td>
                                <td>{summeStunden(adminNichtBarTrainings)}</td>
                              </tr>
                              <tr
                                onClick={() => setAdminTrainerPaymentView(adminTrainerPaymentView === "bar" ? "none" : "bar")}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: adminTrainerPaymentView === "bar" ? "var(--surface-hover)" : undefined
                                }}
                              >
                                <td>Bar</td>
                                <td>{summeStunden(adminBarTrainings)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Detailansicht der Stunden für Admin */}
                        {adminTrainerPaymentView !== "none" && (
                          <div className="card cardInset" style={{ marginTop: 14 }}>
                            <h2>
                              {adminTrainerPaymentView === "bar"
                                ? "Bar bezahlte Stunden"
                                : "Nicht bar bezahlte Stunden"}
                            </h2>
                            {(adminTrainerPaymentView === "bar" ? adminBarTrainings : adminNichtBarTrainings).length === 0 ? (
                              <p className="muted">
                                {adminTrainerPaymentView === "bar"
                                  ? "Keine bar bezahlten Stunden im ausgewählten Zeitraum."
                                  : "Keine nicht bar bezahlten Stunden im ausgewählten Zeitraum."}
                              </p>
                            ) : (
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th>Datum</th>
                                    <th>Zeit</th>
                                    <th>Spieler</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(adminTrainerPaymentView === "bar" ? adminBarTrainings : adminNichtBarTrainings)
                                    .sort((a, b) => a.datum.localeCompare(b.datum) || a.uhrzeitVon.localeCompare(b.uhrzeitVon))
                                    .map((t) => {
                                      const [y, m, d] = t.datum.split("-");
                                      const germanDate = d && m && y ? `${d}.${m}.${y}` : t.datum;
                                      const spielerNamen = t.spielerIds
                                        .map((id) => getSpielerDisplayName(id))
                                        .join(", ") || (t.isTenniscamp ? "Tenniscamp" : "");
                                      return (
                                        <tr key={t.id}>
                                          <td>{germanDate}</td>
                                          <td>{zeitspanneAnzeige(t)}</td>
                                          <td>{spielerNamen}</td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Abrechnungsstatus für Admin - mit Toggle */}
                    {!isTrainer && abrechnungTrainerFilter !== "alle" && (
                      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                        {/* Honorar-Abrechnung */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ minWidth: 140 }}>Honorar:</span>
                          {trainerMonthSettled[trainerMonthSettledKey(abrechnungMonat, abrechnungTrainerFilter)] ? (
                            <>
                              <span style={{ color: "#22c55e", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontSize: 18 }}>✓</span> Abgerechnet
                              </span>
                              <button
                                className="btn btnGhost"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                                onClick={() => {
                                  const key = trainerMonthSettledKey(abrechnungMonat, abrechnungTrainerFilter);
                                  setTrainerMonthSettled((prev) => {
                                    const next = { ...prev };
                                    delete next[key];
                                    return next;
                                  });
                                  // Spiegelung von "Als abgerechnet markieren":
                                  // dieselben Nicht-Bar-Stunden wieder auf "offen" setzen,
                                  // damit exakt der Stand von vor der Abrechnung
                                  // wiederhergestellt wird (sonst bleiben sie "bezahlt").
                                  const trainerTrainings = trainingsInMonth.filter(
                                    (t) => trainingGehoertZuTrainer(t, abrechnungTrainerFilter) && !t.barBezahlt
                                  );
                                  if (trainerTrainings.length > 0) {
                                    setTrainerPayments((prev) => {
                                      const next = { ...prev };
                                      trainerTrainings.forEach((t) => {
                                        delete next[trainerPaymentKey(t, abrechnungTrainerFilter)];
                                      });
                                      return next;
                                    });
                                  }
                                }}
                              >
                                Markierung entfernen
                              </button>
                            </>
                          ) : (
                            <>
                              <span style={{ color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontSize: 18 }}>○</span> Nicht abgerechnet
                              </span>
                              <button
                                className="btn"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                                onClick={() => {
                                  const key = trainerMonthSettledKey(abrechnungMonat, abrechnungTrainerFilter);
                                  setTrainerMonthSettled((prev) => ({ ...prev, [key]: true }));
                                  // Alle nicht-bar Trainings dieses Trainers als abgerechnet markieren
                                  const trainerTrainings = trainingsInMonth.filter(
                                    (t) => trainingGehoertZuTrainer(t, abrechnungTrainerFilter) && !t.barBezahlt
                                  );
                                  if (trainerTrainings.length > 0) {
                                    setTrainerPayments((prev) => {
                                      const next = { ...prev };
                                      trainerTrainings.forEach((t) => {
                                        next[trainerPaymentKey(t, abrechnungTrainerFilter)] = true;
                                      });
                                      return next;
                                    });
                                  }
                                }}
                              >
                                Als abgerechnet markieren
                              </button>
                            </>
                          )}
                        </div>

                        {/* Bar-Abrechnung */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ minWidth: 140 }}>Bar-Rückzahlung:</span>
                          {trainerBarSettled[trainerBarSettledKey(abrechnungMonat, abrechnungTrainerFilter)] ? (
                            <>
                              <span style={{ color: "#22c55e", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontSize: 18 }}>✓</span> Erledigt
                              </span>
                              <button
                                className="btn btnGhost"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                                onClick={() => {
                                  const key = trainerBarSettledKey(abrechnungMonat, abrechnungTrainerFilter);
                                  setTrainerBarSettled((prev) => {
                                    const next = { ...prev };
                                    delete next[key];
                                    return next;
                                  });
                                }}
                              >
                                Markierung entfernen
                              </button>
                            </>
                          ) : (
                            <>
                              <span style={{ color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontSize: 18 }}>○</span> Nicht erledigt
                              </span>
                              <button
                                className="btn"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                                onClick={() => {
                                  const key = trainerBarSettledKey(abrechnungMonat, abrechnungTrainerFilter);
                                  setTrainerBarSettled((prev) => ({ ...prev, [key]: true }));
                                }}
                              >
                                Als erledigt markieren
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Zuschlag / Abzug — Admin: editierbar, Trainer: nur lesen (sofern Einträge vorhanden) */}
                    {abrechnungTrainerFilter !== "alle" && (() => {
                      const zuschlaegeListe = trainerZuschlaege[`${abrechnungMonat}__${abrechnungTrainerFilter}`] ?? [];
                      if (isTrainer && zuschlaegeListe.length === 0) return null;
                      return (
                        <div style={{ marginTop: 14 }}>
                          <div className="card cardInset">
                            <h2>Zuschlag / Abzug</h2>
                            <p className="muted" style={{ marginBottom: 8 }}>
                              {isTrainer
                                ? "Korrekturen vom Admin auf dein Honorar (z.B. Bonus, Reisekosten, Abzug). Bereits in deinem offenen Honorar oben enthalten."
                                : "Manuelle Anpassung des Honorars ohne Verknüpfung zu einer Stunde (z.B. Bonus, Reisekosten, Abzug)."}
                            </p>
                            {zuschlaegeListe.length > 0 && (
                              <table className="table" style={{ marginBottom: 10 }}>
                                <thead>
                                  <tr>
                                    <th>Betrag</th>
                                    <th>Notiz</th>
                                    {!isTrainer && <th></th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {zuschlaegeListe.map((z) => (
                                    <tr key={z.id}>
                                      <td style={{ color: z.betrag >= 0 ? "#166534" : "#991b1b", fontWeight: 600 }}>
                                        {z.betrag >= 0 ? "+" : ""}{euro(z.betrag)}
                                      </td>
                                      <td>{z.notiz || <span className="muted">—</span>}</td>
                                      {!isTrainer && (
                                        <td>
                                          <button
                                            className="btn micro btnGhost"
                                            style={{ fontSize: 11, color: "#ef4444" }}
                                            onClick={() => {
                                              const key = `${abrechnungMonat}__${abrechnungTrainerFilter}`;
                                              setTrainerZuschlaege((prev) => ({
                                                ...prev,
                                                [key]: (prev[key] ?? []).filter((x) => x.id !== z.id),
                                              }));
                                            }}
                                          >
                                            Entfernen
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {!isTrainer && (
                              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                                <div>
                                  <div style={{ fontSize: 12, marginBottom: 2 }}>Betrag (€, negativ = Abzug)</div>
                                  <input
                                    type="number"
                                    step="0.01"
                                    style={{ width: 100, fontSize: 13, padding: "4px 8px" }}
                                    placeholder="z.B. 20 oder -10"
                                    value={zuschlagForm.betrag}
                                    onChange={(e) => setZuschlagForm((f) => ({ ...f, betrag: e.target.value }))}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, marginBottom: 2 }}>Notiz (optional)</div>
                                  <input
                                    type="text"
                                    style={{ width: 180, fontSize: 13, padding: "4px 8px" }}
                                    placeholder="z.B. Regenabbruch, Bonus"
                                    value={zuschlagForm.notiz}
                                    onChange={(e) => setZuschlagForm((f) => ({ ...f, notiz: e.target.value }))}
                                  />
                                </div>
                                <button
                                  className="btn"
                                  style={{ fontSize: 13, padding: "4px 14px" }}
                                  onClick={() => {
                                    const val = parseFloat(zuschlagForm.betrag.replace(",", "."));
                                    if (isNaN(val)) return;
                                    const key = `${abrechnungMonat}__${abrechnungTrainerFilter}`;
                                    setTrainerZuschlaege((prev) => ({
                                      ...prev,
                                      [key]: [
                                        ...(prev[key] ?? []),
                                        { id: uid(), betrag: round2(val), notiz: zuschlagForm.notiz.trim() },
                                      ],
                                    }));
                                    setZuschlagForm({ betrag: "", notiz: "" });
                                  }}
                                >
                                  Hinzufügen
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {isTrainer &&
                      !(
                        (trainerById.get(ownTrainerId)?.name ?? "")
                          .trim()
                          .toLowerCase() === "sascha"
                      ) && (
                        <div className="row" style={{ marginTop: 12 }}>
                          <span className="pill">
                            Honorar bezahlt:{" "}
                            <strong>{euro(eigenerHonorarBezahlt)}</strong>
                          </span>
                          <span className="pill">
                            Honorar offen:{" "}
                            <strong>{euro(eigenerHonorarOffen)}</strong>
                          </span>
                          <span className="pill">
                            Rückzahlung an Schule (bar):{" "}
                            <strong>{euro(rueckzahlungTrainerOffen)}</strong>
                          </span>
                        </div>
                      )}

                    {/* Abrechnungsstatus für Trainer - nur lesen */}
                    {isTrainer && ownTrainerId && (
                      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                        {trainerMonthSettled[trainerMonthSettledKey(abrechnungMonat, ownTrainerId)] ? (
                          <span style={{ color: "#22c55e", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 18 }}>✓</span> Abgerechnet
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 18 }}>○</span> Nicht abgerechnet
                          </span>
                        )}
                      </div>
                    )}

                    {isTrainer && (
                      <>
                        <div style={{ height: 14 }} />
                        <div className="card cardInset">
                          <h2>Übersicht deine Stunden</h2>
                          <p className="muted" style={{ marginBottom: 8 }}>Klicke auf eine Zeile, um die Details anzuzeigen.</p>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Art</th>
                                <th>Anzahl</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                onClick={() => setSelectedTrainerPaymentView(selectedTrainerPaymentView === "nichtBar" ? "none" : "nichtBar")}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: selectedTrainerPaymentView === "nichtBar" ? "var(--surface-hover)" : undefined
                                }}
                              >
                                <td>Nicht bar</td>
                                <td>{summeStunden(nichtBarTrainings)}</td>
                              </tr>
                              <tr
                                onClick={() => setSelectedTrainerPaymentView(selectedTrainerPaymentView === "bar" ? "none" : "bar")}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: selectedTrainerPaymentView === "bar" ? "var(--surface-hover)" : undefined
                                }}
                              >
                                <td>Bar</td>
                                <td>{summeStunden(barTrainings)}</td>
                              </tr>
                              <tr
                                onClick={() => setSelectedTrainerPaymentView(selectedTrainerPaymentView === "privat" ? "none" : "privat")}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: selectedTrainerPaymentView === "privat" ? "var(--surface-hover)" : undefined
                                }}
                              >
                                <td>Privat</td>
                                <td>{summeStunden(privatTrainings)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Detailansicht der Trainerstunden */}
                        {selectedTrainerPaymentView !== "none" && (() => {
                          const detailTrainings =
                            selectedTrainerPaymentView === "bar"
                              ? barTrainings
                              : selectedTrainerPaymentView === "privat"
                                ? privatTrainings
                                : nichtBarTrainings;
                          const detailTitel =
                            selectedTrainerPaymentView === "bar"
                              ? "Bar bezahlte Stunden"
                              : selectedTrainerPaymentView === "privat"
                                ? "Privatstunden"
                                : "Nicht bar bezahlte Stunden";
                          const detailLeer =
                            selectedTrainerPaymentView === "bar"
                              ? "Keine bar bezahlten Stunden im ausgewählten Zeitraum."
                              : selectedTrainerPaymentView === "privat"
                                ? "Keine Privatstunden im ausgewählten Zeitraum."
                                : "Keine nicht bar bezahlten Stunden im ausgewählten Zeitraum.";
                          return (
                          <div className="card cardInset" style={{ marginTop: 14 }}>
                            <h2>{detailTitel}</h2>
                            {detailTrainings.length === 0 ? (
                              <p className="muted">{detailLeer}</p>
                            ) : (
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th>Datum</th>
                                    <th>Zeit</th>
                                    <th>Spieler</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detailTrainings
                                    .sort((a, b) => a.datum.localeCompare(b.datum) || a.uhrzeitVon.localeCompare(b.uhrzeitVon))
                                    .map((t) => {
                                      const [y, m, d] = t.datum.split("-");
                                      const germanDate = d && m && y ? `${d}.${m}.${y}` : t.datum;
                                      const spielerNamen = t.spielerIds
                                        .map((id) => getSpielerDisplayName(id))
                                        .join(", ") || (t.isTenniscamp ? "Tenniscamp" : "");
                                      return (
                                        <tr key={t.id}>
                                          <td>{germanDate}</td>
                                          <td>{zeitspanneAnzeige(t)}</td>
                                          <td>{spielerNamen}</td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            )}
                          </div>
                          );
                        })()}
                      </>
                    )}


                    {!isTrainer && trainers.length > 1 && (
                      <>
                        <div style={{ height: 14 }} />
                        <div className="card cardInset">
                          <h2>Summe pro Trainer</h2>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Trainer</th>
                                <th>Trainings</th>
                                <th>Umsatz</th>
                                <th>Trainer Honorar</th>
                                <th>Honorar bezahlt</th>
                                <th>Honorar offen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {abrechnungTrainer.rows.map((r) => {
                                const isSascha = r.name.trim().toLowerCase() === "sascha";

                                if (isSascha) {
                                  // Für Sascha: Bar/Nicht-Bar Stunden zählen (Vertretung/Camp berücksichtigen)
                                  const saschaTrainings = trainingsForAbrechnung.filter((t) =>
                                    trainingGehoertZuTrainer(t, r.id)
                                  );
                                  const nichtBarCount = saschaTrainings.filter(
                                    (t) => !t.barBezahlt
                                  ).length;
                                  const barCount = saschaTrainings.filter(
                                    (t) => t.barBezahlt
                                  ).length;
                                  
                                  return (
                                    <tr key={r.id}>
                                      <td>{r.name}</td>
                                      <td>{r.trainings}</td>
                                      <td colSpan={2} style={{ textAlign: "center" }}>
                                        Nicht bar: {nichtBarCount}
                                      </td>
                                      <td colSpan={2} style={{ textAlign: "center" }}>
                                        Bar: {barCount}
                                      </td>
                                    </tr>
                                  );
                                }
                                
                                return (
                                  <tr key={r.id}>
                                    <td>{r.name}</td>
                                    <td>{r.trainings}</td>
                                    <td>{euro(r.sum)}</td>
                                    <td>{euro(r.honorar)}</td>
                                    <td>{euro(r.honorarBezahlt)}</td>
                                    <td>{euro(r.honorarOffen)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}


                {!isTrainer && abrechnungTab === "trainer" && (
                    <>
                      <div style={{ height: 14 }} />
                      <h2>Trainings im Monat</h2>
                      <ul className="list">
                        {trainingsForAbrechnung.map((t) => {
                          const tarif = t.tarifId
                            ? tarifById.get(t.tarifId)
                            : undefined;
                          const ta = tarif
                            ? tarif.abrechnung === "monatlich"
                              ? `${tarif.name} (monatlich ${tarif.preisProStunde} EUR)`
                              : tarif.name
                            : t.customPreisProStunde
                            ? `Individuell (${t.customPreisProStunde} EUR pro Stunde)`
                            : t.isTenniscamp
                            ? "Tenniscamp (keine Spieler-Abrechnung)"
                            : "Tarif";
                          
                          // Bei aktiver Spielersuche nur gesuchte Spieler anzeigen
                          const searchQ = abrechnungSpielerSuche.trim().toLowerCase();
                          const filteredSpielerIds = searchQ
                            ? t.spielerIds.filter((sid) => {
                                const s = spielerById.get(sid);
                                return s && (
                                  s.vorname.toLowerCase().includes(searchQ) ||
                                  (s.nachname ?? "").toLowerCase().includes(searchQ) ||
                                  (s.kontaktEmail ?? "").toLowerCase().includes(searchQ)
                                );
                              })
                            : t.spielerIds;
                          
                          const sp = filteredSpielerIds
                            .map(
                              (id) => getSpielerDisplayName(id)
                            )
                            .join(", ");
                          // Vertretungstrainer berücksichtigen
                          const vertretung = vertretungen.find(v => v.trainingId === t.id);
                          const effectiveTrainerId = vertretung?.vertretungTrainerId || t.trainerId || defaultTrainerId;
                          // Tenniscamp mit mehreren Trainern: alle Namen + Gesamt-Honorar zeigen
                          const istCampMulti = !!(t.isTenniscamp && t.trainerIds && t.trainerIds.length > 1);
                          const trainerName = istCampMulti
                            ? t.trainerIds!.map((id) => trainerById.get(id)?.name ?? "Trainer").join(", ")
                            : trainerById.get(effectiveTrainerId)?.name ?? "Trainer";
                          const priceNum = round2(trainingPreisGesamt(t));

                          const honorarNum = trainerHonorarGesamtFuerTraining(t);
                          const honorarBadge = euro(honorarNum);
                          const trainerPaid = honorarBezahltImFilter(t);
                          const showTrainerInfo =
                            isTrainer || abrechnungTab === "trainer";
                          const differenz = round2(priceNum - honorarNum);

                          const [y, m, d] = t.datum.split("-");
                          const germanDate =
                            d && m && y ? `${d}.${m}.${y}` : t.datum;

                          return (
                            <li key={t.id} className="listItem">
                              <div>
                                <strong>
                                  {germanDate} {t.uhrzeitVon} bis{" "}
                                  {t.uhrzeitBis}
                                </strong>
                                <div style={{ marginTop: 4 }}>
                                  {t.status === "abgesagt" ? (
                                    <span className="badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                                      abgesagt{(t.cancelFee ?? 0) > 0 ? ` · ${euro(t.cancelFee ?? 0)} Absagegebühr` : ""}
                                    </span>
                                  ) : (
                                    <span className="badge badgeOk">
                                      durchgeführt
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="muted"
                                  style={{ marginTop: 4 }}
                                >
                                  Spieler: {t.isTenniscamp ? "Tenniscamp" : sp}
                                </div>
                                <div className="muted">
                                  Tarif: {ta}
                                </div>
                                {showTrainerInfo && (
                                  <>
                                    <div className="muted">
                                      Trainer: {trainerName}, Honorar:{" "}
                                      {honorarBadge}
                                      {typeof trainerHonorarAnpassungen[t.id] === "number" && (
                                        <span style={{ marginLeft: 6, color: "#f59e0b", fontSize: 12 }}>(manuell angepasst)</span>
                                      )}
                                    </div>
                                    <div className="muted">
                                      Differenz (Schülerzahlung − Honorar):{" "}
                                      {euro(differenz)}
                                    </div>
                                    {!isTrainer && abrechnungTab === "trainer" && honorarAnpassungEdit?.trainingId === t.id ? (
                                      <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                        <span style={{ fontSize: 12 }}>Honorar (€):</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          style={{ width: 80, fontSize: 12, padding: "2px 6px" }}
                                          value={honorarAnpassungEdit.value}
                                          onChange={(e) => setHonorarAnpassungEdit({ trainingId: t.id, value: e.target.value })}
                                        />
                                        <button
                                          className="btn micro"
                                          style={{ fontSize: 11 }}
                                          onClick={() => {
                                            const val = parseFloat(honorarAnpassungEdit.value.replace(",", "."));
                                            if (!isNaN(val) && val >= 0) {
                                              setTrainerHonorarAnpassungen((prev) => ({ ...prev, [t.id]: round2(val) }));
                                            }
                                            setHonorarAnpassungEdit(null);
                                          }}
                                        >
                                          Speichern
                                        </button>
                                        <button
                                          className="btn micro btnGhost"
                                          style={{ fontSize: 11 }}
                                          onClick={() => setHonorarAnpassungEdit(null)}
                                        >
                                          Abbrechen
                                        </button>
                                        {typeof trainerHonorarAnpassungen[t.id] === "number" && (
                                          <button
                                            className="btn micro btnGhost"
                                            style={{ fontSize: 11, color: "#ef4444" }}
                                            onClick={() => {
                                              setTrainerHonorarAnpassungen((prev) => {
                                                const next = { ...prev };
                                                delete next[t.id];
                                                return next;
                                              });
                                              setHonorarAnpassungEdit(null);
                                            }}
                                          >
                                            Zurücksetzen
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      !isTrainer && abrechnungTab === "trainer" && (
                                        <button
                                          className="btn micro btnGhost"
                                          style={{ fontSize: 11, marginTop: 4 }}
                                          onClick={() => setHonorarAnpassungEdit({ trainingId: t.id, value: String(honorarNum) })}
                                        >
                                          Honorar anpassen
                                        </button>
                                      )
                                    )}
                                  </>
                                )}
                                {!showTrainerInfo && (
                                  <div className="muted">
                                    Trainer: {trainerName}
                                  </div>
                                )}
                                {t.notiz ? (
                                  <div className="muted">{t.notiz}</div>
                                ) : null}
                                {t.serieId ? (
                                  <div className="muted">
                                    Serie: {t.serieId.slice(0, 8)}
                                  </div>
                                ) : null}
                                {t.barBezahlt && (
                                  <div className="muted">Bar bezahlt</div>
                                )}
                              </div>
                              <div className="smallActions">
                                {showTrainerInfo &&
                                  abrechnungTab === "trainer" && (
                                    <span
                                      className={
                                        trainerPaid
                                          ? "badge badgeOk"
                                          : "badge"
                                      }
                                      style={{
                                        cursor: "pointer",
                                        backgroundColor: trainerPaid
                                          ? "#22c55e1a"
                                          : "#fee2e2",
                                        color: trainerPaid
                                          ? "#166534"
                                          : "#991b1b",
                                      }}
                                      onClick={() =>
                                        toggleTrainerPaid(t.id)
                                      }
                                    >
                                      {trainerPaid
                                        ? "Honorar abgerechnet"
                                        : "Honorar offen"}
                                    </span>
                                  )}
                                <button
                                  className="btn micro"
                                  style={{
                                    backgroundColor: "#8b5cf6",
                                    borderColor: "#8b5cf6",
                                  }}
                                  onClick={() => toggleBarBezahlt(t.id)}
                                >
                                  {t.barBezahlt
                                    ? "Barzahlung zurücknehmen"
                                    : "Bar bezahlt"}
                                </button>
                                <button
                                  className="btn micro btnGhost"
                                  onClick={() => fillTrainingFromSelected(t)}
                                >
                                  Bearbeiten
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
              </div>
            )}

            {tab === "weiteres" && !isTrainer && (
              <div className="card">
                <h2>Weiteres</h2>

                {/* Sub-Tabs */}
                <div className="tabs" style={{ marginBottom: 20 }}>
                  <button
                    className={`tabBtn ${weiteresTabs === "vertretung" ? "tabBtnActive" : ""}`}
                    onClick={() => setWeiteresTabs("vertretung")}
                  >
                    Vertretung
                  </button>
                  <button
                    className={`tabBtn ${weiteresTabs === "spontan" ? "tabBtnActive" : ""}`}
                    onClick={() => setWeiteresTabs("spontan")}
                  >
                    Spontan
                  </button>
                  <button
                    className={`tabBtn ${weiteresTabs === "rechner" ? "tabBtnActive" : ""}`}
                    onClick={() => setWeiteresTabs("rechner")}
                  >
                    Sascha-Rechner
                  </button>
                </div>

                {/* Vertretung Tab */}
                {weiteresTabs === "vertretung" && (
                  <>
                    {/* Moderne Tabellen-Übersicht aller Vertretungen */}
                    {vertretungen.length > 0 ? (
                      <div style={{ marginBottom: 24 }}>
                        {(() => {
                          // Gruppiere nach fehlendem Trainer (nur zukünftige Trainings)
                          const jetzt = new Date();
                          const groupedByTrainer = vertretungen.reduce((acc, v) => {
                            const training = trainings.find((t) => t.id === v.trainingId);
                            if (!training) return acc;
                            // Abgesagte Trainings ausblenden
                            if (training.status === "abgesagt") return acc;
                            // Vergangene Trainings ausblenden (basierend auf Endzeit)
                            const trainingsEnde = new Date(`${training.datum}T${training.uhrzeitBis}:00`);
                            if (trainingsEnde <= jetzt) return acc;
                            const trainerId = training.trainerId || defaultTrainerId;
                            if (!acc[trainerId]) acc[trainerId] = [];
                            acc[trainerId].push({ vertretung: v, training });
                            return acc;
                          }, {} as Record<string, { vertretung: Vertretung; training: Training }[]>);

                          const trainerEntries = Object.entries(groupedByTrainer);
                          if (trainerEntries.length === 0) {
                            return (
                              <div style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                color: "var(--text-muted)"
                              }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                                <div>Keine offenen Vertretungen</div>
                              </div>
                            );
                          }

                          return trainerEntries
                            .sort(([, a], [, b]) => {
                              const dateA = a[0]?.training.datum || "";
                              const dateB = b[0]?.training.datum || "";
                              return dateA.localeCompare(dateB);
                            })
                            .map(([trainerId, items]) => {
                              const trainerName = trainerById.get(trainerId)?.name || "Unbekannt";

                              // Sortiere nach Datum und Zeit
                              const sortedItems = [...items].sort((a, b) => {
                                const dateComp = a.training.datum.localeCompare(b.training.datum);
                                if (dateComp !== 0) return dateComp;
                                return a.training.uhrzeitVon.localeCompare(b.training.uhrzeitVon);
                              });

                              const isCollapsed = !expandedVertretungTrainer.includes(trainerId);

                              // Gruppiere nach Datum
                              const groupedByDate = sortedItems.reduce((acc, item) => {
                                const datum = item.training.datum;
                                if (!acc[datum]) acc[datum] = [];
                                acc[datum].push(item);
                                return acc;
                              }, {} as Record<string, typeof sortedItems>);

                              const uniqueDates = Object.keys(groupedByDate).length;

                              // Berechne offene Tage (mindestens ein Training ohne Vertretungstrainer)
                              const openDates = Object.entries(groupedByDate).filter(([, dateItems]) => {
                                return dateItems.some(item => !item.vertretung.vertretungTrainerId);
                              }).length;

                              return (
                                <div key={trainerId} style={{ marginBottom: 20 }}>
                                  <div
                                    onClick={() => {
                                      setExpandedVertretungTrainer(prev =>
                                        prev.includes(trainerId)
                                          ? prev.filter(id => id !== trainerId)
                                          : [...prev, trainerId]
                                      );
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      marginBottom: isCollapsed ? 0 : 12,
                                      padding: "10px 14px",
                                      background: openDates > 0
                                        ? "linear-gradient(135deg, #ef4444 0%, #f97316 100%)"
                                        : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                                      borderRadius: isCollapsed ? "var(--radius-md)" : "var(--radius-md) var(--radius-md) 0 0",
                                      color: "white",
                                      cursor: "pointer",
                                      userSelect: "none"
                                    }}
                                  >
                                    <span style={{
                                      fontSize: 14,
                                      transition: "transform 0.2s",
                                      transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)"
                                    }}>▼</span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 600, fontSize: 15 }}>{trainerName} fehlt</div>
                                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                                        {uniqueDates} Tag{uniqueDates !== 1 ? "e" : ""} betroffen
                                        {openDates > 0
                                          ? ` • ${openDates} offen`
                                          : " • alle gedeckt ✓"}
                                      </div>
                                    </div>
                                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                                      {isCollapsed ? "Aufklappen" : "Zuklappen"}
                                    </span>
                                  </div>

                                  {!isCollapsed && <div style={{ overflowX: "auto" }}>
                                    {Object.entries(groupedByDate)
                                      .sort(([a], [b]) => a.localeCompare(b))
                                      .map(([datum, dateItems]) => {
                                        const d = new Date(datum + "T12:00:00");
                                        const dayNames = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
                                        const formattedDate = `${dayNames[d.getDay()]}, ${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;

                                        return (
                                          <div key={datum} style={{ marginBottom: 12 }}>
                                            {/* Datum Header */}
                                            <div style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              padding: "8px 12px",
                                              background: "var(--bg-card)",
                                              borderRadius: "var(--radius-sm) var(--radius-sm) 0 0",
                                              borderBottom: "2px solid #f97316",
                                              fontWeight: 600,
                                              fontSize: 14
                                            }}>
                                              {formattedDate}
                                              <span style={{
                                                marginLeft: "auto",
                                                fontSize: 12,
                                                color: "var(--text-muted)",
                                                fontWeight: 400
                                              }}>
                                                {dateItems.length} Training{dateItems.length !== 1 ? "s" : ""}
                                              </span>
                                            </div>

                                            {/* Trainings als Unterzeilen */}
                                            <div style={{
                                              background: "var(--bg-inset)",
                                              borderRadius: "0 0 var(--radius-sm) var(--radius-sm)",
                                              overflow: "hidden"
                                            }}>
                                              {dateItems.map(({ vertretung: v, training: t }, idx) => {
                                                const spielerNames = t.spielerIds
                                                  .map((id) => getSpielerDisplayName(id))
                                                  .join(", ");
                                                const isOffen = !v.vertretungTrainerId;

                                                return (
                                                  <div
                                                    key={v.trainingId}
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 12,
                                                      padding: "10px 12px",
                                                      borderTop: idx > 0 ? "1px solid var(--border)" : "none",
                                                      fontSize: 13
                                                    }}
                                                  >
                                                    <div style={{
                                                      minWidth: 90,
                                                      fontWeight: 600,
                                                      color: "var(--text)"
                                                    }}>
                                                      {t.uhrzeitVon}–{t.uhrzeitBis}
                                                    </div>
                                                    <div style={{
                                                      flex: 1,
                                                      color: "var(--text-muted)",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap"
                                                    }} title={spielerNames}>
                                                      {spielerNames}
                                                    </div>
                                                    <select
                                                      value={v.vertretungTrainerId ?? ""}
                                                      onChange={(e) => {
                                                        const newId = e.target.value;
                                                        const oldId = v.vertretungTrainerId;

                                                        // Wenn ein neuer Vertretungstrainer zugewiesen wird
                                                        if (newId && newId !== oldId && t.spielerIds.length > 0) {
                                                          setVertretungNotifyDialog({
                                                            trainingId: v.trainingId,
                                                            newTrainerId: newId,
                                                            training: t
                                                          });
                                                        } else {
                                                          setVertretungen((prev) => {
                                                            const filtered = prev.filter((vt) => vt.trainingId !== v.trainingId);
                                                            return [...filtered, { trainingId: v.trainingId, vertretungTrainerId: newId || undefined }];
                                                          });
                                                        }
                                                      }}
                                                      style={{
                                                        minWidth: 130,
                                                        fontSize: 13,
                                                        padding: "6px 10px",
                                                        borderRadius: 6,
                                                        border: `2px solid ${isOffen ? "#f97316" : "#22c55e"}`,
                                                        background: isOffen ? "rgba(249, 115, 22, 0.08)" : "rgba(34, 197, 94, 0.08)",
                                                        color: isOffen ? "#ea580c" : "#16a34a",
                                                        fontWeight: 600,
                                                        cursor: "pointer"
                                                      }}
                                                    >
                                                      <option value="">⚠ Offen</option>
                                                      {trainers
                                                        .filter((tr) => tr.id !== trainerId)
                                                        .map((tr) => (
                                                          <option key={tr.id} value={tr.id}>
                                                            ✓ {tr.name}
                                                          </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                      style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        padding: 4,
                                                        fontSize: 18,
                                                        color: "var(--text-muted)",
                                                        lineHeight: 1,
                                                        borderRadius: 4
                                                      }}
                                                      title="Vertretung entfernen"
                                                      onClick={() => setVertretungen((prev) => prev.filter((vt) => vt.trainingId !== v.trainingId))}
                                                    >
                                                      ×
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>}
                                </div>
                              );
                            });
                        })()}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: "center",
                        padding: 40,
                        background: "var(--bg-inset)",
                        borderRadius: "var(--radius-md)",
                        marginBottom: 20
                      }}>
                        <div style={{ color: "var(--text-muted)" }}>Keine Vertretungen eingetragen</div>
                      </div>
                    )}

                    {/* Neue Vertretung hinzufügen */}
                    <div style={{
                      background: "var(--bg-inset)",
                      borderRadius: "var(--radius-md)",
                      padding: 16
                    }}>
                      <h3 style={{ marginBottom: 12, fontSize: 15 }}>Neue Vertretung planen</h3>

                      <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
                        <div className="field" style={{ flex: "1 1 180px", minWidth: 0 }}>
                          <label>Trainer fehlt</label>
                          <select
                            value={vertretungTrainerId}
                            onChange={(e) => {
                              setVertretungTrainerId(e.target.value);
                              setVertretungDaten([]);
                              setVertretungDatumPreview("");
                              setVertretungVon("");
                              setVertretungBis("");
                            }}
                          >
                            <option value="">-- wählen --</option>
                            {trainers.map((tr) => (
                              <option key={tr.id} value={tr.id}>
                                {tr.name} {tr.nachname || ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {vertretungTrainerId && (
                          <div className="field" style={{ flex: "0 0 auto" }}>
                            <label>Modus</label>
                            <div style={{ display: "flex", gap: 0, borderRadius: "var(--radius-sm)", overflow: "hidden", border: "1px solid var(--border)" }}>
                              <button
                                type="button"
                                onClick={() => setVertretungModus("einzeln")}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: 13,
                                  border: "none",
                                  cursor: "pointer",
                                  background: vertretungModus === "einzeln" ? "var(--primary)" : "var(--bg-card)",
                                  color: vertretungModus === "einzeln" ? "white" : "var(--text)"
                                }}
                              >
                                Einzeln
                              </button>
                              <button
                                type="button"
                                onClick={() => setVertretungModus("zeitraum")}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: 13,
                                  border: "none",
                                  borderLeft: "1px solid var(--border)",
                                  cursor: "pointer",
                                  background: vertretungModus === "zeitraum" ? "var(--primary)" : "var(--bg-card)",
                                  color: vertretungModus === "zeitraum" ? "white" : "var(--text)"
                                }}
                              >
                                Zeitraum
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Einzeldatum-Modus */}
                      {vertretungTrainerId && vertretungModus === "einzeln" && (
                        <div style={{ marginTop: 12 }}>
                          <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div className="field" style={{ flex: "1 1 150px", minWidth: 0 }}>
                              <label>Datum auswählen</label>
                              <input
                                type="date"
                                value={vertretungDatumPreview}
                                onChange={(e) => {
                                  const datum = e.target.value;
                                  if (datum && /^\d{4}-\d{2}-\d{2}$/.test(datum)) {
                                    if (!vertretungDaten.includes(datum) && !vertretungPendingDates.includes(datum)) {
                                      setVertretungPendingDates(prev => [...prev, datum].sort());
                                    }
                                    setVertretungDatumPreview("");
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Ausgewählte Daten anzeigen */}
                          {vertretungPendingDates.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <label style={{ fontSize: 13, marginBottom: 8, display: "block" }}>
                                Ausgewählte Tage ({vertretungPendingDates.length}):
                              </label>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                                {vertretungPendingDates.map(datum => {
                                  const d = new Date(datum + "T12:00:00");
                                  const formatted = `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
                                  return (
                                    <span
                                      key={datum}
                                      style={{
                                        background: "var(--primary)",
                                        color: "white",
                                        padding: "4px 10px",
                                        borderRadius: 16,
                                        fontSize: 13,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6
                                      }}
                                    >
                                      {formatted}
                                      <button
                                        type="button"
                                        onClick={() => setVertretungPendingDates(prev => prev.filter(d => d !== datum))}
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          color: "white",
                                          cursor: "pointer",
                                          padding: 0,
                                          fontSize: 16,
                                          lineHeight: 1
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  className="btn"
                                  style={{ background: "#22c55e", borderColor: "#22c55e" }}
                                  onClick={() => {
                                    // Alle pending dates hinzufügen
                                    vertretungPendingDates.forEach(datum => {
                                      if (!vertretungDaten.includes(datum)) {
                                        setVertretungDaten(prev => [...prev, datum].sort());
                                        // Trainings als offen markieren
                                        const dayTrainings = trainings.filter(
                                          (t) => t.datum === datum && (t.trainerId || defaultTrainerId) === vertretungTrainerId && t.status !== "abgesagt"
                                        );
                                        if (dayTrainings.length > 0) {
                                          setVertretungen((prev) => {
                                            const newVertretungen = [...prev];
                                            dayTrainings.forEach((t) => {
                                              if (!newVertretungen.some((v) => v.trainingId === t.id)) {
                                                newVertretungen.push({ trainingId: t.id });
                                              }
                                            });
                                            return newVertretungen;
                                          });
                                        }
                                      }
                                    });
                                    setVertretungPendingDates([]);
                                  }}
                                >
                                  {vertretungPendingDates.length} Tag{vertretungPendingDates.length !== 1 ? "e" : ""} hinzufügen
                                </button>
                                <button
                                  className="btn btnGhost"
                                  onClick={() => setVertretungPendingDates([])}
                                >
                                  Auswahl leeren
                                </button>
                              </div>
                            </div>
                          )}

                          {vertretungPendingDates.length === 0 && (
                            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                              Wähle einen oder mehrere Tage aus und bestätige dann.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Zeitraum-Modus */}
                      {vertretungTrainerId && vertretungModus === "zeitraum" && (
                        <div className="row" style={{ gap: 12, flexWrap: "wrap", marginTop: 12, alignItems: "flex-end" }}>
                          <div className="field" style={{ flex: "1 1 140px", minWidth: 0 }}>
                            <label>Von</label>
                            <input
                              type="date"
                              value={vertretungVon}
                              onChange={(e) => setVertretungVon(e.target.value)}
                            />
                          </div>
                          <div className="field" style={{ flex: "1 1 140px", minWidth: 0 }}>
                            <label>Bis</label>
                            <input
                              type="date"
                              value={vertretungBis}
                              min={vertretungVon}
                              onChange={(e) => setVertretungBis(e.target.value)}
                            />
                          </div>
                          {vertretungVon && vertretungBis && vertretungVon <= vertretungBis && (
                            <div className="field" style={{ flex: "0 0 auto" }}>
                              <button
                                className="btn"
                                style={{ background: "#22c55e", borderColor: "#22c55e" }}
                                onClick={() => {
                                  // Alle Trainings des Trainers im Zeitraum als "offen" markieren
                                  const rangeTrainings = trainings.filter(
                                    (t) => t.datum >= vertretungVon && t.datum <= vertretungBis && (t.trainerId || defaultTrainerId) === vertretungTrainerId && t.status !== "abgesagt"
                                  );
                                  if (rangeTrainings.length > 0) {
                                    // Daten für Anzeige sammeln
                                    const datenImZeitraum = Array.from(new Set(rangeTrainings.map(t => t.datum))).sort();
                                    setVertretungDaten(prev => Array.from(new Set([...prev, ...datenImZeitraum])).sort());

                                    setVertretungen((prev) => {
                                      const newVertretungen = [...prev];
                                      rangeTrainings.forEach((t) => {
                                        if (!newVertretungen.some((v) => v.trainingId === t.id)) {
                                          newVertretungen.push({ trainingId: t.id });
                                        }
                                      });
                                      return newVertretungen;
                                    });
                                  }
                                  setVertretungVon("");
                                  setVertretungBis("");
                                }}
                              >
                                Zeitraum hinzufügen ({trainings.filter(
                                  (t) => t.datum >= vertretungVon && t.datum <= vertretungBis && (t.trainerId || defaultTrainerId) === vertretungTrainerId
                                ).length} Trainings)
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {(() => {
                        // Nur Daten anzeigen, wo noch nicht alle Trainings beendet sind
                        const jetzt = new Date();
                        const zukuenftigeDaten = vertretungDaten.filter(datum => {
                          // Finde alle Trainings dieses Trainers an diesem Tag
                          const dayTrainings = vertretungTrainerId
                            ? trainings.filter(t => t.datum === datum && (t.trainerId || defaultTrainerId) === vertretungTrainerId)
                            : trainings.filter(t => t.datum === datum);
                          if (dayTrainings.length === 0) {
                            // Keine Trainings = Datum nur anzeigen wenn heute oder später
                            return datum >= todayISO();
                          }
                          // Prüfe ob das letzte Training noch nicht beendet ist
                          const letztesEnde = dayTrainings
                            .map(t => new Date(`${t.datum}T${t.uhrzeitBis}:00`))
                            .reduce((a, b) => a > b ? a : b);
                          return letztesEnde > jetzt;
                        });

                        return zukuenftigeDaten.length > 0 && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                            {zukuenftigeDaten.map((datum) => {
                              const d = new Date(datum + "T12:00:00");
                              const formatted = `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
                              return (
                                <span
                                  key={datum}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    background: "var(--bg-card)",
                                    padding: "3px 8px",
                                    borderRadius: "var(--radius-md)",
                                    fontSize: 12,
                                    border: "1px solid var(--border)"
                                  }}
                                >
                                  {formatted}
                                  <button
                                    type="button"
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: 0,
                                      fontSize: 13,
                                      color: "var(--text-muted)",
                                      lineHeight: 1
                                    }}
                                    onClick={() => setVertretungDaten(vertretungDaten.filter((dd) => dd !== datum))}
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Trainings und Vertretungen zuweisen */}
                      {(() => {
                        // Nur Daten anzeigen, wo noch nicht alle Trainings beendet sind
                        const jetzt = new Date();
                        const zukuenftigeDaten = vertretungDaten.filter(datum => {
                          const dayTrainings = vertretungTrainerId
                            ? trainings.filter(t => t.datum === datum && (t.trainerId || defaultTrainerId) === vertretungTrainerId && t.status !== "abgesagt")
                            : trainings.filter(t => t.datum === datum && t.status !== "abgesagt");
                          if (dayTrainings.length === 0) {
                            return datum >= todayISO();
                          }
                          const letztesEnde = dayTrainings
                            .map(t => new Date(`${t.datum}T${t.uhrzeitBis}:00`))
                            .reduce((a, b) => a > b ? a : b);
                          return letztesEnde > jetzt;
                        });

                        return vertretungTrainerId && zukuenftigeDaten.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            {zukuenftigeDaten.map((datum) => {
                            const d = new Date(datum + "T12:00:00");
                            const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
                            const formatted = `${dayNames[d.getDay()]}, ${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
                            const dayTrainings = trainings
                              .filter(
                                (t) => t.datum === datum && (t.trainerId || defaultTrainerId) === vertretungTrainerId && t.status !== "abgesagt"
                              )
                              .sort((a, b) => a.uhrzeitVon.localeCompare(b.uhrzeitVon));

                            if (dayTrainings.length === 0) {
                              return (
                                <div key={datum} style={{
                                  padding: "8px 12px",
                                  background: "var(--bg-card)",
                                  borderRadius: "var(--radius-sm)",
                                  marginBottom: 8,
                                  fontSize: 13
                                }}>
                                  <strong>{formatted}</strong>
                                  <span className="muted"> – Keine Trainings</span>
                                </div>
                              );
                            }

                            return (
                              <div key={datum} style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{formatted}</div>
                                {dayTrainings.map((t) => {
                                  const spielerNames = t.spielerIds
                                    .map((id) => getSpielerDisplayName(id))
                                    .join(", ");
                                  const existingVertretung = vertretungen.find((v) => v.trainingId === t.id);

                                  const hatVertretung = existingVertretung?.vertretungTrainerId;
                                  return (
                                    <div
                                      key={t.id}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "8px 12px",
                                        background: hatVertretung
                                          ? "rgba(34, 197, 94, 0.1)"
                                          : existingVertretung
                                          ? "rgba(249, 115, 22, 0.1)"
                                          : "var(--bg-card)",
                                        borderRadius: "var(--radius-sm)",
                                        marginBottom: 4,
                                        borderLeft: `3px solid ${hatVertretung ? "#22c55e" : existingVertretung ? "#f97316" : "#cbd5e1"}`
                                      }}
                                    >
                                      <div style={{ minWidth: 70, fontSize: 13, fontWeight: 500 }}>
                                        {t.uhrzeitVon}-{t.uhrzeitBis}
                                      </div>
                                      <div style={{ flex: 1, fontSize: 12, color: "var(--text-muted)" }}>
                                        {spielerNames}
                                      </div>
                                      <select
                                        value={existingVertretung?.vertretungTrainerId ?? ""}
                                        onChange={(e) => {
                                          const newId = e.target.value;
                                          const oldId = existingVertretung?.vertretungTrainerId;

                                          // Wenn ein neuer Vertretungstrainer zugewiesen wird (nicht "offen" und nicht gleicher Trainer)
                                          if (newId && newId !== oldId && t.spielerIds.length > 0) {
                                            setVertretungNotifyDialog({
                                              trainingId: t.id,
                                              newTrainerId: newId,
                                              training: t
                                            });
                                          } else {
                                            setVertretungen((prev) => {
                                              const filtered = prev.filter((v) => v.trainingId !== t.id);
                                              return [...filtered, { trainingId: t.id, vertretungTrainerId: newId || undefined }];
                                            });
                                          }
                                        }}
                                        style={{ width: 130, fontSize: 13 }}
                                      >
                                        <option value="">-- offen --</option>
                                        {trainers
                                          .filter((tr) => tr.id !== vertretungTrainerId)
                                          .map((tr) => (
                                            <option key={tr.id} value={tr.id}>
                                              {tr.name}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}

                {/* Spontan Tab */}
                {weiteresTabs === "spontan" && (() => {
                  const heute = todayISO();
                  const vergangene = spontaneStunden.filter((s) => s.datum < heute);
                  const aktuelle = spontaneStunden.filter((s) => s.datum >= heute);
                  const offenCount = aktuelle.filter((s) => s.status !== "gebucht").length;
                  const gebuchtCount = aktuelle.filter((s) => s.status === "gebucht").length;
                  const onlineCount = aktuelle.filter((s) => s.status !== "gebucht" && s.veroeffentlicht).length;
                  const basis = spontanZeigeVergangene ? spontaneStunden : aktuelle;
                  const gefiltert = basis.filter((s) =>
                    spontanFilter === "alle"
                      ? true
                      : spontanFilter === "gebucht"
                      ? s.status === "gebucht"
                      : s.status !== "gebucht"
                  );
                  const sortiert = [...gefiltert].sort((a, b) =>
                    (a.datum + a.uhrzeitVon).localeCompare(b.datum + b.uhrzeitVon)
                  );
                  const gruppen: { datum: string; slots: SpontaneStunde[] }[] = [];
                  sortiert.forEach((s) => {
                    const letzte = gruppen[gruppen.length - 1];
                    if (letzte && letzte.datum === s.datum) letzte.slots.push(s);
                    else gruppen.push({ datum: s.datum, slots: [s] });
                  });

                  return (
                    <>
                      {/* Kopfzeile: Titel + Aktion */}
                      <div className="spontanToolbar">
                        <div>
                          <h2 style={{ margin: 0 }}>Spontane Stunden</h2>
                          <p className="muted" style={{ margin: "4px 0 0" }}>
                            Freie Termine anbieten und Buchungen verwalten
                          </p>
                        </div>
                        <button
                          className={`btn ${spontanFormOpen ? "btnGhost" : ""}`}
                          onClick={() => {
                            resetSpontanForm();
                            setSpontanFormOpen((o) => !o);
                          }}
                        >
                          {spontanFormOpen ? "Abbrechen" : "+ Neue Stunde"}
                        </button>
                      </div>

                      {/* Statistik-Kacheln */}
                      <div className="spontanStats">
                        <div className="spontanStat spontanStatOffen">
                          <span className="spontanStatNum">{offenCount}</span>
                          <span className="spontanStatLabel">Offen</span>
                        </div>
                        <div className="spontanStat spontanStatGebucht">
                          <span className="spontanStatNum">{gebuchtCount}</span>
                          <span className="spontanStatLabel">Gebucht</span>
                        </div>
                        <div className="spontanStat spontanStatOnline">
                          <span className="spontanStatNum">{onlineCount}</span>
                          <span className="spontanStatLabel">Online sichtbar</span>
                        </div>
                      </div>

                      {/* Einklappbares Formular */}
                      {spontanFormOpen && (
                        <div className="card cardInset spontanFormCard">
                          <div className="formSectionTitle" style={{ marginTop: 0 }}>
                            {editingSpontanId ? "Stunde bearbeiten" : "Neue Stunde anlegen"}
                          </div>

                          <div className="row">
                            <div className="field">
                              <label>Datum</label>
                              <input
                                type="date"
                                value={spontanDatum}
                                onChange={(e) => setSpontanDatum(e.target.value)}
                              />
                            </div>
                            <div className="field">
                              <label>Von</label>
                              <input
                                type="time"
                                value={spontanVon}
                                onChange={(e) => setSpontanVon(e.target.value)}
                              />
                            </div>
                            <div className="field">
                              <label>Bis</label>
                              <input
                                type="time"
                                value={spontanBis}
                                onChange={(e) => setSpontanBis(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="field">
                              <label>Trainer</label>
                              <select
                                value={spontanTrainerId}
                                onChange={(e) => setSpontanTrainerId(e.target.value)}
                              >
                                <option value="">Trainer auswählen...</option>
                                {trainers.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label>Anlage</label>
                              <select
                                value={spontanAnlage}
                                onChange={(e) => setSpontanAnlage(e.target.value as "Wedding" | "Britz")}
                              >
                                <option value="Wedding">Wedding</option>
                                <option value="Britz">Britz</option>
                              </select>
                            </div>
                          </div>

                          <div className="row">
                            <div className="field">
                              <label>Tarif (optional)</label>
                              <select
                                value={spontanTarifId}
                                onChange={(e) => setSpontanTarifId(e.target.value)}
                              >
                                <option value="">Kein Tarif / Individuell</option>
                                {tarife.filter((t) => !t.archiviert || t.id === spontanTarifId).map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name} ({euro(t.preisProStunde)}/h)
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label>Preis/Stunde (optional)</label>
                              <input
                                type="number"
                                placeholder="z.B. 50"
                                value={spontanCustomPreis}
                                onChange={(e) =>
                                  setSpontanCustomPreis(
                                    e.target.value === "" ? "" : Number(e.target.value)
                                  )
                                }
                              />
                            </div>
                          </div>

                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 14 }}>
                            <input
                              type="checkbox"
                              checked={spontanVeroeffentlicht}
                              onChange={(e) => setSpontanVeroeffentlicht(e.target.checked)}
                            />
                            Sofort auf Wedding-Seite veröffentlichen
                          </label>

                          <div className="row" style={{ marginTop: 18 }}>
                            {editingSpontanId ? (
                              <>
                                <button className="btn" onClick={updateSpontaneStunde}>
                                  Speichern
                                </button>
                                <button
                                  className="btn btnGhost"
                                  onClick={() => {
                                    resetSpontanForm();
                                    setSpontanFormOpen(false);
                                  }}
                                >
                                  Abbrechen
                                </button>
                              </>
                            ) : (
                              <button className="btn" onClick={createSpontaneStunde}>
                                Stunde erstellen
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Liste */}
                      {loadingSpontaneStunden ? (
                        <p className="muted" style={{ marginTop: 20 }}>Lade...</p>
                      ) : spontaneStunden.length === 0 ? (
                        <div className="spontanEmpty">
                          <p style={{ margin: 0, fontWeight: 600 }}>Noch keine spontanen Stunden</p>
                          <p className="muted" style={{ margin: "6px 0 0" }}>
                            Lege oben eine neue Stunde an, um freie Termine anzubieten.
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Segmented-Filter */}
                          <div className="spontanFilterBar">
                            <div className="segmented">
                              {([
                                ["alle", "Alle", aktuelle.length],
                                ["offen", "Offen", offenCount],
                                ["gebucht", "Gebucht", gebuchtCount],
                              ] as const).map(([key, label, count]) => (
                                <button
                                  key={key}
                                  className={`segItem ${spontanFilter === key ? "segItemActive" : ""}`}
                                  onClick={() => setSpontanFilter(key)}
                                >
                                  {label}
                                  <span className="segCount">{count}</span>
                                </button>
                              ))}
                            </div>
                            {vergangene.length > 0 && (
                              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }} className="muted">
                                <input
                                  type="checkbox"
                                  checked={spontanZeigeVergangene}
                                  onChange={(e) => setSpontanZeigeVergangene(e.target.checked)}
                                />
                                Vergangene ({vergangene.length})
                              </label>
                            )}
                          </div>

                          {gruppen.length === 0 ? (
                            <p className="muted">Keine Stunden für diesen Filter.</p>
                          ) : (
                            gruppen.map((g) => {
                              const datumHeader = new Date(g.datum + "T12:00:00").toLocaleDateString("de-DE", {
                                weekday: "long",
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              });
                              const istVergangen = g.datum < heute;
                              return (
                                <div key={g.datum} className="spontanGroup" style={{ opacity: istVergangen ? 0.55 : 1 }}>
                                  <div className="spontanGroupHeader">
                                    <span>{datumHeader}</span>
                                    <span className="muted" style={{ fontWeight: 500 }}>
                                      {g.slots.length} {g.slots.length === 1 ? "Stunde" : "Stunden"}
                                    </span>
                                  </div>
                                  <div className="spontanSlots">
                                    {g.slots.map((s) => {
                                      const trainer = trainers.find((t) => t.id === s.trainerId);
                                      const tarif = tarife.find((t) => t.id === s.tarifId);
                                      const preis = s.customPreisProStunde ?? tarif?.preisProStunde;
                                      const gebucht = s.status === "gebucht";
                                      return (
                                        <div
                                          key={s.id}
                                          className="spontanSlot"
                                          style={{
                                            borderLeft: `4px solid ${gebucht ? "#22c55e" : "#3b82f6"}`,
                                            background: gebucht ? "rgba(34,197,94,0.06)" : "var(--bg-card)",
                                          }}
                                        >
                                          <div className="spontanSlotMain">
                                            <strong className="spontanSlotTime">
                                              {s.uhrzeitVon.slice(0, 5)}–{s.uhrzeitBis.slice(0, 5)}
                                            </strong>
                                            <span style={{ fontSize: 14, fontWeight: 500 }}>{trainer?.name ?? "–"}</span>
                                            <span
                                              className="spontanChip"
                                              style={{ background: s.anlage === "Britz" ? "#f59e0b" : "#2563eb", color: "white" }}
                                            >
                                              {s.anlage === "Britz" ? "Britz" : "Wedding"}
                                            </span>
                                            {preis !== undefined && (
                                              <span className="muted" style={{ fontSize: 13 }}>{euro(preis)}/h</span>
                                            )}
                                            {gebucht ? (
                                              <span className="spontanChip" style={{ background: "#22c55e", color: "white" }}>
                                                Gebucht
                                              </span>
                                            ) : s.veroeffentlicht ? (
                                              <span className="spontanChip" style={{ background: "rgba(37,99,235,0.12)", color: "#1d4ed8" }}>
                                                Online
                                              </span>
                                            ) : (
                                              <span className="spontanChip" style={{ background: "var(--bg-inset)", color: "var(--text-muted)" }}>
                                                Offline
                                              </span>
                                            )}

                                            <div className="spontanSlotActions">
                                              {gebucht && s.buchung && (() => {
                                                const linkedTraining = s.trainingId ? trainings.find((t) => t.id === s.trainingId) : null;
                                                const spielerUebernommen = linkedTraining && linkedTraining.spielerIds.length > 0 && s.buchung && (() => {
                                                  const buchungsEmail = s.buchung!.email.toLowerCase();
                                                  return linkedTraining.spielerIds.some((id) => {
                                                    const sp = spielerById.get(id);
                                                    return sp?.kontaktEmail?.toLowerCase() === buchungsEmail;
                                                  });
                                                })();
                                                if (spielerUebernommen) {
                                                  return (
                                                    <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>✓ Übernommen</span>
                                                  );
                                                }
                                                return (
                                                  <button
                                                    className="btn micro"
                                                    style={{ background: "#eab308" }}
                                                    onClick={() => uebernehmenSpontanBuchung(s)}
                                                  >
                                                    In Kalender übernehmen
                                                  </button>
                                                );
                                              })()}
                                              {gebucht && s.buchung && (
                                                <button
                                                  className="btn micro"
                                                  style={{ background: "#f59e0b" }}
                                                  onClick={() => freigebenSpontaneStunde(s)}
                                                  title="Buchung stornieren, Termin wieder zur Buchung freigeben und den Spieler per E-Mail informieren"
                                                >
                                                  Freigeben
                                                </button>
                                              )}
                                              {!gebucht && (
                                                <button
                                                  className={`btn micro ${s.veroeffentlicht ? "btnPrimary" : "btnGhost"}`}
                                                  onClick={() => toggleSpontanVeroeffentlicht(s.id, s.veroeffentlicht)}
                                                  title={s.veroeffentlicht ? "Auf der Website sichtbar - Klick zum Verstecken" : "Nicht auf der Website - Klick zum Veröffentlichen"}
                                                >
                                                  {s.veroeffentlicht ? "Verstecken" : "Veröffentlichen"}
                                                </button>
                                              )}
                                              {!gebucht && (
                                                <button
                                                  className="btn micro btnGhost"
                                                  onClick={() => startEditSpontaneStunde(s)}
                                                >
                                                  Bearbeiten
                                                </button>
                                              )}
                                              <button
                                                className="btn micro btnWarn"
                                                onClick={() => deleteSpontaneStunde(s.id)}
                                              >
                                                Löschen
                                              </button>
                                            </div>
                                          </div>
                                          {s.buchung && (
                                            <div className="spontanBuchung">
                                              <strong>{s.buchung.name}</strong> · {s.buchung.email}
                                              {s.buchung.telefon && <> · Tel: {s.buchung.telefon}</>}
                                              <span className="muted"> · gebucht am {new Date(s.buchung.gebuchtAm).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
                                              {s.buchung.hinweis && (
                                                <div style={{ marginTop: 3 }}>{s.buchung.hinweis}</div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </>
                      )}
                    </>
                  );
                })()}

                {/* Sascha-Rechner Tab */}
                {weiteresTabs === "rechner" && (
                  <>
                    <p className="muted" style={{ marginBottom: 16 }}>
                      Abrechnung Sascha · Artur · Zlatan. Zusatz-Stunden über die Gehaltsstunden werden mit 10 EUR/h verteilt, Bar-Stunden mit 10 EUR/h von Zlatans Anteil abgezogen.
                    </p>

                    {rechnerStep === 1 && (
                      <div>
                        <div className="row">
                          <div className="field">
                            <label>Monat (übernimmt Werte automatisch)</label>
                            <input
                              type="month"
                              value={rechnerMonat}
                              onChange={(e) => {
                                const m = e.target.value;
                                setRechnerMonat(m);
                                if (!m) return;
                                const saschaTrainerId = trainers.find(
                                  (tr) => tr.name.trim().toLowerCase() === "sascha"
                                )?.id;
                                if (!saschaTrainerId) return;

                                let nichtBarMin = 0;
                                let barMin = 0;
                                trainings.forEach((t) => {
                                  if (!t.datum.startsWith(m)) return;
                                  if (t.status !== "durchgefuehrt") return;
                                  if (t.isPrivat) return;
                                  const vertretung = vertretungen.find(
                                    (v) => v.trainingId === t.id
                                  );
                                  const tid =
                                    vertretung?.vertretungTrainerId ||
                                    t.trainerId ||
                                    defaultTrainerId;
                                  if (tid !== saschaTrainerId) return;
                                  const planned = durationMin(t.uhrzeitVon, t.uhrzeitBis);
                                  const actual =
                                    t.actualMinutes && t.actualMinutes > 0 && t.actualMinutes < planned
                                      ? t.actualMinutes
                                      : planned;
                                  if (t.barBezahlt) barMin += actual;
                                  else nichtBarMin += actual;
                                });

                                const zKey = `${m}__${saschaTrainerId}`;
                                const zList = trainerZuschlaege[zKey] ?? [];
                                const zSum = round2(
                                  zList.reduce((acc, z) => acc + z.betrag, 0)
                                );

                                setRechnerNichtBarStunden(String(round2(nichtBarMin / 60)));
                                setRechnerBarStunden(String(round2(barMin / 60)));
                                setRechnerZuschlag(zSum !== 0 ? String(zSum) : "");
                              }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="field">
                            <label>Gehalt (EUR)</label>
                            <input
                              type="number"
                              value={rechnerGehalt}
                              onChange={(e) => setRechnerGehalt(e.target.value)}
                              placeholder="z.B. 1000"
                              step="0.01"
                            />
                          </div>
                          <div className="field">
                            <label>Stunden für Gehalt</label>
                            <input
                              type="number"
                              value={rechnerStundenFuerGehalt}
                              onChange={(e) => setRechnerStundenFuerGehalt(e.target.value)}
                              placeholder="z.B. 50"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="field">
                            <label>Nicht-Bar Stunden gesamt</label>
                            <input
                              type="number"
                              value={rechnerNichtBarStunden}
                              onChange={(e) => setRechnerNichtBarStunden(e.target.value)}
                              placeholder="z.B. 60"
                              step="0.01"
                            />
                          </div>
                          <div className="field">
                            <label>Bar Stunden gesamt</label>
                            <input
                              type="number"
                              value={rechnerBarStunden}
                              onChange={(e) => setRechnerBarStunden(e.target.value)}
                              placeholder="z.B. 10"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="field">
                            <label>Zuschlag Sascha (EUR, optional)</label>
                            <input
                              type="number"
                              value={rechnerZuschlag}
                              onChange={(e) => setRechnerZuschlag(e.target.value)}
                              placeholder="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <button
                            className="btn"
                            onClick={() => {
                              const g = parseFloat(rechnerGehalt) || 0;
                              const sfg = parseFloat(rechnerStundenFuerGehalt) || 0;
                              const nbs = parseFloat(rechnerNichtBarStunden) || 0;
                              const bs = parseFloat(rechnerBarStunden) || 0;
                              const zuschlag = parseFloat(rechnerZuschlag) || 0;
                              if (!(g > 0 && sfg > 0 && nbs > 0)) return;
                              const effectiveGehalt = g + zuschlag;
                              const zusatzStunden = nbs - sfg;
                              const arturAnteil = effectiveGehalt / 2 + zusatzStunden * 10;
                              const zlatanBrutto = effectiveGehalt / 2 + zusatzStunden * 10;
                              const zlatanAbgabe = bs * 10;
                              const zlatanNetto = zlatanBrutto - zlatanAbgabe;
                              setRechnerResults({
                                zusatzStunden,
                                effectiveGehalt,
                                zuschlag,
                                arturAnteil,
                                zlatanBrutto,
                                zlatanAbgabe,
                                zlatanNetto,
                                abhebeBetrag: arturAnteil + zlatanNetto,
                                ausgleichZahlung: arturAnteil - zlatanNetto,
                              });
                              setRechnerStep(2);
                            }}
                          >
                            Berechnen
                          </button>
                        </div>
                      </div>
                    )}

                    {rechnerStep === 2 && (
                      <div>
                        <p style={{ fontSize: 16, marginBottom: 16 }}>
                          Soll das Geld vom Konto abgehoben und bar an Sascha gezahlt werden?
                        </p>
                        <div style={{ display: "flex", gap: 12 }}>
                          <button
                            className="btn"
                            onClick={() => {
                              setRechnerBarAuszahlung(true);
                              setRechnerStep(3);
                            }}
                          >
                            Ja
                          </button>
                          <button
                            className="btn btnGhost"
                            onClick={() => {
                              setRechnerBarAuszahlung(false);
                              setRechnerStep(3);
                            }}
                          >
                            Nein
                          </button>
                        </div>
                      </div>
                    )}

                    {rechnerStep === 3 && rechnerResults && (
                      <div>
                        <div className="card cardInset" style={{ marginBottom: 12 }}>
                          <h3 style={{ marginTop: 0 }}>Standard-Abrechnung</h3>
                          {rechnerResults.zuschlag > 0 && (
                            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                              Inkl. Zuschlag Sascha: {euro(rechnerResults.zuschlag)} (gesamtes Gehalt: {euro(rechnerResults.effectiveGehalt)})
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                            <span>Zahlung Artur</span>
                            <strong>{euro(rechnerResults.arturAnteil)}</strong>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                            <span>Guthaben bei Zlatan</span>
                            <strong>{euro(rechnerResults.zlatanNetto)}</strong>
                          </div>
                          <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                            Zlatans Anteil wurde um {euro(rechnerResults.zlatanAbgabe)} aus den Bar-Stunden gekürzt.
                          </p>
                        </div>

                        {rechnerBarAuszahlung ? (
                          <div className="card cardInset" style={{ marginBottom: 12 }}>
                            <h3 style={{ marginTop: 0 }}>Bargeld-Logistik (Konto-Auszahlung)</h3>
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                              <span>Betrag für Sascha abheben</span>
                              <strong>{euro(rechnerResults.abhebeBetrag)}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                              <span>Zusatzzahlung an Zlatan</span>
                              <strong>{euro(rechnerResults.ausgleichZahlung)}</strong>
                            </div>
                            <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                              Diese Ausgleichszahlung ist nötig, damit Zlatan seine Provision aus den Bar-Stunden erhält, da Sascha bereits den vollen Bar-Betrag von den Kunden eingesteckt hat.
                            </p>
                          </div>
                        ) : (
                          <div className="card cardInset" style={{ marginBottom: 12 }}>
                            <p style={{ margin: 0 }}>
                              Zlatan verrechnet den Betrag mit Saschas alten Schulden.
                            </p>
                          </div>
                        )}

                        <div className="card cardInset" style={{ marginBottom: 12 }}>
                          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Berechnungsdetails</div>
                          <div style={{ fontSize: 13 }}>Zusatz-Stunden: {rechnerResults.zusatzStunden}</div>
                          <div style={{ fontSize: 13 }}>Zlatan Brutto: {euro(rechnerResults.zlatanBrutto)}</div>
                          <div style={{ fontSize: 13 }}>Zlatan Abgabe (Bar): {euro(rechnerResults.zlatanAbgabe)}</div>
                        </div>

                        <button
                          className="btn btnGhost"
                          onClick={() => {
                            setRechnerGehalt("220");
                            setRechnerStundenFuerGehalt("11");
                            setRechnerNichtBarStunden("");
                            setRechnerBarStunden("");
                            setRechnerZuschlag("");
                            setRechnerMonat("");
                            setRechnerStep(1);
                            setRechnerBarAuszahlung(null);
                            setRechnerResults(null);
                          }}
                        >
                          Neue Berechnung
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>



      {payConfirm && (
        <div className="modalOverlay">
          <div className="modalCard">
            <div className="modalHeader">
              <div className="modalPill">Zahlung bestätigen</div>
              <h3>
                {payConfirm.spielerName} ·{" "}
                {formatMonthLabel(payConfirm.monat)}
              </h3>
              <p className="muted">
                Dieser Betrag wird als bezahlt markiert, Du kannst es später
                wieder auf offen stellen.
              </p>
            </div>
            <div className="modalSummary">
              <span className="muted">Betrag</span>
              <strong>{euro(payConfirm.amount)}</strong>
            </div>
            <div className="modalActions">
              <button className="btn btnGhost" onClick={closePayConfirm}>
                Abbrechen
              </button>
              <button className="btn" onClick={confirmPay}>
                Ja, als bezahlt markieren
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelTrainingDialog && (
        <div className="modalOverlay">
          <div className="modalCard" style={{ maxWidth: 500, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div className="modalHeader">
              <div className="modalPill">
                {cancelTrainingDialog.action === 'delete' ? 'Gruppentraining löschen' : 'Gruppentraining absagen'}
              </div>
              <h3>{cancelTrainingDialog.action === 'delete' ? 'Erstattung buchen?' : 'Abrechnung anpassen?'}</h3>
              <p className="muted">
                {cancelTrainingDialog.trainings.length === 1
                  ? `Dieses Training hat ${cancelTrainingDialog.trainings[0].spielerIds.length} Spieler.`
                  : `${cancelTrainingDialog.trainings.length} Gruppentrainings betroffen.`}
                {" "}
                {cancelTrainingDialog.action === 'delete'
                  ? "Wieviel soll den Spielern für diesen Termin erstattet werden?"
                  : "Möchtest du die Abrechnung für alle Spieler anpassen (z.B. wegen Regenausfall)?"}
              </p>
            </div>

            <div style={{ padding: "0 20px", marginBottom: 16, overflowY: "auto", flex: 1 }}>
              <div style={{ marginBottom: 12 }}>
                <strong>Betroffene Trainings ({cancelTrainingDialog.trainings.length}):</strong>
                <ul style={{ margin: "8px 0", paddingLeft: 20, fontSize: 13, maxHeight: 200, overflowY: "auto" }}>
                  {cancelTrainingDialog.trainings.map((t) => {
                    const spielerNamen = t.spielerIds
                      .map((id) => getSpielerFullName(id))
                      .join(", ");
                    return (
                      <li key={t.id} style={{ marginBottom: 4 }}>
                        {formatShort(t.datum)} {t.uhrzeitVon}-{t.uhrzeitBis}: {spielerNamen}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div style={{ marginBottom: 12 }}>
                <strong>Betroffene Spieler:</strong>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {(() => {
                    const allSpielerIds = new Set<string>();
                    cancelTrainingDialog.trainings.forEach((t) => {
                      t.spielerIds.forEach((id) => allSpielerIds.add(id));
                    });
                    return Array.from(allSpielerIds).map((id) => (
                      <span key={id} className="pill" style={{ fontSize: 12 }}>
                        {getSpielerFullName(id)}
                      </span>
                    ));
                  })()}
                </div>
              </div>

              <div className="field" style={{ marginTop: 16 }}>
                <label>Erstattung pro Spieler (in EUR)</label>
                {cancelTrainingDialog.fullPricePerTraining != null && cancelTrainingDialog.fullPricePerTraining > 0 && (
                  <div style={{ marginBottom: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span className="pill" style={{ fontSize: 12 }}>
                      Wert pro Training: <strong>{euro(cancelTrainingDialog.fullPricePerTraining)}</strong>
                    </span>
                    <button
                      className="btn micro btnGhost"
                      style={{ fontSize: 11 }}
                      onClick={() => setCancelAdjustmentAmount(String(round2(cancelTrainingDialog.fullPricePerTraining! / 2)))}
                    >
                      50 % → −{euro(round2(cancelTrainingDialog.fullPricePerTraining / 2))}
                    </button>
                    <button
                      className="btn micro btnGhost"
                      style={{ fontSize: 11 }}
                      onClick={() => setCancelAdjustmentAmount(String(cancelTrainingDialog.fullPricePerTraining!))}
                    >
                      100 % → −{euro(cancelTrainingDialog.fullPricePerTraining)}
                    </button>
                    <button
                      className="btn micro btnGhost"
                      style={{ fontSize: 11 }}
                      onClick={() => setCancelAdjustmentAmount("0")}
                    >
                      Keine Erstattung
                    </button>
                  </div>
                )}
                <input
                  type="number"
                  value={cancelAdjustmentAmount}
                  onChange={(e) => setCancelAdjustmentAmount(e.target.value)}
                  placeholder="z.B. 7.50"
                  min="0"
                  step="0.01"
                  style={{ maxWidth: 150 }}
                />
                <div className="muted" style={{ marginTop: 4 }}>
                  100 % = voll erstatten (Spieler zahlt für diesen Termin nichts), 50 % = halbe Stunde erstatten, 0 = keine Erstattung (Spieler zahlt den vollen Betrag).
                </div>
                {(cancelTrainingDialog.fullPricePerTraining ?? 0) > 0 && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Bleibt berechnet:{" "}
                    <strong>
                      {euro(round2(Math.max(0, (cancelTrainingDialog.fullPricePerTraining ?? 0) - (parseFloat(cancelAdjustmentAmount) || 0))))}
                    </strong>{" "}
                    pro Spieler
                  </div>
                )}
              </div>
            </div>

            <div className="modalActions" style={{ flexDirection: "column", gap: 8 }}>
              <button
                className="btn"
                onClick={() => handleCancelDialogConfirm(true)}
                style={{ width: "100%" }}
              >
                Erstattung buchen (−{euro(parseFloat(cancelAdjustmentAmount) || 0)} pro Spieler)
              </button>
              <button
                className="btn btnGhost"
                onClick={() => handleCancelDialogConfirm(false)}
                style={{ width: "100%" }}
              >
                {cancelTrainingDialog.action === 'delete'
                  ? `Ohne Erstattung löschen (${euro(cancelTrainingDialog.fullPricePerTraining ?? 0)} bleiben berechnet)`
                  : "Ohne Berechnung absagen (Termin wird nicht berechnet)"}
              </button>
              <button
                className="btn btnGhost"
                onClick={closeCancelDialog}
                style={{ width: "100%" }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Dialog für Trainer */}
      {invoiceDialog && isTrainer && ownTrainerId && (
        <div className="modalOverlay">
          <div className="modalCard" style={{ maxWidth: 500 }}>
            <div className="modalHeader">
              <div className="modalPill">Rechnung erstellen</div>
              <h3>Rechnung für {abrechnungMonat.slice(5, 7)}/{abrechnungMonat.slice(0, 4)}</h3>
              <p className="muted">
                Erstellen Sie eine Rechnung für Ihre geleisteten Trainerstunden.
              </p>
            </div>

            <div style={{ padding: "0 20px", marginBottom: 16 }}>
              {invoiceError && (
                <div style={{
                  backgroundColor: "#fee2e2",
                  color: "#dc2626",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginBottom: 12,
                  fontSize: 13
                }}>
                  {invoiceError}
                </div>
              )}

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Anzahl der abzurechnenden Stunden *</label>
                <input
                  type="number"
                  value={invoiceDialog.stundenAnzahl}
                  onChange={(e) => setInvoiceDialog({
                    ...invoiceDialog,
                    stundenAnzahl: parseInt(e.target.value) || 0
                  })}
                  min="0"
                  style={{ maxWidth: 150 }}
                />
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Ihre IBAN *</label>
                <input
                  type="text"
                  value={invoiceDialog.iban}
                  onChange={(e) => setInvoiceDialog({
                    ...invoiceDialog,
                    iban: e.target.value
                  })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Ihre Rechnungsadresse *</label>
                <textarea
                  rows={3}
                  value={invoiceDialog.adresse}
                  onChange={(e) => setInvoiceDialog({
                    ...invoiceDialog,
                    adresse: e.target.value
                  })}
                  placeholder={"Max Mustermann\nMusterstraße 123\n12345 Berlin"}
                />
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Steuernummer (optional)</label>
                <input
                  type="text"
                  value={invoiceDialog.ustIdNr}
                  onChange={(e) => setInvoiceDialog({
                    ...invoiceDialog,
                    ustIdNr: e.target.value
                  })}
                  placeholder="123/456/78901"
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={invoiceDialog.kleinunternehmer}
                    onChange={(e) => setInvoiceDialog({
                      ...invoiceDialog,
                      kleinunternehmer: e.target.checked
                    })}
                  />
                  <span>Kleinunternehmerregelung anwenden (keine MwSt.)</span>
                </label>
              </div>
            </div>

            <div className="modalActions">
              <button
                className="btn btnGhost"
                onClick={() => {
                  setInvoiceDialog(null);
                  setInvoiceError("");
                }}
              >
                Abbrechen
              </button>
              <button
                className="btn"
                onClick={() => {
                  // Validierung
                  if (invoiceDialog.stundenAnzahl <= 0) {
                    setInvoiceError("Bitte geben Sie eine gültige Stundenanzahl an.");
                    return;
                  }
                  if (!invoiceDialog.iban.trim()) {
                    setInvoiceError("Bitte geben Sie Ihre IBAN an.");
                    return;
                  }
                  if (!invoiceDialog.adresse.trim()) {
                    setInvoiceError("Bitte geben Sie Ihre Rechnungsadresse an.");
                    return;
                  }

                  // Öffne Vorschau-Dialog
                  const trainerData = trainerById.get(ownTrainerId);
                  const stundensatz = trainerData?.stundensatz ?? 0;
                  const fullName = trainerData?.name + (trainerData?.nachname ? ' ' + trainerData.nachname : '');
                  const now = new Date();
                  const rechnungsnummer = `RG-${now.toISOString().slice(0, 10).replace(/-/g, '')}-${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

                  const berechneterBetrag = invoiceDialog.stundenAnzahl * stundensatz * (invoiceDialog.kleinunternehmer ? 1 : 1.19);
                  setInvoicePreview({
                    rechnungssteller: fullName,
                    adresse: invoiceDialog.adresse,
                    ustIdNr: invoiceDialog.ustIdNr,
                    rechnungsnummer,
                    rechnungsdatum: now.toLocaleDateString('de-DE'),
                    leistungszeitraum: `${abrechnungMonat.slice(5, 7)}/${abrechnungMonat.slice(0, 4)}`,
                    positionBeschreibung: "Trainerstunden",
                    stundenAnzahl: invoiceDialog.stundenAnzahl,
                    preisProStunde: stundensatz,
                    iban: invoiceDialog.iban,
                    kleinunternehmer: invoiceDialog.kleinunternehmer,
                    useCustomTotal: false,
                    customGesamtbetrag: berechneterBetrag,
                  });

                  setInvoiceDialog(null);
                  setInvoiceError("");
                }}
              >
                Rechnung erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Dialog für Trainer */}
      {invoicePreview && isTrainer && ownTrainerId && (
        <div className="modalOverlay">
          <div className="modalCard" style={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modalHeader">
              <div className="modalPill">Rechnungsvorschau</div>
              <h3>Rechnungsvorschau - Anpassungen vornehmen</h3>
              <p className="muted">
                Bearbeiten Sie die Rechnung vor der Erstellung
              </p>
            </div>

            <div style={{ padding: "0 20px", marginBottom: 16 }}>
              {/* Rechnungssteller */}
              <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rechnungssteller</h4>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={invoicePreview.rechnungssteller}
                    onChange={(e) => setInvoicePreview({ ...invoicePreview, rechnungssteller: e.target.value })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label>Adresse</label>
                  <textarea
                    rows={3}
                    value={invoicePreview.adresse}
                    onChange={(e) => setInvoicePreview({ ...invoicePreview, adresse: e.target.value })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                <div className="field">
                  <label>Steuernummer (optional)</label>
                  <input
                    type="text"
                    value={invoicePreview.ustIdNr}
                    onChange={(e) => setInvoicePreview({ ...invoicePreview, ustIdNr: e.target.value })}
                    placeholder="123/456/78901"
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
              </div>

              {/* Rechnungsdaten */}
              <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rechnungsdaten</h4>
                <div className="row">
                  <div className="field">
                    <label>Rechnungsnummer</label>
                    <input
                      type="text"
                      value={invoicePreview.rechnungsnummer}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, rechnungsnummer: e.target.value })}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div className="field">
                    <label>Rechnungsdatum</label>
                    <input
                      type="text"
                      value={invoicePreview.rechnungsdatum}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, rechnungsdatum: e.target.value })}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div className="field">
                    <label>Leistungszeitraum</label>
                    <input
                      type="text"
                      value={invoicePreview.leistungszeitraum}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, leistungszeitraum: e.target.value })}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                </div>
              </div>

              {/* Position */}
              <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Position</h4>
                <div className="row">
                  <div className="field" style={{ flex: 2 }}>
                    <label>Beschreibung</label>
                    <input
                      type="text"
                      value={invoicePreview.positionBeschreibung}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, positionBeschreibung: e.target.value })}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div className="field">
                    <label>Anzahl</label>
                    <input
                      type="number"
                      value={invoicePreview.stundenAnzahl}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, stundenAnzahl: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div className="field">
                    <label>Preis pro Stunde (€)</label>
                    <input
                      type="number"
                      value={invoicePreview.preisProStunde}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, preisProStunde: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                </div>
              </div>

              {/* Berechnete Beträge */}
              <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#e8f4fd', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Beträge</h4>

                {/* Checkbox für manuellen Gesamtbetrag */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={invoicePreview.useCustomTotal}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, useCustomTotal: e.target.checked })}
                    />
                    <span>Manuellen Gesamtbetrag verwenden</span>
                  </label>
                </div>

                {invoicePreview.useCustomTotal && (
                  /* Eingabefeld für manuellen Gesamtbetrag */
                  <div className="field" style={{ marginBottom: 16 }}>
                    <label>Gewünschter Gesamtbetrag (€)</label>
                    <input
                      type="number"
                      value={invoicePreview.customGesamtbetrag}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, customGesamtbetrag: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      style={{ backgroundColor: '#fff', fontSize: 18, fontWeight: 'bold' }}
                    />
                  </div>
                )}

                {/* Beträge-Tabelle */}
                {(() => {
                  const positionSumme = invoicePreview.stundenAnzahl * invoicePreview.preisProStunde;
                  const berechneterBetrag = positionSumme * (invoicePreview.kleinunternehmer ? 1 : 1.19);
                  const korrektur = invoicePreview.useCustomTotal
                    ? invoicePreview.customGesamtbetrag - berechneterBetrag
                    : 0;
                  const zwischensummeMitKorrektur = positionSumme + korrektur;
                  const mwstBetrag = invoicePreview.kleinunternehmer ? 0 : zwischensummeMitKorrektur * 0.19;
                  const endBetrag = zwischensummeMitKorrektur + mwstBetrag;

                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px 0' }}>Gesamt Position:</td>
                          <td style={{ padding: '8px 0', textAlign: 'right' }}>
                            {invoicePreview.stundenAnzahl} × {invoicePreview.preisProStunde.toFixed(2).replace('.', ',')} € = <strong>{positionSumme.toFixed(2).replace('.', ',')} €</strong>
                          </td>
                        </tr>
                        {invoicePreview.useCustomTotal && korrektur !== 0 && (
                          <tr style={{ color: korrektur < 0 ? '#c00' : '#060' }}>
                            <td style={{ padding: '8px 0' }}>Korrektur:</td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              <strong>{korrektur >= 0 ? '+' : ''}{korrektur.toFixed(2).replace('.', ',')} €</strong>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ padding: '8px 0' }}>Zwischensumme:</td>
                          <td style={{ padding: '8px 0', textAlign: 'right' }}><strong>{zwischensummeMitKorrektur.toFixed(2).replace('.', ',')} €</strong></td>
                        </tr>
                        {!invoicePreview.kleinunternehmer && (
                          <tr>
                            <td style={{ padding: '8px 0' }}>MwSt. 19%:</td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}><strong>{mwstBetrag.toFixed(2).replace('.', ',')} €</strong></td>
                          </tr>
                        )}
                        <tr style={{ borderTop: '2px solid #333' }}>
                          <td style={{ padding: '12px 0', fontSize: 16, fontWeight: 'bold' }}>Gesamtbetrag:</td>
                          <td style={{ padding: '12px 0', textAlign: 'right', fontSize: 18, fontWeight: 'bold' }}>
                            {endBetrag.toFixed(2).replace('.', ',')} €
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* Kleinunternehmer & IBAN */}
              <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zahlungsinformationen</h4>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={invoicePreview.kleinunternehmer}
                      onChange={(e) => setInvoicePreview({ ...invoicePreview, kleinunternehmer: e.target.checked })}
                    />
                    <span>Kleinunternehmerregelung anwenden (keine MwSt.)</span>
                  </label>
                </div>
                <div className="field">
                  <label>IBAN</label>
                  <input
                    type="text"
                    value={invoicePreview.iban}
                    onChange={(e) => setInvoicePreview({ ...invoicePreview, iban: e.target.value })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
              </div>
            </div>

            <div className="modalActions">
              <button
                className="btn btnGhost"
                onClick={() => {
                  // Zurück zum ersten Dialog
                  setInvoiceDialog({
                    stundenAnzahl: invoicePreview.stundenAnzahl,
                    iban: invoicePreview.iban,
                    adresse: invoicePreview.adresse,
                    ustIdNr: invoicePreview.ustIdNr,
                    kleinunternehmer: invoicePreview.kleinunternehmer,
                  });
                  setInvoicePreview(null);
                }}
              >
                Zurück
              </button>
              <button
                className="btn"
                onClick={() => {
                  // Speichere Einstellungen in localStorage
                  localStorage.setItem(TRAINER_INVOICE_SETTINGS_KEY, JSON.stringify({
                    iban: invoicePreview.iban,
                    ustIdNr: invoicePreview.ustIdNr,
                    kleinunternehmer: invoicePreview.kleinunternehmer,
                  }));

                  // Generiere finale Rechnung
                  const invoiceHTML = generateFinalInvoiceHTML(invoicePreview);

                  const win = window.open('', '_blank');
                  if (win) {
                    win.document.write(invoiceHTML);
                    win.document.close();
                    setTimeout(() => win.print(), 200);
                  }

                  setInvoicePreview(null);
                }}
              >
                PDF erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vertretung Benachrichtigungs-Dialog */}
      {vertretungNotifyDialog && (() => {
        const training = vertretungNotifyDialog.training;
        const newTrainer = trainers.find(t => t.id === vertretungNotifyDialog.newTrainerId);
        if (!training) return null;

        const [y, m, d] = training.datum.split("-");
        const germanDate = d && m && y ? `${d}.${m}.${y}` : training.datum;

        const originalTrainer = trainers.find(t =>
          t.id === (training.trainerId || defaultTrainerId)
        );

        const recipients = training.spielerIds
          .map(id => spielerById.get(id))
          .filter((s): s is Spieler => !!s && !!s.kontaktEmail);

        const emailSubject = `Traineränderung für Ihr Training am ${germanDate}`;

        const trainerTelMap: Record<string, string> = {
          sascha: "0157 73584431",
          konsti: "0173 7255920",
          marc: "01511 6227911",
          jesper: "0172 3104772",
          henri: "0163 9757063",
          leon: "0176 62029303",
          ramon: "0176 56793299",
          max: "0178 6393814",
          nico: "0176 21402241",
        };
        const newTrainerName = newTrainer?.name ?? "der Vertretungstrainer";
        const newTrainerTel = newTrainer ? trainerTelMap[newTrainer.name.trim().toLowerCase()] ?? "" : "";
        const originalTrainerName = originalTrainer?.name ?? "deinem Trainer";

        // Funktion für personalisierten E-Mail-Text
        const getEmailBody = (playerName: string) => `Hallo ${playerName},

am ${germanDate} wird dein reguläres Training mit ${originalTrainerName} von ${newTrainerName} vertreten.

${newTrainerTel ? `Bei Fragen erreichst du ${newTrainerName} unter ${newTrainerTel}.` : `Bei Fragen erreichst du ${newTrainerName} direkt.`}

Viel Spaß beim Training!

Sportliche Grüße
Tennisschule A bis Z`;

        // Vorschau mit erstem Empfänger oder Platzhalter
        const previewName = recipients.length > 0 ? getFullName(recipients[0]) : "[Name]";
        const emailBodyPreview = getEmailBody(previewName);

        return (
          <div className="modalOverlay">
            <div className="modalCard" style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}>
              <div className="modalHeader">
                <div className="modalPill">Vertretung zugewiesen</div>
                <h3>Spieler per E-Mail benachrichtigen?</h3>
                <p className="muted">
                  {newTrainer?.name ?? "Unbekannt"} übernimmt das Training am {germanDate} um {training.uhrzeitVon} - {training.uhrzeitBis} Uhr.
                </p>
              </div>

              <div style={{ padding: "0 20px", marginBottom: 16 }}>
                {/* Empfänger */}
                <div style={{ marginBottom: 16 }}>
                  <strong>Empfänger ({recipients.length}):</strong>
                  {recipients.length > 0 ? (
                    <div style={{
                      marginTop: 8,
                      padding: 12,
                      background: "var(--bg-inset)",
                      borderRadius: 6,
                      fontSize: 13
                    }}>
                      {recipients.map((s, idx) => (
                        <div key={s.id} style={{ marginBottom: idx < recipients.length - 1 ? 4 : 0 }}>
                          {getFullName(s)} <span className="muted">({s.kontaktEmail})</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "var(--warning)", fontSize: 13, marginTop: 8 }}>
                      Keiner der Spieler hat eine E-Mail-Adresse hinterlegt.
                    </p>
                  )}

                  {/* Spieler ohne E-Mail */}
                  {training.spielerIds.filter(id => !spielerById.get(id)?.kontaktEmail).length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--warning)" }}>
                      Ohne E-Mail:{" "}
                      {training.spielerIds
                        .filter(id => !spielerById.get(id)?.kontaktEmail)
                        .map(id => getSpielerFullName(id))
                        .join(", ")}
                    </div>
                  )}
                </div>

                {/* E-Mail Vorschau */}
                {recipients.length > 0 && (
                  <div>
                    <strong>E-Mail Vorschau:</strong>
                    <div style={{
                      marginTop: 8,
                      padding: 16,
                      background: "var(--bg-inset)",
                      borderRadius: 6,
                      border: "1px solid var(--border)"
                    }}>
                      <div style={{ marginBottom: 12 }}>
                        <span className="muted" style={{ fontSize: 12 }}>Betreff:</span>
                        <div style={{ fontWeight: 600 }}>{emailSubject}</div>
                      </div>
                      <div>
                        <span className="muted" style={{ fontSize: 12 }}>Nachricht:</span>
                        <pre style={{
                          margin: "4px 0 0 0",
                          fontFamily: "inherit",
                          fontSize: 13,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.5
                        }}>{emailBodyPreview}</pre>
                        {recipients.length > 1 && (
                          <div className="muted" style={{ marginTop: 8, fontSize: 12, fontStyle: "italic" }}>
                            Jeder Spieler erhält eine personalisierte E-Mail mit seinem Namen.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modalActions">
                <button
                  className="btn btnGhost"
                  disabled={vertretungNotifySending}
                  onClick={() => {
                    // Nur speichern ohne E-Mail
                    setVertretungen((prev) => {
                      const filtered = prev.filter((v) => v.trainingId !== vertretungNotifyDialog.trainingId);
                      return [...filtered, {
                        trainingId: vertretungNotifyDialog.trainingId,
                        vertretungTrainerId: vertretungNotifyDialog.newTrainerId
                      }];
                    });
                    setVertretungNotifyDialog(null);
                  }}
                >
                  Ohne E-Mail speichern
                </button>
                <button
                  className="btn"
                  disabled={vertretungNotifySending || recipients.length === 0}
                  onClick={async () => {
                    if (recipients.length === 0) {
                      setVertretungen((prev) => {
                        const filtered = prev.filter((v) => v.trainingId !== vertretungNotifyDialog.trainingId);
                        return [...filtered, {
                          trainingId: vertretungNotifyDialog.trainingId,
                          vertretungTrainerId: vertretungNotifyDialog.newTrainerId
                        }];
                      });
                      setVertretungNotifyDialog(null);
                      return;
                    }

                    setVertretungNotifySending(true);

                    try {
                      // Sende individuelle E-Mails für jeden Empfänger
                      let successCount = 0;
                      const errors: string[] = [];

                      for (const recipient of recipients) {
                        try {
                          const recipientEmails = [
                            recipient.kontaktEmail,
                            ...(recipient.zusaetzlicheEmails || [])
                          ].filter(Boolean) as string[];

                          const response = await fetch("/api/send-newsletter", {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                              to: recipientEmails,
                              subject: emailSubject,
                              body: getEmailBody(getFullName(recipient)),
                              html: getEmailBody(getFullName(recipient)).replace(/\n/g, "<br>"),
                              fromName: "Tennisschule A bis Z"
                            })
                          });

                          if (!response.ok) {
                            const error = await response.json();
                            errors.push(`${getFullName(recipient)}: ${error.message || "Fehler"}`);
                          } else {
                            successCount++;
                          }
                        } catch (err) {
                          errors.push(`${getFullName(recipient)}: ${err instanceof Error ? err.message : "Fehler"}`);
                        }
                      }

                      // Vertretung speichern (unabhängig vom E-Mail-Erfolg)
                      setVertretungen((prev) => {
                        const filtered = prev.filter((v) => v.trainingId !== vertretungNotifyDialog.trainingId);
                        return [...filtered, {
                          trainingId: vertretungNotifyDialog.trainingId,
                          vertretungTrainerId: vertretungNotifyDialog.newTrainerId
                        }];
                      });

                      if (errors.length > 0) {
                        alert(`${successCount} von ${recipients.length} E-Mails erfolgreich gesendet.\n\nFehler:\n${errors.join("\n")}`);
                      } else {
                        alert(`E-Mail wurde erfolgreich an ${successCount} Spieler gesendet.`);
                      }
                  } catch (err) {
                    alert(`Fehler beim Senden: ${err instanceof Error ? err.message : "Unbekannter Fehler"}\n\nDie Vertretung wird trotzdem gespeichert.`);
                    // Trotzdem speichern
                    setVertretungen((prev) => {
                      const filtered = prev.filter((v) => v.trainingId !== vertretungNotifyDialog.trainingId);
                      return [...filtered, {
                        trainingId: vertretungNotifyDialog.trainingId,
                        vertretungTrainerId: vertretungNotifyDialog.newTrainerId
                      }];
                    });
                  } finally {
                    setVertretungNotifySending(false);
                    setVertretungNotifyDialog(null);
                  }
                }}
              >
                {vertretungNotifySending ? "Wird gesendet..." : "Ja, per E-Mail informieren"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Absage Benachrichtigungs-Dialog */}
      {cancelNotifyDialog && (() => {
        const trainingsToCancel = cancelNotifyDialog.trainings;

        // Alle Spieler mit E-Mail sammeln (dedupliziert)
        const recipientMap = new Map<string, Spieler>();
        trainingsToCancel.forEach((t) => {
          t.spielerIds.forEach((id) => {
            const s = spielerById.get(id);
            if (s && s.kontaktEmail) {
              recipientMap.set(s.id, s);
            }
          });
        });
        const recipients = Array.from(recipientMap.values());

        // Trainingsdetails für E-Mail
        const trainingDetails = trainingsToCancel.map((t) => {
          const [y, m, d] = t.datum.split("-");
          const germanDate = d && m && y ? `${d}.${m}.${y}` : t.datum;
          const trainer = trainers.find((tr) => tr.id === (t.trainerId || defaultTrainerId));
          return {
            datum: germanDate,
            uhrzeit: `${t.uhrzeitVon} - ${t.uhrzeitBis}`,
            trainer: trainer?.name ?? "Unbekannt",
            spieler: t.spielerIds.map((id) => getSpielerFullName(id)).join(", ")
          };
        });

        // Personalisierte E-Mail erstellen (Name wird beim Senden ersetzt)
        const getPersonalizedBody = (body: string, playerName: string) => {
          return body.replace(/\[Name\]/g, playerName);
        };

        return (
          <div className="modalOverlay">
            <div className="modalCard" style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}>
              <div className="modalHeader">
                <div className="modalPill" style={{ background: "#ef4444" }}>Absage</div>
                <h3>Spieler per E-Mail benachrichtigen?</h3>
                <p className="muted">
                  {trainingsToCancel.length === 1
                    ? `Das Training am ${trainingDetails[0].datum} um ${trainingDetails[0].uhrzeit} wird abgesagt.`
                    : `${trainingsToCancel.length} Trainings werden abgesagt.`}
                </p>
              </div>

              <div style={{ padding: "0 20px", marginBottom: 16 }}>
                {/* Empfänger */}
                <div style={{ marginBottom: 16 }}>
                  <strong>Empfänger ({recipients.length}):</strong>
                  {recipients.length > 0 ? (
                    <div style={{
                      marginTop: 8,
                      padding: 12,
                      background: "var(--bg-inset)",
                      borderRadius: 6,
                      fontSize: 13,
                      maxHeight: 100,
                      overflowY: "auto"
                    }}>
                      {recipients.map((s, idx) => (
                        <div key={s.id} style={{ marginBottom: idx < recipients.length - 1 ? 4 : 0 }}>
                          {getFullName(s)} <span className="muted">({s.kontaktEmail})</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "var(--warning)", fontSize: 13, marginTop: 8 }}>
                      Keiner der Spieler hat eine E-Mail-Adresse hinterlegt.
                    </p>
                  )}
                </div>

                {/* E-Mail bearbeiten */}
                {recipients.length > 0 && (
                  <div>
                    <strong>E-Mail bearbeiten:</strong>
                    <p className="muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                      Verwende [Name] als Platzhalter für den Spielernamen.
                    </p>

                    <div className="field" style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 13 }}>Vorlage</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {(() => {
                          const dateSubject = trainingDetails.length === 1
                            ? `Das Training am ${trainingDetails[0].datum}`
                            : `Die Trainings am ${trainingDetails.map((t) => t.datum).join(", ")}`;
                          const verb = trainingDetails.length === 1 ? "kann" : "können";
                          return (
                            <>
                              <button
                                type="button"
                                className="btn btnGhost"
                                style={{ fontSize: 12, padding: "6px 12px" }}
                                onClick={() => setCancelNotifyBody(`Hallo [Name],

${dateSubject} ${verb} krankheitsbedingt nicht stattfinden. Es tut uns leid für die Kurzfristigkeit. Die Trainingsgebühr wird selbstverständlich nicht berechnet.

Nächste Woche geht es wieder regulär weiter.

Sportliche Grüße
Tennisschule A bis Z`)}
                              >
                                Krankheitsbedingt
                              </button>
                              <button
                                type="button"
                                className="btn btnGhost"
                                style={{ fontSize: 12, padding: "6px 12px" }}
                                onClick={() => setCancelNotifyBody(`Hallo [Name],

${dateSubject} ${verb} wetterbedingt nicht stattfinden.

Nächste Woche geht es wieder regulär weiter.

Sportliche Grüße
Tennisschule A bis Z`)}
                              >
                                Wetterbedingt
                              </button>
                              <button
                                type="button"
                                className="btn btnGhost"
                                style={{ fontSize: 12, padding: "6px 12px" }}
                                onClick={() => setCancelNotifyBody(`Hallo [Name],

${dateSubject} ${trainingDetails.length === 1 ? "findet" : "finden"} aufgrund des Feiertages nicht statt.

Wir wünschen dir und deiner Familie einen schönen Feiertag und erholsame Stunden!

Sportliche Grüße
Tennisschule A bis Z`)}
                              >
                                Feiertagsbedingt
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="field" style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 13 }}>Betreff</label>
                      <input
                        type="text"
                        value={cancelNotifySubject}
                        onChange={(e) => setCancelNotifySubject(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div className="field">
                      <label style={{ fontSize: 13 }}>Nachricht</label>
                      <textarea
                        value={cancelNotifyBody}
                        onChange={(e) => setCancelNotifyBody(e.target.value)}
                        rows={10}
                        style={{
                          width: "100%",
                          fontFamily: "inherit",
                          fontSize: 13,
                          resize: "vertical"
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modalActions">
                <button
                  className="btn btnGhost"
                  disabled={cancelNotifySending}
                  onClick={() => {
                    // Absage ohne E-Mail
                    cancelNotifyDialog.onConfirm();
                    setCancelNotifyDialog(null);
                    setCancelNotifySubject("");
                    setCancelNotifyBody("");
                  }}
                >
                  Ohne E-Mail absagen
                </button>
                <button
                  className="btn"
                  disabled={cancelNotifySending || recipients.length === 0 || !cancelNotifySubject.trim() || !cancelNotifyBody.trim()}
                  onClick={async () => {
                    if (recipients.length === 0) {
                      cancelNotifyDialog.onConfirm();
                      setCancelNotifyDialog(null);
                      return;
                    }

                    setCancelNotifySending(true);
                    try {
                      let successCount = 0;
                      const errors: string[] = [];

                      // Personalisierte E-Mails an jeden Empfänger senden
                      for (const recipient of recipients) {
                        try {
                          const recipientEmails = [
                            recipient.kontaktEmail,
                            ...(recipient.zusaetzlicheEmails || [])
                          ].filter(Boolean) as string[];

                          const response = await fetch("/api/send-newsletter", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              to: recipientEmails,
                              subject: cancelNotifySubject.trim(),
                              body: getPersonalizedBody(cancelNotifyBody.trim(), getFullName(recipient)),
                              html: getPersonalizedBody(cancelNotifyBody.trim(), getFullName(recipient)).replace(/\n/g, "<br>"),
                              fromName: "Tennisschule A bis Z"
                            })
                          });

                          if (!response.ok) {
                            const error = await response.json();
                            errors.push(`${getFullName(recipient)}: ${error.message || "Fehler"}`);
                          } else {
                            successCount++;
                          }
                        } catch (err) {
                          errors.push(`${getFullName(recipient)}: ${err instanceof Error ? err.message : "Fehler"}`);
                        }
                      }

                      // Trainings absagen (unabhängig vom E-Mail-Erfolg)
                      cancelNotifyDialog.onConfirm();

                      if (errors.length > 0) {
                        alert(`${successCount} von ${recipients.length} E-Mails erfolgreich gesendet.\n\nFehler:\n${errors.join("\n")}`);
                      } else {
                        alert(`E-Mail wurde erfolgreich an ${successCount} Spieler gesendet.`);
                      }
                    } catch (err) {
                      alert(`Fehler beim Senden: ${err instanceof Error ? err.message : "Unbekannter Fehler"}\n\nDie Absage wird trotzdem durchgeführt.`);
                      cancelNotifyDialog.onConfirm();
                    } finally {
                      setCancelNotifySending(false);
                      setCancelNotifyDialog(null);
                      setCancelNotifySubject("");
                      setCancelNotifyBody("");
                    }
                  }}
                >
                  {cancelNotifySending ? "Wird gesendet..." : "Ja, per E-Mail informieren"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dialog: Abzug rückgängig machen bei Reaktivierung */}
      {reverseAdjustmentDialog && (() => {
        const training = reverseAdjustmentDialog.training;
        const refundPerPlayer = reverseAdjustmentDialog.refundPerPlayer;
        const [y, m, d] = training.datum.split("-");
        const germanDate = d && m && y ? `${d}.${m}.${y}` : training.datum;
        const spielerNamen = training.spielerIds
          .map((id) => getSpielerFullName(id))
          .join(", ");

        return (
          <div className="modalOverlay">
            <div className="modalCard" style={{ maxWidth: 500 }}>
              <div className="modalHeader">
                <div className="modalPill" style={{ background: "#22c55e" }}>Reaktivierung</div>
                <h3>Erstattung rückgängig machen?</h3>
                <p className="muted">
                  Das Training am {germanDate} um {training.uhrzeitVon} - {training.uhrzeitBis} Uhr wird wieder auf "geplant" gesetzt.
                </p>
              </div>

              <div style={{ padding: "0 20px", marginBottom: 16 }}>
                <p style={{ marginBottom: 12 }}>
                  Bei der Absage wurden <strong>{euro(refundPerPlayer)} pro Spieler</strong> erstattet.
                </p>
                <p>
                  Soll diese Erstattung jetzt wieder rückgängig gemacht werden?
                </p>
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: "var(--bg-inset)",
                  borderRadius: 6,
                  fontSize: 13
                }}>
                  <strong>Betroffene Spieler:</strong><br />
                  {spielerNamen}
                </div>
              </div>

              <div className="modalActions">
                <button
                  className="btn btnGhost"
                  onClick={() => {
                    reverseAdjustmentDialog.onConfirm(false);
                    setReverseAdjustmentDialog(null);
                  }}
                >
                  Nein, Erstattung behalten
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    reverseAdjustmentDialog.onConfirm(true);
                    setReverseAdjustmentDialog(null);
                  }}
                >
                  Ja, +{euro(refundPerPlayer)} zurückbuchen
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Trainingsinfo E-Mail Modal */}
      {showTrainingInfoEmail && (
        <div className="modalOverlay" onClick={() => setShowTrainingInfoEmail(false)}>
          <div
            className="modalCard"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Spieler per E-Mail informieren</h2>
              <button
                onClick={() => setShowTrainingInfoEmail(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 24,
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            <div className="muted" style={{ marginBottom: 16 }}>
              <p style={{ margin: "0 0 8px 0" }}>
                Jeder Spieler erhält eine individuelle E-Mail mit seinem Namen.
              </p>
              <div style={{ margin: 0 }}>
                <span>Empfänger:</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {tSpielerIds
                    .map(id => spielerById.get(id))
                    .filter(s => s?.kontaktEmail)
                    .map(s => {
                      const excluded = trainingInfoExcluded.includes(s!.id);
                      return (
                        <label key={s!.id} style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                          background: excluded ? "var(--bg-subtle, #f3f4f6)" : "var(--primary)",
                          color: excluded ? "var(--text-muted, #9ca3af)" : "#fff",
                          textDecoration: excluded ? "line-through" : "none",
                          opacity: excluded ? 0.6 : 1,
                        }}>
                          <input
                            type="checkbox"
                            checked={!excluded}
                            onChange={() => setTrainingInfoExcluded(prev =>
                              excluded ? prev.filter(id => id !== s!.id) : [...prev, s!.id]
                            )}
                            style={{ display: "none" }}
                          />
                          {getFullName(s!)}
                        </label>
                      );
                    })}
                </div>
                {trainingInfoExcluded.length > 0 && (
                  <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--text-muted)" }}>
                    Klicke auf einen Namen, um ihn wieder hinzuzufügen.
                  </p>
                )}
              </div>
              <p style={{ margin: "8px 0 0 0", fontSize: 12 }}>
                Platzhalter: {"{SPIELERNAME}"} = Vorname, {"{ANDERE_TEILNEHMER}"} = andere Gruppenmitglieder
              </p>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={trainingInfoIncludeSepa}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTrainingInfoIncludeSepa(checked);
                  const sepaLink = tAnlage === "Britz"
                    ? `${window.location.origin}/sepa-britz`
                    : `${window.location.origin}/sepa`;
                  const sepaBlock = `Für die Abrechnung erteile uns bitte vor dem ersten Training ein SEPA-Lastschriftmandat:\n${sepaLink}\n\nSolltest du dies schon in einer vorherigen Saison erledigt haben, so kann dieses wieder benutzt werden und eine neue Erteilung ist nicht nötig.\n\n`;
                  setTrainingInfoEmailBody((prev) => {
                    const withoutSepa = prev.replace(
                      /Für die Abrechnung erteile uns bitte vor dem ersten Training ein SEPA-Lastschriftmandat:[\s\S]*?Solltest du dies schon in einer vorherigen Saison erledigt haben, so kann dieses wieder benutzt werden und eine neue Erteilung ist nicht nötig\.\n+/,
                      ""
                    );
                    if (!checked) return withoutSepa;
                    if (withoutSepa.includes("Bei unsicherem Wetter")) {
                      return withoutSepa.replace("Bei unsicherem Wetter", `${sepaBlock}Bei unsicherem Wetter`);
                    }
                    if (withoutSepa.includes("Solltest du Fragen haben")) {
                      return withoutSepa.replace("Solltest du Fragen haben", `${sepaBlock}Solltest du Fragen haben`);
                    }
                    return withoutSepa.trimEnd() + "\n\n" + sepaBlock.trimEnd();
                  });
                }}
              />
              SEPA-Lastschriftmandat anfordern
            </label>

            {(() => {
              const mitgliedschaftHinweis = "Für die Teilnahme am Tennistraining ist eine Mitgliedschaft Voraussetzung. Kinder sind im ersten Jahr beitragsfrei und füllen lediglich den Antrag auf Probemitgliedschaft aus.";
              const applyMitgliedschaftHinweis = (probe: boolean, mitglied: boolean) => {
                setTrainingInfoEmailBody((prev) => {
                  const withoutHinweis = prev.replace(
                    /Für die Teilnahme am Tennistraining ist eine Mitgliedschaft Voraussetzung\. Kinder sind im ersten Jahr beitragsfrei und füllen lediglich den Antrag auf Probemitgliedschaft aus\.\n+/,
                    ""
                  );
                  if (!probe && !mitglied) return withoutHinweis;
                  const block = `${mitgliedschaftHinweis}\n\n`;
                  if (withoutHinweis.includes("Für die Abrechnung erteile uns bitte")) {
                    return withoutHinweis.replace("Für die Abrechnung erteile uns bitte", `${block}Für die Abrechnung erteile uns bitte`);
                  }
                  if (withoutHinweis.includes("Bei unsicherem Wetter")) {
                    return withoutHinweis.replace("Bei unsicherem Wetter", `${block}Bei unsicherem Wetter`);
                  }
                  if (withoutHinweis.includes("Solltest du Fragen haben")) {
                    return withoutHinweis.replace("Solltest du Fragen haben", `${block}Solltest du Fragen haben`);
                  }
                  return withoutHinweis.trimEnd() + "\n\n" + block.trimEnd();
                });
              };
              // Zusätzlicher Hinweis NUR für die Probemitgliedschaft: Antrag ausgefüllt zurücksenden.
              const probeRuecksendeHinweis = "Bitte fülle den beigefügten Antrag auf Probemitgliedschaft aus und sende ihn ausgefüllt per E-Mail an tennisabisz@gmail.com zurück.";
              const applyProbeRuecksendeHinweis = (probe: boolean) => {
                setTrainingInfoEmailBody((prev) => {
                  const without = prev.replace(
                    /Bitte fülle den beigefügten Antrag auf Probemitgliedschaft aus und sende ihn ausgefüllt per E-Mail an tennisabisz@gmail\.com zurück\.\n+/,
                    ""
                  );
                  if (!probe) return without;
                  const block = `${probeRuecksendeHinweis}\n\n`;
                  if (without.includes("Für die Abrechnung erteile uns bitte")) {
                    return without.replace("Für die Abrechnung erteile uns bitte", `${block}Für die Abrechnung erteile uns bitte`);
                  }
                  if (without.includes("Bei unsicherem Wetter")) {
                    return without.replace("Bei unsicherem Wetter", `${block}Bei unsicherem Wetter`);
                  }
                  if (without.includes("Solltest du Fragen haben")) {
                    return without.replace("Solltest du Fragen haben", `${block}Solltest du Fragen haben`);
                  }
                  return without.trimEnd() + "\n\n" + block.trimEnd();
                });
              };
              return (
                <>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 14, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={trainingInfoIncludeProbe}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setTrainingInfoIncludeProbe(checked);
                        applyMitgliedschaftHinweis(checked, trainingInfoIncludeMitglied);
                        applyProbeRuecksendeHinweis(checked);
                      }}
                    />
                    Antrag auf Probemitgliedschaft (PDF anhängen)
                  </label>

                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 14, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={trainingInfoIncludeMitglied}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setTrainingInfoIncludeMitglied(checked);
                        applyMitgliedschaftHinweis(trainingInfoIncludeProbe, checked);
                      }}
                    />
                    Mitgliedschaft Jugend (PDF anhängen)
                  </label>
                </>
              );
            })()}

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={trainingInfoIncludeErwachsene}
                onChange={(e) => setTrainingInfoIncludeErwachsene(e.target.checked)}
              />
              Mitgliedschaft Erwachsene (PDF anhängen)
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={trainingInfoIncludeBeitragsordnung}
                onChange={(e) => setTrainingInfoIncludeBeitragsordnung(e.target.checked)}
              />
              Beitragsordnung (PDF anhängen)
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={trainingInfoIncludeProbetraining}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTrainingInfoIncludeProbetraining(checked);
                  const probetrainingBlock = `Für das Probetraining ist noch keine Vereinsmitgliedschaft erforderlich. Das SEPA-Lastschriftmandat wird ausschließlich für die Abrechnung des Probetrainings genutzt (Einzeltraining 40 € / Stunde, Gruppentraining 25 € / Stunde). Falls du dich nicht für ein dauerhaftes Training entscheidest, wird das SEPA-Mandat nach der Abbuchung des Probetrainings wieder gelöscht.\n\nFür das Probetraining eignen sich einmalig auch Schuhe mit minimalem Profil. Ein Tennisschläger kann ausgeliehen werden – ausschließlich für das Probetraining.\n\n`;
                  setTrainingInfoEmailBody((prev) => {
                    const withoutBlock = prev.replace(
                      /Für das Probetraining ist noch keine Vereinsmitgliedschaft erforderlich\.[\s\S]*?ausschließlich für das Probetraining\.\n+/,
                      ""
                    );
                    if (!checked) return withoutBlock;
                    if (withoutBlock.includes("Bei unsicherem Wetter")) {
                      return withoutBlock.replace("Bei unsicherem Wetter", `${probetrainingBlock}Bei unsicherem Wetter`);
                    }
                    if (withoutBlock.includes("Solltest du Fragen haben")) {
                      return withoutBlock.replace("Solltest du Fragen haben", `${probetrainingBlock}Solltest du Fragen haben`);
                    }
                    return withoutBlock.trimEnd() + "\n\n" + probetrainingBlock.trimEnd();
                  });
                }}
              />
              Probetraining-Hinweis (Text einfügen)
            </label>

            <div className="field" style={{ marginBottom: 12 }}>
              <label>Betreff</label>
              <input
                value={trainingInfoEmailSubject}
                onChange={(e) => setTrainingInfoEmailSubject(e.target.value)}
                placeholder="E-Mail-Betreff"
              />
            </div>

            <div className="field" style={{ marginBottom: 16 }}>
              <label>Nachricht</label>
              <textarea
                value={trainingInfoEmailBody}
                onChange={(e) => setTrainingInfoEmailBody(e.target.value)}
                rows={12}
                style={{ width: "100%", fontFamily: "inherit", fontSize: 14 }}
                placeholder="E-Mail-Text"
              />
            </div>

            <div className="modalActions" style={{ position: "sticky", bottom: 0, background: "var(--bg-card)", marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--border)", marginBottom: -24, paddingBottom: 24, zIndex: 5 }}>
              <button
                className="btn btnGhost"
                onClick={() => setShowTrainingInfoEmail(false)}
              >
                Abbrechen
              </button>
              <button
                className="btn"
                disabled={trainingInfoEmailSending}
                onClick={async () => {
                  const spielerMitEmail = tSpielerIds
                    .map(id => spielerById.get(id))
                    .filter(s => s && s.kontaktEmail && !trainingInfoExcluded.includes(s.id)) as Spieler[];

                  if (spielerMitEmail.length === 0) {
                    alert("Keine Spieler mit E-Mail-Adresse gefunden.");
                    return;
                  }

                  setTrainingInfoEmailSending(true);

                  let erfolgreich = 0;
                  let fehler = 0;

                  const alleTeilnehmer = tSpielerIds
                    .map(id => spielerById.get(id))
                    .filter(Boolean) as Spieler[];

                  const pdfSources: { url: string; filename: string }[] = [];
                  if (trainingInfoIncludeProbe) pdfSources.push({ url: "/pdf/Probemitgliedschaft Jugend.pdf", filename: "Probemitgliedschaft Jugend.pdf" });
                  if (trainingInfoIncludeMitglied) pdfSources.push({ url: "/pdf/Aufnahme Jugend.pdf", filename: "Aufnahme Jugend.pdf" });
                  if (trainingInfoIncludeErwachsene) pdfSources.push({ url: "/pdf/Aufnahme Erwachsene.pdf", filename: "Aufnahme Erwachsene.pdf" });
                  if (trainingInfoIncludeBeitragsordnung) pdfSources.push({ url: "/pdf/Beitragsordnung.pdf", filename: "Beitragsordnung.pdf" });

                  const attachments: { filename: string; content: string; encoding: string; contentType: string }[] = [];
                  try {
                    for (const src of pdfSources) {
                      const r = await fetch(encodeURI(src.url));
                      if (!r.ok) throw new Error(`PDF nicht gefunden: ${src.filename}`);
                      const blob = await r.blob();
                      const base64 = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const result = reader.result as string;
                          resolve(result.split(",")[1] || "");
                        };
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(blob);
                      });
                      attachments.push({ filename: src.filename, content: base64, encoding: "base64", contentType: "application/pdf" });
                    }
                  } catch (err) {
                    alert(`Fehler beim Laden der PDF-Anhänge: ${err instanceof Error ? err.message : "Unbekannter Fehler"}`);
                    setTrainingInfoEmailSending(false);
                    return;
                  }

                  try {
                    for (const spieler of spielerMitEmail) {
                      const spielerName = spieler.vorname;
                      const andereTeilnehmer = alleTeilnehmer
                        .filter(s => s.id !== spieler.id)
                        .map(s => s.vorname)
                        .join(", ") || "Einzeltraining";

                      const personalizedBody = trainingInfoEmailBody
                        .replace("{SPIELERNAME}", spielerName)
                        .replace("{ANDERE_TEILNEHMER}", andereTeilnehmer);

                      const recipients = [spieler.kontaktEmail, ...(spieler.zusaetzlicheEmails || [])].filter(Boolean);

                      const resp = await fetch("/api/send-newsletter", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          to: recipients,
                          subject: trainingInfoEmailSubject,
                          body: personalizedBody + `\n\nSportliche Grüße\nTennisschule A bis Z\n${tAnlage === "Britz" ? "Standort Britz · TC Blau-Weiß Britz" : "Standort Wedding · BSC Rehberge"}`,
                          html: `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:0;background:#f9fafb;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;padding:32px 0;"><tr><td align="center"><table width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#ffffff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.06);"><tr><td style="padding:32px 40px;color:#1a1a1a;font-size:15px;line-height:1.7;">${personalizedBody.replace(/\n/g, "<br>")}</td></tr><tr><td style="background-color:#f8faf8;padding:24px 40px;border-top:1px solid #e5e7eb;"><p style="margin:0 0 4px;color:#333;font-size:14px;font-weight:600;">Sportliche Grüße</p><p style="margin:0;color:#1b471b;font-size:15px;font-weight:700;">Tennisschule A bis Z</p><p style="margin:12px 0 0;color:#999;font-size:12px;">${tAnlage === "Britz" ? "Standort Britz · TC Blau-Weiß Britz" : "Standort Wedding · BSC Rehberge"}</p><img src="${window.location.origin}/logo.png" alt="Tennisschule A bis Z" style="width:140px;height:auto;border-radius:8px;margin-top:16px;" /></td></tr></table></td></tr></table></body></html>`,
                          fromName: "Tennisschule A bis Z",
                          attachments: attachments.length > 0 ? attachments : undefined,
                        }),
                      });

                      const result = await resp.json();

                      if (resp.ok && result.success) {
                        erfolgreich++;
                      } else {
                        fehler++;
                      }
                    }

                    if (fehler === 0) {
                      alert(`E-Mail erfolgreich an ${erfolgreich} Spieler gesendet!`);
                    } else {
                      alert(`${erfolgreich} E-Mails gesendet, ${fehler} fehlgeschlagen.`);
                    }
                    setShowTrainingInfoEmail(false);
                  } catch (err) {
                    alert(`Fehler beim Senden: ${err instanceof Error ? err.message : "Unbekannter Fehler"}`);
                  } finally {
                    setTrainingInfoEmailSending(false);
                  }
                }}
              >
                {trainingInfoEmailSending ? "Sende..." : "E-Mail senden"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spieler-Übernahme Bestätigungs-Dialog */}
      {showAdoptConfirmDialog && (() => {
        const selectedReqs = registrationRequests.filter(r => selectedRequestIds.has(r.id));
        return (
          <div className="modalOverlay" onClick={() => setShowAdoptConfirmDialog(false)}>
            <div
              className="modalCard"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Spieler übernehmen</h2>
                <button
                  onClick={() => setShowAdoptConfirmDialog(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "var(--text-muted)"
                  }}
                >
                  ✕
                </button>
              </div>
              <p style={{ marginBottom: 16 }}>
                Die folgenden {selectedReqs.length} Anmeldungen werden als Spieler übernommen:
              </p>
              <div style={{ maxHeight: 400, overflowY: "auto", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ textAlign: "left", padding: "8px 4px" }}>Spieler/in</th>
                      <th style={{ textAlign: "left", padding: "8px 4px" }}>Kontakt</th>
                      <th style={{ textAlign: "left", padding: "8px 4px" }}>E-Mail</th>
                      <th style={{ textAlign: "left", padding: "8px 4px" }}>Telefon</th>
                      <th style={{ textAlign: "left", padding: "8px 4px" }}>Anlage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReqs.map((req) => {
                      const traineeName = getRegistrationTraineeName(req);
                      const kontaktName = getRegistrationKontaktName(req);
                      const abweichend = isRegistrationAbweichend(req);
                      return (
                        <tr key={req.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "8px 4px" }}>{traineeName}</td>
                          <td style={{ padding: "8px 4px", fontSize: 12 }}>{abweichend ? kontaktName : "—"}</td>
                          <td style={{ padding: "8px 4px", fontSize: 12 }}>{req.email}</td>
                          <td style={{ padding: "8px 4px" }}>{req.telefon || "-"}</td>
                          <td style={{ padding: "8px 4px" }}>
                            {req.anlage && (
                              <span style={{
                                fontSize: 11,
                                fontWeight: 600,
                                background: req.anlage === "Britz" ? "var(--warning)" : "var(--primary)",
                                color: req.anlage === "Britz" ? "#000" : "#fff",
                                padding: "2px 6px",
                                borderRadius: 4
                              }}>
                                {req.anlage}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="muted" style={{ fontSize: 12, marginBottom: 16 }}>
                Es werden Name, E-Mail, Telefon und Anlage (als Label) übernommen. Nachrichten werden nicht übernommen.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  className="btn btnGhost"
                  onClick={() => setShowAdoptConfirmDialog(false)}
                >
                  Abbrechen
                </button>
                <button
                  className="btn"
                  style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                  onClick={adoptMultiplePlayersFromRequests}
                >
                  {selectedReqs.length} Spieler übernehmen
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Kontaktbuch CSV Import Modal */}
      {showKontaktbuchModal && (
        <div className="modalOverlay" onClick={() => setShowKontaktbuchModal(false)}>
          <div
            className="modalCard"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 1000, maxHeight: "90vh", overflow: "auto" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>Kontaktbuch importieren</h2>
              <button
                onClick={() => setShowKontaktbuchModal(false)}
                style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#666" }}
              >
                ×
              </button>
            </div>
            {(() => {
              const matchedCount = kontaktbuchRows.filter((r) => {
                const fullLower = `${r.vorname} ${r.nachname}`.toLowerCase().trim();
                return spieler.some((s) => `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullLower);
              }).length;
              const issuesCount = kontaktbuchRows.filter((r) => r.issues.length > 0).length;
              return (
                <div className="row" style={{ marginBottom: 12, gap: 8 }}>
                  <span className="pill">Zeilen: <strong>{kontaktbuchRows.length}</strong></span>
                  <span className="pill">Im System: <strong>{matchedCount}</strong></span>
                  <span className="pill">Neu (nicht gefunden): <strong>{kontaktbuchRows.length - matchedCount}</strong></span>
                  <span className="pill" style={{ background: issuesCount > 0 ? "#fef3c7" : undefined }}>Probleme: <strong>{issuesCount}</strong></span>
                  <span className="pill">Ausgewählt: <strong>{kontaktbuchSelected.size}</strong></span>
                </div>
              );
            })()}
            <div className="row" style={{ marginBottom: 8, gap: 8 }}>
              <button
                className="btn btnGhost"
                onClick={() => setKontaktbuchSelected(new Set(kontaktbuchRows.map((_, i) => i)))}
              >
                Alle auswählen
              </button>
              <button
                className="btn btnGhost"
                onClick={() => setKontaktbuchSelected(new Set())}
              >
                Auswahl aufheben
              </button>
              <button
                className="btn btnGhost"
                onClick={() => {
                  const sel = new Set<number>();
                  kontaktbuchRows.forEach((r, i) => {
                    if (r.iban && !r.issues.some((x) => x.includes("Gläubiger-ID") || x.includes("ungültig"))) {
                      sel.add(i);
                    }
                  });
                  setKontaktbuchSelected(sel);
                }}
              >
                Nur saubere
              </button>
            </div>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, maxHeight: 500, overflow: "auto", marginBottom: 16 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f5f5f5", position: "sticky", top: 0 }}>
                    <th style={{ padding: 8, textAlign: "left", width: 40 }}></th>
                    <th style={{ padding: 8, textAlign: "left" }}>Name</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Match</th>
                    <th style={{ padding: 8, textAlign: "left" }}>IBAN</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Mandat</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Datum</th>
                    <th style={{ padding: 8, textAlign: "left" }}>E-Mail</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Telefon</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Adresse</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Empfänger</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Anlage</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Probleme</th>
                  </tr>
                </thead>
                <tbody>
                  {kontaktbuchRows.map((r, i) => {
                    const fullLower = `${r.vorname} ${r.nachname}`.toLowerCase().trim();
                    const found = spieler.find((s) => `${s.vorname} ${s.nachname || ""}`.toLowerCase().trim() === fullLower);
                    const isCritical = r.issues.some((x) => x.includes("Gläubiger-ID") || x.includes("ungültig"));
                    return (
                      <tr key={i} style={{ borderTop: "1px solid #eee", background: isCritical ? "#fef3c7" : undefined }}>
                        <td style={{ padding: 8 }}>
                          <input
                            type="checkbox"
                            checked={kontaktbuchSelected.has(i)}
                            onChange={(e) => {
                              setKontaktbuchSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(i); else next.delete(i);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td style={{ padding: 8 }}>{r.name}</td>
                        <td style={{ padding: 8 }}>
                          {found ? (
                            <span style={{ color: "#16a34a" }}>✓ aktualisieren</span>
                          ) : (
                            <span style={{ color: "#dc2626" }}>+ neu anlegen</span>
                          )}
                        </td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{r.iban || "-"}</td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{r.mandatsreferenz || "-"}</td>
                        <td style={{ padding: 8 }}>{r.unterschriftsdatum || "-"}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{r.email || "-"}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{r.telefon || "-"}</td>
                        <td style={{ padding: 8, fontSize: 11, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.adresse}>{r.adresse || "-"}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{r.empfaenger || "-"}</td>
                        <td style={{ padding: 8 }}>{r.anlage || "-"}</td>
                        <td style={{ padding: 8, color: "#92400e", fontSize: 11 }}>
                          {r.issues.join("; ") || ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modalActions">
              <button className="btn btnGhost" onClick={() => setShowKontaktbuchModal(false)}>
                Abbrechen
              </button>
              <button
                className="btn"
                disabled={kontaktbuchSelected.size === 0}
                onClick={applyKontaktbuchImport}
              >
                {kontaktbuchSelected.size} Spieler importieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank-Umsätze Import Modal */}
      <BankImportModal
        open={showBankImportModal}
        onClose={() => setShowBankImportModal(false)}
        abrechnungMonat={abrechnungMonat}
        spielerRows={abrechnung.spielerRows.map((r) => ({ id: r.id, name: r.name, sum: r.sum }))}
        spielerList={spieler}
        getAdjustedSum={getAdjustedSum}
        getSumBarForSpieler={getSumBarForSpieler}
        payments={payments}
        onApply={(spielerIds) => {
          setPayments((prev) => {
            const next = { ...prev };
            for (const sid of spielerIds) {
              next[paymentKey(abrechnungMonat, sid)] = true;
            }
            return next;
          });
        }}
      />

      {/* SEPA-XML Export Modal */}
      {showSepaExportModal && (() => {
        const monatLabel = MONATE_DE(abrechnungMonat);
        const verwendungszweckTemplate = `Abbuchung Tennistraining ${monatLabel}, Rechnung nur auf Anfrage`;
        const items = Array.from(sepaExportSelection)
          .map((spielerId) => {
            const sp = spielerById.get(spielerId);
            const row = filteredSpielerRowsForMonth.find((r) => r.id === spielerId);
            if (!sp || !row) return null;
            const adjustedSum = getAdjustedSum(spielerId, row.sum);
            const sumBar = getSumBarForSpieler(spielerId);
            const betrag = round2(adjustedSum - sumBar);
            // Bei abweichendem Rechnungsempfänger (z.B. Eltern bei Kindern) muss der
            // Kontoinhaber als Dbtr im SEPA-XML stehen, sonst weist die Bank die LS zurück.
            const kontoinhaber = sp.abweichenderEmpfaenger && sp.empfaengerName?.trim()
              ? sp.empfaengerName.trim()
              : getFullName(sp);
            return {
              spielerId,
              name: kontoinhaber,
              spielerName: getFullName(sp),
              iban: sp.iban || "",
              mandatsreferenz: sp.mandatsreferenz || "",
              unterschriftsdatum: sp.unterschriftsdatum || "",
              sequenz: (sp.sepaSequenz ?? "RCUR") as SepaSequenz,
              lastschriftart: (sp.sepaLastschriftart ?? "CORE") as SepaLastschriftart,
              betrag,
              verwendungszweck: verwendungszweckTemplate,
              ready: !!(sp.iban && sp.mandatsreferenz && sp.unterschriftsdatum) && betrag > 0,
            };
          })
          .filter((x): x is NonNullable<typeof x> => x !== null);
        const validItems = items.filter((it) => it.ready);
        const totalAmount = validItems.reduce((acc, it) => acc + it.betrag, 0);

        return (
          <div className="modalOverlay" onClick={() => setShowSepaExportModal(false)}>
            <div
              className="modalCard"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 1100, maxHeight: "90vh", overflow: "auto" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>SEPA-Sammellastschrift exportieren</h2>
                <button
                  onClick={() => setShowSepaExportModal(false)}
                  style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#666" }}
                >
                  ×
                </button>
              </div>

              <div className="row" style={{ gap: 8, marginBottom: 12 }}>
                <span className="pill">Monat: <strong>{monatLabel}</strong></span>
                <span className="pill">Posten: <strong>{validItems.length}</strong> / {items.length}</span>
                <span className="pill">Summe: <strong>{euro(totalAmount)}</strong></span>
                <span className="pill">Gläubiger-ID: <strong>{GLAEUBIGER_ID}</strong></span>
              </div>

              <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
                Verwendungszweck: <em>„{verwendungszweckTemplate}"</em>
              </div>

              <div style={{ border: "1px solid #ddd", borderRadius: 8, maxHeight: 480, overflow: "auto", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5", position: "sticky", top: 0 }}>
                      <th style={{ padding: 8, textAlign: "left" }}>Name</th>
                      <th style={{ padding: 8, textAlign: "left" }}>IBAN</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Mandat</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Datum</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Seq</th>
                      <th style={{ padding: 8, textAlign: "right" }}>Betrag</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.spielerId} style={{ borderTop: "1px solid #eee", background: it.ready ? undefined : "#fef3c7" }}>
                        <td style={{ padding: 8 }}>
                          {it.name}
                          {it.name !== it.spielerName && (
                            <div className="muted" style={{ fontSize: 11 }}>für {it.spielerName}</div>
                          )}
                        </td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{it.iban || "—"}</td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{it.mandatsreferenz || "—"}</td>
                        <td style={{ padding: 8 }}>{it.unterschriftsdatum || "—"}</td>
                        <td style={{ padding: 8 }}>{it.sequenz}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>{euro(it.betrag)}</td>
                        <td style={{ padding: 8 }}>
                          {it.ready ? (
                            <span style={{ color: "#16a34a" }}>✓ ok</span>
                          ) : (
                            <span style={{ color: "#dc2626" }}>
                              {!it.iban ? "IBAN fehlt" : !it.mandatsreferenz ? "Mandat fehlt" : !it.unterschriftsdatum ? "Datum fehlt" : it.betrag <= 0 ? "Betrag 0" : "?"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="modalActions">
                <button className="btn btnGhost" onClick={() => setShowSepaExportModal(false)}>
                  Abbrechen
                </button>
                <button
                  className="btn"
                  disabled={validItems.length === 0}
                  onClick={() => {
                    const xml = generateSepaXml(validItems);
                    const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `SEPA_Sammellastschrift_${abrechnungMonat}.xml`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);

                    // Markiere automatisch als "abgebucht"
                    setWirdAbgebucht((prev) => {
                      const next = { ...prev };
                      validItems.forEach((it) => {
                        next[`${abrechnungMonat}__${it.spielerId}`] = true;
                      });
                      return next;
                    });

                    setShowSepaExportModal(false);
                    setSepaExportSelection(new Set());
                  }}
                >
                  XML herunterladen ({validItems.length} Posten / {euro(totalAmount)})
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tenniscamp: SEPA-XML für Teilnehmer */}
      {campSepaModal && (() => {
        const rows = campSepaModal.rows;
        const verwendungszweck = `Tenniscamp ${campSepaModal.titel}`.trim();
        const isReady = (r: CampSepaRow) =>
          !!(r.iban && r.mandatsreferenz && r.unterschriftsdatum) && r.betrag > 0;
        const validRows = rows.filter(isReady);
        const totalAmount = validRows.reduce((acc, r) => acc + r.betrag, 0);
        return (
          <div className="modalOverlay" onClick={() => setCampSepaModal(null)}>
            <div
              className="modalCard"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 1000, maxHeight: "90vh", overflow: "auto" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>SEPA-XML – Tenniscamp</h2>
                <button
                  onClick={() => setCampSepaModal(null)}
                  style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#666" }}
                >
                  ×
                </button>
              </div>

              <div className="row" style={{ gap: 8, marginBottom: 12 }}>
                <span className="pill">Teilnehmer: <strong>{validRows.length}</strong> / {rows.length}</span>
                <span className="pill">Summe: <strong>{euro(totalAmount)}</strong></span>
                <span className="pill">Gläubiger-ID: <strong>{GLAEUBIGER_ID}</strong></span>
              </div>

              <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
                Verwendungszweck: <em>„{verwendungszweck}"</em>
              </div>

              <div style={{ border: "1px solid #ddd", borderRadius: 8, maxHeight: 480, overflow: "auto", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5", position: "sticky", top: 0 }}>
                      <th style={{ padding: 8, textAlign: "left" }}>Name</th>
                      <th style={{ padding: 8, textAlign: "left" }}>IBAN</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Mandat</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Datum</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Seq</th>
                      <th style={{ padding: 8, textAlign: "right" }}>Betrag</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={r.spielerId} style={{ borderTop: "1px solid #eee", background: isReady(r) ? undefined : "#fef3c7" }}>
                        <td style={{ padding: 8 }}>
                          {r.name}
                          {r.name !== r.spielerName && (
                            <div className="muted" style={{ fontSize: 11 }}>für {r.spielerName}</div>
                          )}
                        </td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{r.iban || "—"}</td>
                        <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{r.mandatsreferenz || "—"}</td>
                        <td style={{ padding: 8 }}>{r.unterschriftsdatum || "—"}</td>
                        <td style={{ padding: 8 }}>{r.sequenz}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={r.betrag}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              const betrag = Number.isFinite(n) && n >= 0 ? n : 0;
                              setCampSepaModal((m) =>
                                m ? { ...m, rows: m.rows.map((rr, i) => (i === idx ? { ...rr, betrag } : rr)) } : m
                              );
                            }}
                            style={{ width: 90, textAlign: "right" }}
                          />
                        </td>
                        <td style={{ padding: 8 }}>
                          {isReady(r) ? (
                            <span style={{ color: "#16a34a" }}>✓ ok</span>
                          ) : (
                            <span style={{ color: "#dc2626" }}>
                              {!r.iban ? "IBAN fehlt" : !r.mandatsreferenz ? "Mandat fehlt" : !r.unterschriftsdatum ? "Datum fehlt" : r.betrag <= 0 ? "Betrag 0" : "?"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="modalActions">
                <button className="btn btnGhost" onClick={() => setCampSepaModal(null)}>
                  Abbrechen
                </button>
                <button
                  className="btn"
                  disabled={validRows.length === 0}
                  onClick={() => {
                    const items = validRows.map((r) => ({
                      spielerId: r.spielerId,
                      name: r.name,
                      iban: r.iban,
                      mandatsreferenz: r.mandatsreferenz,
                      unterschriftsdatum: r.unterschriftsdatum,
                      sequenz: r.sequenz,
                      lastschriftart: r.lastschriftart,
                      betrag: r.betrag,
                      verwendungszweck,
                    }));
                    const xml = generateSepaXml(items);
                    const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `SEPA_Tenniscamp_${todayISO()}.xml`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setCampSepaModal(null);
                  }}
                >
                  XML herunterladen ({validRows.length} Posten / {euro(totalAmount)})
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Undo Toast */}
      {undoSnapshot && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1f2937",
            color: "white",
            padding: "12px 20px",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            zIndex: 9999,
            fontSize: 14,
          }}
        >
          <span>{undoSnapshot.message}</span>
          <button
            onClick={undo}
            style={{
              background: "#3b82f6",
              color: "white",
              border: "none",
              padding: "6px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Rückgängig
          </button>
          <button
            onClick={() => setUndoSnapshot(null)}
            style={{
              background: "transparent",
              color: "#9ca3af",
              border: "none",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}